/** Modelul editabil al unui tronson: ce se mișcă atunci când tragi de ceva.
 *
 *  PROBLEMA CENTRALĂ, și de ce nu se rezolvă prin vârfuri comune.
 *
 *  Într-un editor ca Homestyler tragi de un perete și camerele de-o parte și de
 *  alta îl urmează. Soluția evidentă e să legi vârfurile care coincid și să
 *  muți grupul. Aici NU MERGE, fiindcă în documentele noastre vârfurile nu
 *  coincid: peretele W3 al lui M2 are fața la x = −0.003 și bucătăria de lângă
 *  el are fața la x = 0 — trei milimetri între ele —, iar capetele lor pe
 *  cealaltă axă sunt cu totul altele (peretele merge de la −7.504 la −4.054,
 *  camera de la −7.2 la −2.95). Sunt două transcrieri ale aceleiași planșe, nu
 *  o construcție cu noduri comune.
 *
 *  CE AU ÎN SCHIMB: sunt pe aceeași LINIE. Deci se leagă pe linie, nu pe vârf.
 *  Când un perete se mută cu δ perpendicular pe el, se caută toate vârfurile —
 *  din pereți ȘI din camere — care stau pe una din cele două fețe ale lui, în
 *  dreptul lui, și se mută cu același δ. Camera din stânga se strânge, cea din
 *  dreapta crește, exact ca la mutarea unui zid adevărat.
 *
 *  TOATE COORDONATELE RĂMÂN AFINE. Un vârf e `{a, k}`: `a` e poziția la front
 *  nominal, `k` e cât se mișcă la întindere. Editorul mută `a` și NU atinge `k`
 *  — elasticitatea modulului e o proprietate a lui, decisă când a fost desenat,
 *  nu ceva ce se schimbă trăgând de un perete. Un editor care ar rescrie `k`
 *  ar putea face un modul să se întindă altfel decât declară `absorbers`, iar
 *  proba aia se dă abia la încărcarea catalogului.
 *
 *  NIMIC DERIVAT NU SE CALCULEAZĂ AICI. Ariile, `useful_m2`, `alpha` și
 *  amprenta le pune serverul, la salvare (`layout/tronson_edit.py`). Ce arată
 *  editorul între timp — aria unei camere sub cursor — e o socoteală de afișat,
 *  nu una care se salvează; de-aia stă în `arieAfisata` și nu în document.
 */
import type { Afin, Balama, CameraDoc, ElectricDoc, GolDoc, ModelUsa, PereteDoc, PiesaDoc,
              Pt, StratDoc, TronsonDoc, VarfAfin } from "../doc";
import { anvelopa, arcDinAx, deriva, golulPentruLumina, H_NIVEL, H_PARAPET,
         inInel as inPlic, LATIME_GOL, LUMINA_MIN, pePeretele, TOC_IMPLICIT,
         TOC_INTRARE } from "../doc";
import { MOBILABILE, mobileazaCamera as asezareaNoua } from "../design/mobilare";
import { pardoseala, pereteleCamerei } from "../design/finisaje";
import {
  aparatulEAlCamerei, asezareaLangaPiesa, caPiesa, desfaSuprapunerile, inGol,
  pePeretDeBeton, type GolSimplu, type ZidMaterial,
} from "../design/langaMobila";
import { aparatul, areIndice, familiaInDoza } from "../design/electric";
import { FEL_IMPLICIT } from "../feluri";
import { cutiaScarii, intinderileScarii, partileScarii, podestulLungeste }
  from "../../plan/ScaraDesen";
import { capeteleFetei, cheiaCotei, cotelePlanului } from "../cote";
import type { Polygon as PoligonTaiat } from "polygon-clipping";
import { lipesteUnghiul, SNAP_MAX } from "./aliniere";
import { FELURI_STRAT } from "../design/straturi";
import { inAceeasiBucata } from "./camere";
import { instalatiaCamerei } from "../design/auto";
import { asezareaCamerei, cadruCamerei, felulPiesei, MOBILATE_PLAN, rangDesen }
  from "../../plan/arhMobilierPlan";
import { scauneleMesei } from "../design/masaCuScaune";
import { contratrepteDinLungime, lungimeDinContratrepte,
         lungimeaApropiata, lungimileScarii, OCHI_MIN, TREPTE_MAX,
         treaptaNivelului, contratreptePeRampe,
         rampaUrmatoare, PRESETURI_SCARA, felulScarii, formaScarii,
         imparteContratrepte, potrivesteBlondel,
         rampeleDinCutie, scaraDinRampe,
         sensulImplicit, laturaDinVector,
         TREAPTA as TREAPTA_SCARA, type Latura } from "../../plan/scara";

/** Cât de aproape de o linie trebuie să cadă un vârf ca să fie AL EI.
 *
 *  2 cm: mai mult decât slăbiciunea transcrierii (măsurat: până la 3 mm între
 *  fața unui perete și cea a camerei de lângă) și mai puțin decât cel mai
 *  subțire perete din catalog (10 cm), ca fețele opuse ale aceluiași zid să nu
 *  ajungă niciodată în aceeași linie. */
export const LIPIRE = 0.02;

/** Ce se poate selecta. Definit în `Desen.tsx`, fiindcă desenul e cel care
 *  produce selecția; aici se re-exportă ca modelul să nu ceară un al doilea
 *  import de la fiecare apelant. Două definiții structurale identice ar trece
 *  amândouă la typecheck și ar diverge la prima adăugare. */
export type { Ales } from "../Desen";
import type { Ales } from "../Desen";
import { taie } from "../../taietor";

/** O apucătoare de pe desen. `capat` mișcă un capăt al peretelui, `mijloc` îl
 *  mută întreg — cele două gesturi din Homestyler. */
export type Manere = {
  fel: "capat" | "mijloc";
  p: Pt;
  /** Direcția în care are voie să se miște, ca versor. Un perete se mută
   *  perpendicular pe el; un capăt se mișcă pe lungul lui. */
  dir: Pt;
};

const clona = <T,>(x: T): T => JSON.parse(JSON.stringify(x)) as T;

export const val = (z: { a: number } | number): number =>
  typeof z === "number" ? z : z.a;

/** Cadrul de încadrare al unui inel. */
export function cadru(ring: readonly Pt[]) {
  const xs = ring.map((p) => p[0]);
  const ys = ring.map((p) => p[1]);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}

/** Aria poligonului — formula lui Gauss. DOAR DE AFIȘAT: cea care se salvează
 *  o calculează serverul, ca să existe o singură implementare a ei. */
export function arieAfisata(ring: readonly Pt[]): number {
  let s = 0;
  for (let i = 0; i < ring.length; i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[(i + 1) % ring.length];
    s += x1 * y2 - x2 * y1;
  }
  return Math.abs(s) / 2;
}

const inelul = (poly: VarfAfin[]): Pt[] =>
  poly.map((q) => [val(q[0]), val(q[1])] as Pt);

/** Ce poligoane prinde o linie mutată.
 *
 *  `tot` — pereții ȘI camerele: mutarea unei linii trebuie să le prindă pe
 *  amândouă, altfel peretele pleacă și camera rămâne.
 *  `camere` — numai camerele, când zidurile pleacă pe altă cale (prin graf).
 *  `afara` — numai spațiile exterioare: singurele camere care NU se derivă din
 *  ziduri (v. `camere::refacute`), deci singurele care rămân pe loc când n-are
 *  cine să le miște. Un balcon își urmează parapetul așa, și numai așa.
 */
type CeSeMuta = "tot" | "camere" | "afara";

function poligoane(doc: TronsonDoc, ce: CeSeMuta = "tot"): VarfAfin[][] {
  const plan = doc.plan;
  if (!plan) return [];
  if (ce === "afara") {
    return (plan.rooms ?? []).filter((r) => r.kind === "balcony")
      .map((r) => r.poly);
  }
  return [
    ...(plan.rooms ?? []).map((r) => r.poly),
    // PRAGURILE merg cu camerele. Un prag stă pe conturul unei camere — pe
    // trecerea liberă dintre living și hol, de pildă. Lăsat pe loc când zidul
    // de lângă se mută, ar rămâne o linie prin mijlocul camerei, iar derivarea
    // ar tăia acolo o cameră care nu există.
    ...(plan.thresholds ?? []),
    ...(ce === "camere" ? [] : (plan.walls ?? []).map((w) => w.poly)),
  ];
}

/** Un perete e un dreptunghi subțire: pe ce axă e lung, și unde-i sunt fețele. */
export function osiaPeretelui(ring: readonly Pt[]) {
  const c = cadru(ring);
  /** `x` = peretele e lung pe orizontală, deci se mută pe verticală. */
  const lungPeX = c.w >= c.h;
  return {
    lungPeX,
    /** axa pe care se mută peretele când e împins */
    axa: (lungPeX ? 1 : 0) as 0 | 1,
    lungime: lungPeX ? c.w : c.h,
    grosime: lungPeX ? c.h : c.w,
    /** cele două fețe, ca valori pe axa de mutare */
    fete: lungPeX ? [c.y, c.y + c.h] : [c.x, c.x + c.w],
    /** întinderea peretelui pe axa lui lungă */
    span: lungPeX ? [c.x, c.x + c.w] : [c.y, c.y + c.h],
    c,
  };
}

/** MUTĂ O LINIE. Miezul editorului — v. nota din capul fișierului.
 *
 *  SE MUTĂ MUCHII ÎNTREGI, NU VÂRFURI. Și asta e diferența dintre un editor
 *  care merge și unul care strâmbă camerele.
 *
 *  Prima variantă muta orice vârf care cădea pe linie, în dreptul peretelui.
 *  Dar un perete acoperă doar o PARTE din latura camerei de lângă el: peretele
 *  W3 al lui M2 ține de la −7.504 la −4.054, iar bucătăria pe care o mărginește
 *  merge de la −7.2 la −2.95. Din cele două colțuri ale ei de pe linia aia,
 *  unul cădea în dreptul peretelui și celălalt nu — deci se muta unul singur, și
 *  bucătăria ieșea trapez. Măsurat: în loc să se strângă cu 0,5 × 4,25 m², se
 *  strângea cu jumătate și se apleca.
 *
 *  Deci se caută MUCHII: o latură a cărei ambele capete stau pe linie și care se
 *  suprapune, oricât de puțin, cu întinderea peretelui. Se mută amândouă
 *  capetele ei. O față se duce întreagă sau rămâne întreagă — un poligon nu se
 *  poate frânge dintr-un gest care mută un zid.
 */
export function mutaLinie(
  doc: TronsonDoc,
  { axa, valoare, span, delta, doarCamere = false, ce }: {
    axa: 0 | 1; valoare: number; span: [number, number]; delta: number;
    /** Numai camerele. Zidurile au capete cu nume, deci pentru ele graful e mai
     *  bun — v. nota din `mutaPeretele`. */
    doarCamere?: boolean;
    /** Mai strâmt de-atât: numai spațiile exterioare (v. `CeSeMuta`). */
    ce?: CeSeMuta;
  },
): TronsonDoc {
  const d = clona(doc);
  const alta = (1 - axa) as 0 | 1;
  const jos = Math.min(...span);
  const sus = Math.max(...span);
  const dd = Math.round(delta * 1000) / 1000;
  for (const poly of poligoane(d, ce ?? (doarCamere ? "camere" : "tot"))) {
    // Întâi se ALEG capetele de mutat, apoi se mută: mutate pe loc, muchia
    // următoare ar fi comparată cu o coordonată deja schimbată și n-ar mai fi
    // găsită pe linie.
    const demutat = new Set<number>();
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i];
      const b = poly[(i + 1) % poly.length];
      if (Math.abs(a[axa].a - valoare) > LIPIRE) continue;
      if (Math.abs(b[axa].a - valoare) > LIPIRE) continue;
      const m0 = Math.min(a[alta].a, b[alta].a);
      const m1 = Math.max(a[alta].a, b[alta].a);
      if (m1 < jos - LIPIRE || m0 > sus + LIPIRE) continue;   // nu se suprapun
      demutat.add(i);
      demutat.add((i + 1) % poly.length);
    }
    for (const i of demutat) {
      poly[i][axa].a = Math.round((poly[i][axa].a + dd) * 1000) / 1000;
    }
  }
  // COTELE DE MÂNĂ MERG CU LINIA, ca pragurile de mai sus.
  //
  // O cotă pusă de mână e o pereche de puncte SCRISE, nu una dedusă din desen:
  // nimic n-o mai mișca vreodată, fiindcă `plan.dims` se scria numai la pus și
  // la șters. Deci zidul pleca și cota rămânea în urmă, arătând măsura veche
  // lângă cea nouă — «dacă modifici cota, să nu ți-o lase și pe aia veche (...)
  // că acum rămâne și aia veche» (26.10.2026). Nu se năștea o cotă în plus: era
  // cea veche, înțepenită.
  //
  // UN CAPĂT SINGUR, nu o muchie: o cotă n-are laturi, are două puncte, deci
  // regula muchiei de mai sus se citește pe un punct — să stea PE linia care
  // pleacă, și în dreptul bucății care pleacă.
  //
  // NUMAI LA MUTAREA ÎNTREAGĂ: pe drumul „doar camerele" zidurile vin pe
  // urmele lor cu o a doua chemare (v. `schimbaPeretele`), iar cota ar fi
  // plecat de două ori.
  if ((ce ?? (doarCamere ? "camere" : "tot")) === "tot") {
    const prins = (cap: number[]) =>
      Math.abs(cap[axa] - valoare) <= LIPIRE
      && cap[alta] >= jos - LIPIRE && cap[alta] <= sus + LIPIRE;
    for (const c of d.plan?.dims ?? []) {
      // ÎNTREAGĂ SAU DELOC, spre deosebire de un poligon. Un poligon are voie
      // să iasă în trepte — chiar așa arată un zid mutat pe o bucată. O MĂSURĂ
      // nu: mutat un singur capăt, cota ar ieși oblică și ar scrie altă
      // lungime decât cea cerută, fără ca nimeni s-o fi cerut. Dacă numai o
      // parte din ce măsoară a plecat, cota nu mai e a nimănui și rămâne unde
      // e, ca omul să vadă că trebuie refăcută.
      if (!prins(c.a) || !prins(c.b)) continue;
      for (const cap of [c.a, c.b]) {
        cap[axa] = Math.round((cap[axa] + dd) * 1000) / 1000;
      }
    }
  }
  return d;
}

// ------------------------------------------ ce stă pe ziduri și merge cu ele ---
//
// ORICE MUTARE LĂSA GOLURILE ÎN URMĂ. `sterge` știa de mult că un gol ține de
// zidul lui — «o ușă rămasă fără zid n-are ce tăia, iar desenul i-ar arăta arcul
// plutind în gol» —, dar mutarea nu știa: `mutaPeretele`, `mutaCapatul` și
// `dubleaza` mutau poligoane și lăsau pe loc ușile, ferestrele și prizele. Se
// vedea din prima tragere: zidul pleacă, arcul ușii rămâne desenat în mijlocul
// camerei.
//
// Se repară o dată, aici, fiindcă altfel s-ar fi reparat de cinci ori și ar fi
// divergit la a șasea mutare adăugată.

/** Cât de departe de fața unui zid mai poate sta un aparat AL LUI. 6 cm: mai
 *  mult decât slăbiciunea cu care a fost lipit, și mai puțin decât cel mai
 *  subțire zid din catalog, ca un aparat de pe fața cealaltă a unei placări să
 *  nu fie luat drept al acestuia. */
const APARAT_PE_ZID = 0.06;

/** Cât poate depăși un aparat capătul zidului și să fie tot al lui. */
const APARAT_PESTE_CAPAT = 0.02;

/** Stă punctul pe una din fețele zidului, în dreptul lui?
 *
 *  Se măsoară pe cele două axe ale zidului separat — de-a lungul și de-a
 *  curmezișul. Cu o singură distanță până la segment, un aparat de pe zidul
 *  perpendicular de lângă colț ar fi ieșit «al acestuia»: proiecția lui cade pe
 *  capăt, iar de-acolo distanța e mică. */
function peFataZidului(p: Pt, w: PereteDoc): boolean {
  const [a, b] = axaPeretelui(w);
  const vx = b[0] - a[0];
  const vy = b[1] - a[1];
  const L = Math.hypot(vx, vy);
  if (L < 1e-9) return false;
  const ux = vx / L;
  const uy = vy / L;
  const dx = p[0] - a[0];
  const dy = p[1] - a[1];
  const dLung = dx * ux + dy * uy;                    // cât de departe pe zid
  const dLat = Math.abs(dx * -uy + dy * ux);          // cât de departe de ax
  return dLung >= -APARAT_PESTE_CAPAT && dLung <= L + APARAT_PESTE_CAPAT
    && dLat <= w.thickness_m / 2 + APARAT_PE_ZID;
}

/** GOLURILE ȘI APARATELE CARE STAU PE UN GRUP DE ZIDURI.
 *
 *  GOLURILE SE ȘTIU PE NUME: fiecare își scrie gazda în `wall`, deci nu e nimic
 *  de ghicit și nimic de nimerit cu toleranțe.
 *
 *  APARATELE SE ȘTIU PE GEOMETRIE, fiindcă nu-și scriu peretele — și nici n-ar
 *  avea de ce: o priză nu e o parte a zidului, e pusă pe fața lui (v.
 *  `lipitDeZid`). Atât se poate măsura, deci atât se măsoară.
 *
 *  Se cheamă ÎNAINTE de mutare: caută geometria de-acum, iar după ce zidul a
 *  plecat n-ar mai găsi nimic pe ea. */
export function ceStaPeZiduri(
  plan: NonNullable<TronsonDoc["plan"]>, pereti: Iterable<number>,
): { goluri: { fel: "doors" | "windows"; i: number }[]; aparate: number[] } {
  const ziduri = [...pereti]
    .map((j) => plan.walls?.[j])
    .filter((w): w is PereteDoc => !!w);
  const iduri = new Set(ziduri.map((w) => w.id));

  const goluri: { fel: "doors" | "windows"; i: number }[] = [];
  for (const fel of ["doors", "windows"] as const) {
    (plan[fel] ?? []).forEach((g, i) => {
      if (g.wall && iduri.has(g.wall)) goluri.push({ fel, i });
    });
  }

  const aparate: number[] = [];
  (plan.electrical ?? []).forEach((ap, i) => {
    if (ziduri.some((w) => peFataZidului([ap.x.a, ap.y.a], w))) aparate.push(i);
  });
  return { goluri, aparate };
}

/** Duce cu `dep` ce s-a găsit pe ziduri.
 *
 *  `gasite` vine din `ceStaPeZiduri` chemat pe geometria DE DINAINTE; `plan` e
 *  cel în care se scrie. Două plan-uri fiindcă zidurile s-au mutat deja când se
 *  ajunge aici, iar căutarea pe geometria nouă n-ar mai găsi nimic — pozițiile
 *  din liste sunt însă aceleași, deci indicii trec dintr-unul în altul. */
function duCeAmGasit(
  plan: NonNullable<TronsonDoc["plan"]>,
  gasite: ReturnType<typeof ceStaPeZiduri>, dep: Pt,
) {
  if (!dep[0] && !dep[1]) return;
  for (const g of gasite.goluri) {
    const o = plan[g.fel]![g.i];
    o.x = { ...o.x, a: r3(o.x.a + dep[0]) };
    o.y = { ...o.y, a: r3(o.y.a + dep[1]) };
  }
  for (const i of gasite.aparate) {
    const ap = plan.electrical![i];
    ap.x = { ...ap.x, a: r3(ap.x.a + dep[0]) };
    ap.y = { ...ap.y, a: r3(ap.y.a + dep[1]) };
  }
}

/** Copiază, decalat, ce s-a găsit pe niște ziduri — pentru `dubleaza`.
 *
 *  `gazdeNoi` leagă zidul vechi de copia lui. Fără ea, ușa copiei ar rămâne
 *  scrisă pe peretele originalului, iar cele două s-ar tăia una în alta la
 *  prima mutare. */
/** `dec` e o deplasare PE AMÂNDOUĂ AXELE, nu un singur număr: copia unui zid
 *  pleacă altfel pe axa pe care se mută decât pe cea lungă (v. `dubleaza`),
 *  iar golurile și aparatele trebuie să plece EXACT cât zidul lor — altfel
 *  ușa copiată rămâne lângă peretele copiat, nu în el. */
function copiazaCeStaPeZiduri(
  plan: NonNullable<TronsonDoc["plan"]>,
  gasite: ReturnType<typeof ceStaPeZiduri>, dec: readonly [number, number],
  gazdeNoi: Record<string, string>,
) {
  for (const g of gasite.goluri) {
    const o = clona(plan[g.fel]![g.i]);
    o.x = { ...o.x, a: r3(o.x.a + dec[0]) };
    o.y = { ...o.y, a: r3(o.y.a + dec[1]) };
    if (o.wall && gazdeNoi[o.wall]) o.wall = gazdeNoi[o.wall];
    plan[g.fel]!.push(o);
  }
  for (const i of gasite.aparate) {
    const ap = clona(plan.electrical![i]);
    ap.x = { ...ap.x, a: r3(ap.x.a + dec[0]) };
    ap.y = { ...ap.y, a: r3(ap.y.a + dec[1]) };
    plan.electrical!.push(ap);
  }
}

/** Împinge un perete perpendicular pe el, cu TOT ce ține de el.
 *
 *  Trei lucruri se mișcă, și toate trei trebuie:
 *
 *    * zidul însuși;
 *    * CAMERELE care se sprijină pe fețele lui — prin `mutaLinie`, fiindcă
 *      vârfurile lor nu coincid cu ale zidului (3 mm între ele) și singurul
 *      lucru pe care îl au în comun e linia;
 *    * ZIDURILE VECINE lipite de capetele lui — prin graf, ca să nu rămână
 *      colțul deschis. Vecinul își mută doar capătul comun, deci se lungește sau
 *      se rotește, exact ca în Homestyler.
 */
/** Toți pereții care alcătuiesc LINIA văzută de om, pornind de la unul.
 *
 *  Ce se vede ca un zid sunt aproape întotdeauna mai multe intrări în document:
 *  pe M1, 12 din cele 18 linii poartă între doi și cinci pereți, tăiați la
 *  fiecare gol și schimbând felul pe drum — o porțiune structurală continuată
 *  de una de compartimentare. Sunt entități diferite fiindcă au grosimi și
 *  materiale diferite, dar pentru cine trage de ele sunt UN zid.
 *
 *  De ce contează la mutare: prin graf, un vecin i se mișcă doar CAPĂTUL lipit,
 *  ca să se lungească. Bun pentru un zid perpendicular, care se întinde după
 *  cel mutat. Dezastruos pentru unul colinear — trasă lateral doar de un capăt,
 *  continuarea lui se apleca și intra peste zidul mutat. Ăsta era «se
 *  suprapun ciudat»: nu o suprapunere, o oblicire.
 *
 *  Deci: colinearii se mută ÎNTREGI, cu tot cu linie; perpendicularii se
 *  lungesc, ca până acum.
 *
 *  `tine` TAIE RUNUL MAI DEVREME, și e nevoie de el fiindcă nu toate gesturile
 *  vor același run. Pentru GEOMETRIE runul e întreg și trebuie să fie: un zid
 *  împins pe jumătate din lungimea lui se rupe în trepte. Pentru o ÎNSUȘIRE nu:
 *  o porțiune de beton continuată de una de zidărie e un singur zid pentru
 *  ochi, dar două lucruri diferite pentru grosime — iar Cătălin a dat exact
 *  peste asta: a pus 15 cm pe zidăria din continuarea unui perete de rezistență
 *  și i s-a subțiat și rezistența. Cine schimbă o însușire trimite deci un
 *  `tine` care se oprește la schimbarea de fel sau de material.
 */
export function runulLiniei(doc: TronsonDoc, i: number,
                            tine?: (w: PereteDoc, capul: PereteDoc) => boolean,
): Set<number> {
  const zid = doc.plan?.walls?.[i];
  const grup = new Set<number>();
  if (!zid) return grup;
  grup.add(i);
  const o = osiaPeretelui(inelul(zid.poly));
  const peLinie = (w: PereteDoc) => {
    const ax = axaPeretelui(w);
    return (ax[0][o.axa] + ax[1][o.axa]) / 2;
  };
  const linia = peLinie(zid);
  const noduri = nodurile(doc);
  for (let creste = true; creste;) {
    creste = false;
    for (const nd of noduri) {
      if (!nd.capete.some((c) => grup.has(c.perete))) continue;
      for (const c of nd.capete) {
        if (grup.has(c.perete)) continue;
        const w = doc.plan!.walls![c.perete];
        if (!w) continue;
        const ow = osiaPeretelui(inelul(w.poly));
        if (ow.axa !== o.axa) continue;               // perpendicular: se lungește
        // ACEEAȘI LINIE, nu doar paralel. Doi pereți alăturați — o placare pe
        // lângă o fațadă — sunt paraleli și se ating, dar nu sunt aceeași linie;
        // mutați împreună, s-ar mânca unul pe altul.
        //
        // Toleranța e diferența de grosime, nu suma: două porțiuni ale aceleiași
        // linii se aliniază pe o față, deci axele lor stau la |t1−t2|/2 una de
        // alta. Cu suma (0.30 pentru două ziduri de 0.30) runul se schimba DUPĂ
        // mutare — pe M2, W23 intra în el abia la întoarcere, și atunci dus-întors
        // nu mai dădea documentul de la început.
        if (Math.abs(peLinie(w) - linia)
            > Math.abs(o.grosime - ow.grosime) / 2 + LIPIRE) continue;
        if (tine && !tine(w, zid)) continue;
        grup.add(c.perete);
        creste = true;
      }
    }
  }
  return grup;
}

/** CAPETELE REZEMATE ÎN CORPUL UNUI ZID DIN GRUP — îmbinările în T.
 *
 *  Un perete de compartimentare care nu ajunge la CAPĂTUL fațadei, ci intră în
 *  coasta ei, nu e în niciun nod cu ea: pe M1, capătul lui `W20` stă la
 *  (9,547; −0,004), adică la 1,10 m de cel mai apropiat capăt al zidurilor
 *  axului E. Deci graful nu-l vedea, iar axul tras îi lăsa un rost cât
 *  deplasarea, cu camera deschisă prin el.
 *
 *  ASTA A ÎNLOCUIT REGULA «ÎMBINĂRILE ÎN T NU URMEAZĂ», care ținea din 09.09
 *  dintr-o înregistrare Homestyler. Cerut de Stefan (17.09): «când trag de axul
 *  E trebuie lungit și peretele Partition · W20». Și e răspunsul drept: un
 *  capăt rezemat pe fața unui zid e acolo ca să ÎNCHIDĂ camera, iar zidul plecat
 *  de sub el lasă o gaură pe care nimeni n-a cerut-o.
 *
 *  REZEMAT ÎNSEAMNĂ ATINS, nu «prin preajmă». Pragul e `ROST_LIPIT` fiindcă
 *  atâta e: măsurate pe catalog toate cele 802 capete de perete față de corpul
 *  celui mai apropiat zid perpendicular, 256 cad la 0,000, două la 0,003
 *  (slăbiciunea transcrierii), iar următoarea distanță e 0,050 — un rost
 *  adevărat. Între 3 mm și 5 cm nu cade nimic, deci pragul nu e ales, e citit.
 *  Așa ies 88 de capete rezemate pe tot catalogul, pe lângă cele 164 din noduri.
 *
 *  Numai PERPENDICULARELE: un zid paralel rezemat cu capătul pe fața celui
 *  mutat ar fi doar suprapus peste el, iar tras de capăt s-ar strâmba. `duCapat`
 *  taie oricum ce nu se lungește, dar aici nici nu se pune problema. */
function capeteleRezemate(doc: TronsonDoc,
                          grup: ReadonlySet<number>): Capat[] {
  const ziduri = doc.plan?.walls ?? [];
  const corpuri: { axa: 0 | 1; fata0: number; fata1: number;
                   jos: number; sus: number }[] = [];
  for (const j of grup) {
    const w = ziduri[j];
    if (!w) continue;
    const o = osiaPeretelui(inelul(w.poly));
    corpuri.push({ axa: o.axa,
                   fata0: Math.min(...o.fete), fata1: Math.max(...o.fete),
                   jos: Math.min(...o.span), sus: Math.max(...o.span) });
  }
  const out: Capat[] = [];
  ziduri.forEach((w, perete) => {
    if (grup.has(perete) || !w.axis) return;
    const aw = osiaPeretelui(inelul(w.poly)).axa;
    const ax = axaPeretelui(w);
    ([0, 1] as const).forEach((capat) => {
      const p = ax[capat];
      const rezemat = corpuri.some((c) => {
        if (c.axa === aw) return false;                      // paralel: nu e T
        const alta = (1 - c.axa) as 0 | 1;
        return p[c.axa] >= c.fata0 - ROST_LIPIT
          && p[c.axa] <= c.fata1 + ROST_LIPIT
          && p[alta] >= c.jos - ROST_LIPIT
          && p[alta] <= c.sus + ROST_LIPIT;
      });
      if (rezemat) out.push({ perete, capat });
    });
  });
  return out;
}

/** LUNGEȘTE VECINII ZIDULUI MUTAT — scrie pe `rez`.
 *
 *  Vecinii nu se translatează niciodată: li se mută doar capătul lipit de grup,
 *  deci se lungesc sau se scurtează, iar colțul rămâne închis. Cine e lipit se
 *  citește în două feluri, și amândouă trebuie:
 *
 *    * NODURILE — capătul vecinului la capătul zidului mutat (colțul în L);
 *    * CAPETELE REZEMATE — capătul vecinului în coasta lui (îmbinarea în T,
 *      v. `capeteleRezemate`).
 *
 *  `doc` e documentul de DINAINTE de mutare: acolo se citesc legăturile, ca
 *  fețele să fie încă unde le-a lăsat desenul. `rez` e cel pe care se scrie. */
function lungesteVecinii(rez: TronsonDoc, doc: TronsonDoc,
                         grup: ReadonlySet<number>, dep: Pt,
                         fara?: ReadonlySet<number>): Set<number> {
  const vazute = new Set<string>();
  /** Zidurile cărora chiar li s-a mutat un capăt — deci care și-au schimbat
   *  lungimea. Le cere `golurileUrmeazaLiniile`. */
  const atinse = new Set<number>();

  // UN COLȚ DEJA ÎNCHIS NU SE MAI MIȘCĂ — bucățile rămase din ACEEAȘI LINIE.
  //
  // De când se poate trage numai o BUCATĂ dintr-o linie (v.
  // `imparteZidulLaCamera`), zidul rămâne cu un prag, iar compartimentarea
  // care stă chiar în dreptul pragului se reazemă pe amândouă bucățile: și pe
  // cea dusă, și pe cea rămasă. Trasă după cea dusă, ea se scurta și lăsa o
  // crestătură cât pragul pe grosimea ei, în camera de dincolo — măsurat pe
  // M1: `W21` tras odată cu jumătatea de jos a lui `W17` scotea 0,045 m²
  // (0,30 × 0,15) din camera de deasupra, care n-are nicio treabă cu gestul.
  //
  // Dar capătul ei nu avea de ce să plece: fața pe care se sprijinea e tot
  // acolo, pe bucata rămasă. Colțul e închis, deci zidul stă.
  //
  // NUMAI BUCĂȚILE ACELEIAȘI LINII, nu orice zid din preajmă: un capăt prins
  // într-un colț, între fațada care pleacă și întoarcerea ei, trebuie să se
  // lungească mai departe — acolo chiar se deschide camera.
  const ziduri = doc.plan?.walls ?? [];
  const osiiGrup = [...grup].map((k) => ziduri[k])
    .filter((w): w is PereteDoc => !!w)
    .map((w) => ({ w, o: osiaPeretelui(inelul(w.poly)) }));
  const ramase = ziduri.flatMap((w, j) => {
    if (grup.has(j) || !w) return [];
    const ow = osiaPeretelui(inelul(w.poly));
    const peLinie = osiiGrup.some(({ o, w: g }) => o.axa === ow.axa
      && Math.abs((axaPeretelui(g)[0][o.axa] + axaPeretelui(g)[1][o.axa]) / 2
                - (axaPeretelui(w)[0][o.axa] + axaPeretelui(w)[1][o.axa]) / 2)
         <= Math.abs(o.grosime - ow.grosime) / 2 + LIPIRE);
    return peLinie ? [cadru(inelul(w.poly))] : [];
  });
  const sprijinRamas = (q: Pt) => ramase.some((c) =>
    q[0] >= c.x - ROST_LIPIT && q[0] <= c.x + c.w + ROST_LIPIT
    && q[1] >= c.y - ROST_LIPIT && q[1] <= c.y + c.h + ROST_LIPIT);

  const duCapat = (c: Capat) => {
    if (grup.has(c.perete)) return;      // s-au mutat deja, întregi
    // ZIDURILE OPRITE NU SE CLINTESC NICI DIN GRAF — v. `fara`.
    if (fara?.has(c.perete)) return;
    // ...DAR NUMAI DACĂ ASTA NU LASĂ UN CAPĂT ÎN AER.
    //
    // Colțul e închis pe bucata rămasă; treapta dinspre bucata DUSĂ nu e
    // închisă de nimeni. Măsurat pe planul importat, trăgând fiecare zid din
    // fiecare cameră: 115 capete din 313 de gesturi rămâneau fără niciun
    // vecin — chiar «pereții nu se conectează corect, deși fac parte din
    // aceeași cameră» (Bianca, 12.10.2026). Compartimentarea din dreptul
    // pragului e singurul lucru care POATE să închidă treapta: ea stă chiar pe
    // linia tăieturii, cu un capăt pe amândouă bucățile.
    //
    // Deci se desparte după ce i se întâmplă vecinului:
    //   · SE LUNGEȘTE (bucata dusă pleacă de lângă el) ⇒ se trage după ea și
    //     închide treapta. Nu ia nimic din camera de dincolo: capătul lui
    //     celălalt stă, iar partea nouă e chiar pragul.
    //   · SE SCURTEAZĂ (bucata dusă vine peste el) ⇒ rămâne, ca până acum.
    //     Ăsta e cazul pentru care a fost scrisă garda: `W21` tras odată cu
    //     jumătatea de jos a lui `W17` scotea 0,045 m² din camera de deasupra,
    //     care n-are nicio treabă cu gestul.
    if (ramase.length
        && sprijinRamas(axaPeretelui(doc.plan!.walls![c.perete])[c.capat])) {
      const ax0 = axaPeretelui(doc.plan!.walls![c.perete]);
      const fix = ax0[1 - c.capat];
      const acum = Math.hypot(ax0[c.capat][0] - fix[0], ax0[c.capat][1] - fix[1]);
      const dupa = Math.hypot(ax0[c.capat][0] + dep[0] - fix[0],
                              ax0[c.capat][1] + dep[1] - fix[1]);
      if (dupa <= acum + 1e-9) return;
    }
    // UN VECIN SE TRAGE DE CAPĂT DOAR DACĂ ASTA ÎL LUNGEȘTE, NU DACĂ ÎL STRÂMBĂ.
    // Capătul plecat cu `dep` lasă direcția neatinsă numai când `dep` cade pe
    // lungul vecinului — atunci zidul doar crește sau scade, și colțul rămâne
    // închis. Dacă `dep` are și o parte de-a latul, capătul tras rotește zidul:
    // un vecin PARALEL cu cel mutat (deci de-a latul drumului) ar ieși oblic
    // prin cameră. Acolo preferăm un rost deschis unui perete strâmb — el stă
    // pe altă linie, nu e colțul nimănui.
    const [pa, pb] = axaPeretelui(rez.plan!.walls![c.perete]);
    const ux = pb[0] - pa[0], uy = pb[1] - pa[1];
    const L = Math.hypot(ux, uy);
    // PRAGUL E ÎN MILIMETRI DE DESEN, NU ÎN ZEROURI DE VIRGULĂ MOBILĂ.
    //
    // Cifra măsurată aici e cât s-ar duce capătul vecinului DE-A CURMEZIȘUL
    // lui — zero pentru un vecin pe direcția deplasării, și chiar `|dep|`
    // pentru unul perpendicular pe ea. Între cele două nu e nimic, deci pragul
    // nu trebuie să fie fin, trebuie doar să nu cadă pe zero curat.
    //
    // Cu `1e-6` cădea: un zid al planșei nu e niciodată perfect drept odată ce
    // a trecut prin mâna cuiva. Măsurat pe M6-ul editat (`m6-editat.json`),
    // W31 coboară 3 mm pe 2,95 m — 0,058° —, iar la o tragere de 0,75 m cifra
    // iese 7,6e-4, adică de 763 de ori pragul. Vecinul pica din verificare,
    // zidul pleca singur și camera rămânea deschisă: exact «trag de un perete
    // și nu vin și pereții lipiți», de data asta din geometria documentului,
    // nu din mână. Catalogul transcris e perfect drept (abatere maximă
    // 0,00000°), de-aia probele pe el n-au văzut niciodată nimic.
    //
    // 5% din deplasare înseamnă că se primesc vecini strâmbi până pe la 3°, și
    // se resping tot cei perpendiculari, care dau 100%.
    if (L > 1e-9
        && Math.abs((-uy * dep[0] + ux * dep[1]) / L)
           > 0.05 * Math.hypot(dep[0], dep[1]) + 1e-9) return;
    const cheie = `${c.perete}:${c.capat}`;
    if (vazute.has(cheie)) return;
    vazute.add(cheie);
    mutaCapatUnic(rez, c.perete, c.capat, dep);
    atinse.add(c.perete);
  };
  for (const nd of nodurile(doc)) {
    if (nd.capete.some((c) => grup.has(c.perete))) nd.capete.forEach(duCapat);
  }
  capeteleRezemate(doc, grup).forEach(duCapat);
  return atinse;
}

/** GOLURILE DE PE O LINIE CARE S-A SCURTAT.
 *
 *  Cerut: «if we increase the length of the wall and there is a window on that
 *  wall we leave the size of the window intact. If we decrease the wall length
 *  the window should decrease proportionally».
 *
 *  ============================== CE E GREU AICI ==============================
 *
 *  SE MĂSOARĂ PE LINIE, NU PE ZIDUL-ÎNREGISTRARE. Un zid e TĂIAT la fiecare
 *  gol, deci „peretele" pe care omul vede o fereastră e linia întreagă, nu
 *  bucata care-i poartă `id`-ul: măsurat pe catalog, aproape jumătate din
 *  goluri sunt mai late decât zidul lor gazdă — o ușă de 0,90 pe un ciot de
 *  0,20. `runulLiniei` nu ajută, fiindcă merge prin graf și se oprește exact
 *  la gol; de-aia linia se adună aici după geometrie (v. `liniaIntreaga`).
 *
 *  GOLURILE CARE NU ÎNCĂPEAU NICI ÎNAINTE NU SE ATING. Sunt puține și sunt
 *  degenerate: o ușă care și-a mâncat tot segmentul de zid nu lasă nicio
 *  înregistrare în dreptul ei, deci cade în afara reuniunii zidurilor rămase
 *  (M1 patru, M6 două, toate uși, toate pe linia lor la 4 mm). Despre ele nu
 *  se poate spune nimic cinstit — «încape?» n-are răspuns —, deci se lasă cum
 *  sunt. Fără regula asta, socoteala strângea goluri pe care nu le strâmba
 *  nimeni.
 *
 *  SE STRÂNGE DOAR CE NU MAI ÎNCAPE. Aplicată la orice scurtare, oricât de
 *  mică, regula ar rupe reversibilitatea gestului: dus-întors, linia se
 *  întoarce la lungimea ei, dar fereastra strânsă la ducere nu se mai
 *  lărgește la întoarcere — fiindcă lungirea o lasă intactă, cum s-a cerut.
 *  Reversibilitatea s-ar putea cumpăra ținând minte lățimea nominală a
 *  golului, dar schema catalogului e închisă (`additionalProperties: false`
 *  pe `windows`/`doors`), deci n-avem unde s-o scriem. Rămâne regula care nu
 *  minte: golul se strânge exact când altfel ar ieși prin capătul liniei, cu
 *  raportul liniei — iar când chiar se strânge, gestul nu mai e reversibil,
 *  fiindcă alternativa e o fereastră ieșită din zid.
 *
 *  REPERUL E CAPĂTUL CARE N-A PLECAT: vecinul nu se translatează, i se mută un
 *  singur capăt (v. `lungesteVecinii`). */
function golurileUrmeazaLiniile(
  rez: TronsonDoc, doc: TronsonDoc, atinse: ReadonlySet<number>,
): void {
  const vechi = doc.plan?.walls ?? [];
  const nou = rez.plan?.walls ?? [];
  const facute = new Set<string>();

  /** TOATE ZIDURILE DE PE ACEEAȘI LINIE, atinse sau nu între ele — aceeași
   *  direcție și același ax. Toleranța de-a latul e strânsă: fațada e scrisă
   *  în două foi paralele la vreo 22 cm, iar alea sunt două linii, nu una. */
  const liniaIntreaga = (j: number): Set<number> => {
    const w0 = vechi[j];
    if (!w0) return new Set([j]);
    const a0 = axaPeretelui(w0);
    const dx0 = a0[1][0] - a0[0][0], dy0 = a0[1][1] - a0[0][1];
    const Lm = Math.hypot(dx0, dy0);
    if (!(Lm > 1e-9)) return new Set([j]);
    const ux = dx0 / Lm, uy = dy0 / Lm;
    const out = new Set<number>();
    vechi.forEach((w, k) => {
      if (!w) return;
      const a = axaPeretelui(w);
      const dx = a[1][0] - a[0][0], dy = a[1][1] - a[0][1];
      const L = Math.hypot(dx, dy);
      if (!(L > 1e-9)) return;
      if (Math.abs((dx * uy - dy * ux) / L) > 1e-6) return;
      for (const q of a) {
        if (Math.abs(-uy * (q[0] - a0[0][0]) + ux * (q[1] - a0[0][1])) > 0.05) return;
      }
      out.add(k);
    });
    return out.size ? out : new Set([j]);
  };

  for (const i of atinse) {
    const run = liniaIntreaga(i);
    const cheie = [...run].sort((a, b) => a - b).join(",");
    if (facute.has(cheie)) continue;
    facute.add(cheie);

    const w0 = vechi[i];
    if (!w0) continue;
    const ax0 = axaPeretelui(w0);
    const dx = ax0[1][0] - ax0[0][0], dy = ax0[1][1] - ax0[0][1];
    const L = Math.hypot(dx, dy);
    if (!(L > 1e-9)) continue;
    const u: Pt = [dx / L, dy / L];
    const o: Pt = ax0[0];
    const peLinie = (p: Pt) => (p[0] - o[0]) * u[0] + (p[1] - o[1]) * u[1];
    const deLaLinie = (p: Pt) => -u[1] * (p[0] - o[0]) + u[0] * (p[1] - o[1]);

    const intinderea = (ziduri: readonly PereteDoc[]) => {
      let lo = Infinity, hi = -Infinity;
      for (const j of run) {
        const w = ziduri[j];
        if (!w) continue;
        for (const q of axaPeretelui(w)) {
          const t = peLinie(q);
          if (t < lo) lo = t;
          if (t > hi) hi = t;
        }
      }
      return [lo, hi] as const;
    };
    const [lo0, hi0] = intinderea(vechi);
    const [lo1, hi1] = intinderea(nou);
    if (!Number.isFinite(lo0) || !Number.isFinite(lo1)) continue;
    const L0 = hi0 - lo0, L1 = hi1 - lo1;
    if (!(L0 > 1e-9) || !(L1 < L0 - 1e-9)) continue;     // nu s-a scurtat

    const jos = Math.abs(lo1 - lo0) <= Math.abs(hi1 - hi0);
    const fix = jos ? lo1 : hi1;
    const spre = jos ? 1 : -1;
    const s = L1 / L0;
    const capat0 = jos ? lo0 : hi0;

    const iduri = new Set<string>();
    for (const j of run) { const id = vechi[j]?.id; if (id) iduri.add(id); }

    for (const fel of ["doors", "windows"] as const) {
      for (const g of rez.plan?.[fel] ?? []) {
        if (!g.wall || !iduri.has(g.wall)) continue;
        const p: Pt = [g.x.a, g.y.a];
        const t = (peLinie(p) - fix) * spre;
        const jum = (g.w_m ?? 0) / 2;
        // Nu încăpea nici înainte? Atunci nu se poate spune nimic despre el.
        const tVechi = (peLinie(p) - capat0) * spre;
        if (tVechi - jum < -1e-9 || tVechi + jum > L0 + 1e-9) continue;
        if (t - jum >= -1e-9 && t + jum <= L1 + 1e-9) continue;   // încape și acum
        const tNou = t * s;
        const q = fix + spre * tNou;
        const lat = deLaLinie(p);
        g.x = { ...g.x, a: r3(o[0] + u[0] * q - u[1] * lat) };
        g.y = { ...g.y, a: r3(o[1] + u[1] * q + u[0] * lat) };
        if (g.w_m) g.w_m = r3(g.w_m * s);
      }
    }
  }
}

export function mutaPeretele(
  doc: TronsonDoc, i: number, delta: number,
  optiuni: {
    /** Ziduri care NU se clintesc, nici măcar cu un capăt tras din graf.
     *
     *  Nodurile se deduc strângând capetele apropiate cu o toleranță de un
     *  sfert de metru (v. `LIPIRE_NOD`) — mai mare decât slăbiciunea
     *  transcrierii, dar și decât distanța dintre două ziduri paralele care trec
     *  unul pe lângă altul. Măsurat pe M1: camera 0 trasă în sus ducea cu ea
     *  fațada W31, care stă la 30 cm sub ea, fiindcă amândouă capetele ei
     *  cădeau în noduri ale zidului mutat — deci se translata întreagă și
     *  anvelopa modulului pleca din loc. Tragerea de mână a unui zid rămâne cum
     *  era; cine are un motiv să oprească ceva îl spune aici. */
    fara?: ReadonlySet<number>;
    /** Ce se mută ca UN zid, în locul runului dedus din `i`.
     *
     *  O linie poate purta ziduri care NU se ating prin noduri — două bucăți
     *  colineare despărțite de un gol, pe M1 chiar laturile unei camere. Runul
     *  merge prin graf, deci nu le adună; mutate una câte una, fiecare chemare
     *  își mișcă fețele, iar muchia camerei care stă pe amândouă pleacă de două
     *  ori. Măsurat: latura se ducea cu 0,40 m la o tragere de 0,20.
     *
     *  Deci cine știe că are de-a face cu o singură linie o spune, iar fețele se
     *  strâng și se mută o dată. */
    grup?: ReadonlySet<number>;
  } = {},
): TronsonDoc {
  const zid = doc.plan?.walls?.[i];
  if (!zid || !delta) return doc;
  const o = osiaPeretelui(inelul(zid.poly));
  const dep: Pt = o.axa === 0 ? [delta, 0] : [0, delta];
  const { fara, grup: grupDat } = optiuni;
  const grup = grupDat ?? runulLiniei(doc, i);

  // FIECARE LUCRU SE MUTĂ PRINTR-UN SINGUR MECANISM. De aici veneau toate
  // defectele: camerele și zidurile treceau amândouă prin `mutaLinie`, iar un
  // zid de colț era prins ȘI de ea (avea o muchie pe fața zidului mutat) ȘI de
  // graf (îi împărțea un capăt) — pleca de două ori la ducere și o dată la
  // întoarcere, deci modulul nu se mai închidea.
  //
  //   camerele  -> `mutaLinie`: n-au capete cu nume, deci linia e singurul fel
  //                de a ști ce ține de zid;
  //   zidul i   -> translatat direct, întreg;
  //   vecinii   -> graful: numai capătul lipit de el, deci se lungesc.
  // FEȚELE RUNULUI, ADUNATE ÎNTÂI, MUTATE O SINGURĂ DATĂ.
  //
  // Chemat o dată pentru fiecare perete al runului, `mutaLinie` ar prinde de
  // două ori muchia unei camere care se întinde peste doi dintre ei — o dată în
  // dreptul fiecăruia — și camera ar pleca cu 2δ. Deci fețele care cad pe
  // aceeași valoare se strâng într-una singură, cu întinderea lor reunită.
  const fete: { valoare: number; jos: number; sus: number }[] = [];
  for (const j of grup) {
    const oj = osiaPeretelui(inelul(doc.plan!.walls![j].poly));
    for (const fata of oj.fete) {
      const jos = Math.min(...oj.span);
      const sus = Math.max(...oj.span);
      const gasit = fete.find((f) => Math.abs(f.valoare - fata) <= LIPIRE);
      if (gasit) {
        gasit.jos = Math.min(gasit.jos, jos);
        gasit.sus = Math.max(gasit.sus, sus);
      } else fete.push({ valoare: fata, jos, sus });
    }
  }

  // DE LA FAȚA DINSPRE CARE SE ÎMPINGE, ÎNAPOI.
  //
  // Fețele se mută una câte una, iar un zid tras cu EXACT grosimea lui își duce
  // fața dinspre care pleacă acolo unde era cealaltă. Muchia camerei care
  // stătea pe prima o prindea atunci și a doua chemare, deci pleca de două ori:
  // măsurat pe M1, un zid de 0,30 împins cu 0,30 ducea livingul cu 0,60. Se
  // vedea ca o cameră care sare, și numai la mărimea aia de pas — de-aia a
  // stat ascunsă.
  //
  // Mutate dinspre fața care o ia înainte, liniile nu se mai ajung din urmă.
  let d = doc;
  const randul = [...fete].sort((a, b) =>
    delta > 0 ? b.valoare - a.valoare : a.valoare - b.valoare);
  for (const f of randul) {
    d = mutaLinie(d, { axa: o.axa, valoare: f.valoare,
                       span: [f.jos, f.sus], delta, doarCamere: true });
  }

  const rez = clona(d);
  // CE STĂ PE RUN, ÎNAINTE SĂ PLECE. Căutat după mutare n-ar mai găsi nimic pe
  // fața zidului — v. `ceStaPeZiduri`.
  const gasite = ceStaPeZiduri(rez.plan!, grup);
  for (const j of grup) {
    const mutat = rez.plan!.walls![j];
    for (const q of mutat.poly) q[o.axa].a = r3(q[o.axa].a + delta);
    const ax0 = axaPeretelui(doc.plan!.walls![j]);
    mutat.axis = [[r3(ax0[0][0] + dep[0]), r3(ax0[0][1] + dep[1])],
                  [r3(ax0[1][0] + dep[0]), r3(ax0[1][1] + dep[1])]];
    if (mutat.bulge) {
      const na = mutat.axis as [Pt, Pt];
      mutat.poly = poligonDinAx(na[0], na[1], mutat.thickness_m, mutat.bulge);
    }
  }

  golurileUrmeazaLiniile(rez, doc,
                         lungesteVecinii(rez, doc, grup, dep, fara));
  // Golurile și prizele pleacă cu zidul lor. Ale VECINILOR nu: acolo se mută
  // doar un capăt, zidul se lungește, iar o ușă din el rămâne la aceeași
  // distanță de capătul care n-a plecat — adică exact unde era.
  duCeAmGasit(rez.plan!, gasite, dep);

  // AL CINCILEA DRUM CARE SCURTEAZĂ UN ZID — v. `strangeGolurile`.
  //
  // Pe-aici trece TRAGEREA UNEI LATURI DE CAMERĂ (`mutaLaturaCamerei` →
  // `mutaPeretele`), adică exact gestul pe care-l face omul când zice «trag un
  // perete din cameră»: apucă muchia camerei, nu zidul. Strângerea era pusă pe
  // celelalte patru drumuri (`redimPeretele`, `mutaCapatul`,
  // `mutaCapatulSingur`, `mutaPereteleSingur`), deci fereastra rămânea întreagă
  // tocmai la gestul cel mai la îndemână. Reclamat a treia oară pe 02.10.2026.
  return strangeGolurile(doc, rez);
}

// ------------------------------------------------------- graful zidurilor ---
//
// PÂNĂ AICI PEREȚII ERAU POLIGOANE SINGURATICE. Fiecare se muta pe cont propriu,
// iar ce se mișca odată cu el se găsea pe LINIE (`mutaLinie`) — bun pentru
// camerele care se sprijină pe fața lui, dar nu și pentru vecinul lipit de
// capătul lui, care rămânea pe loc și lăsa colțul deschis.
//
// Un editor de plan lucrează pe un GRAF: zidurile au capete, capetele lipite
// sunt UN NOD, iar mutarea unui nod mută tot ce ține de el. De-aia tragerea unui
// capăt roteşte și lungeşte zidul, iar vecinii îl urmează.
//
// NODURILE SE DEDUC, nu sunt scrise. Documentele transcrise n-au noduri: sunt
// două transcrieri ale aceleiași planșe, cu capete la câțiva centimetri unul de
// altul. Se strâng deci capetele apropiate într-un nod, cu o toleranță de un
// sfert de metru — mai mare decât slăbiciunea transcrierii și mai mică decât
// orice cameră.

/** Cât de aproape trebuie să fie două capete ca să fie același nod. */
export const LIPIRE_NOD = 0.25;

export type Capat = { perete: number; capat: 0 | 1 };
export type Nod = { p: Pt; capete: Capat[] };

/** MODULUL, CU AXELE SCRISE PE TOȚI PEREȚII.
 *
 *  Se cheamă o dată, când editorul deschide documentul, pe geometria încă
 *  neatinsă — acolo dreptunghiul de încadrare chiar spune care latură e
 *  lungimea. Dedus din nou după fiecare mutare, ar ieși altul: un zid aproape
 *  pătrat își schimbă capătul 0 cu capătul 1 și se rupe în două direcții.
 *
 *  `axis` e METADATE: `poly` rămâne ce citește toată lumea, iar motoarele nu se
 *  uită niciodată la ax — deci scrierea lui nu schimbă nici desenul, nici
 *  ariile, nici `alpha`. Ce schimbă e că de-acum fiecare zid are două capete cu
 *  nume, adică poate sta într-un graf: fără asta, un perete transcris nu era în
 *  graf și vecinii nu-l urmau când era mutat.
 */
export function cuAxeScrise(doc: TronsonDoc): TronsonDoc {
  const pereti = doc.plan?.walls;
  if (!pereti?.length || pereti.every((w) => w.axis)) return doc;
  const d = clona(doc);
  for (const w of d.plan!.walls!) {
    if (w.axis) continue;
    // ROTUNJIT LA MILIMETRU, ca tot ce se scrie în document. Mutările rotunjesc
    // (`r3`), deci un ax scris cu mai multe zecimale decât ele nu se mai
    // întoarce niciodată exact de unde a plecat: −0,1515 pleacă și revine
    // −0,151, iar dus-întors modulul nu mai e identic.
    const [a, b] = axulPeretelui(inelul(w.poly));
    w.axis = [[r3(a[0]), r3(a[1])], [r3(b[0]), r3(b[1])]];
  }
  return d;
}

/** Capetele axului unui perete: din `axis` când e scris, altfel din dreptunghi. */
export function axaPeretelui(w: PereteDoc): [Pt, Pt] {
  return (w.axis as [Pt, Pt] | undefined) ?? axulPeretelui(inelul(w.poly));
}

/** Nodurile grafului: capete de perete strânse pe apropiere.
 *
 *  NUMAI PEREȚII CU AX SCRIS intră în graf, și limitarea e deliberată.
 *
 *  Un perete transcris n-are ax — i se poate deduce din dreptunghiul de
 *  încadrare, dar deducția e bună doar cât timp poligonul CHIAR e un
 *  dreptunghi subțire. Catalogul are și altfel de ziduri: bucăți aproape
 *  pătrate, unde «care latură e lungimea» n-are răspuns, iar capătul 0 și
 *  capătul 1 ies pe dos de la o citire la alta. Măsurat pe M2: un asemenea zid
 *  ajungea, la o mutare dus-întors, cu un colț la −0,40 m și celălalt la +0,40.
 *
 *  Deci graful se construiește pe ce E DECLARAT, nu pe ce se poate ghici. Un
 *  perete capătă ax la prima atingere din editor (`mutaCapatUnic` îl scrie),
 *  deci un plan la care chiar lucrezi devine graf sub mână. Ce ține pereții
 *  transcriși laolaltă până atunci e `mutaLinie` — legătura pe LINIE, care nu
 *  cere să știi care capăt e care.
 */
export function nodurile(doc: TronsonDoc, tol = LIPIRE_NOD): Nod[] {
  const out: Nod[] = [];
  (doc.plan?.walls ?? []).forEach((w, perete) => {
    // Fără ax nu se poate ști care capăt e care, deci zidul rămâne în afara
    // grafului. După `cuAxeScrise` nu mai există niciunul.
    if (!w.axis) return;
    const ax = axaPeretelui(w);
    ([0, 1] as const).forEach((capat) => {
      const p = ax[capat];
      const gasit = out.find((nd) =>
        Math.hypot(nd.p[0] - p[0], nd.p[1] - p[1]) <= tol);
      if (gasit) gasit.capete.push({ perete, capat });
      else out.push({ p, capete: [{ perete, capat }] });
    });
  });
  return out;
}

/** Vârfurile poligonului care țin de un capăt al peretelui.
 *
 *  SE MUTĂ VÂRFURI, NU SE REGENEREAZĂ POLIGONUL. Fiecare vârf poartă un `k` —
 *  cât se mișcă la întinderea modulului — iar un poligon refăcut din ax ar ieși
 *  cu `k = 0` peste tot. Modulul și-ar pierde elasticitatea în tăcere, și n-ar
 *  spune-o decât proba de la încărcarea catalogului, la următorul job.
 *
 *  ÎMPĂRȚIRE PE JUMĂTĂȚI, nu «cele mai apropiate două». Prima variantă lua
 *  vârfurile cele mai apropiate de capăt, iar setul ăla se SCHIMBĂ pe măsură ce
 *  zidul se mișcă: dus-întors cu 0,40 m, un perete ieșea cu un colț la −0,40 și
 *  altul la +0,40, fiindcă la întoarcere «cele mai apropiate» erau altele.
 *  Proiecția pe axul zidului nu se schimbă: un vârf din jumătatea dinspre
 *  capătul 0 rămâne acolo oricât s-ar muta capătul.
 */
function varfurileDeLa(poly: VarfAfin[], ax: [Pt, Pt], capat: 0 | 1): number[] {
  const [a, b] = ax;
  const ux = b[0] - a[0];
  const uy = b[1] - a[1];
  const L = Math.hypot(ux, uy) || 1;
  // Proiecția fiecărui vârf pe direcția axului, apoi ÎMPĂRȚIRE PE JUMĂTATE DUPĂ
  // RANG — nu după semnul față de mijloc.
  //
  // Mijlocul se mută odată cu capătul: după ce ai tras de un capăt, axul e mai
  // lung, mijlocul lui e altundeva, iar un vârf care era la limită trece în
  // cealaltă jumătate. Atunci întoarcerea mișcă alte vârfuri decât ducerea, și
  // zidul rămâne strâmb — măsurat: dus-întors cu 0,40 m lăsa un colț la −0,40 și
  // altul la +0,40. Rangul nu se schimbă: un vârf din jumătatea dinspre capătul
  // 0 rămâne cel mai din spate oricât ar fi împins capătul 1.
  const rang = poly
    .map((q, i) => ({ i, t: ((q[0].a - a[0]) * ux + (q[1].a - a[1]) * uy) / L }))
    .sort((x, y) => x.t - y.t);
  const jum = Math.max(1, Math.floor(rang.length / 2));
  return (capat === 0 ? rang.slice(0, jum) : rang.slice(rang.length - jum))
    .map((x) => x.i);
}

/** ÎMPARTE ZIDUL ÎN DREPTUL CAMEREI DE SUB MÂNĂ și spune ce anume se trage.
 *
 *  Cerut: «if the next room shares a wall like fatada or smth it should only
 *  pull down the part of the wall that is part of that room, not the other
 *  part that is inside another room».
 *
 *  O fațadă merge pe lângă mai multe camere — pe M6, 16 ziduri din 75 au două
 *  sau mai multe camere pe ACEEAȘI față. Trasă întreagă, ea duce și camera de
 *  alături, care n-a fost atinsă de nimeni. Deci linia se taie întâi, în
 *  dreptul camerei apucate, și se trage numai bucata aia: zidul rămâne cu un
 *  prag, exact ca atunci când scobești o nișă într-o fațadă.
 *
 *  SE TAIE TOATĂ LINIA, NU BUCATA DE SUB DEGET, și fără asta tăierea nu
 *  înseamnă nimic. Ce vede ochiul ca UN perete sunt mai multe înregistrări:
 *  foile sandvișului (miez + termoizolație) și bucățile despărțite de goluri
 *  (v. `totPeretele`). Tăiată numai înregistrarea apucată, cele două jumătăți
 *  rezultate rămâneau în același run — se ating cap la cap, deci
 *  `runulLiniei` le lega la loc — iar foaia de-alături nu era tăiată deloc.
 *  Măsurat pe planul importat: fațada stângă (`L1`) trasă 0,35 m în dreptul
 *  unui dormitor mișca PATRU camere din DOUĂ apartamente (17,22→18,45;
 *  13,18→14,31; 17,20→18,44; 13,21→14,33); acum numai una (17,22→18,47).
 *  Asta e tragerea din înregistrarea de ecran.
 *
 *  UNDE SE TAIE: în dreptul compartimentărilor care se reazemă în coasta
 *  liniei. Ele SUNT hotarele dintre camere — nu se caută camerele, fiindcă un
 *  hotar fără zid (v. `thresholds`) n-ar trebui să taie o fațadă. Punctul
 *  apucat spune între care două hotare se lucrează.
 *
 *  BUCATA TRASĂ RĂMÂNE PE INDICELE EI, iar ciotul rămas pe loc se adaugă la
 *  coadă: altfel s-ar fi mutat indicii tuturor zidurilor de după, iar gestul
 *  din pânză ține minte un indice. La fel pentru fiecare bucată a liniei.
 *
 *  GOLURILE MERG FIECARE LA BUCATA LOR, după mijlocul lor. Bucata dinăuntru
 *  păstrează `id`-ul vechi, deci golurile ei nici nu se ating.
 *
 *  `grup` E RĂSPUNSUL, nu documentul: cine trage trebuie să știe CE să tragă,
 *  iar o nouă căutare pe documentul tăiat ar reface runul peste tăietură. Când
 *  n-are ce tăia — un zid care mărginește o singură cameră, unul curb, o
 *  apucare în afara lui — întoarce documentul neatins și toată linia. */
/** Sub atâta nu se taie o bucată de zid: un ciot de câțiva centimetri n-ar fi
 *  o latură de cameră, ci un rest. Tot ea ține mâna departe de hotare. */
const BUCATA_MINIMA = 0.30;

/** Sub atâta, ciotul lăsat dincolo de hotar nu e o bucată de zid, e zgomot de
 *  transcriere: hotarul cade pe capătul bucății și ea se duce întreagă.
 *
 *  Nu e `BUCATA_MINIMA`: acolo se ALEGE unde se taie linia (și un hotar la
 *  20 cm de capăt n-ar fi o cameră), aici se APLICĂ hotarul deja ales pe
 *  fiecare foaie și pe fiecare ciot dintre două ferestre — bucăți care pot fi
 *  ele însele scurte, și care trebuie tăiate la locul lor. */
const CIOT_DE_TAIERE = 0.02;

export type ZidImpartit = {
  doc: TronsonDoc;
  /** Bucățile care mărginesc camera apucată — numai ele se mută. */
  grup: number[];
};

/** Cât de aproape de fața zidului trebuie să stea muchia unei camere ca să fie
 *  A EI. Aceeași măsură ca în `obiecteleUrmeazaZidurile`. */
const MUCHIA_PE_FATA = 0.05;

export function imparteZidulLaCamera(
  doc: TronsonDoc, i: number, punct: Pt,
  camere?: readonly { inel: readonly Pt[] }[],
): ZidImpartit {
  const ziduri = doc.plan?.walls;
  const zid = ziduri?.[i];
  if (!ziduri || !zid) return { doc, grup: [i] };
  const linia = [...totPeretele(doc, i)].filter((k) => ziduri[k]);
  if (zid.bulge) return { doc, grup: linia };

  const o = osiaPeretelui(inelul(zid.poly));
  const lung = (1 - o.axa) as 0 | 1;          // axa pe care zidul e lung
  /** Bucățile liniei care se pot tăia: drepte și pe aceeași direcție. */
  const bucatile = linia.filter((k) => !ziduri[k].bulge
    && osiaPeretelui(inelul(ziduri[k].poly)).axa === o.axa);
  const osii = new Map(bucatile.map(
    (k) => [k, osiaPeretelui(inelul(ziduri[k].poly))] as const));
  const intins = (f: (o: ReturnType<typeof osiaPeretelui>) => number[]) =>
    bucatile.flatMap((k) => f(osii.get(k)!));
  // Linia întreagă, cu toate foile ei: întinderea pe lung și banda de grosime.
  const s0 = Math.min(...intins((x) => x.span));
  const s1 = Math.max(...intins((x) => x.span));
  const f0 = Math.min(...intins((x) => x.fete));
  const f1 = Math.max(...intins((x) => x.fete));
  if (!(s1 - s0 > 2 * BUCATA_MINIMA)) return { doc, grup: linia };

  // HOTARELE: capetele compartimentărilor rezemate în coasta liniei.
  //
  // CU PARTEA DIN CARE VIN. O compartimentare care se reazemă în linie dinspre
  // DINCOLO nu închide camera de dincoace — v. lărgirea de mai jos.
  const taieturi: number[] = [];
  const dinspre = new Map<number, number>();     // hotar -> fața de care ține
  const aLiniei = new Set(bucatile);
  ziduri.forEach((w, j) => {
    if (aLiniei.has(j) || !w) return;
    const ow = osiaPeretelui(inelul(w.poly));
    if (ow.axa === o.axa) return;                        // paralel: nu e hotar
    // De ce parte a liniei stă compartimentarea: după mijlocul ei pe axa
    // grosimii liniei. Una care o străbate (rară, dar există) ține de amândouă
    // și atunci nu se ignoră niciodată.
    const mij = (Math.min(...ow.span) + Math.max(...ow.span)) / 2;
    const strabate = Math.min(...ow.span) < f0 - ROST_LIPIT
                     && Math.max(...ow.span) > f1 + ROST_LIPIT;
    for (const q of axaPeretelui(w)) {
      if (q[o.axa] < f0 - ROST_LIPIT || q[o.axa] > f1 + ROST_LIPIT) continue;
      const t = q[lung];
      if (t > s0 + BUCATA_MINIMA && t < s1 - BUCATA_MINIMA) {
        taieturi.push(t);
        if (!strabate) dinspre.set(t, mij <= (f0 + f1) / 2 ? f0 : f1);
      }
    }
  });
  if (!taieturi.length) return { doc, grup: linia };

  // Intervalul în care a căzut mâna.
  const margini = [s0, ...taieturi.sort((x, y) => x - y), s1];
  const p = Math.min(Math.max(punct[lung], s0), s1);
  let a = s0, b = s1;
  for (let k = 0; k < margini.length - 1; k++) {
    if (p >= margini[k] - 1e-9 && p <= margini[k + 1] + 1e-9) {
      a = margini[k]; b = margini[k + 1];
      break;
    }
  }
  // ...ȘI SE SARE PESTE HOTARELE CARE VIN DE DINCOLO.
  //
  // ==========================================================================
  // Hotarele de mai sus sunt capetele compartimentărilor rezemate în linie —
  // toate, de pe amândouă părțile ei. Cele de DINCOLO taie bucata în dreptul
  // unei camere pe care mâna n-o atinge: măsurat pe importul Biancăi (IZ,
  // 12.10.2026), fațada `E_L` e un singur zid de 20,25 m, iar livingul apucat
  // (x 5,50–11,45, y 3,80–8,65) primea o bucată de la y 3,63 la 7,35 — 3,72 m
  // din latura lui de 4,85. Restul laturii rămânea pe loc, iar peretele camerei
  // ieșea în trepte: «when I pull down it doesn't pull all of the room walls
  // too».
  //
  // CAMERA SPUNE UNDE SE TERMINĂ LATURA. Muchia ei care stă pe fața zidului dă
  // întinderea pe care o vede omul; hotarele dinăuntrul ei, venite de pe
  // cealaltă parte, nu închid nimic aici. Se sare DOAR peste ele: un hotar al
  // camerei apucate rămâne hotar, deci bucata nu trece niciodată în camera
  // vecină de pe aceeași parte — care a fost tot rostul tăierii.
  //
  // (Prima formă a lărgirii întindea bucata la camera întreagă, orice hotar ar
  // fi fost pe drum. Pe planul importat asta unea două apartamente oglindite
  // la tragerea unui hol — v. `conectare.test.ts`, care o prinde.)
  // ==========================================================================
  if (camere?.length) {
    const laturaCamerei = (c: { inel: readonly Pt[] }): [number, number, number] | null => {
      let de = Infinity;
      let la = -Infinity;
      let fata = NaN;
      for (let k = 0; k < c.inel.length; k++) {
        const u = c.inel[k];
        const v2 = c.inel[(k + 1) % c.inel.length];
        if (Math.abs(u[o.axa] - v2[o.axa]) > MUCHIA_PE_FATA) continue;
        const m = (u[o.axa] + v2[o.axa]) / 2;
        const pe = Math.abs(m - f0) <= MUCHIA_PE_FATA ? f0
          : Math.abs(m - f1) <= MUCHIA_PE_FATA ? f1 : null;
        if (pe === null) continue;
        de = Math.min(de, u[lung], v2[lung]);
        la = Math.max(la, u[lung], v2[lung]);
        fata = pe;
      }
      return la > de ? [de, la, fata] : null;
    };
    let cea: [number, number, number] | null = null;
    for (const c of camere) {
      const l = laturaCamerei(c);
      if (!l) continue;
      if (p < l[0] - 1e-9 || p > l[1] + 1e-9) continue;   // mâna nu e la ea
      if (!cea || l[1] - l[0] > cea[1] - cea[0]) cea = l;
    }
    if (cea) {
      const [de, la, fata] = cea;
      // Hotarele care rămân: cele ale camerei apucate (de pe fața ei), plus
      // tot ce cade în afara laturii ei.
      const alCamerei = (t: number) =>
        t <= de + 1e-9 || t >= la - 1e-9 || dinspre.get(t) === fata
        || dinspre.get(t) === undefined;
      const tinute = [s0, ...taieturi.filter(alCamerei).sort((x, y) => x - y), s1];
      a = s0;
      b = s1;
      for (let k = 0; k < tinute.length - 1; k++) {
        if (p >= tinute[k] - 1e-9 && p <= tinute[k + 1] + 1e-9) {
          a = tinute[k];
          b = tinute[k + 1];
          break;
        }
      }
    }
  }
  if (a <= s0 + 1e-9 && b >= s1 - 1e-9) return { doc, grup: linia };

  const d = clona(doc);
  const lista = d.plan!.walls!;
  const folosite = new Set(lista.map((w) => w.id));
  const idNou = (baza: string) => {
    let k = 2;
    while (folosite.has(`${baza}-${k}`)) k++;
    const id = `${baza}-${k}`;
    folosite.add(id);
    return id;
  };

  const grup: number[] = [];
  /** Golurile de mutat: `id`-ul gazdei de dinainte, bucățile ei de-acum. */
  const mutate: { gazda: string; lung: 0 | 1;
                  facute: { id: string; de: number; la: number }[] }[] = [];
  for (const k of bucatile) {
    const ok = osii.get(k)!;
    const k0 = Math.min(...ok.span), k1 = Math.max(...ok.span);
    // Hotarele care cad ÎNĂUNTRUL bucății ăsteia. Un hotar lipit de capătul ei
    // n-o taie — bucata e deja de o singură parte a lui.
    // NICIO BUCATĂ MAI SCURTĂ DECÂT E DE GROASĂ. Pragul de 2 cm singur taie și
    // la 2,1 cm de capăt, iar ciotul de 2,1 cm al unui zid de 15 cm e mai lat
    // decât lung: `axaPeretelui` ia atunci GROSIMEA drept lungime și axul se
    // întoarce cu 90°, deci `poly` și `axis` ajung să spună lucruri diferite —
    // apucat a doua oară, ciotul pleacă de-a curmezișul lui însuși. Măsurat:
    // 483 din 2156 de tăieri de pe catalog lăsau așa ceva.
    const ciot = Math.max(CIOT_DE_TAIERE, ziduri[k].thickness_m ?? 0);
    const prin = [a, b].filter((t) => t > k0 + ciot && t < k1 - ciot);
    const margini2 = [k0, ...prin.sort((x, y) => x - y), k1];
    const felii: [number, number][] = [];
    for (let m = 0; m < margini2.length - 1; m++) {
      felii.push([margini2[m], margini2[m + 1]]);
    }
    // Bucata care ține de camera apucată: mijlocul ei cade între hotare.
    const inauntru = (de: number, la: number) =>
      (de + la) / 2 > a - 1e-9 && (de + la) / 2 < b + 1e-9;
    if (felii.length < 2) {                      // n-are ce tăia: merge întreagă
      if (inauntru(k0, k1)) grup.push(k);
      continue;
    }
    const ax = axaPeretelui(ziduri[k]);
    const fix = ax[0][o.axa];               // bucata e dreaptă: axul are un
    const capat = (t: number): Pt =>        // singur `o.axa`, iar `lung` variază
      (o.axa === 0 ? [fix, t] : [t, fix]) as Pt;
    const facute: { id: string; de: number; la: number }[] = [];
    for (const [de, la] of felii) {
      const tras = inauntru(de, la);
      const w: PereteDoc = tras ? lista[k] : clona(lista[k]);
      w.poly = poligonDinAx(capat(de), capat(la), w.thickness_m, 0);
      w.axis = [capat(de), capat(la)] as PereteDoc["axis"];
      if (tras) grup.push(k);
      else { w.id = idNou(ziduri[k].id); lista.push(w); }
      facute.push({ id: w.id, de, la });
    }
    mutate.push({ gazda: ziduri[k].id, lung, facute });
  }

  // GOLURILE, fiecare la bucata în care îi cade mijlocul.
  for (const fel of ["doors", "windows"] as const) {
    for (const g of d.plan![fel] ?? []) {
      const sursa = mutate.find((x) => x.gazda === g.wall);
      if (!sursa) continue;
      const t = (sursa.lung === 0 ? g.x.a : g.y.a);
      const gasit = sursa.facute.find((f) => t >= f.de - 1e-9 && t <= f.la + 1e-9);
      if (gasit) g.wall = gasit.id;
    }
  }

  // BUCATA DE SUB DEGET TREBUIE SĂ FIE ÎN GRUP, altfel gestul n-ar mișca nimic:
  // mâna a apucat zidul `i` și el e cel care trebuie să plece. Nu se întâmplă
  // cât timp punctul chiar cade pe el, dar un hit-test nu promite asta.
  if (!grup.includes(i)) return { doc, grup: linia };
  // HOTARUL A CĂZUT PE CAPETELE BUCĂȚILOR: nimeni n-a fost tăiat, doar unele
  // bucăți ale liniei rămân pe loc. Documentul se întoarce NEATINS — o copie
  // identică ar pune un pas gol în istoric la fiecare clic pe un zid.
  if (lista.length === ziduri.length) return { doc, grup };
  return { doc: d, grup };
}

/** MUTĂ UN CAPĂT DE PERETE, liber în plan.
 *
 *  Se mută numai capătul: zidul se rotește și se lungește după el, exact ca
 *  atunci când tragi de un capăt în Homestyler. Toate capetele LIPITE de el se
 *  mută odată, deci pereții vecini îl urmează și colțul rămâne închis. */
/** Mută capătul UNUI SINGUR perete, fără să se uite la graf. Scrie pe `d`.
 *
 *  AXUL SE SCRIE LA PRIMA ATINGERE, și fără asta nimic nu se ține.
 *
 *  Un perete transcris n-are ax: i se deduce din dreptunghiul de încadrare. Dar
 *  după ce i-ai mutat un capăt nu mai e dreptunghi, iar dreptunghiul lui de
 *  încadrare descrie altceva — deci a doua mutare pornește de la alt ax decât
 *  prima, împarte vârfurile altfel și duce colțurile în direcții opuse.
 *  Măsurat: dus-întors cu 0,40 m lăsa un zid cu un colț la −0,40 și celălalt la
 *  +0,40. De-aia axul se scrie o dată, la prima atingere, și de-atunci e el
 *  autoritatea.
 *
 *  POLIGONUL SE MUTĂ PE VÂRFURI, nu se reface, câtă vreme zidul e drept: fiecare
 *  vârf poartă un `k` — cât se mișcă la întinderea modulului — pe care o
 *  regenerare l-ar pune pe zero în tăcere. Un arc se reface, fiindcă oricum nu
 *  se întinde (v. `axis` în schemă).
 */
function mutaCapatUnic(d: TronsonDoc, perete: number, capat: 0 | 1, delta: Pt) {
  const w = d.plan?.walls?.[perete];
  if (!w) return;
  const ax = axaPeretelui(w);
  const nouAx: [Pt, Pt] = [[...ax[0]] as Pt, [...ax[1]] as Pt];
  nouAx[capat] = [r3(ax[capat][0] + delta[0]), r3(ax[capat][1] + delta[1])];
  // SE FORFECĂ, SAU DOAR SE LUNGEȘTE?
  //
  // Vârfurile de la un capăt, translatate, sunt răspunsul BUN cât timp
  // deplasarea merge pe lungul zidului: atunci el doar se lungește, iar
  // poligonul transcris rămâne cuvânt cu cuvânt cum era — cu `k`-urile lui, și
  // cu dus-întorsul care dă exact documentul de la început.
  //
  // Când deplasarea are însă și o parte PERPENDICULARĂ pe zid, aceleași două
  // vârfuri pleacă lateral și celelalte două rămân: iese un paralelogram. Așa se
  // umplea planșa de pereți oblici la o tragere — măsurat pe M1, 10 ziduri
  // strâmbate dintr-un singur gest. Zidul TREBUIE să se încline (altfel colțul
  // comun rămâne deschis), dar înclinat trebuie să rămână dreptunghi.
  //
  // Deci: pe lungul lui, se translatează vârfurile; altfel se reface din noul
  // ax. Regenerarea pune `k`-urile pe zero, așa că se iau de la cel mai
  // apropiat vârf vechi.
  const ux = ax[1][0] - ax[0][0];
  const uy = ax[1][1] - ax[0][1];
  const L = Math.hypot(ux, uy) || 1;
  const perpendicular = Math.abs((-uy * delta[0] + ux * delta[1]) / L);
  if (w.bulge) {
    w.poly = poligonDinAx(nouAx[0], nouAx[1], w.thickness_m, w.bulge);
  } else if (perpendicular <= 1e-6) {
    for (const k of varfurileDeLa(w.poly, ax, capat)) {
      w.poly[k][0].a = r3(w.poly[k][0].a + delta[0]);
      w.poly[k][1].a = r3(w.poly[k][1].a + delta[1]);
    }
  } else {
    const vechi = w.poly.map((q) => ({
      p: [q[0].a, q[1].a] as Pt, kx: q[0].k ?? 0, ky: q[1].k ?? 0,
    }));
    w.poly = poligonDinAx(nouAx[0], nouAx[1], w.thickness_m, 0).map((q) => {
      let cel = vechi[0];
      let best = Infinity;
      for (const v of vechi) {
        const dd = (v.p[0] - q[0].a) ** 2 + (v.p[1] - q[1].a) ** 2;
        if (dd < best) { best = dd; cel = v; }
      }
      if (cel.kx) q[0].k = cel.kx;
      if (cel.ky) q[1].k = cel.ky;
      return q;
    });
  }
  w.axis = nouAx;
}

/** FERESTRELE UNUI ZID SCURTAT SE STRÂNG ODATĂ CU EL, și numai atunci.
 *
 *  Lungit, zidul lasă fereastra cum e: tâmplăria nu crește fiindcă a crescut
 *  peretele, iar ce se câștigă e perete plin. Scurtat, însă, fereastra rămasă
 *  întreagă ajunge să iasă din el — pe un zid tras la jumătate, un geam de
 *  1,80 atârnă în aer. Deci se strânge în aceeași măsură: și locul ei pe lungul
 *  zidului, măsurat de la capătul care NU s-a mișcat, și lățimea.
 *
 *  UȘILE NU. O ușă are lățimi de catalog — 0,80, 0,90, 1,00 — și una de 0,73 nu
 *  se comandă de nicăieri; rămâne unde e, se vede că nu mai încape și se mută
 *  sau se îngustează de mână.
 *
 *  DE CE AICI ȘI NU ÎN GESTUL CARE TRAGE. Întâi a stat în `redimPeretele`, adică
 *  pe UN drum: cel al cadrului albastru. Numai că lungimea unui zid se schimbă
 *  pe patru drumuri — capătul tras liber în plan (`mutaCapatulSingur`, gestul
 *  obișnuit: apuci capătul și tragi), capătul cu vecinii după el
 *  (`mutaCapatul`), cadrul (`redimPeretele`) și corpul tras care lungește
 *  vecinii perpendiculari (`mutaPereteleSingur`). Pus pe unul singur, nu se
 *  întâmpla la gestul pe care-l face omul, iar probele chemau direct funcția
 *  care-l avea și treceau. Deci se măsoară GEOMETRIA, nu gestul: ce zid s-a
 *  scurtat între documentul de dinainte și cel de după, oricine l-a scurtat.
 *
 *  Capătul care a stat se află tot din geometrie — cel care s-a mutat mai
 *  puțin —, așa că merge și la un capăt tras care ROTEȘTE zidul, unde nu există
 *  o «prindere» din care să se citească.
 *
 *  ȘI «CE ZID» ÎNSEAMNĂ TOATĂ LINIA, nu înregistrarea care poartă golul — v.
 *  `liniaZidului`. Cele cinci drumuri de mai sus erau toate acoperite și
 *  fereastra tot nu se strângea, fiindcă regula întreba de bucata greșită: pe o
 *  fațadă ruptă în bucăți (catalogul o are așa din transcriere, planul importat
 *  o taie singur la prima tragere de corp — v. `imparteZidulLaCamera`) capătul
 *  liniei se trage de pe o bucată, iar geamul stă pe alta, care nu și-a
 *  schimbat lungimea. Măsurat: fațada scădea de la 8,20 la 6,11 m și geamul
 *  rămânea 1,80. Al șaselea raport, 02.10.2026. */
/** TĂIETURILE DE PE UN ZID: unde-l taie pereții care vin de-a curmezișul în
 *  el, plus capetele lui. Măsurate ca PROIECȚII ÎN PLAN pe direcția zidului,
 *  nu de la capătul lui: capătul se poate muta între două documente, și atunci
 *  toate tăieturile ar părea mutate odată cu el, deși despărțiturile au stat.
 *
 *  Pe planurile importate fațada e UN zid lung — 19,90 m cu șase ferestre pe el
 *  —, iar camerele sunt despărțiturile care vin în ea. Deschiderea unei camere
 *  pe fațadă e, deci, bucata dintre două astfel de despărțituri. */
function taieturile(doc: TronsonDoc, w: PereteDoc): number[] {
  const ax = axaPeretelui(w);
  const L = Math.hypot(ax[1][0] - ax[0][0], ax[1][1] - ax[0][1]);
  if (L < 1e-9) return [];
  const u: Pt = [(ax[1][0] - ax[0][0]) / L, (ax[1][1] - ax[0][1]) / L];
  const n: Pt = [-u[1], u[0]];
  const s0 = ax[0][0] * u[0] + ax[0][1] * u[1];
  const taieri = [s0, s0 + L];
  for (const p of (doc.plan?.walls ?? [])) {
    if (p.id === w.id || p.bulge) continue;
    const b = axaPeretelui(p);
    const Lp = Math.hypot(b[1][0] - b[0][0], b[1][1] - b[0][1]);
    if (Lp < 1e-9) continue;
    const up: Pt = [(b[1][0] - b[0][0]) / Lp, (b[1][1] - b[0][1]) / Lp];
    if (Math.abs(up[0] * u[0] + up[1] * u[1]) > 0.2) continue;
    // AJUNGE PÂNĂ LA ZID? Capetele lui, măsurate de-a latul zidului nostru,
    // ori îl încalecă, ori se opresc în fața lui.
    const q0 = (b[0][0] - ax[0][0]) * n[0] + (b[0][1] - ax[0][1]) * n[1];
    const q1 = (b[1][0] - ax[0][0]) * n[0] + (b[1][1] - ax[0][1]) * n[1];
    const prag = (w.thickness_m ?? 0) / 2 + 0.05;
    if (Math.min(q0, q1) > prag || Math.max(q0, q1) < -prag) continue;
    const c = ((b[0][0] + b[1][0]) * u[0] + (b[0][1] + b[1][1]) * u[1]) / 2;
    if (c > s0 + 0.01 && c < s0 + L - 0.01) taieri.push(c);
  }
  return taieri.sort((x, y) => x - y);
}

/** În a câta deschidere de pe zid cade punctul; `-1` dacă în niciuna. */
function deschiderea(taieri: number[], s: number): number {
  for (let i = 0; i + 1 < taieri.length; i++) {
    if (s >= taieri[i] - 0.01 && s <= taieri[i + 1] + 0.01) return i;
  }
  return -1;
}

/** Cât de departe de axul unui zid mai poate cădea axul altuia și să fie tot
 *  ACEEAȘI LINIE. Cinci centimetri — strâns înadins: fațada catalogului e
 *  scrisă în două foi paralele la vreo 22 cm una de alta, iar alea sunt două
 *  linii, nu una (v. `golurileUrmeazaLiniile`, care măsoară la fel). */
const LIPIRE_LINIE = 0.05;

/** Cât poate rătăci capătul unui zid de la direcția liniei și să fie tot pe ea.
 *
 *  TREI MILIMETRI DE DESEN, nu un epsilon de virgulă mobilă. Pragul a fost
 *  `1e-6` pe SINUSUL unghiului — adică 0,00006°, o cifră care nu înseamnă nimic
 *  pe hârtie. Coordonatele se scriu rotunjite la milimetru (`r3`), deci o
 *  singură tragere mută un capăt cu până la 0,3 mm; pe un zid de 5 m asta e
 *  0,0034°, de cincizeci și nouă de ori peste prag. Bucata cădea de pe linie la
 *  a DOUA mișcare, iar fereastra nu se mai strângea niciodată — defectul arăta
 *  ca «merge o dată, pe urmă nu».
 *
 *  Scris neîmpărțit la lungime, `|dx·uy − dy·ux|` CHIAR E abaterea capătului
 *  depărtat, în metri: pragul se citește direct ca milimetri de desen. Aceeași
 *  lecție ca la lungirea vecinilor, unde un zid cu 0,058° abatere rupea totul.
 */
const ABATEREA_LINIEI = 0.003;

/** ZIDURILE DE PE ACEEAȘI LINIE CA `w`, pe `id` — aceeași direcție și același
 *  ax, atinse sau nu între ele.
 *
 *  DE CE PE LINIE ȘI NU PE ÎNREGISTRARE. «Peretele» pe care omul vede o
 *  fereastră aproape niciodată nu e înregistrarea care-i poartă `id`-ul:
 *
 *    * în catalogul transcris din DWG zidurile sunt TĂIATE la fiecare gol, deci
 *      o fereastră de 1,80 stă scrisă pe o foaie de 0,75;
 *    * iar pe planurile importate, unde fațada e UN zid lung, prima tragere de
 *      corp o taie ea însăși în dreptul camerei apucate (v.
 *      `imparteZidulLaCamera`) — din gestul al doilea încolo arată ca în
 *      catalog.
 *
 *  Măsurată pe înregistrare, scurtarea nu se vedea deloc în cazul ăsta: capătul
 *  liniei se trage de pe o bucată, iar fereastra stă pe alta, care nu și-a
 *  schimbat lungimea. Fațada se scurta de la 8,20 la 6,11 m și geamul rămânea
 *  întreg (v. `strangeGolurile`). `runulLiniei` nu ajută: el merge prin graf și
 *  se oprește exact la gol. */
function liniaZidului(ziduri: readonly PereteDoc[], w: PereteDoc): string[] {
  const a0 = axaPeretelui(w);
  const dx0 = a0[1][0] - a0[0][0];
  const dy0 = a0[1][1] - a0[0][1];
  const L = Math.hypot(dx0, dy0);
  if (!(L > 1e-9)) return [w.id];
  const ux = dx0 / L;
  const uy = dy0 / L;
  const out: string[] = [];
  for (const q of ziduri) {
    if (q.bulge) continue;
    const a = axaPeretelui(q);
    const dx = a[1][0] - a[0][0];
    const dy = a[1][1] - a[0][1];
    const Lq = Math.hypot(dx, dy);
    if (!(Lq > 1e-9)) continue;
    if (Math.abs(dx * uy - dy * ux) > ABATEREA_LINIEI) continue;  // altă direcție
    if (a.some((p) => Math.abs(-uy * (p[0] - a0[0][0]) + ux * (p[1] - a0[0][1]))
                      > LIPIRE_LINIE)) continue;               // alt ax
    out.push(q.id);
  }
  return out.length ? out : [w.id];
}

/** Întinderea unui set de ziduri, ca proiecție pe direcția `u`. Zidurile se
 *  caută pe `id`, ca să fie ACELEAȘI în amândouă documentele: un set recitit
 *  din geometria de după ar pierde bucata care s-a mutat de pe linie și ar
 *  arăta o scurtare care nu s-a întâmplat. */
function intindereaLiniei(
  ziduri: readonly PereteDoc[], iduri: readonly string[], u: Pt,
): [number, number] | null {
  let lo = Infinity;
  let hi = -Infinity;
  for (const id of iduri) {
    const q = ziduri.find((x) => x.id === id);
    if (!q) continue;
    for (const p of axaPeretelui(q)) {
      const t = p[0] * u[0] + p[1] * u[1];
      if (t < lo) lo = t;
      if (t > hi) hi = t;
    }
  }
  return Number.isFinite(lo) && Number.isFinite(hi) ? [lo, hi] : null;
}

export function strangeGolurile(doc0: TronsonDoc, d: TronsonDoc): TronsonDoc {
  const geamuri = d.plan?.windows;
  const geamuri0 = doc0.plan?.windows;
  if (!geamuri?.length || geamuri0?.length !== geamuri.length) return d;
  const vechi = doc0.plan?.walls ?? [];
  for (const w of (d.plan?.walls ?? [])) {
    if (!geamuri.some((g) => g.wall === w.id)) continue;
    const w0 = vechi.find((q) => q.id === w.id);
    if (!w0 || w0.bulge || w.bulge) continue;
    const a0 = axaPeretelui(w0);
    const a1 = axaPeretelui(w);
    const L0 = Math.hypot(a0[1][0] - a0[0][0], a0[1][1] - a0[0][1]);
    const L = Math.hypot(a1[1][0] - a1[0][0], a1[1][1] - a1[0][1]);
    // FINIT, nu doar nenul: un ax cu NaN (se întâmplă — v. mai jos) ar trece de
    // o comparație cu zero, fiindcă `NaN < 1e-9` e fals, și-ar duce NaN până în
    // lățimea ferestrei, adică ar STRICA documentul. Mai bine lăsat neatins.
    if (!(L0 > 1e-9) || !(L > 1e-9)) continue;
    const u0: Pt = [(a0[1][0] - a0[0][0]) / L0, (a0[1][1] - a0[0][1]) / L0];
    const u1: Pt = [(a1[1][0] - a1[0][0]) / L, (a1[1][1] - a1[0][1]) / L];
    const t0 = taieturile(doc0, w0);
    const t1 = taieturile(d, w);
    const baza1 = a1[0][0] * u1[0] + a1[0][1] * u1[1];
    // CAPĂTUL CARE A STAT = cel care s-a mutat mai puțin.
    const m0 = Math.hypot(a1[0][0] - a0[0][0], a1[0][1] - a0[0][1]);
    const m1 = Math.hypot(a1[1][0] - a0[1][0], a1[1][1] - a0[1][1]);
    const dinA = m0 <= m1;
    const kZid = Math.min(1, L / L0);

    // ȘI A DOUA MĂSURĂ, A LINIEI ÎNTREGI — v. `liniaZidului`. Se adaugă celei
    // de sus, nu o înlocuiește: ele răspund la două întrebări diferite, iar
    // fereastra se strânge după cea mai strâmtă.
    //
    //   înregistrarea — s-a retras zidul de sub geam? (o foaie trasă de capăt
    //                   lasă o spărtură în fațadă chiar dacă linia ei e tot
    //                   atât de lungă)
    //   linia         — s-a scurtat fațada? (capătul ei se trage de pe ALTĂ
    //                   bucată, iar aia e situația din care a ieșit raportul)
    //
    // RAPORTUL SE MĂSOARĂ ÎN AMÂNDOUĂ DOCUMENTELE PE DIRECȚIA DE DINAINTE.
    // Măsurat fiecare pe direcția lui, un capăt tras care ÎNCLINĂ zidul cu 8°
    // schimba proiecția celorlalte bucăți ale liniei și arăta o scurtare de 5
    // cm din nimic: pe M1, un zid LUNGIT strângea geamul de la 0,900 la 0,895.
    // Direcția e a liniei, nu a gestului, deci se ia o dată, de la început.
    const iduri = liniaZidului(vechi, w0);
    const e0 = intindereaLiniei(vechi, iduri, u0);
    const eU0 = intindereaLiniei(d.plan?.walls ?? [], iduri, u0);
    // Capetele liniei PE AXUL DE ACUM, pentru locul în care se duce golul:
    // poziția se scrie în cadrul lui `u1`, ca tot restul funcției.
    const e1 = intindereaLiniei(d.plan?.walls ?? [], iduri, u1);
    const Lin0 = e0 ? e0[1] - e0[0] : 0;
    const Lin = eU0 ? eU0[1] - eU0[0] : 0;
    const peLinie = !!(e0 && eU0 && e1 && Lin0 > 1e-9 && Lin > 1e-9);
    const kLinie = peLinie ? Math.min(1, Lin / Lin0) : 1;
    const dinJos = peLinie
      ? Math.abs(eU0![0] - e0![0]) <= Math.abs(eU0![1] - e0![1]) : true;
    const [lo1, hi1] = e1 ?? [0, 0];

    geamuri.forEach((g, k) => {
      if (g.wall !== w.id) return;
      const v = geamuri0![k];
      if (!v || v.wall !== w.id) return;
      const s0 = v.x.a * u0[0] + v.y.a * u0[1];
      const s1 = g.x.a * u1[0] + g.y.a * u1[1];
      const i0 = deschiderea(t0, s0);
      const i1 = deschiderea(t1, s1);
      // DESCHIDEREA SE SOCOTEȘTE DOAR CÂND E DE ÎNCREDERE: golul chiar pe
      // zidul lui, tot atâtea despărțituri în amândouă documentele, aceeași
      // deschidere, și un zid care NU s-a lungit.
      //
      // «GOLUL CHIAR PE ZIDUL LUI» nu e de la sine înțeles: în catalogul
      // transcris din DWG zidurile sunt TĂIATE la fiecare gol, iar fereastra e
      // scrisă pe o foaie de 0,75 m deși ea are 1,80 și stă dincolo de capătul
      // foii — golul e al ȘIRULUI de foi, nu al uneia. Acolo nu există
      // «deschiderea camerei» de socotit pe zidul ăsta, și rămâne regula veche:
      // scade cât scade zidul, măsurat de la capătul care a stat.
      const alLui = i0 >= 0 && i1 >= 0;
      const bun = alLui && i0 === i1 && t0.length === t1.length
        && L <= L0 + 1e-6 && Lin <= Lin0 + 1e-6 && t0[i0 + 1] - t0[i0] > 1e-9;
      const kCam = bun
        ? Math.min(1, (t1[i1 + 1] - t1[i1]) / (t0[i0 + 1] - t0[i0])) : 1;
      // Se strânge după ce e mai strâmt: camera, zidul de sub geam sau fațada
      // întreagă.
      const kk = Math.min(kZid, kLinie, kCam);
      if (kk >= 1 - 1e-6) return;                   // lărgit sau neatins
      const peN = (v.x.a - a0[0][0]) * (-u0[1]) + (v.y.a - a0[0][1]) * u0[0];
      // DE CE ANUME S-A STRÂNS hotărăște și de unde se măsoară locul: cine
      // strânge geamul e și cel al cărui capăt a stat pe loc. Linia intră la
      // rând numai când ea e cea mai strâmtă — altfel socoteala rămâne cea
      // dinainte, la virgulă.
      //
      // PE DRUMUL VECHI SE MĂSOARĂ POZIȚIA DE ACUM, pe axul de acum: golul a
      // fost deja dus pe linia nouă de gestul care a tras (v.
      // `golurileUrmeazaLiniile`), iar de-acolo se strânge spre capătul care a
      // stat. Măsurată în schimb poziția dinainte pe axul dinainte, un gol de
      // pe un șir de foi ajungea pe foaia greșită.
      const cerut = bun && kCam <= Math.min(kZid, kLinie)
        ? t1[i1] + (s0 - t0[i0]) * (t1[i1 + 1] - t1[i1]) / (t0[i0 + 1] - t0[i0])
        : kLinie < kZid
          ? (dinJos ? lo1 + (s1 - lo1) * kLinie : hi1 - (hi1 - s1) * kLinie)
          : dinA ? baza1 + (s1 - baza1) * kZid
                 : baza1 + L - (baza1 + L - s1) * kZid;
      // ȘI SĂ NU IASĂ DIN ZID — dar numai dacă era înăuntru. Așezat după
      // deschiderea lui, un gol lipit de marginea ei ajunge cu jumătatea lui
      // dincolo de capăt (pe socoteala veche nu se putea: și locul, și lățimea
      // scădeau cu același număr). Cine stătea dinainte peste capătul foii lui
      // e lăsat cum l-am găsit — altfel l-am trage pe o foaie de 15 cm.
      //
      // MARGINILE SUNT ALE FOII CÂT TIMP GOLUL ÎNCĂPEA ÎN EA; altminteri sunt
      // ale liniei. O fereastră de 1,80 scrisă pe o foaie de 0,75 (catalogul e
      // plin de ele) n-ar avea unde să încapă, iar strânsă la mijlocul foii
      // s-ar muta cu un metru dintr-un gest care n-a cerut-o.
      const lat = g.w_m ? Math.max(0.1, g.w_m * kk) : 0;
      const jum = (v.w_m ?? 0) / 2;
      const inFoaie = s0 - jum >= t0[0] - 0.01
        && s0 + jum <= t0[t0.length - 1] + 0.01;
      const jos = (inFoaie ? t1[0] : lo1) + lat / 2;
      const sus = (inFoaie ? t1[t1.length - 1] : hi1) - lat / 2;
      const nou = !alLui ? cerut
        : sus >= jos ? Math.min(Math.max(cerut, jos), sus) : (jos + sus) / 2;
      g.x = { ...g.x, a: r3(a1[0][0] + u1[0] * (nou - baza1) + (-u1[1]) * peN) };
      g.y = { ...g.y, a: r3(a1[0][1] + u1[1] * (nou - baza1) + u1[0] * peN) };
      if (g.w_m) g.w_m = r3(lat);
    });
  }
  return d;
}

/** CORPUL UNUI ZID AJUNGE CHIAR LA PUNCTUL ĂSTA?
 *
 *  Nu „e aproape de ax", ci „e în DREPTUNGHIUL lui": pe lungul zidului
 *  înăuntrul lui, iar de-a latul în grosimea lui. Măsurate separat, fiindcă
 *  sunt două întrebări diferite, iar o singură distanță la ax le-ar topi
 *  într-una — un punct aflat dincolo de capătul unui zid colinear e la o
 *  jumătate de grosime de axul lui, deci ar trece drept atins, cu zidul
 *  terminat la 15 cm înaintea lui.
 *
 *  Toleranța e `ROST_LIPIT`: cât rost poate rămâne între două ziduri și ele
 *  să fie tot lipite. */
function corpulAjungeLa(w: PereteDoc, p: Pt): boolean {
  const [a, b] = axaPeretelui(w);
  const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
  if (!(L > 1e-9)) return false;
  // CÂT DE MULT TRECE PE DINCOLO DE CAPETE, pe lungul axului. Zero cât e
  // între ele; pe un zid curb, piciorul lui `pePeretele` e deja ținut între
  // capete, deci numai depărtarea laterală mai are ce spune.
  const pe = pePeretele(a, b, w.bulge ?? 0, p);
  let peste = 0;
  if (!w.bulge) {
    const u = ((p[0] - a[0]) * (b[0] - a[0]) + (p[1] - a[1]) * (b[1] - a[1])) / L;
    peste = u < 0 ? -u : u > L ? u - L : 0;
  }
  if (peste > ROST_LIPIT) return false;
  // DE-A LATUL: depărtarea de ax, față de jumătatea grosimii.
  const lat = Math.hypot(pe.punct[0] - p[0], pe.punct[1] - p[1]);
  return lat <= (w.thickness_m ?? 0) / 2 + ROST_LIPIT;
}

/** MUTĂ UN CAPĂT DE PERETE, liber în plan.
 *
 *  Se mută numai capătul: zidul se rotește și se lungește după el, exact ca
 *  atunci când tragi de un capăt în Homestyler. Toate capetele LIPITE de el se
 *  mută odată, deci pereții vecini îl urmează și colțul rămâne închis. */
/** CARE CAPETE ALE UNUI ZID SUNT PRINSE ÎN ALT ZID.
 *
 *  Un capăt e PRINS dacă CORPUL altui perete ajunge chiar la el. Din asta iese
 *  de care parte se lungește un zid căruia i se scrie altă lungime: capătul
 *  liber e cel care are unde să plece.
 *
 *  SE CAUTĂ CORPUL, nu un capăt vecin în același nod — și nici nu se mai trece
 *  prin `nodurile`. Două motive, amândouă văzute pe spărtură:
 *
 *    · NODURILE STRÂNG CAPETELE PE 25 CM, cât să absoarbă slăbiciunea
 *      transcrierii, iar atât e mai mult decât o spărtură: după
 *      `demoleazaBucata` cele două bucăți rămase stau cu capetele la 20 cm una
 *      de alta, în ACELAȘI nod, deci fiecare o socotea pe cealaltă drept zidul
 *      care o ține. Amândouă capetele ieșeau prinse, lungimea scrisă în panou
 *      se împărțea în două, și zidul pleca chiar din colțul în care e prins —
 *      «it shortens or increases the side that is connected to another wall and
 *      not the one disconnected» (20.10.2026). Tot acolo cade și capătul
 *      despărțiturii care TAIE zidul: ea stă pe FAȚA lui, nu îi închide
 *      capătul, iar peste spărtură nu ține nimic.
 *    · ÎN GRAF INTRĂ NUMAI PEREȚII CU AX SCRIS, iar axele le scrie editorul de
 *      module la deschidere (`cuAxeScrise`) — planul de nivel nu. Acolo graful
 *      era gol, deci TOATE capetele ieșeau libere și lungimea scrisă pleca
 *      mereu din capătul 1, care pe bucata a doua a unei spărturi e chiar cel
 *      prins. Întrebarea «mă ține cineva de capăt?» n-are nevoie de ax pe
 *      vecin, doar de corpul lui — deci nu se mai pune.
 *
 *  Un capăt care intră în MIJLOCUL altui zid e prins și el: așa arată o
 *  despărțitură oprită în fațadă, iar ea chiar n-are unde să crească pe-acolo.
 *
 *  FOILE LIPITE ALE ACELUIAȘI PERETE NU ÎL FAC PRINS. Un zid scris în două
 *  rânduri — miez plus termoizolație — are în nod și capătul celeilalte foi,
 *  dar aia nu e „alt perete", e același perete văzut de-a curmezișul: socotit
 *  prins, un zid de fațadă n-ar mai fi avut niciodată un capăt liber. */
export function capetePrinse(doc: TronsonDoc, i: number): [boolean, boolean] {
  const lipite = lipiteleLui(doc, i);
  const ziduri = doc.plan?.walls ?? [];
  if (!ziduri[i]) return [false, false];
  const ax = axaPeretelui(ziduri[i]);
  const tinut = (capat: 0 | 1) => ziduri.some((w, j) =>
    j !== i && !lipite.has(j) && corpulAjungeLa(w, ax[capat]));
  return [tinut(0), tinut(1)];
}

export function mutaCapatul(
  doc: TronsonDoc, i: number, capat: 0 | 1, delta: Pt,
  fara: ReadonlySet<number> = new Set(),
): TronsonDoc {
  const zid0 = doc.plan?.walls?.[i];
  if (!zid0 || (!delta[0] && !delta[1])) return doc;
  const nod = nodurile(doc).find((nd) =>
    nd.capete.some((c) => c.perete === i && c.capat === capat));
  const d = clona(doc);
  for (const c of nod?.capete ?? [{ perete: i, capat }]) {
    if (fara.has(c.perete)) continue;
    mutaCapatUnic(d, c.perete, c.capat, delta);
  }
  // Scurtat, fiecare zid mutat își strânge ferestrele — v. `strangeGolurile`.
  return strangeGolurile(doc, d);
}

/** Cât rost poate rămâne între două ziduri și ele să fie tot LIPITE. Trei
 *  centimetri: mai mult decât slăbiciunea transcrierii (măsurat 4 mm între
 *  fețele celor două foi ale fațadei) și mai puțin decât orice gol pe care
 *  l-ar desena cineva între doi pereți. */
export const ROST_LIPIT = 0.03;

/** UN ZID PENTRU MÂNĂ E TOT SANDVIȘUL, nu o foaie din el.
 *
 *  Fațada catalogului e scrisă în două rânduri: o foaie de rezistență de 30 cm
 *  și una de zidărie de 15 lipită pe ea, pe aceeași întindere, tăiate
 *  amândouă în dreptul acelorași ferestre. Pe ecran se văd ca UN perete —
 *  două dungi lipite —, iar axele de trasare ies și ele două (A și B pe M5,
 *  cu rost 0,000 între corpuri).
 *
 *  Trasă de corp, o foaie pleca singură: camera se sprijină pe fața
 *  CEALALTĂ — a foii rămase pe loc —, deci nu se schimba nimic, nici camera,
 *  nici mobila din ea. Măsurat pe catalog: din 306 pereți, 56 (18 %) trași cu
 *  0,30 m nu clinteau nicio cameră, iar aproape toți erau foaia subțire a
 *  fațadei sau o compartimentare lipită de alta. Exact «uneori merge și
 *  uneori nu».
 *
 *  Nu e o abatere de la «când trag de un perete nu vreau să tragă și alți
 *  pereți conectați după el»: acolo era vorba de zidurile care vin în
 *  CONTINUAREA lui sau îi ating capătul. Foaia de-alături nu vine după el, e
 *  el, văzut de-a curmezișul.
 *
 *  LIPIT înseamnă trei lucruri deodată, și toate trei trebuie:
 *
 *    * aceeași direcție — un zid perpendicular se atinge de fața lui, dar nu-i
 *      e foaie, se sprijină pe el;
 *    * corpurile lipite pe axa de mutare — rost sub `ROST_LIPIT`, sau
 *      suprapuse;
 *    * unul ÎN DREPTUL celuilalt, pe lungul lor. Fără asta, două bucăți ale
 *      aceleiași linii aflate la capete opuse ale modulului ar pleca împreună.
 *      Pragul e jumătate din cea mai scurtă dintre ele, plafonat la o jumătate
 *      de metru: foaia de 2,13 m a lui M5 e lipită și de bucata de 0,53 m care
 *      închide fereastra, iar aceea trebuie să plece cu ea.
 *
 *  Se crește până nu mai crește: un sandviș poate avea trei foi. */
export function lipiteleLui(doc: TronsonDoc, i: number): Set<number> {
  const grup = new Set<number>([i]);
  const ziduri = doc.plan?.walls ?? [];
  if (!ziduri[i]) return grup;
  const osii = ziduri.map((w) => osiaPeretelui(inelul(w.poly)));
  const axa = osii[i].axa;
  const corp = (o: ReturnType<typeof osiaPeretelui>) =>
    [Math.min(...o.fete), Math.max(...o.fete)] as [number, number];
  for (let creste = true; creste;) {
    creste = false;
    for (const j of [...grup]) {
      const oj = osii[j];
      if (oj.axa !== axa) continue;
      ziduri.forEach((_, k) => {
        if (grup.has(k)) return;
        const ok = osii[k];
        if (ok.axa !== axa) return;
        const a = corp(oj);
        const b = corp(ok);
        if (Math.max(a[0], b[0]) - Math.min(a[1], b[1]) > ROST_LIPIT) return;
        const acoperit = Math.min(Math.max(...oj.span), Math.max(...ok.span))
                       - Math.max(Math.min(...oj.span), Math.min(...ok.span));
        if (acoperit < Math.min(0.5, Math.min(oj.lungime, ok.lungime) / 2)) return;
        grup.add(k);
        creste = true;
      });
    }
  }
  return grup;
}

/** Cât de aproape trebuie adus un zid de altul ca să se lipească de el.
 *
 *  3 cm. A fost 8, și era prea mult: cerut pe nume, «la pereți să reduci
 *  range-ul de snap». Cu opt centimetri un zid tras lângă altul nu se mai
 *  lăsa pus la un rost mic — orice poziție dinăuntrul rostului se întorcea
 *  la fața vecinului, iar cine voia un decalaj de câțiva centimetri nu-l
 *  putea obține deloc. Trei centimetri prind nimereala mâinii la mărirea la
 *  care se lucrează și lasă în pace tot ce e ales anume. */
export const LIPIRE_LA_VECIN = 0.03;

/** UNDE S-AR AȘEZA ZIDUL DACĂ L-AI LĂSA AICI: poziția lipită de vecinul cel mai
 *  apropiat de pe aceeași direcție.
 *
 *  Ce rezolvă: «l-am pus greșit și nu pot să-l aliniez». Un zid de zidărie pus
 *  în continuarea unuia de rezistență trebuie să ajungă FAȚĂ ÎN FAȚĂ cu el, iar
 *  asta nu se nimerește din mână — la 60 px/m, un centimetru e jumătate de
 *  pixel. Deci se caută, printre zidurile de aceeași direcție, o față aflată la
 *  mai puțin de `LIPIRE_LA_VECIN` de una a zidului mutat, și se întoarce
 *  deplasarea care le pune exact una peste alta.
 *
 *  Se aliniază pe FEȚE, nu pe axe: două ziduri de grosimi diferite puse pe
 *  același ax ies în trepte pe amândouă părțile, iar ce se vede pe planșă și ce
 *  se tencuiește e fața.
 *
 *  `fara` scoate din căutare zidurile care PLEACĂ ODATĂ cu el — foile
 *  sandvișului (v. `lipiteleLui`). Fața lor e lipită de a lui prin definiție,
 *  deci orice tragere sub 8 cm s-ar fi întors la zero: fațada părea că nu se
 *  lasă mutată deloc. O țintă care se mută odată cu tine nu e o țintă. */
/** TOT PERETELE, așa cum îl vede ochiul: foile lui lipite ȘI bucățile de pe
 *  aceeași linie.
 *
 *  O fațadă e scrisă în document ca o mână de dreptunghiuri — tăiată pe
 *  LUNGIME la fiecare fereastră (`W14`, `W24`, `W26` pe M2) și pe GROSIME în
 *  foi lipite, miez plus termoizolație. Ochiul vede un perete; documentul are
 *  șase bucăți.
 *
 *  Trasă de una singură, camera nu se clintea: ea se sprijină pe fața bucății
 *  rămase, iar între cea dusă și cea rămasă se căsca o fâșie care nu e nici
 *  zid, nici cameră — «it leaves a lot of empty space sometimes in the
 *  flooring» (01.10.2026).
 *
 *  SE CREȘTE PÂNĂ NU MAI CREȘTE, alternând cele două reguli: o foaie aduce
 *  bucățile de pe linia ei, fiecare bucată își aduce foile. `lipiteleLui`
 *  singur ia doar sandvișul de sub deget; `runulLiniei` singur, doar o foaie
 *  pe toată lungimea.
 *
 *  NU ADUCE VECINII PERPENDICULARI: cine se reazemă în coasta peretelui rămâne
 *  unde e (cerut de Stefan — «când trag de un perete nu vreau să tragă și alți
 *  pereți conectați după el»). Aici nu pleacă alt perete, pleacă bucățile
 *  ACELUIAȘI perete. */
export function totPeretele(doc: TronsonDoc, i: number): Set<number> {
  const grup = new Set<number>([i]);
  for (let creste = true; creste;) {
    creste = false;
    for (const k of [...grup]) {
      for (const j of [...lipiteleLui(doc, k), ...runulLiniei(doc, k),
                       ...pesteGol(doc, k)]) {
        if (!grup.has(j)) { grup.add(j); creste = true; }
      }
    }
  }
  return grup;
}

/** BUCĂȚILE DE PE ACEEAȘI LINIE DESPĂRȚITE DE UN GOL — tot un perete.
 *
 *  `runulLiniei` merge prin NODURI, deci se oprește la prima fereastră: fațada
 *  lui M2 e scrisă `W14`, `W24`, `W26`, trei bucăți care nu se ating. Trasă de
 *  una, camera creștea doar cât ținea bucata aia.
 *
 *  PRAGUL NU E O DISTANȚĂ. Măsurate pe catalog toate cele 449 de perechi de pe
 *  aceeași linie, distanțele dintre ele merg continuu de la 0 la peste 14 m,
 *  fără nicio treaptă: orice prag ales ar fi tăiat la întâmplare prin ele, iar
 *  două compartimentări colineare de la capetele opuse ale planului ar fi
 *  plecat împreună.
 *
 *  Ce deosebește bucățile ACELUIAȘI perete de două ziduri colineare oarecare nu
 *  e cât de departe sunt, ci CE E ÎNTRE ELE: o ușă sau o fereastră. De-aia se
 *  citește din document, nu se ghicește din geometrie — golul chiar e motivul
 *  pentru care peretele e scris în bucăți. */
function pesteGol(doc: TronsonDoc, i: number): number[] {
  const ziduri = doc.plan?.walls ?? [];
  const zid = ziduri[i];
  if (!zid) return [];
  const o = osiaPeretelui(inelul(zid.poly));
  const peLinie = (w: PereteDoc) => {
    const ax = axaPeretelui(w);
    return (ax[0][o.axa] + ax[1][o.axa]) / 2;
  };
  const linia = peLinie(zid);
  const lung = o.axa === 0 ? 1 : 0;          // direcția LUNGĂ a liniei
  const goluri = [...(doc.plan?.doors ?? []), ...(doc.plan?.windows ?? [])]
    .map((g) => ({ pe: [g.x.a, g.y.a][lung], peN: [g.x.a, g.y.a][o.axa],
                   lat: g.w_m ?? 0.9 }));
  const span = (k: number) => {
    const ok = osiaPeretelui(inelul(ziduri[k].poly));
    return [Math.min(...ok.span), Math.max(...ok.span)] as [number, number];
  };
  const a = span(i);
  const out: number[] = [];
  ziduri.forEach((w, k) => {
    if (k === i) return;
    const ow = osiaPeretelui(inelul(w.poly));
    if (ow.axa !== o.axa) return;
    if (Math.abs(peLinie(w) - linia)
        > Math.abs(o.grosime - ow.grosime) / 2 + LIPIRE) return;
    const b = span(k);
    const de = Math.min(a[1], b[1]);
    const la = Math.max(a[0], b[0]);
    if (la <= de) return;                    // se ating deja: treaba runului
    // Un gol care stă pe linie ȘI cade în rostul dintre ele.
    const peste = goluri.some((g) =>
      Math.abs(g.peN - linia) <= o.grosime / 2 + ow.grosime / 2 + LIPIRE
      && g.pe - g.lat / 2 < la + LIPIRE && g.pe + g.lat / 2 > de - LIPIRE);
    if (peste) out.push(k);
  });
  return out;
}

export function lipirea(
  doc: TronsonDoc, i: number, dep: Pt, fara?: ReadonlySet<number>,
): { dep: Pt; lipit: boolean } {
  const zid = doc.plan?.walls?.[i];
  if (!zid) return { dep, lipit: false };
  const o = osiaPeretelui(inelul(zid.poly));
  const axa = o.axa;
  const fete = o.fete.map((f) => f + dep[axa]);

  let cel: { d: number; delta: number } | null = null;
  (doc.plan?.walls ?? []).forEach((w, j) => {
    if (j === i || fara?.has(j)) return;
    const ow = osiaPeretelui(inelul(w.poly));
    if (ow.axa !== axa) return;                      // altă direcție: n-are ce alinia
    for (const a of fete) {
      for (const b of ow.fete) {
        const d = Math.abs(b - a);
        if (d > LIPIRE_LA_VECIN) continue;
        if (!cel || d < cel.d) cel = { d, delta: b - a };
      }
    }
  });
  if (!cel) return { dep, lipit: false };
  const gasit = cel as { d: number; delta: number };
  const nou: Pt = [...dep] as Pt;
  nou[axa] = r3(nou[axa] + gasit.delta);
  return { dep: nou, lipit: true };
}

/** MUTĂ UN SINGUR PERETE, liber în plan — fără runul lui și fără graf.
 *
 *  E CEALALTĂ MUTARE, și îi trebuie un loc al ei. Trasă de corp, o bucată de
 *  zid duce cu ea toată LINIA din care face parte și împinge camerele de-o parte
 *  și de alta — corect, fiindcă pentru ochi linia aia e un zid. Dar cine tocmai
 *  a desenat o bucată în locul greșit nu vrea să miște linia: vrea s-o aducă pe
 *  ea unde trebuie, fără să atingă nimic altceva. Cătălin a dat peste asta exact
 *  așa: zidărie pusă în continuarea unui perete de rezistență, pusă strâmb, și
 *  niciun gest care s-o aducă la linie.
 *
 *  NU MIȘCĂ NICIO CAMERĂ, prin el însuși. Un zid mutat singur nu împinge
 *  nimic: camerele se refac oricum din pereți la fiecare mișcare, iar ce se
 *  vede e chiar ce s-a cerut — bucata dusă acolo unde a pus-o mâna.
 *
 *  `cuLipitele` DUCE ȘI FOILE DE-ALĂTURI (v. `lipiteleLui`), fiindcă ele nu
 *  sunt alt perete, sunt același perete văzut de-a curmezișul. Fără ele,
 *  fațada trasă de foaia subțire lăsa camera pe loc — se sprijină pe fața
 *  celeilalte foi. Gestul de glisare liberă nu-l cere: acolo omul chiar duce o
 *  bucată anume în alt loc.
 *
 *  GOLURILE ȘI PRIZELE MERG CU EL, ca la orice mutare (v. `ceStaPeZiduri`).
 *
 *  `cuVecinii` LUNGEȘTE PEREȚII LIPIȚI DE EL, și numai atât: cine e prins de un
 *  capăt al lui sau rezemat în coasta lui își mută capătul ăla, deci se
 *  lungește sau se scurtează (v. `lungesteVecinii`). Nu se bate cu «când trag
 *  de un perete nu vreau să tragă și alți pereți conectați după el»: niciun
 *  vecin nu PLEACĂ, toți rămân unde sunt cu celălalt capăt. Cerut de Stefan
 *  (17.09): «dacă peretele era lipit de un perete perpendicular, acel perete
 *  trebuia să se lungească».
 *
 *  `grup` DAT DE AFARĂ ÎNLOCUIEȘTE CĂUTAREA, și e singurul fel în care se pot
 *  muta mai multe bucăți cu o deplasare. Chemat o dată pentru fiecare bucată,
 *  cu `cuLipitele`, fiecare chemare mai aducea o dată foile de-alături: un
 *  sandviș de trei foi pleca de trei ori — măsurat pe M1 (`W17`), tras 0,30 m
 *  se ducea 0,90. Grupul se află o dată, la apucare, și de-atunci se mută ca
 *  un tot.
 *
 *  Îl cer gesturile care mută un ZID AL PLANULUI — tras de corp, sau dus la o
 *  cotă scrisă în panou. Nu-l cer săgețile: acolo omul împinge o BUCATĂ cu 5 cm,
 *  și vrea bucata aia acolo. */
/** ZIDUL MUTAT SE DEZLIPEȘTE DE AXA LUI: axa rămâne unde era, iar la locul nou
 *  nu răsare alta.
 *
 *  Cerut de arhitect (22.10.2026): «când mut un perete nu ar trebui să mute axa
 *  de care e agățat cu el. Adică dacă avem un perete pe axa A și îl mutăm, se
 *  detașează de axă și se mută separat și nu creează o axă nouă. Putem pune noi
 *  manual o axă nouă după. În cazul în care mutăm direct axa se comportă cum
 *  funcționează acum, se mută toți pereții de pe axă. Dacă aveam doar un singur
 *  perete atașat de o axă și îl mutăm, în cazul ăla tot așa rămâne axa pe
 *  poziție și o putem șterge noi după».
 *
 *  AXELE SE CITESC DIN PEREȚI, nu se declară (v. nota lui `axele`): o linie pe
 *  care se aliniază ziduri E un ax. De-aia mutarea zidului muta și axa — nu era
 *  o legătură scrisă undeva, era chiar deducția care se uita unde stau zidurile
 *  ACUM. Nu se poate deci «dezlipi» o legătură; ce se poate e să se scrie
 *  hotărârea omului peste deducție, și pentru asta există de mult `plan.axes`.
 *
 *  DOUĂ ÎNSEMNĂRI, câte una pentru fiecare jumătate a cererii:
 *
 *    · pe locul VECHI, o axă ADUSĂ — altfel, plecând singurul zid de pe ea, ea
 *      ar fi dispărut cu totul. Adusă, ea se desenează peste tot cadrul, fiindcă
 *      nu se mai sprijină pe nimic (v. `axele`, «ADUSE»), și se poate șterge cu
 *      mâna când omul vrea;
 *    · pe locul NOU, una STINSĂ (`ascuns`), ca zidul ajuns acolo să nu-și
 *      nască singur o axă. Numai dacă acolo nu era deja una: una care chiar
 *      exista nu se stinge, fiindcă n-a creat-o zidul ăsta.
 *
 *  SE CERE PE NUME, nu se face din oficiu, fiindcă prin aceeași funcție trece
 *  și mutarea unei AXE (`mutaAxul`, cu `grup`), unde dezlipirea ar fi fost
 *  exact pe dos: acolo axa TREBUIE să plece cu zidurile ei. */
function cuAxeleDezlipite(
  doc0: TronsonDoc, d: TronsonDoc, grup: Iterable<number>, dep: Pt,
): TronsonDoc {
  const mutate = new Set(grup);
  const peCare = axele(doc0).filter((a) => a.ziduri.some((j) => mutate.has(j)));
  if (!peCare.length) return d;
  const aveaDeja = (a: Ax, v: number) => axele(doc0).some(
    (b) => b.axa === a.axa && Math.abs(b.valoare - v) <= LIPIRE_AX);
  const insemnari = (d.plan?.axes ?? []).slice();
  const pune = (axa: 0 | 1, valoare: number, ascuns?: boolean) => {
    const v = r3(valoare);
    const i = insemnari.findIndex(
      (x) => x.axa === axa && Math.abs(x.valoare - v) <= LIPIRE_AX);
    if (i >= 0) insemnari.splice(i, 1);
    insemnari.push(ascuns ? { axa, valoare: v, ascuns: true } : { axa, valoare: v });
  };
  for (const a of peCare) {
    // cât s-a dus zidul PE NORMALA axei: ortogonală = pe axa ei
    const mers = a.n ? dep[0] * a.n[0] + dep[1] * a.n[1] : dep[a.axa];
    if (Math.abs(mers) < 1e-9) continue;
    pune(a.axa, a.valoare);                       // axa veche rămâne
    const nou = a.valoare + mers;
    if (!aveaDeja(a, nou)) pune(a.axa, nou, true);  // la locul nou, niciuna nouă
  }
  const iesire = clona(d);
  if (!iesire.plan) return d;
  iesire.plan.axes = insemnari;
  return iesire;
}

export function mutaPereteleSingur(
  doc: TronsonDoc, i: number, dep: Pt,
  { cuLipitele = false, cuVecinii = false, grup: dat, dezlipeste = false }:
    { cuLipitele?: boolean; cuVecinii?: boolean;
      grup?: Iterable<number>;
      /** ZIDUL SE DEZLIPEȘTE DE AXA LUI — v. `cuAxeleDezlipite`. Îl cer
       *  gesturile care mută un ZID (tras de corp, din săgeți, dus la o cotă
       *  scrisă); NU-l cere `mutaAxul`, unde axa trebuie să plece cu zidurile
       *  ei. De-aia e cerut pe nume și nu se face din oficiu: prin funcția asta
       *  trec amândouă. */
      dezlipeste?: boolean } = {},
): TronsonDoc {
  const zid0 = doc.plan?.walls?.[i];
  if (!zid0 || (!dep[0] && !dep[1])) return doc;
  const grup = dat
    ? new Set<number>([i, ...dat].filter((j) => doc.plan?.walls?.[j]))
    : cuLipitele ? lipiteleLui(doc, i) : new Set([i]);

  // SPAȚIILE EXTERIOARE SE MUTĂ DE MÂNĂ, fiindcă nu se derivă din ziduri.
  //
  // Camerele n-au nevoie de nimic aici: ele se refac din pereți la fiecare
  // mișcare, deci urmează zidul oricum. Balconul nu — el e o alegere scrisă în
  // document (v. `camere::refacute`), iar parapetul tras îl lăsa pe loc:
  // singura cameră din plan care nu-și urma peretele. Se mută pe LINIE, ca
  // orice cameră împinsă de un zid (v. `mutaLinie`).
  let baza = doc;
  for (const j of grup) {
    const oj = osiaPeretelui(inelul(doc.plan!.walls![j].poly));
    const dl = dep[oj.axa];
    if (!dl) continue;
    for (const fata of oj.fete) {
      baza = mutaLinie(baza, { axa: oj.axa, valoare: fata, delta: dl,
                               span: [Math.min(...oj.span), Math.max(...oj.span)],
                               ce: "afara" });
    }
  }

  const d = clona(baza);
  const gasite = ceStaPeZiduri(d.plan!, grup);
  for (const j of grup) {
    const w = d.plan!.walls![j];
    for (const q of w.poly) {
      q[0].a = r3(q[0].a + dep[0]);
      q[1].a = r3(q[1].a + dep[1]);
    }
    const ax = axaPeretelui(doc.plan!.walls![j]);
    w.axis = [[r3(ax[0][0] + dep[0]), r3(ax[0][1] + dep[1])],
              [r3(ax[1][0] + dep[0]), r3(ax[1][1] + dep[1])]];
  }
  duCeAmGasit(d.plan!, gasite, dep);
  if (cuVecinii) {
    golurileUrmeazaLiniile(d, doc, lungesteVecinii(d, doc, grup, dep));
  }
  // VECINII SCURTAȚI DE MUTARE ÎȘI STRÂNG FERESTRELE — v. `strangeGolurile`.
  //
  // Nu zidul mutat se scurtează aici (el se duce întreg), ci vecinii pe care
  // `lungesteVecinii` îi trage de capăt. `golurileUrmeazaLiniile` doar le MUTĂ
  // golurile pe noua linie, întregi — așa că o fereastră de 2,11 rămânea 2,11
  // pe un zid scăzut de la 19,90 la 19,60 și ieșea prin capătul lui.
  //
  // Drumul ăsta e cel mai umblat dintre toate: pe pânză se mută un zid mai des
  // decât i se trage de capăt. Strângerea era pusă numai pe celelalte trei
  // (`redimPeretele`, `mutaCapatul`, `mutaCapatulSingur`), și de-aia probele
  // treceau în timp ce mâna vedea fereastra neschimbată (02.10.2026).
  const iesire = strangeGolurile(doc, d);
  // DEZLIPIREA LA URMĂ, pe documentul gata mutat: ea scrie numai `plan.axes`,
  // deci nu atinge geometria pe care au lucrat pașii de mai sus.
  return dezlipeste ? cuAxeleDezlipite(doc, iesire, grup, dep) : iesire;
}

// ------------------------------------------ lipirea CAPETELOR de vecini ---
//
// TREI GESTURI CER ACELAȘI RĂSPUNS — «unde se lipește capătul ăsta?»: peretele
// nou, care trebuie să pornească DIN zidul din stânga și să se oprească ÎN cel
// din dreapta; capătul tras sau colțul tras, care trebuie să ajungă exact la
// vecin; și zidul mutat, care își găsește linia prin `lipirea`. Un singur loc
// care măsoară, ca fantoma de dinainte de apăsare și zidul de după ea să cadă
// în același punct.
//
// SE LIPEȘTE PE FAȚĂ, NU PE AX. A fost pe ax o zi: zidul nou intra cu jumătate
// de grosime în vecin, ca în `zideste`. Cerut altfel, și cu dreptate: «să facă
// snap pe marginea lor, nu pe centru». Ce se vede pe planșă și ce se
// tencuiește e fața, iar un zid care se oprește la fața vecinului are colțul
// închis fără să se suprapună cu el. Fața ALEASĂ e cea dinspre care vine zidul
// — cea de partea punctului `spre` —, fiindcă a lipi de fața cealaltă ar
// însemna să treci prin vecin.
//
// LA CAPĂTUL VECINULUI SUNT DOUĂ LOCURI BUNE, ȘI NICIUNUL NU E PE AX. A fost
// pe capătul axului o vreme, ca «punctul în care se întâlnesc și axele, și
// fețele» — dar capătul axului e ÎN corpul vecinului (e mijlocul feței lui de
// capăt), iar retras de-acolo cu jumătatea grosimii zidului care vine intră de
// tot înăuntru. Spus scurt și fără drept de apel: «are un punct de snap în
// interiorul peretelui cu care vreau să conectez, nu e niciodată nevoie de așa
// ceva».
//
// CE CERE DE FAPT MÂNA, la un capăt, e un COLȚ ÎNCHIS: două dreptunghiuri care
// se ating pe o muchie întreagă, fără să se suprapună și fără să lase o crăpă
// tură. Cu A zidul care vine (grosime a) și B vecinul (grosime b), asta se
// poate în exact două feluri, și amândouă se scriu din colțurile lui B:
//
//   * LÂNGĂ FAȚA LUI B  — capătul lui A se oprește pe fața lungă a lui B,
//     dinspre care a venit, iar fața lui A de dincolo continuă fața de capăt a
//     lui B. Ăsta e L-ul obișnuit, cu colțul de afară chiar colțul lui B.
//   * PESTE CAPĂTUL LUI B — A trece prin dreptul capătului și se oprește pe
//     fața lui de dincolo, iar fața lui A dinspre B se așază pe fața de capăt
//     a lui B. Aici B se termină ÎN A: «vreau să aliniez peretele lângă un
//     perete», adică lipit de capătul lui, nu intrat în coasta lui.
//
// Amândouă sunt «colțul lui B, plus jumătate din grosimea lui A pe lungul lui
// B» — o dată spre interiorul lui B, o dată dincolo de capăt. Care din ele se
// alege o spune mâna: cel mai apropiat de unde a lăsat capătul. Nicio unealtă
// în plus, niciun modificator de apăsat.
//
// PARALEL CU B, amândouă se topesc în capătul axului: acolo nu există colț de
// făcut, ci o continuare, iar fețele se potrivesc singure dacă grosimile sunt
// egale. De-aia ambele deplasări se înmulțesc cu sinusul unghiului dintre A și
// B — plin la perpendicular, zero în prelungire.

/** Cât de aproape de FAȚA unui zid trebuie lăsat un capăt ca să se lipească de
 *  el.
 *
 *  15 cm. A fost un sfert de metru — «cât nimerește mâna» —, și de-acolo vine
 *  senzația de zid care nu se lasă desenat unde vrei: un capăt lăsat oriunde pe
 *  o palmă în jurul unui vecin sărea la el. Un capăt lăsat ÎN corpul zidului
 *  (între fețe) e tot la distanță zero, deci colțul închis se face mai departe
 *  fără să țintești. */
export const LIPIRE_CAPAT = 0.15;

/** Cât de departe de mână poate cădea piciorul perpendicularei ca zidul nou
 *  să se mai așeze PERPENDICULAR pe fața vecinului.
 *
 *  Jumătate de metru, și nu se micșorează odată cu lipirea: nu e o rază de
 *  lipire, e pragul de la care se vede că zidul a fost tras oblic anume. Lipit,
 *  capătul ajunge tot pe fața vecinului în amândouă cazurile; ce se hotărăște
 *  aici e doar dacă zidul rămâne drept sau păstrează înclinarea mâinii. */
export const PERPENDICULAR_LA_MANA = 0.5;

export type CapatLipit = {
  /** Punctul de pe FAȚA vecinului, de partea dinspre care vine capătul — sau,
   *  când lipirea e la un capăt al lui, capătul AXULUI. Capătul de ax e un
   *  punct de lucru, nu o țintă: el stă în corpul vecinului, iar ținta se
   *  scrie din el cu `coltulLipit`, care știe și grosimea zidului care vine. */
  p: Pt;
  perete: number;
  /** Versorul axului vecinului. */
  u: Pt;
  /** Normala vecinului, spre partea de pe care s-a venit. */
  n: Pt;
  /** Grosimea vecinului: din ea ies colțurile lui de capăt. */
  grosime: number;
  /** Cât a fost de mutat capătul ca să ajungă acolo. */
  d: number;
  /** Capătul vecinului de care s-a lipit — `p` e atunci pe axul lui —, sau
   *  `null` când lipirea e pe o față lungă, undeva la mijlocul zidului. */
  laCapat: 0 | 1 | null;
};

/** Pe care parte a axului `(a, u)` stă punctul: +1, −1, sau 0 pe ax. */
function partea(a: Pt, n: Pt, p: Pt): number {
  const s = (p[0] - a[0]) * n[0] + (p[1] - a[1]) * n[1];
  return Math.abs(s) < 1e-9 ? 0 : Math.sign(s);
}

/** Punctul de pe FAȚA celui mai apropiat zid, dacă vreunul e la mai puțin de
 *  `tol`. `fara` scoate din căutare zidul care se mișcă: el nu se poate lipi de
 *  sine. `spre` spune de pe ce parte vine capătul, când punctul însuși nu o
 *  spune (e chiar pe ax) — celălalt capăt al zidului tras, de pildă. Arcele
 *  nu intră: n-au o față dreaptă pe care să cadă un capăt. */
export function capatLipit(
  doc: TronsonDoc, p: Pt, fara: ReadonlySet<number> = new Set(),
  tol = LIPIRE_CAPAT, spre?: Pt,
): CapatLipit | null {
  let cel: CapatLipit | null = null;
  (doc.plan?.walls ?? []).forEach((w, j) => {
    if (fara.has(j) || w.bulge) return;
    const [a, b] = axaPeretelui(w);
    const vx = b[0] - a[0];
    const vy = b[1] - a[1];
    const L2 = vx * vx + vy * vy;
    if (L2 < 1e-12) return;
    const L = Math.sqrt(L2);
    const u: Pt = [vx / L, vy / L];
    const n0: Pt = [-u[1], u[0]];
    const t = Math.max(0, Math.min(1,
      ((p[0] - a[0]) * vx + (p[1] - a[1]) * vy) / L2));
    const q: Pt = [a[0] + vx * t, a[1] + vy * t];          // pe ax
    // Partea: a punctului; dacă e chiar pe ax, a lui `spre`; altfel «+».
    const semn = partea(a, n0, p) || (spre ? partea(a, n0, spre) : 0) || 1;
    const n: Pt = [n0[0] * semn, n0[1] * semn];
    const h = w.thickness_m / 2;
    const f: Pt = [q[0] + n[0] * h, q[1] + n[1] * h];     // pe față
    // Distanța până la ZID: până la față când e afară, zero când e înăuntru.
    const dAx = Math.hypot(p[0] - q[0], p[1] - q[1]);
    const inLung = Math.abs(((p[0] - a[0]) * vx + (p[1] - a[1]) * vy) / L2 - t) < 1e-9;
    // LÂNGĂ UN CAPĂT, ȚINTA E CENTRUL FEȚEI DE CAPĂT — adică chiar capătul
    // axului —, nu colțul. Proiecția pe ax singură ducea acolo: un capăt adus
    // dincolo de capătul vecinului se aduna la capătul axului, iar jumătatea de
    // grosime pe normală îl împingea de-a latul, pe un COLȚ al dreptunghiului.
    // Două ziduri lipite colț la colț ieșeau atunci decalate cu o jumătate de
    // grosime, iar colțul rămânea deschis pe cealaltă parte. Pe capăt de ax se
    // închide singur: e punctul în care se întâlnesc și axele, și fețele.
    const dCap0 = Math.hypot(p[0] - a[0], p[1] - a[1]);
    const dCap1 = Math.hypot(p[0] - b[0], p[1] - b[1]);
    const laCapat: 0 | 1 | null = Math.min(dCap0, dCap1) > tol ? null
      : (dCap0 <= dCap1 ? 0 : 1);
    const d = laCapat != null ? Math.min(dCap0, dCap1)
      : inLung ? Math.max(0, dAx - h)
      : Math.hypot(p[0] - f[0], p[1] - f[1]);
    if (d > tol) return;
    const tinta: Pt = laCapat != null ? (laCapat === 0 ? a : b) : f;
    // Un capăt de ax bate o față oricât de apropiată: lângă colțul unui zid,
    // fața lungă e la distanță zero (mâna e chiar în corpul lui) și ar câștiga
    // mereu, deși ce s-a țintit se vede că e colțul.
    const maiBun = !cel
      || ((laCapat != null) === (cel.laCapat != null) ? d < cel.d : laCapat != null);
    if (maiBun) {
      cel = { p: [r3(tinta[0]), r3(tinta[1])], perete: j, u, n, d, laCapat,
              grosime: w.thickness_m };
    }
  });
  return cel;
}

/** CELE DOUĂ COLȚURI ÎNCHISE de la capătul vecinului, și cel ales de mână.
 *
 *  `e` e capătul găsit de `capatLipit` (cu `p` pe axul vecinului, deci în
 *  corpul lui); `gros` e grosimea zidului CARE VINE; `tinta` e locul unde mâna
 *  a lăsat capătul, din care se alege între cele două.
 *
 *  `dinspre` e direcția zidului care vine, DINSPRE CORPUL LUI SPRE CAPĂTUL
 *  care se lipește, și sensul ei contează: din ea se află pe ce parte a
 *  vecinului stă zidul. Partea nu se poate lua din `e.n` — aceea e partea
 *  PUNCTULUI, iar la un capăt punctul e chiar pe colț și trece dincolo de ax
 *  la un centimetru de tremurat de mână; cu ea, un zid venit din stânga și
 *  lăsat cu un fir peste ax primea colțul din dreapta și ieșea suprapus peste
 *  vecin.
 *
 *  Socoteala, cu `s` sinusul unghiului dintre ele (v. antetul secțiunii):
 *  jumătate din grosimea VECINULUI pe normală duce în colțul lui, iar jumătate
 *  din grosimea ZIDULUI CARE VINE pe lungul vecinului așază fața. Pe partea
 *  dinspre care s-a venit, retras în vecin, se iese LÂNGĂ FAȚA lui; pe partea
 *  cealaltă, dincolo de capăt, se iese PESTE CAPĂTUL lui. Paralel (`s = 0`)
 *  amândouă se întorc în capătul axului, care acolo e chiar continuarea
 *  căutată. */
export function coltulLipit(
  e: CapatLipit, gros: number, dinspre: Pt, tinta: Pt,
): Pt {
  if (e.laCapat == null) return e.p;
  const L = Math.hypot(dinspre[0], dinspre[1]);
  if (L < 1e-9) return e.p;
  const d: Pt = [dinspre[0] / L, dinspre[1] / L];
  const n0: Pt = [-e.u[1], e.u[0]];
  const s = Math.abs(d[0] * e.u[1] - d[1] * e.u[0]);       // |sin|
  // Partea de pe care VINE zidul: mergând pe `d`, a plecat din partea opusă.
  const spre = d[0] * n0[0] + d[1] * n0[1];
  const n: Pt = spre > 0 ? [-n0[0], -n0[1]] : n0;
  const afara: Pt = e.laCapat === 1 ? e.u : [-e.u[0], -e.u[1]];
  const peNormala = (e.grosime / 2) * s;     // până la colțul vecinului
  const peLung = (gros / 2) * s;             // cât ocupă zidul care vine
  const pe = (k: number, j: number): Pt => [
    r3(e.p[0] + n[0] * k + afara[0] * j),
    r3(e.p[1] + n[1] * k + afara[1] * j)];
  const langaFata = pe(peNormala, -peLung);
  const pesteCapat = pe(-peNormala, peLung);
  const cat = (q: Pt) => Math.hypot(q[0] - tinta[0], q[1] - tinta[1]);
  return cat(langaFata) <= cat(pesteCapat) ? langaFata : pesteCapat;
}

/** Unde taie dreapta `(p, u)` FAȚA zidului `j` de partea lui `spre`: punctul
 *  și parametrul lui de-a lungul zidului (0 la capătul 0, 1 la capătul 1).
 *  `null` când sunt paralele. */
function taieFata(
  doc: TronsonDoc, j: number, p: Pt, u: Pt, spre: Pt,
): { q: Pt; t: number } | null {
  const w = doc.plan?.walls?.[j];
  if (!w) return null;
  const [a0, b0] = axaPeretelui(w);
  const vx = b0[0] - a0[0];
  const vy = b0[1] - a0[1];
  const L = Math.hypot(vx, vy);
  if (L < 1e-9) return null;
  const n0: Pt = [-vy / L, vx / L];
  const semn = partea(a0, n0, spre) || 1;
  const h = w.thickness_m / 2;
  const a: Pt = [a0[0] + n0[0] * semn * h, a0[1] + n0[1] * semn * h];
  const den = u[0] * vy - u[1] * vx;
  if (Math.abs(den) < 1e-9) return null;
  const s = ((a[0] - p[0]) * vy - (a[1] - p[1]) * vx) / den;
  const q: Pt = [r3(p[0] + u[0] * s), r3(p[1] + u[1] * s)];
  const t = ((q[0] - a[0]) * vx + (q[1] - a[1]) * vy) / (L * L);
  return { q, t };
}

/** Cât poate depăși intersecția capătul feței și să fie tot «pe zid» —
 *  jumătatea unui zid gros, ca lipirea la colț să prindă. */
const PESTE_CAPATUL_FETEI = 0.15;

/** Cât de departe de o direcție însemnată poate ținti mâna și tot să cadă pe
 *  ea — cosinusul a 12°.
 *
 *  Conul ăsta e tot ce a mai rămas din «direcția e HOTĂRÂTĂ»: la 60 px/m un
 *  centimetru e jumătate de pixel, deci un unghi drept nu se nimerește din
 *  mână și trebuie lipit. Dar lipit ÎNTR-UN CON, nu impus: 12° e cât greșește
 *  un braț pe o tragere scurtă, și destul de strâmt cât să lase oblicele
 *  posibile. */
const LIPIRE_DIRECTIE = Math.cos((12 * Math.PI) / 180);

/** Direcția `d` (versor), căzută pe cea mai apropiată dintre cele patru ale
 *  vecinului — de-a lungul lui și perpendicular pe el, în ambele sensuri —,
 *  sau `null` dacă mâna ține un unghi care nu e al niciuneia. */
function directiaLipita(d: Pt, u: Pt, n: Pt): Pt | null {
  let cel: Pt | null = null;
  let best = LIPIRE_DIRECTIE;
  for (const c of [u, [-u[0], -u[1]] as Pt, n, [-n[0], -n[1]] as Pt]) {
    const cos = d[0] * c[0] + d[1] * c[1];
    if (cos > best) { best = cos; cel = c; }
  }
  return cel;
}

/** UN PERETE NOU, LIPIT DE VECINI — DAR ÎN DIRECȚIA ÎN CARE ÎL DUCE MÂNA.
 *
 *  Gestul cerut: pui zidul între două ziduri și el trebuie să fie LIPIT de cel
 *  din stânga și de cel din dreapta și PERPENDICULAR pe ele — nu «cam pe lângă»
 *  și «cam drept», fiindcă la 60 px/m un centimetru e jumătate de pixel și
 *  nimic din asta nu se nimerește din mână.
 *
 *  DIRECȚIA A FOST ÎNSĂ IMPUSĂ, nu lipită, și asta a rupt gestul: pornit de pe
 *  un zid orizontal, noul perete se putea duce DOAR pe verticală, fiindcă tot
 *  ce spunea cursorul era proiectat pe perpendiculara vecinului. Tras spre
 *  dreapta, proiecția ieșea ~0 — adică «nu mă lasă să trasez decât în sus».
 *  Acum direcția urmează mâna și cade pe una din cele patru ale vecinului
 *  numai când e ținută în conul de 12° din jurul ei (v. `directiaLipita`).
 *
 *  Deci, în ordine:
 *    1. capătul de pornire se lipește de FAȚA zidului cel mai apropiat, pe
 *       partea dinspre care a apăsat mâna;
 *    2. direcția e a mâinii, lipită de cele patru ale vecinului în con; dacă a
 *       ieșit PARALELĂ cu el iar apăsarea a fost lângă un capăt al lui,
 *       pornirea se mută pe CAPĂTUL axului — un zid paralel pornit de pe față
 *       e un zid mutat lateral cu o jumătate de grosime, nu o continuare;
 *    3. capătul de sfârșit se duce până în fața primului zid pe care îl
 *       întâlnește pe direcția aleasă;
 *    4. dacă pornirea e liberă și numai sfârșitul cade lângă un zid, zidul nou
 *       se așază pe piciorul perpendicularei din pornire pe fața lui — tot
 *       perpendicular, deci — când piciorul e aproape de unde a lăsat mâna.
 *
 *  Chemată și la apăsare (cu `la = de`), și la fiecare mișcare: fantoma se
 *  desenează din ce întoarce ea, iar zidul se pune din aceleași puncte. */
export function pereteNouLipit(
  doc: TronsonDoc, de: Pt, la: Pt,
  unealta: "perete" | "parapet" = "perete",
): { de: Pt; la: Pt; lipit: [number | null, number | null] } {
  // GROSIMEA CU CARE SE VA PUNE, fiindcă de ea depinde unde cade colțul: un
  // zid care încă nu există se retrage din capătul vecinului cu jumătatea
  // grosimii lui implicite (v. `coltulLipit`).
  const gros = (IMPLICITELE_ZIDULUI[unealta] ?? IMPLICITELE_ZIDULUI.perete)
    .grosime;
  const s = capatLipit(doc, de, new Set(), LIPIRE_CAPAT, la);
  if (s) {
    const u = s.u;
    const n: Pt = [-u[1], u[0]];
    // LA APĂSARE NU EXISTĂ DIRECȚIE, și nu e una nici de ghicit: măsurat de la
    // punctul lipit, `la` nemișcat ar da chiar deplasarea lipirii — un vector
    // care spune de unde a fost adus capătul, nu încotro pleacă zidul. Luat
    // drept direcție, el trăgea pornirea strâmb din colț înainte ca mâna să fi
    // arătat ceva.
    const brut: Pt = la[0] === de[0] && la[1] === de[1] ? [0, 0]
      : [la[0] - s.p[0], la[1] - s.p[1]];
    const Lb = Math.hypot(brut[0], brut[1]);
    const lipita = Lb < 1e-9 ? null
      : directiaLipita([brut[0] / Lb, brut[1] / Lb], u, n);
    // Direcția chiar folosită: cea lipită, altfel cea a mâinii. La apăsare
    // (`la === de`) nu există niciuna, și nici n-are ce fi proiectat.
    const dir: Pt | null = lipita
      ?? (Lb < 1e-9 ? null : [brut[0] / Lb, brut[1] / Lb]);

    // DE LA CAPĂTUL VECINULUI, când apăsarea a fost chiar lângă unul — «fix de
    // la marginea unui alt perete». Nu mai e nimic de făcut aici: `capatLipit`
    // întoarce capătul axului de câte ori mâna a apăsat lângă el (v. nota de
    // acolo). Apăsată la mijlocul unei fețe, pornirea rămâne pe față: acolo un
    // zid paralel e un zid al doilea, nu continuarea primului.
    //
    // Pornit de la capătul vecinului, se așază în colțul închis cel mai
    // apropiat de locul apăsării (v. `coltulLipit`). La apăsare încă nu există
    // direcție, deci nici colț de socotit: alegerea se face la prima mișcare,
    // când se vede încotro pleacă zidul.
    // `dir` pleacă DE LA capătul lipit, deci corpul zidului nou stă pe +dir,
    // iar `coltulLipit` cere direcția dinspre corp spre capăt: se întoarce.
    const de2 = dir ? coltulLipit(s, gros, [-dir[0], -dir[1]], de) : s.p;

    const t = dir ? (la[0] - de2[0]) * dir[0] + (la[1] - de2[1]) * dir[1] : 0;
    let la2: Pt = dir ? [r3(de2[0] + dir[0] * t), r3(de2[1] + dir[1] * t)]
      : [r3(de2[0]), r3(de2[1])];
    let lipitLa: number | null = null;
    const e = dir ? capatLipit(doc, la2, new Set([s.perete]), LIPIRE_CAPAT, de2)
      : null;
    if (e && e.laCapat != null) {
      // Oprit lângă capătul unui zid, zidul nou ajunge pe capătul axului lui —
      // colț închis, nu colț peste colț (v. `capatLipit`). Direcția cedează
      // atâta cât e nevoie: un sfert de metru, cât ține lipirea.
      la2 = coltulLipit(e, gros, dir!, la);
      lipitLa = e.perete;
    } else if (e) {
      const x = taieFata(doc, e.perete, de2, dir!, de2);
      if (x) {
        const [a, b] = axaPeretelui(doc.plan!.walls![e.perete]);
        const L = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
        const peste = PESTE_CAPATUL_FETEI / L;
        if (x.t >= -peste && x.t <= 1 + peste
            && Math.hypot(x.q[0] - la2[0], x.q[1] - la2[1]) <= LIPIRE_CAPAT) {
          la2 = x.q;
          lipitLa = e.perete;
        }
      }
    }
    return { de: de2, la: la2, lipit: [s.perete, lipitLa] };
  }
  const e = capatLipit(doc, la, new Set(), LIPIRE_CAPAT, de);
  if (!e) return { de, la, lipit: [null, null] };
  // Lângă capătul vecinului, tot pe capătul axului lui: acolo nu e nicio față
  // lungă pe care să cadă un picior de perpendiculară (v. `capatLipit`).
  if (e.laCapat != null) {
    const q = coltulLipit(e, gros, [e.p[0] - de[0], e.p[1] - de[1]], la);
    return { de, la: q, lipit: [null, e.perete] };
  }
  // Piciorul perpendicularei din `de` pe FAȚA vecinului dinspre `de`.
  const x = taieFata(doc, e.perete, de, e.n, de);
  const aproape = !!x && x.t >= 0 && x.t <= 1
    && Math.hypot(x.q[0] - la[0], x.q[1] - la[1]) <= PERPENDICULAR_LA_MANA;
  return { de, la: aproape ? x!.q : e.p, lipit: [null, e.perete] };
}

/** Unde ajunge capătul `capat` al zidului `i` dus la `tinta`, lipit de fața
 *  vecinului când e vreunul aproape.
 *
 *  LIPIREA ÎNTÂI PE PROPRIA DREAPTĂ: un zid lungit până la vecin se oprește
 *  exact unde dreapta lui taie fața vecinului, deci se lungește fără să se
 *  rotească. Abia dacă intersecția aia e departe de mână — zidul e tras oblic,
 *  deliberat — capătul cade pe proiecția cursorului pe fața vecinului. */
function capatulLipitDeVecin(doc: TronsonDoc, i: number, capat: 0 | 1,
                             tinta: Pt): Pt {
  const ax = axaPeretelui(doc.plan!.walls![i]);
  const alt = ax[1 - capat];
  const e = capatLipit(doc, tinta, new Set([i]), LIPIRE_CAPAT, alt);
  if (!e) return tinta;
  // CAPĂT LA CAPĂT: acolo nu mai e nimic de intersectat. Dreapta zidului tras
  // taie și fața lungă a vecinului, chiar lângă capătul lui, iar punctul ăla e
  // colțul — decalat cu o jumătate de grosime față de capătul axului. Colțul se
  // închide pe colțul lui, nu pe fața lungă (v. nota din `capatLipit`), iar
  // care din cele două colțuri închise o spune locul în care a fost lăsat
  // capătul. Direcția cu care se socotesc e a zidului de DUPĂ mutare: de la
  // celălalt capăt al lui spre capătul vecinului.
  if (e.laCapat != null) {
    return coltulLipit(e, doc.plan!.walls![i].thickness_m,
                       [e.p[0] - alt[0], e.p[1] - alt[1]], tinta);
  }
  // Propria dreaptă e a ZIDULUI DE-ACUM, nu a mâinii: direcția de la celălalt
  // capăt spre capătul tras înainte să plece.
  const L = Math.hypot(ax[capat][0] - alt[0], ax[capat][1] - alt[1]) || 1;
  const u: Pt = [(ax[capat][0] - alt[0]) / L, (ax[capat][1] - alt[1]) / L];
  const x = taieFata(doc, e.perete, alt, u, alt);
  const peFata = x && x.t >= -1e-6 && x.t <= 1 + 1e-6
    && Math.hypot(x.q[0] - tinta[0], x.q[1] - tinta[1]) <= LIPIRE_CAPAT;
  return peFata ? x!.q : e.p;
}

/** UN ZID ÎN «L», RUPT ÎN DOUĂ DREPTUNGHIURI.
 *
 *  DE CE. Câteva ziduri de colț sunt desenate ca un singur poligon în L — patru
 *  în tot catalogul (M5 W60/W67, M6 W52, M8 W33). Ca formă e corect, ca obiect
 *  de editat nu: un «L» n-are nici ax, nici lungime, nici grosime — toate trei
 *  presupun un dreptunghi. Tras de un capăt se strâmbă fără noimă, rotit se
 *  rupe, iar cotele lui spun întinderea cutiei, nu a zidului. Două
 *  dreptunghiuri lipite fac același desen și se poartă ca orice alt zid.
 *
 *  Merge doar pe «L»-uri aliniate pe axe, adică pe toate cele din catalog.
 *  Întoarce `null` pe orice altceva — dreptunghiuri, parapeți teșiți, forme cu
 *  mai multe colțuri —, ca să nu se atingă de ce nu știe să refacă. */
export function desfaLul(poly: readonly VarfAfin[]): [Pt, Pt][] | null {
  if (poly.length !== 6) return null;
  const P = poly.map((q) => [q[0].a, q[1].a] as Pt);
  // Numai colțuri drepte: fiecare latură pe orizontală sau pe verticală.
  for (let i = 0; i < 6; i++) {
    const a = P[i], b = P[(i + 1) % 6];
    if (Math.abs(a[0] - b[0]) > 1e-6 && Math.abs(a[1] - b[1]) > 1e-6) return null;
  }
  const xs = [...new Set(P.map((q) => r3(q[0])))].sort((a, b) => a - b);
  const ys = [...new Set(P.map((q) => r3(q[1])))].sort((a, b) => a - b);
  if (xs.length !== 3 || ys.length !== 3) return null;

  const inauntru = (x: number, y: number) => {
    let c = false;
    for (let i = 0, j = 5; i < 6; j = i++) {
      const [xi, yi] = P[i], [xj, yj] = P[j];
      if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) c = !c;
    }
    return c;
  };
  // Cele patru sferturi ale cutiei; un «L» le are pe exact trei.
  const are = [0, 1].map((i) => [0, 1].map((j) =>
    inauntru((xs[i] + xs[i + 1]) / 2, (ys[j] + ys[j + 1]) / 2)));
  const cate = are.flat().filter(Boolean).length;
  if (cate !== 3) return null;

  // Coloana întreagă rămâne întreagă; din cealaltă se ia sfertul care există.
  const stangaPlina = are[0][0] && are[0][1];
  const c = stangaPlina ? 0 : 1;          // coloana întreagă
  const d = stangaPlina ? 1 : 0;          // coloana ciuntită
  const jos = are[d][0];
  return [
    [[xs[c], ys[0]], [xs[c + 1], ys[2]]],
    [[xs[d], jos ? ys[0] : ys[1]], [xs[d + 1], jos ? ys[1] : ys[2]]],
  ];
}

/** Documentul cu toate «L»-urile rupte în două. Se cheamă la deschiderea
 *  editorului, lângă `cuAxeScrise`: de-acolo încolo nu mai există zid în L. */
export function cuLuriDesfacute(doc: TronsonDoc): TronsonDoc {
  const ziduri = doc.plan?.walls;
  if (!ziduri?.length) return doc;
  if (!ziduri.some((w) => desfaLul(w.poly))) return doc;
  const d = clona(doc);
  const iesite: PereteDoc[] = [];
  const folosite = new Set(ziduri.map((w) => w.id));
  for (const w of d.plan!.walls!) {
    const buc = desfaLul(w.poly);
    if (!buc) { iesite.push(w); continue; }
    buc.forEach(([a, b], k) => {
      const lat = b[0] - a[0], inalt = b[1] - a[1];
      const peX = lat >= inalt;
      const gros = r3(peX ? inalt : lat);
      const ax: [Pt, Pt] = peX
        ? [[r3(a[0]), r3(a[1] + inalt / 2)], [r3(b[0]), r3(a[1] + inalt / 2)]]
        : [[r3(a[0] + lat / 2), r3(a[1])], [r3(a[0] + lat / 2), r3(b[1])]];
      let id = k === 0 ? w.id : `${w.id}b`;
      while (k > 0 && folosite.has(id)) id += "b";
      folosite.add(id);
      iesite.push({ ...clona(w), id, thickness_m: gros, axis: ax,
                    poly: poligonDinAx(ax[0], ax[1], gros, 0) });
    });
  }
  d.plan!.walls = iesite;
  return d;
}

/** UNGHIUL AXULUI unui perete, în grade, în [0, 360). */
export function unghiulPeretelui(w: PereteDoc): number {
  const [a, b] = axaPeretelui(w);
  const u = (Math.atan2(b[1] - a[1], b[0] - a[0]) * 180) / Math.PI;
  return ((u % 360) + 360) % 360;
}

/** ROTEȘTE UN PERETE LA UN UNGHI ANUME, în jurul mijlocului lui.
 *
 *  Mijlocul, nu un capăt: un perete întors de la un capăt pleacă din loc, și
 *  cine scrie «0» într-un câmp de unghi vrea zidul drept, nu mutat. Lungimea și
 *  grosimea rămân; curbura, dacă o are, se poartă mai departe. */
export function roteastePeretele(doc: TronsonDoc, i: number, grade: number): TronsonDoc {
  const w0 = doc.plan?.walls?.[i];
  if (!w0) return doc;
  const [a, b] = axaPeretelui(w0);
  const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
  if (!(L > 1e-9)) return doc;
  const mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
  const rad = (grade * Math.PI) / 180;
  const hx = (Math.cos(rad) * L) / 2, hy = (Math.sin(rad) * L) / 2;
  const na: Pt = [r3(mx - hx), r3(my - hy)];
  const nb: Pt = [r3(mx + hx), r3(my + hy)];
  if (na[0] === a[0] && na[1] === a[1] && nb[0] === b[0] && nb[1] === b[1]) return doc;
  const d = clona(doc);
  const w = d.plan!.walls![i];
  // CE STĂ PE EL PLEACĂ CU EL: golurile se numără de la capăt, deci rămân unde
  // erau pe zid — v. `ceStaPeZiduri`, aceeași regulă ca la mutare.
  w.axis = [na, nb];
  w.poly = poligonDinAx(na, nb, w.thickness_m, w.bulge ?? 0);
  return d;
}

/** ÎNDREAPTĂ PERETELE: îl duce la cel mai apropiat unghi drept.
 *
 *  CERUT DUPĂ O ÎNREGISTRARE. Un perete lăsat strâmb — 3,8° pe M1B — nu se mai
 *  putea aduce la loc cu mâna: tras de capăt ajungeai la 0,8° și tot strâmb
 *  rămânea, fiindcă nimic nu-l prindea de orizontală. Un buton face într-o
 *  apăsare ce zece trageri nu reușeau. */
export function indreaptaPeretele(doc: TronsonDoc, i: number): TronsonDoc {
  const w = doc.plan?.walls?.[i];
  if (!w) return doc;
  return roteastePeretele(doc, i, Math.round(unghiulPeretelui(w) / 90) * 90);
}

/** MUTĂ UN CAPĂT AL UNUI SINGUR PERETE, liber în plan, lipit de vecin când
 *  ajunge lângă el.
 *
 *  Fără graf: vecinii care împărțeau nodul NU urmează. Cine trage de un capăt
 *  vrea să lungească sau să rotească ZIDUL ĂSTA, nu să care după el tot colțul
 *  — iar colțul se închide oricum, fiindcă capătul se lipește de fața
 *  vecinului când ajunge lângă ea. */
export function mutaCapatulSingur(
  doc: TronsonDoc, i: number, capat: 0 | 1, delta: Pt, lipeste = true,
): TronsonDoc {
  const w = doc.plan?.walls?.[i];
  if (!w || (!delta[0] && !delta[1])) return doc;
  const ax = axaPeretelui(w);
  const tinta: Pt = [ax[capat][0] + delta[0], ax[capat][1] + delta[1]];
  let dest = lipeste ? capatulLipitDeVecin(doc, i, capat, tinta) : tinta;
  // ÎN AER LIBER, UNGHIUL SE LIPEȘTE DE DREPT; lângă un vecin, NU.
  //
  // Cele două cazuri sunt ale unor gesturi diferite, chiar dacă mâna face la
  // fel. Lângă un vecin, capătul se duce pe colțul lui, și acolo o rotire de o
  // jumătate de grad e CHIAR ce închide colțul — v. probele de mai jos, care
  // cer (5.9, 0.05). Departe de orice, nimic nu mai justifică un unghi strâmb:
  // acolo s-a născut peretele de 3,8° din înregistrare, pe care nu-l mai
  // îndrepta nimic. Deci se lipește doar când capătul n-a găsit pe nimeni.
  // CAPĂTUL LĂSAT APROAPE DE DREPT SE DUCE LA DREPT, și lângă un vecin, și în
  // aer liber.
  //
  // Lipirea de vecin (mai sus) alege PUNCTUL: fața sau colțul lui. Alegea și
  // unghiul, fără să vrea — capătul plecat pe fața vecinului, iar celălalt
  // rămas pe loc, înclină zidul. Pe un perete de 1,34 m, 7 cm de fața vecinului
  // înseamnă 3,1°, și exact așa arăta fațada din înregistrarea din 24.09: o
  // așchie oblică, nici dreaptă, nici aliniată cu zidurile de lângă ea.
  //
  // Deci punctul rămâne al vecinului, direcția e a unghiului drept: se păstrează
  // DEPĂRTAREA găsită de lipire și se așază pe direcția dreaptă. Ce se pierde e
  // potrivirea exactă a fețelor — o jumătate de grosime, sub un centimetru pe
  // zidurile astea, nevăzută la orice mărire de lucru. Ce se câștigă e un zid
  // drept, care se vede de la prima privire. V. probele care cer `[5.9, 0]`.
  if (lipeste) {
    const alt = ax[1 - capat];
    const brut = (Math.atan2(tinta[1] - alt[1], tinta[0] - alt[0]) * 180) / Math.PI;
    const drept = lipesteUnghiul(brut, []);
    if (Math.abs(((drept - brut + 540) % 360) - 180) > 1e-9) {
      const L = Math.hypot(dest[0] - alt[0], dest[1] - alt[1]);
      const rad = (drept * Math.PI) / 180;
      dest = [r3(alt[0] + Math.cos(rad) * L), r3(alt[1] + Math.sin(rad) * L)];
    }
  }
  const dep: Pt = [r3(dest[0] - ax[capat][0]), r3(dest[1] - ax[capat][1])];
  if (!dep[0] && !dep[1]) return doc;
  const d = clona(doc);
  mutaCapatUnic(d, i, capat, dep);
  // Scurtat, zidul își strânge ferestrele — v. `strangeGolurile`. ASTA E
  // GESTUL OBIȘNUIT: apuci capătul și tragi.
  return strangeGolurile(doc, d);
}

// ------------------------------------------ redimensionarea din cadru ---
//
// ZIDUL ALES ARE UN CADRU, ca o piesă de mobilier: patru laturi și patru
// colțuri de tras. Cerut anume: «să pot trage de colțuri și laturi pentru
// resize», și «sus-jos dacă apuc de marginea albastră de sus sau jos».
//
//   * o LATURĂ LUNGĂ (o față) trasă -> se schimbă GROSIMEA: fața aia se duce
//     după mână, cealaltă rămâne pe loc — un zid se îngroașă într-o parte;
//   * o LATURĂ SCURTĂ (un capăt) trasă -> se schimbă LUNGIMEA: capătul se duce
//     pe lungul zidului după mână, zidul nu se rotește; se lipește de fața
//     vecinului când ajunge lângă ea;
//   * un COLȚ tras -> amândouă deodată.
//
// Corpul zidului NU e în cadru: tras de corp, zidul se MUTĂ întreg (v.
// `mutaPereteleSingur`), cu selecție sau fără.

/** Cel mai subțire zid care se poate trage din cadru. Sub el, o latură trasă
 *  peste cealaltă ar întoarce zidul pe dos. */
export const GROSIME_MIN = 0.05;

/** Cel mai scurt zid care se poate trage din cadru. */
export const LUNGIME_MIN = 0.1;

/** Cât de aproape de grosimea unui vecin trebuie ajuns ca să se lipească de ea.
 *
 *  2,5 cm, adică jumătate din pasul pânzei: o față trasă cade oricum din 5 în 5
 *  cm, deci pragul ăsta prinde exact treapta de dinainte și pe cea de după
 *  grosimea vecinului și nicio alta. Mai mare, un zid de 25 nu s-ar mai putea
 *  trage lângă unul de 30 fără să sară pe el. */
export const LIPIRE_GROSIME = 0.025;

/** Distanța de la punctul `p` la segmentul `(a, b)`. */
function pctLaSegment(p: Pt, a: Pt, b: Pt): number {
  const vx = b[0] - a[0];
  const vy = b[1] - a[1];
  const L2 = vx * vx + vy * vy;
  const t = L2 < 1e-12 ? 0
    : Math.max(0, Math.min(1, ((p[0] - a[0]) * vx + (p[1] - a[1]) * vy) / L2));
  return Math.hypot(p[0] - (a[0] + vx * t), p[1] - (a[1] + vy * t));
}

/** GROSIMILE PEREȚILOR ALĂTURAȚI ZIDULUI `i` — cele de care se poate lipi o
 *  față trasă.
 *
 *  «Alăturat» = axele se ating: fie un capăt al vecinului cade pe axul nostru
 *  (colț, sau T lipit în mijlocul zidului), fie un capăt al nostru cade pe al
 *  lui. Măsurat pe axe și nu pe fețe, ca să nu depindă chiar de mărimea care se
 *  schimbă acum.
 *
 *  Fără ordine și fără duplicate: cine cheamă alege cea mai apropiată. */
export function grosimiVecine(doc: TronsonDoc, i: number,
                              tol = LIPIRE_NOD): number[] {
  const w0 = doc.plan?.walls?.[i];
  if (!w0) return [];
  const [a0, b0] = axaPeretelui(w0);
  const out = new Set<number>();
  (doc.plan?.walls ?? []).forEach((w, j) => {
    if (j === i) return;
    const [a, b] = axaPeretelui(w);
    const atinge = pctLaSegment(a, a0, b0) <= tol || pctLaSegment(b, a0, b0) <= tol
      || pctLaSegment(a0, a, b) <= tol || pctLaSegment(b0, a, b) <= tol;
    if (atinge) out.add(w.thickness_m);
  });
  return [...out];
}

/** Grosimea `gros`, lipită de a celui mai apropiat vecin când e lângă ea. */
export function grosimeLipita(doc: TronsonDoc, i: number, gros: number): number {
  let cel = gros;
  let best = LIPIRE_GROSIME;
  for (const t of grosimiVecine(doc, i)) {
    const d = Math.abs(t - gros);
    if (d < best) { best = d; cel = t; }
  }
  return cel;
}

/** Un colț sau o latură a cadrului, ca ce anume schimbă. */
export type Prindere = {
  /** Capătul care se mută pe lungul zidului, sau nimic. */
  capat: 0 | 1 | null;
  /** Fața care se mută de-a latul — +1 pe partea normalei `(-uy, ux)` a
   *  axului, −1 pe cealaltă —, sau nimic. */
  fata: 1 | -1 | null;
};

/** Cadrul zidului drept `w`: colțurile, în ordinea capăt 0 față +, capăt 1
 *  față +, capăt 1 față −, capăt 0 față −. `null` pe un zid curb. */
export function cadrulPeretelui(w: PereteDoc): [Pt, Pt, Pt, Pt] | null {
  if (w.bulge) return null;
  const [a, b] = axaPeretelui(w);
  const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
  if (L < 1e-9) return null;
  const nx = (-(b[1] - a[1]) / L) * (w.thickness_m / 2);
  const ny = ((b[0] - a[0]) / L) * (w.thickness_m / 2);
  return [[r3(a[0] + nx), r3(a[1] + ny)], [r3(b[0] + nx), r3(b[1] + ny)],
          [r3(b[0] - nx), r3(b[1] - ny)], [r3(a[0] - nx), r3(a[1] - ny)]];
}

/** REDIMENSIONEAZĂ ZIDUL DIN CADRU: duce prinderea la punctul `p`.
 *
 *  Pornește mereu din documentul de la apăsare (chemată cu `doc0`), deci
 *  poziția e ABSOLUTĂ, nu un pas peste pasul de dinainte — ca orice gest de
 *  pe pânză (v. nota de la `Gest` din `Panza.tsx`).
 *
 *  Poligonul se REFACE din ax și grosime, ca la schimbarea grosimii din panou
 *  (`schimbaPeretele`): un zid tras din cadru e un dreptunghi cu alte
 *  dimensiuni, nu același dreptunghi cu vârfurile mutate. `k`-urile se iau de
 *  la cel mai apropiat vârf vechi, cum face și `mutaCapatUnic`. */
export function redimPeretele(
  doc: TronsonDoc, i: number, prindere: Prindere, p: Pt, lipeste = true,
): TronsonDoc {
  const w0 = doc.plan?.walls?.[i];
  if (!w0 || w0.bulge) return doc;
  const ax0 = axaPeretelui(w0);
  const L0 = Math.hypot(ax0[1][0] - ax0[0][0], ax0[1][1] - ax0[0][1]);
  if (L0 < 1e-9) return doc;
  const u: Pt = [(ax0[1][0] - ax0[0][0]) / L0, (ax0[1][1] - ax0[0][1]) / L0];
  const n: Pt = [-u[1], u[0]];
  let ax: [Pt, Pt] = [[...ax0[0]] as Pt, [...ax0[1]] as Pt];
  let gros = w0.thickness_m;

  if (prindere.fata) {
    const s = prindere.fata;
    // Distanța semnată a cursorului față de ax, pe normala `n`.
    const f = (p[0] - ax0[0][0]) * n[0] + (p[1] - ax0[0][1]) * n[1];
    // Fața opusă stă la −s·t/2; noua grosime e cât e de la ea până la mână.
    gros = Math.max(GROSIME_MIN, r3(s * f + w0.thickness_m / 2));
    // LIPITĂ DE GROSIMEA VECINULUI când ajunge lângă ea. Un zid tras din mână
    // iese cu 0,27 lângă unul de 0,30, iar pe planșă cei doi metri de diferență
    // se văd ca o treaptă în colț. V. `grosimeLipita`.
    if (lipeste) gros = Math.max(GROSIME_MIN, grosimeLipita(doc, i, gros));
    // Axul ține mijlocul, deci pleacă cu jumătate din creștere, spre fața
    // trasă — ca fața opusă să rămână exact unde era.
    const dj = ((gros - w0.thickness_m) / 2) * s;
    ax = [[r3(ax[0][0] + n[0] * dj), r3(ax[0][1] + n[1] * dj)],
          [r3(ax[1][0] + n[0] * dj), r3(ax[1][1] + n[1] * dj)]];
  }

  if (prindere.capat != null) {
    const c = prindere.capat;
    const alt = ax[1 - c];
    // Pe LUNGUL zidului: proiecția mâinii pe dreapta axului, nu mâna însăși —
    // un capăt tras din cadru lungește, nu rotește.
    const dir: Pt = c === 1 ? u : [-u[0], -u[1]];
    let t = (p[0] - alt[0]) * dir[0] + (p[1] - alt[1]) * dir[1];
    t = Math.max(LUNGIME_MIN, t);
    let cap: Pt = [r3(alt[0] + dir[0] * t), r3(alt[1] + dir[1] * t)];
    if (lipeste) {
      // Lipirea se măsoară pe zidul CU AXUL DE-ACUM (grosimea poate fi alta),
      // deci pe o copie în care axul e scris deja.
      const d1 = clona(doc);
      d1.plan!.walls![i].axis = ax;
      d1.plan!.walls![i].thickness_m = gros;
      cap = capatulLipitDeVecin(d1, i, c, cap);
      // TOT PE LUNGUL ZIDULUI, și după lipire: din cadru se trage LUNGIMEA, iar
      // un capăt lipit de capătul unui vecin care nu stă chiar pe dreapta
      // noastră ar roti zidul — alt gest decât cel apucat. Lipirea rămâne, dar
      // atât cât intră pe direcția zidului: piciorul perpendicularei.
      const tc = (cap[0] - alt[0]) * dir[0] + (cap[1] - alt[1]) * dir[1];
      cap = [r3(alt[0] + dir[0] * tc), r3(alt[1] + dir[1] * tc)];
      const Lc = Math.hypot(cap[0] - alt[0], cap[1] - alt[1]);
      if (Lc < LUNGIME_MIN) cap = [r3(alt[0] + dir[0] * LUNGIME_MIN),
                                   r3(alt[1] + dir[1] * LUNGIME_MIN)];
    }
    ax = c === 0 ? [cap, ax[1]] : [ax[0], cap];
  }

  const neschimbat = gros === w0.thickness_m
    && ax[0][0] === ax0[0][0] && ax[0][1] === ax0[0][1]
    && ax[1][0] === ax0[1][0] && ax[1][1] === ax0[1][1];
  if (neschimbat) return doc;

  const d = clona(doc);
  const w = d.plan!.walls![i];
  const vechi = w.poly.map((q) => ({
    p: [q[0].a, q[1].a] as Pt, kx: q[0].k ?? 0, ky: q[1].k ?? 0,
  }));
  w.axis = ax;
  w.thickness_m = gros;
  w.poly = poligonDinAx(ax[0], ax[1], gros, 0).map((q) => {
    let cel = vechi[0];
    let best = Infinity;
    for (const v of vechi) {
      const dd = (v.p[0] - q[0].a) ** 2 + (v.p[1] - q[1].a) ** 2;
      if (dd < best) { best = dd; cel = v; }
    }
    if (cel.kx) q[0].k = cel.kx;
    if (cel.ky) q[1].k = cel.ky;
    return q;
  });
  // Ferestrele zidului scurtat se strâng cu el — v. `strangeGolurile`, care o
  // face pentru TOATE drumurile pe care se schimbă o lungime, nu doar pentru
  // cadrul ăsta.
  //
  // Restul golurilor și aparatele rămân unde erau: o față mutată nu le mută,
  // iar un capăt lungit nu schimbă distanța lor față de capătul rămas. Ce ar
  // cădea în afara zidului scurtat rămâne al lui, la locul lui — ca la
  // `mutaCapatul`; se vede și se trage înapoi.
  return strangeGolurile(doc, d);
}

/** PE CE AXĂ SE OGLINDEȘTE: `"h"` = stânga-dreapta, adică în jurul unei
 *  verticale; `"v"` = sus-jos. Numele sunt ale BUTOANELOR, nu ale axelor de
 *  coordonate — «oglindește H» e ce vede omul pe ecran. */
export type Axa = "h" | "v";

/** Oglinda în jurul unui punct. */
const oglinditorul = (c: Pt, axa: Axa) => (q: Pt): Pt => (
  axa === "h" ? [r3(2 * c[0] - q[0]), r3(q[1])]
    : [r3(q[0]), r3(2 * c[1] - q[1])]);

/** Rotitorul în jurul unui punct, cu `grade`. */
const rotitorul = (c: Pt, grade: number) => {
  const rad = (grade * Math.PI) / 180;
  const co = Math.cos(rad);
  const si = Math.sin(rad);
  return (q: Pt): Pt => [
    r3(c[0] + (q[0] - c[0]) * co - (q[1] - c[1]) * si),
    r3(c[1] + (q[0] - c[0]) * si + (q[1] - c[1]) * co),
  ];
};

const in360 = (v: number) => ((v % 360) + 360) % 360;

/** UNGHIUL UNEI DIRECȚII OGLINDITE. Direcția lui `rot_deg` e `(cos θ, sin θ)`
 *  (v. `doc::tocurile`), iar oglinda o duce în `(−cos θ, sin θ)` pe orizontală
 *  și în `(cos θ, −sin θ)` pe verticală — adică θ → 180° − θ și θ → −θ. */
const unghiOglindit = (deg: number, axa: Axa) =>
  r3(in360(axa === "h" ? 180 - deg : -deg));

/** MÂNA UNUI SIMBOL OGLINDIT: unghiul și cele două steaguri ale unei piese de
 *  mobilier sau ale unui aparat, după ce e întors în oglindă pe axa dată.
 *
 *  Desenul unei piese e `rotate(θ) scale(±1, ±1)` (v. `Desen`), deci oglindirea
 *  ei ÎN LUME nu e simpla basculare a unui steag: la 90° cele două steaguri își
 *  schimbă rolurile între ele, fiindcă piesa stă culcată. Cine apasă «oglindește
 *  H» se uită la ecran, nu la axa lungă a piesei — deci socoteala se face aici,
 *  o dată, pentru toate locurile care oglindesc (butoanele, tastele H/V, zidul
 *  care-și duce aparatele).
 *
 *  ACEEAȘI SCRIERE, DOUĂ FORME. `(θ, x, y)` și `(θ + 180°, !x, !y)` sunt
 *  același desen — `scale(-1,-1)` E o rotire cu 180°. Dintre ele se alege cea
 *  care lasă UNGHIUL pe loc: altfel o piesă așezată la 90° ar sări în panou la
 *  270° la fiecare oglindire, fără ca pe planșă să se schimbe ceva din ce se
 *  vede. La unghiuri care nu sunt sferturi de tur nu există alegere, iar
 *  unghiul chiar se schimbă — acolo oglindirea nu se poate scrie altfel. */
export function mainaOglindita(
  deg: number, oglX: boolean, oglY: boolean, axa: Axa,
): { rot: number; oglX: boolean; oglY: boolean } {
  let rot = r3(in360(-deg));
  let x = axa === "h" ? !oglX : oglX;
  let y = axa === "v" ? !oglY : oglY;
  if (Math.abs(in360(rot - deg) - 180) < 1e-9) {
    rot = r3(in360(deg));
    x = !x;
    y = !y;
  }
  return { rot, oglX: x, oglY: y };
}

/** A DEVICE IN A MIRROR. `rot_deg` of a box or a footed symbol is the wall's
 *  normal into the room, not a drawing's angle: its frame is `rotate(deg − 90)`,
 *  a quarter turn off the frame `mainaOglindita` is written for, so the normal
 *  mirrors as a direction, and the flag left to write is the row's order or the
 *  hand. */
function oglindesteAparatul(a: ElectricDoc, axa: Axa): void {
  // A strip runs along `u`, the normal turned −90°: a mirror turns that
  // direction against the mirrored normal, so its end lies at −length.
  if (a.kind === "banda_led") {
    a.rot_deg = unghiOglindit(a.rot_deg ?? 0, axa);
    if (a.length_m != null) a.length_m = -a.length_m;
    return;
  }
  if (a.kind === "doza" || aparatul(a.kind)?.picior) {
    a.rot_deg = unghiOglindit(a.rot_deg ?? 0, axa);
    if (a.flip_x) delete a.flip_x; else a.flip_x = true;
    return;
  }
  const m = mainaOglindita(a.rot_deg ?? 0, a.flip_x === true,
                           a.flip_y === true, axa);
  a.rot_deg = m.rot;
  if (m.oglX) a.flip_x = true; else delete a.flip_x;
  if (m.oglY) a.flip_y = true; else delete a.flip_y;
}

/** ROTEȘTE ZIDUL în jurul mijlocului lui, cu `grade`.
 *
 *  Axul se întoarce și poligonul se REFACE din el — ca la `redimPeretele`: un
 *  zid rotit e un dreptunghi cu alt unghi, nu același dreptunghi cu vârfurile
 *  târâte. `k`-urile se iau de la cel mai apropiat vârf vechi.
 *
 *  GOLURILE ȘI APARATELE SE ÎNTORC CU EL, în jurul aceluiași centru, iar
 *  `rot_deg` li se adună — ținut în [0, 360), ca la piese (v. `schimbaMobila`):
 *  rotit de destule ori, ar fi crescut la nesfârșit și panoul ar fi arătat 450°. Fără asta, o ușă rămasă la unghiul vechi ar sta
 *  de-a curmezișul zidului ei — lucru pe care mutarea (`mutaPereteleSingur`)
 *  îl face deja, doar că prin translație.
 *
 *  NU-ȘI CARĂ VECINII și nu se lipește de nimic: rotirea e a ZIDULUI ĂSTA, iar
 *  colțurile se închid pe urmă trăgând de capete (v. `mutaCapatulSingur`). */
export function rotestePeretele(
  doc: TronsonDoc, i: number, grade: number,
): TronsonDoc {
  const w0 = doc.plan?.walls?.[i];
  if (!w0 || !grade) return doc;
  const [a0, b0] = axaPeretelui(w0);
  const cx = (a0[0] + b0[0]) / 2;
  const cy = (a0[1] + b0[1]) / 2;
  const rad = (grade * Math.PI) / 180;
  const co = Math.cos(rad);
  const si = Math.sin(rad);
  const rot = (q: Pt): Pt => [
    r3(cx + (q[0] - cx) * co - (q[1] - cy) * si),
    r3(cy + (q[0] - cx) * si + (q[1] - cy) * co),
  ];

  const d = clona(doc);
  const gasite = ceStaPeZiduri(d.plan!, [i]);
  const w = d.plan!.walls![i];
  const vechi = w.poly.map((q) => ({
    p: [q[0].a, q[1].a] as Pt, kx: q[0].k ?? 0, ky: q[1].k ?? 0,
  }));
  const ax: [Pt, Pt] = [rot(a0), rot(b0)];
  w.axis = ax;
  w.poly = poligonDinAx(ax[0], ax[1], w.thickness_m, w.bulge ?? 0)
    .map((q) => {
      let cel = vechi[0];
      let best = Infinity;
      for (const v of vechi) {
        const dd = (v.p[0] - q[0].a) ** 2 + (v.p[1] - q[1].a) ** 2;
        if (dd < best) { best = dd; cel = v; }
      }
      if (cel.kx) q[0].k = cel.kx;
      if (cel.ky) q[1].k = cel.ky;
      return q;
    });

  for (const g of gasite.goluri) {
    const o = d.plan![g.fel]![g.i];
    const q = rot([o.x.a, o.y.a]);
    o.x = { ...o.x, a: q[0] };
    o.y = { ...o.y, a: q[1] };
    o.rot_deg = r3(in360((o.rot_deg ?? 0) + grade));
  }
  for (const j of gasite.aparate) {
    const ap = d.plan!.electrical![j];
    const q = rot([ap.x.a, ap.y.a]);
    ap.x = { ...ap.x, a: q[0] };
    ap.y = { ...ap.y, a: q[1] };
    ap.rot_deg = r3(in360((ap.rot_deg ?? 0) + grade));
  }
  return d;
}

/** OGLINDEȘTE ZIDUL în jurul mijlocului lui, pe axa dată.
 *
 *  O OGLINDIRE NU E O ROTIRE, nici pe un dreptunghi: ce se întoarce nu e forma
 *  — un dreptunghi oglindit e tot el —, ci MÂNA zidului: alcătuirea lui pe
 *  straturi și tot ce stă pe el. Un zid orizontal oglindit pe orizontală își
 *  mută ușile la celălalt capăt; oglindit pe verticală, își răstoarnă
 *  straturile. De-aia comanda are rost și aici, unde conturul iese identic.
 *
 *  AXUL SE OGLINDEȘTE ȘI POLIGONUL SE REFACE din el, ca la `rotestePeretele`;
 *  `k`-urile se iau de la cel mai apropiat vârf vechi.
 *
 *  STRATURILE SE ÎNTORC CA ZIDUL SĂ RĂMÂNĂ EL. Lista lor e scrisă de la stânga
 *  la dreapta față de sensul axului (v. `PereteDoc::layers`), iar o oglindire
 *  schimbă mâna: stânga axului oglindit e dreapta celui de dinainte. Lista
 *  întoarsă lasă deci alcătuirea fizică pe loc acolo unde oglindirea n-are ce
 *  muta, și o răstoarnă chiar acolo unde trebuie. Din același motiv `bulge` își
 *  schimbă semnul — el se citește tot față de sensul axului.
 *
 *  NU E `intoarcePeretele`, care întoarce ALCĂTUIREA pe tot runul și lasă
 *  golurile unde sunt. Aia e o comandă despre straturi; asta e o oglindă.
 *
 *  GOLURILE ȘI APARATELE SE OGLINDESC CU EL. Ușa își păstrează balamaua
 *  (`hinge` numește un capăt al golului, iar capătul se oglindește odată cu
 *  direcția lui) și își întoarce sensul SCRIS: `swing` e un semn față de
 *  normala golului, iar normala se întoarce cu oglinda — deci semnul trebuie să
 *  se întoarcă și el, ca foaia să baleieze în aceeași parte fizică. Sensul
 *  DEDUS rămâne dedus: el se citește din camerele de lângă, care n-au plecat
 *  nicăieri. */
export function oglindestePeretele(
  doc: TronsonDoc, i: number, axa: Axa,
): TronsonDoc {
  const w0 = doc.plan?.walls?.[i];
  if (!w0) return doc;
  const [a0, b0] = axaPeretelui(w0);
  const ogl = oglinditorul([(a0[0] + b0[0]) / 2, (a0[1] + b0[1]) / 2], axa);

  const d = clona(doc);
  const gasite = ceStaPeZiduri(d.plan!, [i]);
  const w = d.plan!.walls![i];
  const vechi = w.poly.map((q) => ({
    p: [q[0].a, q[1].a] as Pt, kx: q[0].k ?? 0, ky: q[1].k ?? 0,
  }));
  const ax: [Pt, Pt] = [ogl(a0), ogl(b0)];
  w.axis = ax;
  if (w.bulge) w.bulge = r3(-w.bulge);
  if (w.layers?.length) w.layers = [...w.layers].reverse();
  w.poly = poligonDinAx(ax[0], ax[1], w.thickness_m, w.bulge ?? 0)
    .map((q) => {
      let cel = vechi[0];
      let best = Infinity;
      for (const v of vechi) {
        const dd = (v.p[0] - q[0].a) ** 2 + (v.p[1] - q[1].a) ** 2;
        if (dd < best) { best = dd; cel = v; }
      }
      if (cel.kx) q[0].k = cel.kx;
      if (cel.ky) q[1].k = cel.ky;
      return q;
    });

  for (const g of gasite.goluri) {
    const o = d.plan![g.fel]![g.i];
    const q = ogl([o.x.a, o.y.a]);
    o.x = { ...o.x, a: q[0] };
    o.y = { ...o.y, a: q[1] };
    o.rot_deg = unghiOglindit(o.rot_deg ?? 0, axa);
    if (o.swing) o.swing = o.swing === 1 ? -1 : 1;
  }
  for (const j of gasite.aparate) {
    const ap = d.plan!.electrical![j];
    const q = ogl([ap.x.a, ap.y.a]);
    ap.x = { ...ap.x, a: q[0] };
    ap.y = { ...ap.y, a: q[1] };
    oglindesteAparatul(ap, axa);
  }
  return d;
}

/** Schimbă o însușire a peretelui: grosime, înălțime, fel, material.
 *
 *  GROSIMEA MIȘCĂ O FAȚĂ, nu umflă dreptunghiul din centru. Un zid se
 *  îngroașă într-o parte — cea dinspre care se tencuiește — iar cealaltă
 *  rămâne pe axul ei. Umflat simetric, ar intra cu jumătate în fiecare din
 *  camerele vecine și amândouă ar pierde arie fără ca cineva să ceară asta.
 */
export function schimbaPeretele(
  doc: TronsonDoc, i: number,
  ce: { grosime?: number; inaltime?: number | null; fel?: string; material?: string },
): TronsonDoc {
  const zid0 = doc.plan?.walls?.[i];
  if (!zid0) return doc;

  // CLONĂ DE LA ÎNCEPUT. `mutaLinie` întoarce una, dar când se schimbă doar o
  // însușire (felul, materialul, înălțimea) nu se trece pe acolo — iar fără
  // clonă, cele trei rânduri de mai jos ar scrie ÎN DOCUMENTUL PRIMIT. React
  // ar primi înapoi aceeași referință, n-ar vedea nicio schimbare și n-ar
  // redesena: în editor, alegerea «Parapet» din listă schimba documentul și
  // lăsa ecranul pe «Structural». Iar istoricul ar fi ținut minte un obiect
  // care se schimbă sub el, deci ⌘Z n-ar mai fi avut la ce să se întoarcă.
  let d = clona(doc);

  if (ce.grosime != null) {
    // GROSIMEA E A LINIEI, nu a bucății pe care s-a nimerit clicul.
    //
    // Ce se vede ca un zid sunt mai multe intrări în document — v.
    // `runulLiniei`. Îngroșând doar bucata aleasă, ieșea o linie în trepte, iar
    // pe un zid tăiat în cinci schimbarea se vedea pe o pătrime din el și părea
    // că n-a făcut nimic. Mutarea și ștergerea lucrează deja pe linie întreagă;
    // grosimea le urmează.
    //
    // O FAȚĂ RĂMÂNE PE LOC — cea dinspre care s-a măsurat —, deci zidul crește
    // într-o parte, ca în Homestyler, iar linia rămâne aliniată pe partea aia
    // oricâte bucăți ar avea.
    //
    // GROSIMEA SE CITEȘTE DIN `thickness_m`, NU DIN POLIGON. Pe un zid curb,
    // cutia încadratoare a arcului e cu mult mai lată decât zidul — măsurat pe
    // M2, 0.95 în loc de 0.30 —, deci scăderea din ea dădea o creștere aiurea.
    // Câmpul e cel care spune adevărul, iar de-acum el se și scrie.
    const ax00 = axaPeretelui(zid0);
    const axaMutarii: 0 | 1 =
      Math.abs(ax00[1][0] - ax00[0][0]) >= Math.abs(ax00[1][1] - ax00[0][1]) ? 1 : 0;
    const delta = ce.grosime - zid0.thickness_m;
    if (Math.abs(delta) > 1e-6) {
      // NUMAI PORȚIUNILE DE ACELAȘI FEL ȘI ACELAȘI MATERIAL. Restul runului e
      // alt zid, oricât ar părea unul singur — v. nota lui `runulLiniei`.
      const grup = runulLiniei(doc, i, (w, capul) =>
        w.kind === capul.kind && w.material === capul.material);
      const gros = r3(ce.grosime);

      // CAMERELE ÎNTÂI, pe fețele vechi ale bucăților drepte. Un arc n-are față
      // dreaptă de care să se sprijine o cameră, deci nu intră aici. Fețele care
      // cad pe aceeași valoare se strâng într-una: altfel muchia unei camere
      // întinse peste două bucăți ar pleca de două ori.
      const fete: { valoare: number; jos: number; sus: number; dl: number }[] = [];
      for (const j of grup) {
        const w0 = doc.plan!.walls![j];
        if (w0.bulge) continue;
        const oj = osiaPeretelui(inelul(w0.poly));
        const dj = ce.grosime - w0.thickness_m;
        if (Math.abs(dj) < 1e-9) continue;
        const gasit = fete.find((f) => Math.abs(f.valoare - oj.fete[1]) <= LIPIRE);
        if (gasit) {
          gasit.jos = Math.min(gasit.jos, Math.min(...oj.span));
          gasit.sus = Math.max(gasit.sus, Math.max(...oj.span));
        } else {
          fete.push({ valoare: oj.fete[1], jos: Math.min(...oj.span),
                      sus: Math.max(...oj.span), dl: dj });
        }
      }
      for (const f of fete) {
        d = mutaLinie(d, { axa: axaMutarii, valoare: f.valoare,
                           span: [f.jos, f.sus], delta: f.dl, doarCamere: true });
      }

      // APOI ZIDURILE, refăcute din axele lor. Prin ax și nu mutând o muchie: așa
      // merge la fel pe unul drept și pe unul curb, unde poligonul nu e
      // dreptunghi și n-are «a doua față» de mutat.
      for (const j of grup) {
        const w0 = doc.plan!.walls![j];
        const dj = ce.grosime - w0.thickness_m;
        const w = d.plan!.walls![j];
        w.thickness_m = gros;
        // Axul ține mijlocul, deci pleacă cu jumătate din creștere, în partea
        // opusă feței care rămâne pe loc. Lăsat pe loc, ar fi ieșit descentrat
        // față de zidul lui, iar graful — care leagă zidurile prin capetele
        // AXELOR — ar fi început să găsească alte noduri.
        const ax0 = axaPeretelui(w0);
        const ax: [Pt, Pt] = ax0.map((q) => (axaMutarii === 0
          ? [r3(q[0] + dj / 2), q[1]] : [q[0], r3(q[1] + dj / 2)])) as [Pt, Pt];
        w.axis = ax;
        w.poly = poligonDinAx(ax[0], ax[1], gros, w0.bulge ?? 0);
      }
    }
  }

  const zid = d.plan!.walls![i];
  if (ce.inaltime !== undefined) zid.height_m = ce.inaltime;
  if (ce.fel != null) {
    zid.kind = ce.fel;
    // Un parapet TREBUIE să-și declare înălțimea — fără ea ajunge la placă și
    // închide balconul pe care îl mărginește; catalogul o refuză la încărcare.
    // Deci trecerea pe `parapet` îi dă una, ca documentul să nu treacă printr-o
    // stare pe care serverul o respinge doar fiindcă omul n-a apucat să scrie
    // cifra.
    if (ce.fel === "parapet" && zid.height_m == null) zid.height_m = 1.0;
    if (ce.fel !== "parapet" && ce.fel === "structural") zid.material = "beton";
  }
  // materialul ales de om e o declarație, nu presupunerea importului
  if (ce.material != null) {
    zid.material = ce.material;
    delete zid.material_presupus;
  }
  return d;
}

// ------------------------------------------------- straturile unui perete ---
//
// CE E UN STRAT ȘI DE CE NU E UN AL DOILEA MATERIAL. `material` rămâne ce era:
// întrebarea cu două răspunsuri pe care o pun structura și parcajul — duce sau
// nu duce. Straturile sunt ce se desenează în secțiune și ce se măsoară la
// antemăsurătoare: zidărie, termosistem, tencuială, gips-carton. Un zid poate
// să n-aibă niciunul, și așa sunt toate cele transcrise din planșă.
//
// GROSIMEA E SUMA LOR. Un zid cu straturi nu mai are o grosime de sine
// stătătoare: grosimea LUI e cât ocupă straturile, iar scrisă separat ar fi a
// doua măsură a aceluiași lucru, adică două cifre care se despart. De-aia
// fiecare gest de aici trece prin `schimbaPeretele`, care mută geometria
// runului și camerele de lângă exact ca atunci când cineva scrie o grosime.
//
// STRATURILE SUNT ALE LINIEI, ca și grosimea. Ce se vede ca un zid sunt de
// obicei mai multe intrări în document (v. `runulLiniei`); straturi puse doar
// pe bucata sub cursor ar fi însemnat un perete care își schimbă alcătuirea la
// fiecare gol pe care îl traversează, și un total care crește pe o pătrime din
// el. Deci se scriu pe tot runul de același fel și același material.
//
// ȘI AXELE LOR SE ÎNTORC LA FEL. Stânga și dreapta se numără pe `axis` (v.
// `design/straturi.ts`), iar două bucăți ale aceleiași linii pot foarte bine
// să aibă axele în sensuri opuse — dreptunghiul din care se deduce un ax nu
// știe dinspre ce capăt a fost desenat zidul. Lăsate așa, aceeași listă de
// straturi s-ar fi desenat în OGLINDĂ pe o bucată și normal pe cealaltă. Deci
// la scrierea straturilor axele runului se aliniază după a bucății alese.

/** Cât de subțire poate fi un strat: un milimetru. O barieră de vapori chiar
 *  atât are, iar sub milimetru nu mai e un strat, e o greșeală de tastat. */
export const GROSIME_STRAT_MIN = 0.001;

/** Cu ce strat intră în listă un zid omogen când i se pune primul strat:
 *  corpul lui de acum, cu materialul pe care îl declară. Fără asta, primul
 *  «Adaugă strat» ar fi aruncat zidul existent și ar fi lăsat un perete de 2 cm
 *  de tencuială acolo unde era unul de 25 de zidărie. */
export const STRAT_AL_MATERIALULUI: Readonly<Record<string, string>> = {
  beton: "beton",
  zidarie: "caramida",
  polistiren: "polistiren",
  gips_carton: "gips_carton",
  sticla: "sticla",
};

/** Lista de straturi a unui zid, ca vedere de lucru — goală când n-are. */
export const straturile = (w: PereteDoc): StratDoc[] =>
  (w.layers ?? []).map((st) => ({ kind: st.kind, thickness_m: st.thickness_m }));

/** Zidul, cu axul întors ca să arate în direcția cerută.
 *
 *  CURBURA MERGE CU EL. `bulge` e cu semn față de sensul axului, deci un arc
 *  căruia i se schimbă capetele între ele și i se lasă semnul devine arcul
 *  celălalt — se umflă în partea opusă. Întors cu tot cu semn, poligonul iese
 *  identic; de-aia `poly` nici nu se recalculează aici. */
function cuAxulSpre(w: PereteDoc, dir: Pt): void {
  const [a, b] = axaPeretelui(w);
  const d: Pt = [b[0] - a[0], b[1] - a[1]];
  if (d[0] * dir[0] + d[1] * dir[1] >= 0) {
    w.axis = [[r3(a[0]), r3(a[1])], [r3(b[0]), r3(b[1])]];
    return;
  }
  w.axis = [[r3(b[0]), r3(b[1])], [r3(a[0]), r3(a[1])]];
  if (w.bulge) w.bulge = -w.bulge;
}

/** SCRIE STRATURILE unui zid — miezul tuturor gesturilor de mai jos.
 *
 *  Trei lucruri, în ordinea asta și nu în alta: grosimea (care mută geometria
 *  runului și camerele de lângă), apoi axele runului aliniate pe sensul bucății
 *  alese, apoi lista însăși. Lista scrisă înaintea grosimii ar fi fost ștearsă
 *  de `schimbaPeretele`, care reface poligoanele din axe.
 *
 *  O LISTĂ GOALĂ ÎNSEAMNĂ «înapoi la zid omogen», și atunci grosimea NU se
 *  atinge: zidul rămâne cât e, doar că nu mai spune din ce e alcătuit. Altfel
 *  ștergerea ultimului strat ar fi dus grosimea la zero, adică ar fi șters
 *  zidul printr-un gest care spunea altceva. */
export function scrieStraturile(doc: TronsonDoc, i: number,
                                straturi: readonly StratDoc[]): TronsonDoc {
  const zid0 = doc.plan?.walls?.[i];
  if (!zid0) return doc;
  const curate = straturi
    .filter((st) => st && st.thickness_m > 0)
    .map((st) => ({ kind: st.kind,
                    thickness_m: Math.max(GROSIME_STRAT_MIN, r3(st.thickness_m)) }));
  const total = r3(curate.reduce((t, st) => t + st.thickness_m, 0));
  // Runul se ia pe documentul de INTRARE: indicii sunt pozițiile din aceeași
  // listă, iar geometria de după schimbarea de grosime ar putea să nu mai dea
  // exact aceeași apartenență (fețele s-au mutat).
  const grup = runulLiniei(doc, i, (w, capul) =>
    w.kind === capul.kind && w.material === capul.material);
  const d = curate.length && Math.abs(total - zid0.thickness_m) > 1e-9
    ? schimbaPeretele(doc, i, { grosime: total })
    : clona(doc);
  const ax = axaPeretelui(d.plan!.walls![i]);
  const dir: Pt = [ax[1][0] - ax[0][0], ax[1][1] - ax[0][1]];
  for (const j of grup) {
    const w = d.plan!.walls![j];
    if (!w) continue;
    if (!curate.length) { delete w.layers; continue; }
    cuAxulSpre(w, dir);
    w.layers = curate.map((st) => ({ ...st }));
    // straturile puse de om spun din ce e zidul: nu mai e o presupunere
    delete w.material_presupus;
  }
  return d;
}

/** ÎNTOARCE SENSUL PERETELUI — și, cu el, alcătuirea lui.
 *
 *  UN PERETE ARE UN SENS de când a fost desenat: de unde ai apăsat până unde ai
 *  dat drumul. Nu se vede pe desen — un dreptunghi arată la fel din amândouă
 *  părțile — dar el hotărăște unde e stânga, deci pe ce față cade primul strat.
 *  Cine a tras zidul dinspre celălalt capăt ajunge cu termosistemul în interior
 *  și cu gletuiala afară, și n-are cum să-și dea seama din desen decât după ce
 *  se uită la hașuri.
 *
 *  DE-AIA GESTUL ĂSTA MUTĂ STRATURILE, NU DOAR SENSUL. Se întoarce axul, atât;
 *  lista rămâne cum e, iar fiindcă stânga e acum în partea cealaltă, întreaga
 *  alcătuire se răstoarnă. E o singură scriere, nu două care trebuie ținute în
 *  pas — o listă întoarsă ȘI un ax întors s-ar fi anulat.
 *
 *  POLIGONUL NU SE ATINGE. Un zid întors e același zid: aceleași colțuri,
 *  aceeași grosime, aceeași curbură — doar citită dinspre celălalt capăt, de
 *  unde `bulge` își schimbă semnul (v. `cuAxulSpre`).
 *
 *  SE ÎNTOARCE TOT RUNUL, ca și straturile. Întorsă o singură bucată dintr-o
 *  linie, alcătuirea ei ar fi ieșit în oglindă față de vecinele ei — adică
 *  exact greșeala pe care gestul ăsta există ca s-o repare. */
export function intoarcePeretele(doc: TronsonDoc, i: number): TronsonDoc {
  const zid0 = doc.plan?.walls?.[i];
  if (!zid0) return doc;
  const grup = runulLiniei(doc, i, (w, capul) =>
    w.kind === capul.kind && w.material === capul.material);
  const ax = axaPeretelui(zid0);
  const invers: Pt = [ax[0][0] - ax[1][0], ax[0][1] - ax[1][1]];
  const d = clona(doc);
  for (const j of grup) {
    const w = d.plan!.walls![j];
    if (w) cuAxulSpre(w, invers);
  }
  return d;
}

/** UN STRAT NOU, la stânga sau la dreapta celor de acum.
 *
 *  Pe un zid care n-are niciunul, corpul lui de azi intră primul în listă, cu
 *  materialul și grosimea pe care le are — v. `STRAT_AL_MATERIALULUI`. Așa
 *  primul strat pus e o ADĂUGARE peste ce era, nu o înlocuire, iar zidul crește
 *  cu grosimea lui. */
export function adaugaStrat(doc: TronsonDoc, i: number, kind: string,
                            unde: "stanga" | "dreapta" = "dreapta"): TronsonDoc {
  const zid = doc.plan?.walls?.[i];
  if (!zid) return doc;
  const gros = FELURI_STRAT.find((f) => f.kind === kind)?.grosime
    ?? GROSIME_STRAT_MIN;
  const nou: StratDoc = { kind, thickness_m: gros };
  const acum = straturile(zid);
  const baza = acum.length ? acum : [{
    kind: STRAT_AL_MATERIALULUI[zid.material] ?? zid.material,
    thickness_m: zid.thickness_m,
  }];
  return scrieStraturile(doc, i,
    unde === "stanga" ? [nou, ...baza] : [...baza, nou]);
}

/** Felul sau grosimea unui strat. Felul schimbat NU rescrie grosimea: cine a
 *  scris 8 cm de vată și se răzgândește la polistiren vrea tot 8 cm, nu
 *  implicitul catalogului. */
export function schimbaStratul(doc: TronsonDoc, i: number, k: number,
                               ce: { kind?: string; grosime?: number }): TronsonDoc {
  const zid = doc.plan?.walls?.[i];
  if (!zid) return doc;
  const st = straturile(zid);
  if (!st[k]) return doc;
  if (ce.kind != null) st[k].kind = ce.kind;
  if (ce.grosime != null) st[k].thickness_m = ce.grosime;
  return scrieStraturile(doc, i, st);
}

/** Scoate un strat. Scos ultimul, zidul se întoarce la omogen și rămâne cât e
 *  — v. `scrieStraturile`. */
export function stergeStratul(doc: TronsonDoc, i: number, k: number): TronsonDoc {
  const zid = doc.plan?.walls?.[i];
  if (!zid) return doc;
  const st = straturile(zid);
  if (!st[k]) return doc;
  st.splice(k, 1);
  return scrieStraturile(doc, i, st);
}

/** Mută un strat cu unul mai la stânga (`-1`) sau mai la dreapta (`+1`).
 *  Ordinea e chiar alcătuirea peretelui: tencuiala pusă din greșeală sub vată
 *  se repară mutând-o, nu ștergând-o și punând-o din nou. */
export function mutaStratul(doc: TronsonDoc, i: number, k: number,
                            spre: -1 | 1): TronsonDoc {
  const zid = doc.plan?.walls?.[i];
  if (!zid) return doc;
  const st = straturile(zid);
  const j = k + spre;
  if (!st[k] || !st[j]) return doc;
  [st[k], st[j]] = [st[j], st[k]];
  return scrieStraturile(doc, i, st);
}

/** Cât de îngustă poate ajunge o cameră împinsă spre o latură care nu se mișcă. */
const CAMERA_MINIMA = 0.5;

/** Laturile DREPTE ale unui inel, strânse pe linie.
 *
 *  O latură verticală (x constant) e o margine care se mută pe x, deci `axa: 0`;
 *  una orizontală, `axa: 1` — aceeași convenție ca `osiaPeretelui`. Laturile
 *  care cad pe aceeași linie se strâng într-una, cu întinderile reunite: o
 *  cameră în L are două bucăți de perete pe aceeași margine, iar mutate separat
 *  ar pleca de două ori. */
export function laturile(ring: readonly Pt[]) {
  const out: { axa: 0 | 1; valoare: number; jos: number; sus: number }[] = [];
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    const dx = Math.abs(b[0] - a[0]);
    const dy = Math.abs(b[1] - a[1]);
    if (dx < LIPIRE && dy < LIPIRE) continue;          // vârf dublat
    if (Math.min(dx, dy) > LIPIRE) continue;           // oblică: n-are axă
    const axa: 0 | 1 = dx < dy ? 0 : 1;
    const alta = (1 - axa) as 0 | 1;
    const valoare = (a[axa] + b[axa]) / 2;
    const jos = Math.min(a[alta], b[alta]);
    const sus = Math.max(a[alta], b[alta]);
    const gasit = out.find((g) => g.axa === axa
      && Math.abs(g.valoare - valoare) <= LIPIRE);
    if (gasit) {
      gasit.jos = Math.min(gasit.jos, jos);
      gasit.sus = Math.max(gasit.sus, sus);
    } else out.push({ axa, valoare, jos, sus });
  }
  return out;
}

/** Zidurile care țin o latură: cele care se mută pe aceeași axă, cu o FAȚĂ pe
 *  linia ei și cu întinderea suprapusă peste a ei. */
export function zidurilePeLatura(
  doc: TronsonDoc, l: { axa: 0 | 1; valoare: number; jos: number; sus: number },
): number[] {
  const out: number[] = [];
  (doc.plan?.walls ?? []).forEach((w, j) => {
    const o = osiaPeretelui(inelul(w.poly));
    if (o.axa !== l.axa) return;
    if (!o.fete.some((f) => Math.abs(f - l.valoare) <= LIPIRE)) return;
    const jos = Math.min(...o.span);
    const sus = Math.max(...o.span);
    if (sus < l.jos - LIPIRE || jos > l.sus + LIPIRE) return;
    out.push(j);
  });
  return out;
}

/** ZIDURILE CARE ȚIN ANVELOPA: cele cu o față pe conturul modulului.
 *
 *  GEOMETRIC, NU DUPĂ STEAG. `on_facade` pare făcut pentru asta, dar nu ține:
 *  pe M1 îl poartă opt bucăți de zidărie de 15 cm de pe laturile lungi, în timp
 *  ce peretele care chiar închide modulul la capăt — W2, W8, W16, la x = 9,85,
 *  adică exact pe frontul nominal — nu-l poartă deloc. Steagul spune «e la
 *  fațadă», nu «ține conturul», iar noi avem nevoie de a doua.
 *
 *  Conturul îl dă `anvelopa(doc)`, aceeași funcție pe care o desenează planul,
 *  iar laturile lui se citesc cu `laturile` — deci și crestătura unui colț intră
 *  în socoteală fără nicio ramură în plus. */
export function zidurileAnvelopei(doc: TronsonDoc): Set<number> {
  const out = new Set<number>();
  for (const l of laturile(anvelopa(doc))) {
    for (const j of zidurilePeLatura(doc, l)) out.add(j);
  }
  return out;
}

/** Ce pleacă odată cu latura ținută de zidurile date.
 *
 *  RUNUL COLINEAR ÎNTREG, ca peste tot în editor (v. `mutaPeretele`) — DAR NU
 *  LA UN BALCON. Parapetul lui e al lui: pe M2, parapetul de la x = 6,10 cade pe
 *  aceeași linie cu o compartimentare dinăuntru și împarte cu ea un nod, deci
 *  runul îi lega. Legate, balconul nici nu se mai putea mișca — latura ieșea
 *  țintuită de anvelopa pe care o ține compartimentarea —, iar dacă s-ar fi
 *  mișcat, ar fi tras după el un perete de apartament. Un parapet nu e
 *  continuarea niciunui zid: e marginea unei dale în consolă. */
function zidurileCareMerg(doc: TronsonDoc, ziduri: readonly number[],
                          balcon: boolean): Set<number> {
  const linia = new Set<number>(ziduri);
  if (!balcon) {
    for (const j of ziduri) for (const k of runulLiniei(doc, j)) linia.add(k);
  }
  return linia;
}

/** Deplasarea tăiată ca NIMIC DIN CE SE MUTĂ SĂ NU IASĂ DIN MODUL.
 *
 *  A ține pe loc zidurile de pe conturul exact nu ajunge. Pe M1, banda merge
 *  până la y = 7,65, dar acolo stă doar o umplutură de fațadă de 15 cm
 *  (7,50–7,65); zidul de structură care închide de fapt camerele e ÎNAINTEA ei,
 *  la 7,20–7,50, și nu atinge conturul, deci nu era oprit de nimic. Împins cu
 *  0,25, ajungea la 7,45–7,75 — adică prin umplutura oprită și afară din modul
 *  —, iar toate camerele de pe latura aia creșteau în ce fusese zid: măsurat,
 *  2,28 m² apăruți din nimic la o singură tragere. Deci camera se împinge până
 *  dă de peretele de afară și se oprește acolo, ceea ce e și gestul așteptat.
 *
 *  UN ZID CARE E DEJA AFARĂ NU E ȚINUT DE CONTUR. Parapetul unui balcon în
 *  consolă stă, prin definiție, dincolo de anvelopă; măsurat față de ea, locul
 *  rămas îi iese negativ, iar tăierea la zero închidea gestul cu totul — un
 *  balcon nu se putea duce nici cu un centimetru mai adânc, nici întoarce
 *  înapoi. Conturul oprește ce IESE din el, nu ce n-a fost niciodată înăuntru. */
function taiatLaAnvelopa(doc: TronsonDoc, ziduri: Iterable<number>,
                         axa: 0 | 1, delta: number): number {
  const valAnv = anvelopa(doc).map((q) => q[axa]);
  const anvJos = Math.min(...valAnv);
  const anvSus = Math.max(...valAnv);
  let d = delta;
  for (const j of ziduri) {
    const zid = doc.plan?.walls?.[j];
    if (!zid) continue;
    const v = inelul(zid.poly).map((q) => q[axa]);
    const loc = d > 0 ? anvSus - Math.max(...v) : anvJos - Math.min(...v);
    if (d > 0 ? loc < -LIPIRE : loc > LIPIRE) continue;      // deja afară
    d = d > 0 ? Math.min(d, Math.max(0, loc)) : Math.max(d, Math.min(0, loc));
  }
  return d;
}

/** Cât de departe de linia conturului mai poate sta fața unui zid ca zidul să
 *  fie AL CAPĂTULUI. Aceeași măsură ca `ATINGE` din `conturUrmeazaZidurile`, și
 *  din același motiv: la cusătura dintre două module conturul trece PRIN zid
 *  (pe M1 zidul stă între −0,300 și −0,003, iar conturul la 0), iar banda de
 *  fațadă e făcută din două straturi suprapuse — structura la 7,20–7,50 și
 *  umplutura la 7,50–7,65. Cu toleranța de lipire (2 cm) ar fi plecat numai
 *  stratul de afară, și capătul s-ar fi rupt în două. */
const CAPAT_ATINGE = 0.3;

/** Cât trece de contur o linie dusă la `tinta`. 0 = rămâne înăuntru. */
function pesteContur(doc: TronsonDoc, axa: 0 | 1, tinta: number): number {
  const vals = anvelopa(doc).map((q) => q[axa]);
  return Math.max(0, tinta - Math.max(...vals), Math.min(...vals) - tinta);
}

/** CÂT CEDEAZĂ CAPĂTUL unei linii duse de la `valoare` cu `delta`.
 *
 *  ZERO DACĂ LINIA ERA DEJA AFARĂ, și asta nu e o subtilitate: o cameră
 *  desenată lângă modul, nu în el — pe proba din `model.test`, una pusă la
 *  x ∈ [20, 26] pe un modul de 9,85 — e afară cu totul, iar plimbată cu un
 *  metru ar fi cerut capătului să vină după ea cu unsprezece. Tot de-aici trece
 *  și balconul: dala lui în consolă stă, prin definiție, dincolo de anvelopă.
 *  Conturul cedează la ce IESE din el, nu la ce n-a fost niciodată înăuntru —
 *  aceeași măsură ca la `taiatLaAnvelopa`. */
function cedareaCapatului(doc: TronsonDoc, axa: 0 | 1,
                          valoare: number, delta: number): number {
  if (pesteContur(doc, axa, valoare) > 0) return 0;
  return pesteContur(doc, axa, valoare + delta);
}

/** CAPĂTUL MODULULUI CEDEAZĂ: împins de dinăuntru, pleacă și duce conturul.
 *
 *  DE CE. «Nu mă lasă să trag holul comun la stânga — e clar că pentru că ar
 *  ieși din tronson, dar ar trebui să fie ok și să mărească și tronsonul»
 *  (17.09.2026). Până aici conturul era un gard, și un gard care nici măcar nu
 *  se vedea: latura împinsă peste el pleca în document, iar derivarea o tăia
 *  înapoi (camerele se citesc din `anvelopă ∩ cadrul zidurilor` — v.
 *  `camere::bucatileDerivate`), deci gestul se încheia cu documentul schimbat
 *  și desenul neschimbat. Măsurat pe M1: holul comun stă la x ∈ [0, 3,30],
 *  conturul începe chiar la x = 0, iar latura lui din stânga trasă cu 0,50 se
 *  întorcea la 0,000.
 *
 *  CONTURUL RĂMÂNE O CONSECINȚĂ. Nu pleacă singur: pleacă ZIDURILE capătului,
 *  iar conturul le urmează prin regula care există de mult (v.
 *  `conturUrmeazaZidurile`) — cu tot ce ține de ea: frontul recitit din
 *  poligon, un modul neatins lăsat în pace, idempotența pe durata gestului.
 *  Aici se scrie și de-a dreptul, cu aceeași cifră, ca funcția să spună singură
 *  adevărul: tăierile de mai jos citesc `anvelopa(doc)`, iar cu conturul rămas
 *  în urmă s-ar fi oprit la el tot ce mai are de mutat gestul.
 *
 *  PLEACĂ TOT CAPĂTUL, nu doar linia împinsă: camerele de pe fețele zidurilor
 *  mutate cresc cu ele, cele perpendiculare se lungesc din graf, și modulul
 *  iese mai lung cu exact cât s-a cerut. Măsurat pe M1, la o tragere de 0,50 a
 *  laturii din stânga a holului comun: frontul 9,85 → 10,35, holul, cele două
 *  dormitoare și cele două băi ale cusăturii cu 0,50 mai lungi, W14, W15, W35,
 *  W37, W42 și W43 lungite din graf, niciun zid rămas în urmă.
 *
 *  UN CAPĂT FĂRĂ ZIDURI NU CEDEAZĂ, iar asta nu e o scăpare: fără zid pe linia
 *  lui, conturul n-are ce să urmeze, și declarația scrisă de mână ar fi ștearsă
 *  de prima mișcare următoare — `conturUrmeazaZidurile` o socotește din nou la
 *  fiecare gest. */
function capatulCedeaza(
  doc: TronsonDoc, axa: 0 | 1, spre: 1 | -1, cat: number,
): TronsonDoc {
  if (!(cat > 0)) return doc;
  const vals = anvelopa(doc).map((q) => q[axa]);
  const linia = spre > 0 ? Math.max(...vals) : Math.min(...vals);
  const grup = new Set<number>();
  (doc.plan?.walls ?? []).forEach((w, j) => {
    // Parapetul nu ține capătul, oricât de aproape ar trece de el — v. nota din
    // `conturUrmeazaZidurile`: întoarcerea unei logii stă suprapusă peste
    // capătul modulului, iar dusă de-aici i-ar lungi frontul.
    if (w.kind === "parapet") return;
    const o = osiaPeretelui(inelul(w.poly));
    if (o.axa !== axa) return;
    if (Math.min(...o.fete.map((f) => Math.abs(f - linia))) > CAPAT_ATINGE) return;
    grup.add(j);
  });
  if (!grup.size) return doc;
  // Capul se alege dintre zidurile care chiar se mută pe axa asta — pe unul
  // aproape pătrat `osiaPeretelui` ghicește, și un cap prost ales ar muta toată
  // linia pe cealaltă axă. FĂRĂ `fara`: aici tocmai zidurile anvelopei sunt cele care
  // trebuie să plece, iar cele perpendiculare pe ele au de ce să se lungească —
  // fațada unui modul mai lung e mai lungă și ea.
  const cap = [...grup].find((j) =>
    osiaPeretelui(inelul(doc.plan!.walls![j].poly)).axa === axa) ?? [...grup][0];
  const d = cuConturScris(mutaPeretele(doc, cap, spre * cat, { grup }));
  for (const q of d.plan!.outline!) {
    if (Math.abs(q[axa] - linia) <= LIPIRE) q[axa] = r3(q[axa] + spre * cat);
  }
  cifreleDinContur(d, doc);
  return d;
}

// ------------------------------------------ LATURILE CAMEREI, TRASE DE MÂNĂ ---
//
// SINGURUL GEST CARE SCHIMBĂ O CAMERĂ. O cameră nu se mută și nu se rotește: e
// golul dintre ziduri (v. antetul lui `camere.ts`), deci ce se mișcă sunt
// zidurile. Se putea și una și alta — poligonul camerei pleca rigid, cu
// zidurile care o mărgineau și cu mobila din ea —, și tocmai de-aia s-a scos:
// un zid ține și de camera de dincolo, iar dus de-aici o rupea pe aceea. Cerut
// explicit, 17.09.2026.
//
// Lucrul pe care îl vede cineva pe planșă e CAMERA, iar mărimea ei e ce se
// hotărăște — «dormitorul are 3,20 pe 4,10», nu «W7 stă la x = 5,45». Deci
// mărimea se ia de aici, de pe marginile ei, nu căutând zidul care o închide.
//
// MECANISMUL E AL ZIDURILOR. Latura trasă e o linie, iar linia se mută cu
// `mutaPeretele`, cu tot ce a învățat el: runul colinear întreg, camerele de pe
// amândouă fețele, vecinii care se lungesc din graf, golurile și prizele.
// Camera de dincolo se strânge cu exact cât crește asta, fiindcă e același
// perete — nu se poate ajunge la două camere care se suprapun.
//
// DOUĂ LATURI NU PLEACĂ, ȘI AMÂNDOUĂ AU MOTIV:
//
//   * cea ținută de un zid al ANVELOPEI — o cameră trasă n-are voie să schimbe
//     frontul modulului: catalogul îl verifică la încărcare, iar bara compusă
//     din el s-ar lungi fără ca cineva s-o fi cerut;
//   * niciuna, dacă mutarea ar strânge camera sub `CAMERA_MINIMA`: întoarsă pe
//     dos, derivarea ar șterge-o la salvare.
//
// O LATURĂ FĂRĂ ZID SE MUTĂ TOTUȘI. Trecerea liberă dintre living și hol e un
// PRAG, nu un perete (v. `thresholds`): acolo nu e nimic de împins, dar linia
// tot trebuie să plece, altfel camera s-ar întinde peste ea.

/** Cât de aproape de fața unui zid se lipește latura trasă.
 *
 *  8 cm, măsurați în plan și nu în pixeli. Au fost 20, și era o palmă: latura
 *  trasă cădea pe fața vecinului de la o distanță de la care încă nu se vedea
 *  că acolo vrea să ajungă, iar o cameră lărgită cu 15 cm nu se putea lărgi cu
 *  15 cm. Opt centimetri rămân sub jumătatea celui mai îngust rost dintre două
 *  ziduri, deci lipirea tot nu poate sări peste zidul de dincolo. */
export const LIPIRE_LATURA = 0.08;

/** O latură a camerei, așa cum se apucă de pe desen. */
export type LaturaCamerei = {
  axa: 0 | 1;
  valoare: number;
  jos: number;
  sus: number;
  /** Zidurile care o țin. Goală = latura e un prag, o trecere liberă: nu e
   *  nimic de împins, dar linia tot pleacă (v. antetul secțiunii). */
  ziduri: number[];
  /** Ține conturul modulului, deci NU pleacă: o cameră trasă n-are voie să
   *  schimbe frontul (v. antetul secțiunii). */
  tintuita: boolean;
};

/** Laturile drepte ale camerei, cu ce le ține — apucătoarele de pe desen. */
export function laturileCamerei(doc: TronsonDoc, i: number): LaturaCamerei[] {
  const cam = doc.plan?.rooms?.[i];
  if (!cam) return [];
  const anv = zidurileAnvelopei(doc);
  const balcon = cam.kind === "balcony";
  return laturile(inelul(cam.poly)).map((l) => {
    const ziduri = zidurilePeLatura(doc, l);
    return {
      ...l,
      ziduri,
      // Tot ce pleacă odată cu latura contează: un zid
      // interior colinear cu unul de contur l-ar lua cu el, deci latura lui e
      // tot țintuită.
      tintuita: [...zidurileCareMerg(doc, ziduri, balcon)].some((j) => anv.has(j)),
    };
  });
}

/** UNDE SE OPREȘTE LATURA TRASĂ: fața de zid cea mai apropiată de unde a
 *  ajuns mâna.
 *
 *  Pe FEȚE, nu pe axe — v. nota lipirii capetelor, din același motiv: fața e
 *  ce se vede pe planșă și ce se tencuiește, iar o cameră care se oprește pe
 *  fața zidului de dincolo e chiar camera pe care o va desena derivarea. Se
 *  caută numai zidurile care trec prin dreptul laturii: unul aflat pe aceeași
 *  linie, dar la celălalt capăt al modulului, n-are ce să oprească aici.
 *
 *  Propriile ziduri ies din căutare — o latură nu se poate lipi de sine. */
export function lipestePeFata(
  doc: TronsonDoc, l: LaturaCamerei, valoare: number,
): number {
  let cea = valoare;
  let dist = LIPIRE_LATURA;
  (doc.plan?.walls ?? []).forEach((w, j) => {
    if (l.ziduri.includes(j)) return;
    const o = osiaPeretelui(inelul(w.poly));
    if (o.axa !== l.axa) return;
    if (Math.max(...o.span) < l.jos - LIPIRE) return;
    if (Math.min(...o.span) > l.sus + LIPIRE) return;
    for (const f of o.fete) {
      const d = Math.abs(f - valoare);
      if (d < dist) { dist = d; cea = f; }
    }
  });
  return cea;
}

/** MUTĂ O SINGURĂ LATURĂ a camerei, împingând zidurile de pe ea.
 *
 *  Cele trei opreliști, din motivele scrise în antetul secțiunii: o latură care
 *  ține conturul nu se clintește, nimic din ce se mută nu iese din anvelopă,
 *  iar camera strânsă până la `CAMERA_MINIMA` oprește latura, altfel s-ar
 *  întoarce pe dos și derivarea ar șterge-o la prima salvare. */
export function mutaLaturaCamerei(
  doc: TronsonDoc, i: number, l: LaturaCamerei, delta: number,
): TronsonDoc {
  const cam = doc.plan?.rooms?.[i];
  if (!cam || !delta || l.tintuita) return doc;
  const ring = inelul(cam.poly);
  const vals = ring.map((q) => q[l.axa]);
  const jos = Math.min(...vals);
  const sus = Math.max(...vals);
  const loc = Math.max(0, sus - jos - CAMERA_MINIMA);
  // Care margine a camerei e latura asta: cea de jos, împinsă în sus, strânge;
  // cea de sus, împinsă în jos, la fel.
  if (Math.abs(l.valoare - jos) <= LIPIRE && delta > 0) delta = Math.min(delta, loc);
  if (Math.abs(l.valoare - sus) <= LIPIRE && delta < 0) delta = Math.max(delta, -loc);
  if (!delta) return doc;

  const anvelopaEi = zidurileAnvelopei(doc);
  const linia = zidurileCareMerg(doc, l.ziduri, cam.kind === "balcony");

  // UN PRAG CARE AJUNGE LA CAPĂTUL MODULULUI ÎL ÎMPINGE (v. `capatulCedeaza`).
  //
  // NUMAI un prag, și limitarea e ce ține regula cinstită. O linie cu ziduri pe
  // ea se oprește la contur ca înainte: acolo, dincolo de zidul împins mai stă
  // unul — pe M1, structura de la 7,20–7,50 are umplutura de fațadă în fața ei,
  // la 7,50–7,65 —, iar capătul mutat cu cât iese zidul din anvelopă i-ar trece
  // prin ea. Un prag n-are ce să ciocnească, fiindcă n-are zid. Împingerea în
  // lanț, dacă se cere, e alt gest.
  let d = doc;
  if (!linia.size) {
    d = capatulCedeaza(doc, l.axa, delta > 0 ? 1 : -1,
                       cedareaCapatului(doc, l.axa, l.valoare, delta));
  }

  // NIMIC NU IESE DIN MODUL — v. `taiatLaAnvelopa`.
  delta = taiatLaAnvelopa(d, linia, l.axa, delta);
  if (!delta) return doc;

  if (!linia.size) {
    // Prag: nimic de împins, dar linia tot pleacă, cu camerele de pe amândouă
    // fețele ei.
    return mutaLinie(d, { axa: l.axa, valoare: l.valoare,
                          span: [l.jos, l.sus], delta, doarCamere: true });
  }
  // Capul se alege dintre zidurile care chiar se mută pe axa asta: pe unul
  // aproape pătrat `osiaPeretelui` ghicește, și un cap prost ales ar muta toată
  // linia pe cealaltă axă.
  const cap = [...linia].find((j) =>
    osiaPeretelui(inelul(doc.plan!.walls![j].poly)).axa === l.axa) ?? [...linia][0];
  return mutaPeretele(d, cap, delta, { fara: anvelopaEi, grup: linia });
}

// ------------------------------------------------ DOUĂ CAMERE, UNITE ÎN UNA ---
//
// O cameră E golul dintre ziduri (v. `editor/camere.ts`), deci «unește-le» nu
// poate însemna să le lipim poligoanele: la prima derivare zidul dintre ele
// le-ar despărți la loc. Unirea se face în plan, nu în listă — SE DESFACE
// DESPĂRȚITURA, iar camera una singură iese de la sine, din aceeași derivare
// care le ținea două.
//
// SE TAIE DOAR CÂT E ÎNTRE ELE, nu tot zidul. Un perete lung desparte de multe
// ori un hol de DOUĂ dormitoare; scos întreg, ar uni și ce nu s-a cerut. Deci
// se taie fâșia pe care camerele alese chiar se privesc una pe alta, iar
// cioturile rămân — exact golul pe care l-ar fi lăsat cineva spărgând peretele.

/** Sub atâta, despărțitura dintre două camere e un colț în care se ating, nu un
 *  perete între ele. */
const DESPARTITURA_MIN = 0.3;

/** Sub atâta, ciotul rămas dintr-un zid tăiat e un pinten de câțiva centimetri
 *  pe care nu-l zidește nimeni: pleacă cu tot cu despărțitură. */
const CIOT_MIN = 0.12;

/** Cel mai gros zid din catalog are 45 cm — atât de departe de hotarul dintre
 *  două camere mai poate cădea un prag care le desparte pe ele. */
const ZID_GROS_MAX = 0.5;

/** Ce desparte două camere: bucata de zid dintre ele, ca (zid, întindere), și
 *  pragurile de pe hotarul lor.
 *
 *  UN ZID E ÎNTRE ELE când una stă pe o față a lui și cealaltă pe fața opusă,
 *  pe o porțiune comună. Testul ăsta — și nu «le atinge pe amândouă» — e cel
 *  care nu confundă despărțitura cu zidul de colț: acolo camerele stau pe fețe
 *  care nu sunt opuse, iar zidul nu e între ele, ci pe lângă amândouă. */
export function despartiturile(doc: TronsonDoc, iA: number, iB: number): {
  ziduri: { zid: number; span: [number, number] }[];
  praguri: number[];
} {
  const plan = doc.plan;
  const a = plan?.rooms?.[iA];
  const b = plan?.rooms?.[iB];
  const ziduri: { zid: number; span: [number, number] }[] = [];
  const praguri: number[] = [];
  if (!plan || !a || !b) return { ziduri, praguri };
  const lA = laturile(inelul(a.poly));
  const lB = laturile(inelul(b.poly));

  (plan.walls ?? []).forEach((w, j) => {
    const o = osiaPeretelui(inelul(w.poly));
    for (const [f0, f1] of [[0, 1], [1, 0]] as const) {
      const la = lA.find((x) => x.axa === o.axa
        && Math.abs(x.valoare - o.fete[f0]) <= LIPIRE);
      const lb = lB.find((x) => x.axa === o.axa
        && Math.abs(x.valoare - o.fete[f1]) <= LIPIRE);
      if (!la || !lb) continue;
      const de = Math.max(la.jos, lb.jos, Math.min(...o.span));
      const pana = Math.min(la.sus, lb.sus, Math.max(...o.span));
      if (pana - de >= DESPARTITURA_MIN) ziduri.push({ zid: j, span: [de, pana] });
    }
  });

  // PRAGURILE, la fel. Trecerea liberă dintre living și hol nu e zid, dar tot
  // ea ține camerele două (v. `plan.thresholds`): lăsat pe loc, pragul le-ar
  // despărți la loc la prima derivare, iar butonul n-ar face nimic.
  //
  // UN PRAG NU STĂ NEAPĂRAT PE AMÂNDOUĂ CAMERELE. Pe M2, trecerea dintre
  // bucătărie și living e scrisă ca DOUĂ segmente paralele, câte unul pe fiecare
  // față a zidului care se oprește acolo (x = 2,10 și x = 2,25): fiecare atinge
  // o cameră și e la 15 cm de cealaltă. Cerute pe amândouă, niciunul nu ieșea —
  // zidul pleca, pragurile rămâneau, iar camerele se derivau la loc două, adică
  // butonul părea că nu face nimic. Deci se cere doar ca fiecare cameră să aibă
  // o latură în dreptul pragului, la cel mult o grosime de zid.
  const mijlocul = (poly: VarfAfin[], axa: 0 | 1) => {
    const v = inelul(poly).map((q) => q[axa]);
    return (Math.min(...v) + Math.max(...v)) / 2;
  };
  (plan.thresholds ?? []).forEach((t, j) => {
    const s = inelul(t);
    if (s.length < 2) return;
    const p0 = s[0];
    const p1 = s[s.length - 1];
    const axa: 0 | 1 = Math.abs(p1[0] - p0[0]) < Math.abs(p1[1] - p0[1]) ? 0 : 1;
    const valoare = (p0[axa] + p1[axa]) / 2;
    const alta = (1 - axa) as 0 | 1;
    const jos = Math.min(p0[alta], p1[alta]);
    const sus = Math.max(p0[alta], p1[alta]);
    const peEl = (laturi: ReturnType<typeof laturile>) => laturi.some((x) =>
      x.axa === axa && Math.abs(x.valoare - valoare) <= ZID_GROS_MAX
      && Math.min(x.sus, sus) - Math.max(x.jos, jos) > 0);
    // DE O PARTE ȘI DE ALTA. Fără asta, un prag de la marginea uneia dintre ele
    // ar intra în socoteală și ar fi desfăcut un hotar cu o a treia cameră.
    const fataInFata = Math.sign(mijlocul(a.poly, axa) - valoare)
      !== Math.sign(mijlocul(b.poly, axa) - valoare);
    if (peEl(lA) && peEl(lB) && fataInFata) praguri.push(j);
  });

  return { ziduri, praguri };
}

/** Zidul, tăiat pe întinderile date: bucățile care rămân, ca pereți întregi.
 *
 *  Numai zidurile DREPTE se taie. Unul curb n-are o întindere pe o axă în care
 *  să se măsoare tăietura, iar «jumătate de arc» nu e o bucată pe care s-o
 *  poată reface `poligonDinAx` din două capete — acela pleacă întreg. */
function taiat(w: PereteDoc, taieturi: [number, number][]): PereteDoc[] {
  if (w.bulge) return [];
  const o = osiaPeretelui(inelul(w.poly));
  let bucati: [number, number][] = [[Math.min(...o.span), Math.max(...o.span)]];
  for (const [de, pana] of taieturi) {
    bucati = bucati.flatMap(([b0, b1]): [number, number][] => {
      if (pana <= b0 + 1e-9 || de >= b1 - 1e-9) return [[b0, b1]];
      const out: [number, number][] = [];
      if (de - b0 > CIOT_MIN) out.push([b0, de]);
      if (b1 - pana > CIOT_MIN) out.push([pana, b1]);
      return out;
    });
  }
  const mij = (o.fete[0] + o.fete[1]) / 2;
  return bucati.map(([b0, b1]) => {
    const ax: [Pt, Pt] = o.lungPeX
      ? [[r3(b0), r3(mij)], [r3(b1), r3(mij)]]
      : [[r3(mij), r3(b0)], [r3(mij), r3(b1)]];
    return { ...w, axis: ax, poly: poligonDinAx(ax[0], ax[1], w.thickness_m, 0) };
  });
}

/** CAMERA ABSORBITĂ IESE DIN LISTĂ — și finisajele se mută după rândurile care
 *  alunecă. Același drum ca la ștergerea unei camere, cu care e și aceeași
 *  faptă: o cameră scrisă mai puțin. */
function faraCamera(doc: TronsonDoc, i: number): TronsonDoc {
  const d = clona(doc);
  const p = d.plan!;
  p.rooms.splice(i, 1);
  p.finishes = (p.finishes ?? [])
    .filter((f) => f.room !== i)
    .map((f) => (f.room > i ? { ...f, room: f.room - 1 } : f));
  if (!p.finishes.length) delete p.finishes;
  return d;
}

/** UNEȘTE CAMERELE ALESE: desface ce le desparte ȘI scoate din listă camera
 *  absorbită.
 *
 *  POLIGONUL UNIT TOT DERIVAREA ÎL FACE, din pereți, imediat după — aici nu se
 *  scrie nicio geometrie de cameră, ca să nu fie două locuri care hotărăsc
 *  unde sunt camerele. Ce se scrie e că din două camere a rămas una: derivarea
 *  nu mai unește singură două camere (cerut anume, v. `camere::impartita`),
 *  deci cât timp amândouă sunt scrise, amândouă rămân — și butonul n-ar fi
 *  făcut decât să scoată un zid. Camera care rămâne e cea ALEASĂ ÎNTÂI, cu
 *  felul, aria declarată și finisajul ei; spațiul celeilalte i se adaugă la
 *  prima derivare.
 *
 *  Camerele care nu se ating nu au ce uni: documentul se întoarce neatins.
 *
 *  SPAȚIUL EXTERIOR NU SE UNEȘTE cu o cameră: un balcon nu e mărginit de
 *  ziduri, nu se derivă din ele (v. `camere::refacute`) și își poartă
 *  declarația pe locuință — unit, ar fi rămas declarat și nedesenat. */
export function uneste(doc: TronsonDoc, camere: readonly number[]): TronsonDoc {
  if (!doc.plan || camere.length < 2) return doc;
  let d = doc;
  // Pozițiile alese alunecă pe măsură ce camerele absorbite ies din listă.
  const acum = camere.map((i) => i);
  for (let a = 0; a < camere.length; a++) {
    for (let b = a + 1; b < camere.length; b++) {
      const ia_ = acum[a];
      const ib_ = acum[b];
      if (ia_ < 0 || ib_ < 0) continue;
      const ra = d.plan!.rooms?.[ia_];
      const rb = d.plan!.rooms?.[ib_];
      if (!ra || !rb) continue;
      if (ra.kind === "balcony" || rb.kind === "balcony") continue;
      const ia = inelul(ra.poly);
      const ib = inelul(rb.poly);
      const r = despartiturile(d, ia_, ib_);
      // Deja una: două camere unite amândouă cu a treia s-au întâlnit pe drum,
      // și atunci nu mai e nimic de desfăcut între ele.
      if (r.ziduri.length || r.praguri.length) {
        const incercat = desfacut(d, r);
        // ȘI SE VERIFICĂ. Despărțitura se află geometric, dar între două camere
        // poate sta ceva ce testul nu vede ca despărțitură — un living și
        // balconul lui, pe M5, sunt despărțite de un prag ȘI de peretele de
        // fațadă cu ușa în el. Desfăcut doar pragul, camerele rămân două, iar
        // documentul ar fi rămas cu ceva lipsă și fără nimic de arătat. Deci
        // tăietura se ține DOAR dacă a făcut din ele o singură bucată.
        if (!inAceeasiBucata(incercat, ia, ib)) continue;
        d = incercat;
      } else if (!inAceeasiBucata(d, ia, ib)) {
        continue;
      }
      d = faraCamera(d, ib_);
      acum[b] = -1;
      for (let k = 0; k < acum.length; k++) if (acum[k] > ib_) acum[k]--;
    }
  }
  return d;
}

/** Documentul cu despărțitura desfăcută: zidurile tăiate pe întinderile date,
 *  golurile din bucata spartă scoase, pragurile șterse. */
function desfacut(
  doc: TronsonDoc,
  r: { ziduri: { zid: number; span: [number, number] }[]; praguri: number[] },
): TronsonDoc {
  const taieturi = new Map<number, [number, number][]>();
  for (const z of r.ziduri) {
    taieturi.set(z.zid, [...(taieturi.get(z.zid) ?? []), z.span]);
  }
  const praguri = new Set(r.praguri);
  const d = clona(doc);
  const p = d.plan!;
  const luate = p.walls?.map((w) => w.id) ?? [];
  const ziduri: PereteDoc[] = [];
  /** Ce s-a ales de fiecare zid tăiat: bucățile lui, ca (id, întindere pe axa
   *  lungă). Golurile din el se împart după ele. */
  const bucatile = new Map<string, { id: string; de: number; pana: number;
                                     axa: 0 | 1 }[]>();
  (p.walls ?? []).forEach((w, j) => {
    const t = taieturi.get(j);
    if (!t) { ziduri.push(w); return; }
    const parti = taiat(w, t);
    const o = osiaPeretelui(inelul(w.poly));
    const lista: { id: string; de: number; pana: number; axa: 0 | 1 }[] = [];
    parti.forEach((bucata, k) => {
      // Prima bucată ține id-ul zidului: golurile și prizele care rămân în ea
      // nu trebuie rescrise, și nici legăturile din document.
      const id = k === 0 ? w.id : idNou(luate);
      luate.push(id);
      const ob = osiaPeretelui(inelul(bucata.poly));
      lista.push({ id, de: Math.min(...ob.span), pana: Math.max(...ob.span),
                   axa: o.lungPeX ? 0 : 1 });
      ziduri.push({ ...bucata, id });
    });
    bucatile.set(w.id, lista);
  });
  p.walls = ziduri;

  // GOLURILE DIN BUCATA SPARTĂ PLEACĂ. O ușă rămasă acolo n-ar mai tăia niciun
  // zid, iar desenul i-ar arăta arcul plutind în golul proaspăt făcut. Cele din
  // cioturile rămase trec la ciotul lor, cu id-ul lui.
  for (const fel of ["doors", "windows"] as const) {
    if (!p[fel]) continue;
    p[fel] = p[fel]!.flatMap((g) => {
      const lista = g.wall ? bucatile.get(g.wall) : undefined;
      if (!lista) return [g];
      const t = lista[0] ? (lista[0].axa === 0 ? g.x.a : g.y.a) : 0;
      const unde = lista.find((b) => t >= b.de - LIPIRE && t <= b.pana + LIPIRE);
      return unde ? [{ ...g, wall: unde.id }] : [];
    });
  }

  if (praguri.size) {
    p.thresholds = (p.thresholds ?? []).filter((_, j) => !praguri.has(j));
    if (!p.thresholds.length) delete p.thresholds;
  }
  return d;
}

/** Ce ține de o cameră: zidurile ei și mobila din ea.
 *
 *  Aceeași socoteală pentru mutare, rotire și dublare — altfel cele trei ar fi
 *  ajuns să răspundă altfel la «care sunt zidurile camerei ăsteia». */
function alECamerei(plan: NonNullable<TronsonDoc["plan"]>, contur: Pt[]) {
  /** Cât de aproape de contur trebuie să cadă un capăt de ax ca să fie AL ei:
   *  jumătate de zid gros, plus slăbiciunea transcrierii. */
  const TOL = 0.2;
  const langaSegment = (q: Pt, a: Pt, b: Pt) => {
    const vx = b[0] - a[0];
    const vy = b[1] - a[1];
    const l2 = vx * vx + vy * vy;
    const t = l2 > 0
      ? Math.max(0, Math.min(1, ((q[0] - a[0]) * vx + (q[1] - a[1]) * vy) / l2))
      : 0;
    return Math.hypot(q[0] - (a[0] + vx * t), q[1] - (a[1] + vy * t)) <= TOL;
  };
  const peContur = (q: Pt) => contur.some(
    (a, k) => langaSegment(q, a, contur[(k + 1) % contur.length]));

  const ziduri: number[] = [];
  (plan.walls ?? []).forEach((w, j) => {
    const ax = axaPeretelui(w);
    // AMÂNDOUĂ capetele: un zid care doar atinge un colț, sau care trece pe
    // lângă, ține și de camera de dincolo, iar dus de aici ar rupe-o pe aceea.
    if (peContur(ax[0]) && peContur(ax[1])) ziduri.push(j);
  });
  const mobila: number[] = [];
  (plan.furniture ?? []).forEach((f, j) => {
    if (inPoligon([f.x.a, f.y.a], contur)) mobila.push(j);
  });
  return { ziduri, mobila };
}

/** Centrul unui inel — mijlocul cutiei lui, nu centrul de greutate: pe o cameră
 *  în L, centrul de greutate cade lângă cotul ei, deci nu spune unde e camera. */
function centrul(ring: readonly Pt[]): Pt {
  const xs = ring.map((p) => p[0]);
  const ys = ring.map((p) => p[1]);
  return [(Math.min(...xs) + Math.max(...xs)) / 2,
          (Math.min(...ys) + Math.max(...ys)) / 2];
}

/** Cade punctul în poligon? Raycasting — cel mai scurt test care merge și pe
 *  camerele în L din releveu. */
function inPoligon(p: Pt, ring: readonly Pt[]): boolean {
  let inauntru = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if ((yi > p[1]) !== (yj > p[1])
        && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inauntru = !inauntru;
  }
  return inauntru;
}

/** TOCUL EFECTIV al unui gol, cum îl vede și panoul: cel scris în document,
 *  altfel implicitul ROLULUI. Iar rolul, când nu-i scris, se DEDUCE din plan
 *  (v. `doc::esteIntrare`) — o ușă care dă din coridorul comun e ușă de
 *  intrare fără să scrie nimeni asta, și tocul ei e de 8 cm. De-aia se citește
 *  din planul derivat: ghicit din document, ar fi dat 6 cm acolo unde ecranul
 *  arată 8, iar lumina scrisă de om ar fi ieșit cu 4 cm pe lângă. */
function toculGolului(doc: TronsonDoc, fel: "doors" | "windows",
                      i: number): number {
  const g = doc.plan?.[fel]?.[i];
  if (g?.frame_m != null) return g.frame_m;
  if (fel === "windows") return TOC_IMPLICIT;
  const der = deriva(doc).goluri.find((x) => x.fel === "usa" && x.idx === i);
  return der?.toc ?? (g?.kind === "entrance" ? TOC_INTRARE : TOC_IMPLICIT);
}

/** Mută un gol de-a lungul peretelui lui, sau îi schimbă lățimea.
 *
 *  LĂȚIMILE UNEI UȘI SUNT TREI CIFRE ȘI O SINGURĂ MĂSURĂ: golul din zid =
 *  lumina + două tocuri. Oricare se scrie, celelalte se așază ca egalitatea
 *  să rămână adevărată:
 *
 *    * scris GOLUL sau LUMINA — tocul rămâne cum e, cealaltă lățime urmează;
 *    * scris TOCUL — GOLUL RĂMÂNE, lumina urmează. Golul e tăietura din zid,
 *      coordonată cu peretele și cu restul a ce e pe el; un toc mai gros e o
 *      alegere despre ușă, nu un motiv ca spărtura din perete să se lărgească
 *      singură și să se apropie de colț.
 *
 *  Toate trei se leagă la `LUMINA_MIN`: sub atât nu mai e o ușă, iar câmpurile
 *  legate între ele s-ar împinge unul pe altul în negativ. */
export function schimbaGolul(
  doc: TronsonDoc, fel: "doors" | "windows", i: number,
  ce: { deplasare?: number; latime?: number; inaltime?: number; parapet?: number;
        model?: ModelUsa; toc?: number;
        /** LUMINA cerută — golul din zid se face cât trebuie ca ea să iasă. */
        lumina?: number;
        /** ROLUL ușii — `entrance`, `room`, `fire`. Scris, bate deducția din
         *  plan (v. `doc::esteIntrare`); `null` îl șterge, adică întoarce ușa
         *  la ce deduce desenul. */
        rol?: "entrance" | "room" | "fire" | null;
        /** ÎN CARE TOC E BALAMAUA. `start` e implicitul și se scrie ca lipsă,
         *  ca peste tot: documentul poartă alegerea, nu implicitul. */
        balama?: Balama;
        /** SENSUL de deschidere, ca semn față de normala golului. */
        sens?: 1 | -1 },
): TronsonDoc {
  const d = clona(doc);
  const g = d.plan?.[fel]?.[i];
  if (!g) return doc;
  if (ce.deplasare != null) {
    // pe direcția golului: `rot_deg` 0 = pe x, 90 = pe y
    const rad = ((g.rot_deg ?? 0) * Math.PI) / 180;
    g.x.a = Math.round((g.x.a + Math.cos(rad) * ce.deplasare) * 1000) / 1000;
    g.y.a = Math.round((g.y.a + Math.sin(rad) * ce.deplasare) * 1000) / 1000;
  }
  const mm = (v: number) => Math.round(v * 1000) / 1000;
  // TOCUL ÎNTÂI: dacă s-a schimbat și el, lățimile care vin după se socotesc
  // cu tocul NOU — altfel scrisul unei lumini odată cu tocul ar fi ieșit cu
  // diferența dintre tocuri pe lângă.
  if (ce.toc != null) {
    // Tocul nu poate înghiți lumina: golul rămâne cât e, deci tocul are un
    // plafon în el.
    const gol = g.w_m ?? LATIME_GOL;
    const maxim = Math.max(0.02, (gol - LUMINA_MIN) / 2);
    g.frame_m = mm(Math.min(maxim, Math.max(0.02, ce.toc)));
  }
  // Derivarea din `toculGolului` costă, deci se cere doar când chiar se scrie
  // o lățime — nu la fiecare mutare a golului de-a lungul zidului.
  const toc = () => (ce.toc != null ? g.frame_m! : toculGolului(doc, fel, i));
  if (ce.latime != null) {
    g.w_m = mm(Math.max(ce.latime, golulPentruLumina(LUMINA_MIN, toc())));
  }
  if (ce.lumina != null) {
    g.w_m = mm(golulPentruLumina(Math.max(LUMINA_MIN, ce.lumina), toc()));
  }
  if (ce.inaltime != null) g.h_m = mm(Math.max(LUMINA_MIN, ce.inaltime));
  if (ce.parapet != null) g.parapet_m = ce.parapet;
  if (ce.model != null) {
    // v. `adaugaGol`: implicitul se șterge, nu se scrie
    if (ce.model === "single") delete g.model; else g.model = ce.model;
  }
  if (ce.rol !== undefined) {
    if (ce.rol === null) delete g.kind; else g.kind = ce.rol;
  }
  if (ce.balama != null) {
    if (ce.balama === "start") delete g.hinge; else g.hinge = ce.balama;
  }
  if (ce.sens != null) g.swing = ce.sens;
  return d;
}

/** ÎNTOARCE SENSUL de deschidere al unei uși, în DOCUMENT.
 *
 *  Sensul e o decizie de arhitectură, nu o stare de ecran: o foaie care
 *  baleiază într-un coridor îl blochează, iar pe traseul de evacuare al unui
 *  tronson asta e chiar ce nu se face. Formatul canonic îl poartă (`hinge`,
 *  `tip`); al nostru îl poartă de-acum ca `swing`, semn față de normala golului.
 *
 *  Se scrie SEMNUL DEDUS ÎNTORS, nu doar «întors»: câmpul trebuie să fie o
 *  afirmație de sine stătătoare, care să nu depindă de ce ar deduce cititorul
 *  de mâine. Dacă regula de deducție se schimbă (azi «niciodată în circulație»),
 *  ușile întoarse de mână rămân unde le-a pus omul. */
export function intoarceUsa(doc: TronsonDoc, i: number,
                            semnCurent: 1 | -1): TronsonDoc {
  const d = clona(doc);
  const g = d.plan?.doors?.[i];
  if (!g) return doc;
  g.swing = (semnCurent === 1 ? -1 : 1);
  return d;
}

/** OGLINDEȘTE BALAMAUA: din tocul din stânga în cel din dreapta, foaia
 *  baleind tot în aceeași parte a zidului. E cealaltă jumătate a sensului
 *  (v. `intoarceUsa`) și se scrie la fel, în document (`hinge`), ca să intre
 *  în istoric și să se salveze. Se scrie VALOAREA, nu «oglindit»: `end` e o
 *  afirmație de sine stătătoare despre ușă. */
export function oglindesteUsa(doc: TronsonDoc, i: number): TronsonDoc {
  const d = clona(doc);
  const g = d.plan?.doors?.[i];
  if (!g) return doc;
  if (g.hinge === "end") delete g.hinge; else g.hinge = "end";
  return d;
}

/** MUTĂ CE E SELECTAT, cu o deplasare dată — o singură poartă pentru toate
 *  felurile de obiect.
 *
 *  E nevoie de ea fiindcă deplasarea nu mai vine doar din mână. O cotă de lucru
 *  se poate SCRIE (v. `cote::coteleObiectului`), iar cifra tastată se preface
 *  într-o deplasare care trebuie să ajungă la obiectul ales oricare ar fi el.
 *  Fără poarta asta, fiecare loc care scrie o cotă ar fi trebuit să repete
 *  tabelul de mai jos — iar tabelele repetate se despart.
 *
 *  FIECARE FEL PRIMEȘTE EXACT MUTAREA PE CARE I-O FACE ȘI MÂNA, nu una nouă:
 *  cifra scrisă trebuie să lase planul în starea în care l-ar fi lăsat
 *  tragerea, altfel editorul ar avea două mecanici pentru același lucru.
 *  Zidul pleacă deci cu foile lipite de el (v. `mutaPereteleSingur`), ca la
 *  tragerea de corp; mobila și aparatele de pe el merg cu el, iar mobila din
 *  camerele din jur îl urmează prin `obiecteleUrmeazaZidurile`, chemată de gazdă
 *  peste documentul de dinaintea mutării.
 *
 *  CAMERA ȘI CONTURUL NU SE MUTĂ — v. antetul lui `Panza`: camera e golul
 *  dintre ziduri, deci ce se mișcă sunt zidurile. Nici nu primesc cote de
 *  lucru, deci nu se poate ajunge aici cu ele. */
export function mutaObiectul(doc: TronsonDoc, ales: Ales, dep: Pt): TronsonDoc {
  if (!ales || (!dep[0] && !dep[1])) return doc;
  switch (ales.fel) {
    case "perete":
      // Cifra scrisă lasă planul în starea în care l-ar fi lăsat mâna, deci
      // dezlipește de axă ca și tragerea (v. `cuAxeleDezlipite`).
      return mutaPereteleSingur(doc, ales.i, dep,
                                { cuLipitele: true, dezlipeste: true });
    case "mobila": return mutaMobila(doc, ales.i, dep);
    case "gol": {
      // UN GOL ALUNECĂ PE ZIDUL LUI, deci deplasarea nu e un vector, e o
      // lungime pe direcția lui: se ia proiecția, ca la tragerea cu mâna. O
      // cotă măsurată de-a curmezișul zidului n-are atunci ce mișca — corect,
      // fiindcă acolo ușa nu se poate duce fără să iasă din perete.
      const g = doc.plan?.[ales.fel2]?.[ales.i];
      if (!g) return doc;
      const rad = ((g.rot_deg ?? 0) * Math.PI) / 180;
      const l = dep[0] * Math.cos(rad) + dep[1] * Math.sin(rad);
      return l ? schimbaGolul(doc, ales.fel2, ales.i, { deplasare: l }) : doc;
    }
    case "aparat": return mutaAparat(doc, ales.i, dep);
    case "nucleuParte": return mutaParteaNucleului(doc, ales.i, dep);
    default: return doc;
  }
}

/** Șterge ce e selectat. */
/** ȘTERGE TOT CE E ALES — selecția multiplă, ca un corp.
 *
 *  Cerut de arhitect (21.10.2026): odată alese mai multe lucruri cu Shift, ele
 *  se mută, se șterg, se dublează și își schimbă rangul ÎMPREUNĂ, până la un
 *  clic în altă parte. Până acum listele de selecție hrăneau numai cele două
 *  butoane de unire, iar tasta `Delete` lucra pe `ales`, adică pe UNUL — cel
 *  din urmă adunat —, deci dintr-o selecție de cinci pleca una.
 *
 *  CHEAMĂ `sterge` PE FIECARE, în loc să scrie a doua ștergere pe filtre:
 *  ștergerea unui zid duce după sine GOLURILE lui, iar a unei camere exterioare
 *  o DEZICE din variantă (v. `sterge`). O ștergere „de grup" scrisă de la zero
 *  ar fi pierdut tocmai părțile alea, iar omul care șterge cinci lucruri
 *  așteaptă exact ce ar fi obținut ștergându-le pe rând cu mâna.
 *
 *  DE LA INDICELE MARE LA CEL MIC, și asta e toată grija: ștergerea scoate din
 *  listă, deci indicii de după cel șters scad cu unu. Luate crescător, a doua
 *  ștergere ar fi nimerit vecinul, iar pe o selecție de trei al treilea ar fi
 *  fost cu totul altcineva. Felurile nu se calcă între ele — fiecare are lista
 *  lui în document —, deci pot veni amestecate.
 *
 *  Niciun fel aici: o selecție multiplă e omogenă prin construcție, fiindcă
 *  fiecare ramură de adunare golește celelalte două liste (v. `Masa::alege`). */
/** CÂTE UNA PE ANSAMBLU — o selecție curățată de piesele care ar face a doua
 *  oară același lucru.
 *
 *  `dubleaza` pe o piesă dintr-un ansamblu copiază ANSAMBLUL ÎNTREG, cu un nume
 *  de grup nou, fiindcă asta vrea cine dublează o masă cu scaune. De când un
 *  clic aprinde tot ansamblul (v. `Masa::alege`), selecția are toate piesele
 *  lui — deci dublarea chemată pe fiecare făcea câte o copie de fiecare piesă:
 *  o insulă cu trei scaune ieșea în PATRU ansambluri, nu în două. Arătat de
 *  arhitect (21.10.2026): «când selectez o insulă și trei scaune și apăs
 *  dublează creează 3 grupări în plus în loc de 1».
 *
 *  PĂSTREAZĂ PRIMA piesă a fiecărui ansamblu și lasă restul: ea duce după ea
 *  tot grupul oricum. Piesele neunite trec toate, fiindcă `grupulLui` le
 *  întoarce singure — cinci scaune răzlețe alese cu Shift se dublează în cinci.
 *
 *  CELELALTE FELURI TREC NEATINSE: numai mobila are ansambluri. */
export function cateUnaPeGrup(
  doc: TronsonDoc, alesele: readonly Ales[],
): Ales[] {
  const vazute = new Set<string>();
  const iese: Ales[] = [];
  for (const a of alesele) {
    if (a?.fel === "mobila") {
      const g = doc.plan?.furniture?.[a.i]?.grup;
      // Fără nume de grup, piesa e singură — se ține de indicele ei, ca două
      // piese neunite să nu se calce una pe alta.
      const cheie = g ? `g:${g}` : `i:${a.i}`;
      if (vazute.has(cheie)) continue;
      vazute.add(cheie);
    }
    iese.push(a);
  }
  return iese;
}

export function stergeAlesele(
  doc: TronsonDoc, alesele: readonly Ales[],
): TronsonDoc {
  const cu = alesele.filter((a): a is NonNullable<Ales> => !!a);
  if (!cu.length) return doc;
  const indicele = (a: NonNullable<Ales>) => ("i" in a ? a.i : 0);

  // MOBILA SE ȘTERGE ÎNTR-O SINGURĂ TRECERE, nu piesă cu piesă.
  //
  // `sterge` scoate ANSAMBLUL ÎNTREG al piesei primite (v. ramura `mobila`),
  // iar un clic pe o masă aprinde toate piesele ansamblului ei. Chemat o dată
  // pentru fiecare, primul apel scotea toate cele șapte piese, iar următoarele
  // șase cădeau pe indici care între timp SE MUTASERĂ — adică pe piese
  // nevinovate de alături: «când încerc să șterg masa, câteodată îmi șterge și
  // canapeaua și celelalte obiecte din apropiere» (26.10.2026, cu înregistrare).
  //
  // Ordinea descrescătoare singură NU ajunge, și de-aia nu s-a reparat așa: ea
  // apără doar când fiecare ștergere scoate O piesă. Un ansamblu cu indici
  // neînvecinați — 3 și 9, cu altceva ales la 5 — mută oricum piesa de la 5
  // sub mâna următorului apel. Deci indicii se STRÂNG întâi, cu ansamblurile
  // desfăcute, și se scot o dată, de la coadă spre cap.
  const piese = new Set<number>();
  for (const a of cu) {
    if (a.fel === "mobila") for (const j of grupulLui(doc, a.i)) piese.add(j);
  }
  let d = doc;
  if (piese.size) {
    d = clona(d);
    for (const j of [...piese].sort((x, y) => y - x)) {
      d.plan!.furniture!.splice(j, 1);
      legaturiFaraPiesa(d.plan!, j);
    }
  }
  // RESTUL, ca înainte: fiecare fel stă în lista lui, deci ordinea
  // descrescătoare pe indice e de-ajuns acolo.
  for (const a of [...cu].filter((x) => x.fel !== "mobila")
    .sort((x, y) => indicele(y) - indicele(x))) {
    d = sterge(d, a);
  }
  return d;
}

export function sterge(doc: TronsonDoc, ales: Ales): TronsonDoc {
  if (!ales) return doc;
  // AXA IESE DIN TRASARE, nu din geometrie: zidurile ei rămân toate
  // (v. `stergeAxul`). Trece pe aici fiindcă se șterge cu aceeași tastă ca
  // orice altceva, deci și cu același istoric.
  // O COTĂ se stinge pe cheia ei — v. `stingeCheiaCotei`. Trece pe aici din
  // același motiv ca axa: se șterge cu aceeași tastă ca orice altceva ales.
  if (ales.fel === "cota") return stingeCheiaCotei(doc, ales.cheie);
  if (ales.fel === "ax") {
    const ax = axele(doc)[ales.i];
    return ax ? stergeAxul(doc, ax) : doc;
  }
  const d = clona(doc);
  const plan = d.plan!;
  if (ales.fel === "perete") {
    // NUMAI ZIDUL ALES, nu linia din care face parte.
    //
    // Ștergerea a lucrat o vreme pe RUN (`runulLiniei`): un zid văzut e mai
    // multe intrări în document, iar ștergând una rămâneau cioturile
    // celorlalte. Dar runul se deduce — colinear, pe aceeași linie, prin noduri
    // strânse la un sfert de metru — și deducția lua uneori și zidul de alături,
    // pe care omul îl vedea ca pe altul: «când șterg un perete, uneori șterge
    // și pereții alăturați». Un buton de ștergere care ia mai mult decât ce e
    // aprins pe ecran e mai rău decât unul care lasă cioturi: cioturile se văd
    // și se șterg cu încă un clic, zidul dispărut se caută.
    //
    // Golurile care stăteau în el pleacă și ele: o ușă rămasă fără zid n-are ce
    // tăia, iar desenul i-ar arăta arcul plutind în gol.
    const id = plan.walls![ales.i]?.id;
    plan.walls = plan.walls!.filter((_, j) => j !== ales.i);
    for (const fel of ["doors", "windows"] as const) {
      if (plan[fel]) {
        plan[fel] = plan[fel]!.filter((g) => !(g.wall && g.wall === id));
      }
    }
  } else if (ales.fel === "camera") {
    // UN SPAȚIU EXTERIOR ȘTERS SE ȘI DEZICE. Camera plecată, declarația
    // variantei ar fi rămas să spună că locuința are logie — iar o logie
    // declarată și nedesenată e chiar eroarea pe care catalogul o ridică la
    // publicare («has a LOGGIA and nothing is drawn for it»).
    const plecata = plan.rooms[ales.i];
    if (plecata?.kind === "balcony") {
      declaraExteriorul(d, locuintaModulului(d, plecata.side, plecata.flat ?? 0),
                        null);
      // ȘI PARAPEȚII PLEACĂ CU DALA. Altfel rămân trei ziduri de un metru
      // desenate în aer, în afara anvelopei, pe care nimeni nu le mai poate
      // lega de nimic — camera care le explica nu mai e. Sunt ale ei:
      // `alECamerei` le găsește pe conturul dalei (aceeași socoteală ca la
      // rotire și dublare), iar filtrul pe fel lasă fațada de care se sprijină
      // pe loc.
      const aiEi = new Set(alECamerei(plan, inelul(plecata.poly)).ziduri
        .filter((j) => plan.walls![j].kind === "parapet"));
      if (aiEi.size) {
        const idn = new Set([...aiEi].map((j) => plan.walls![j].id));
        plan.walls = plan.walls!.filter((_, j) => !aiEi.has(j));
        for (const fel of ["doors", "windows"] as const) {
          if (plan[fel]) {
            plan[fel] = plan[fel]!.filter((g) => !(g.wall && idn.has(g.wall)));
          }
        }
      }
    }
    plan.rooms.splice(ales.i, 1);
    // FINISAJELE MERG CU CAMERA. Ele o arată prin POZIȚIE în listă, deci o
    // cameră ștearsă din mijloc le-ar muta pe toate cele de după ea cu un rând:
    // parchetul dormitorului ar ajunge în baie fără ca nimeni să fi atins nimic.
    plan.finishes = (plan.finishes ?? [])
      .filter((f) => f.room !== ales.i)
      .map((f) => (f.room > ales.i ? { ...f, room: f.room - 1 } : f));
    if (!plan.finishes.length) delete plan.finishes;
  } else if (ales.fel === "mobila") {
    // ANSAMBLUL SE ȘTERGE ÎNTREG. O masă ștearsă fără scaunele ei ar lăsa în
    // cameră șase scaune în jurul nimicului, iar cine a apăsat Delete pe ce
    // vedea ca o unitate asta nu a cerut. Cine vrea doar un scaun îl separă.
    // DE LA COADĂ SPRE CAP: fiecare `splice` mută indicii de după el, iar
    // `legaturiFaraPiesa` renumerotează legăturile aparatelor la fel —
    // ștergând de la cel mai mare indice, cele rămase de șters nu se mișcă.
    for (const j of [...grupulLui(doc, ales.i)].sort((a, b) => b - a)) {
      plan.furniture!.splice(j, 1);
      legaturiFaraPiesa(plan, j);
    }
  }
  else if (ales.fel === "aparat") plan.electrical!.splice(ales.i, 1);
  else if (ales.fel === "nucleuParte") {
    // O PARTE SE POATE ȘTERGE. Un nucleu cu o scară și un singur lift e un
    // nucleu valid, iar catalogul nu cere un număr anume de puțuri. Ce nu se
    // poate e să dispară nucleul însuși — v. ramura următoare.
    plan.core_parts = (plan.core_parts ?? []).filter((_, j) => j !== ales.i);
  } else if (ales.fel === "anvelopa") {
    // CONTURUL NU SE ȘTERGE. Un modul fără contur nu e un modul; mărimea lui se
    // schimbă din panou, prin front (v. `schimbaFrontul`).
    return doc;
  } else if (ales.fel === "nucleu") {
    // NUCLEUL SE ȘTERGE, CU TOT CU ROLUL LUI.
    //
    // Întâi refuzam: un modul cu `role: "core"` fără nucleu e respins la
    // încărcarea catalogului («role 'core' with no core declared»), deci
    // ștergerea lui nu e doar o ștergere de obiect, e o schimbare de rol.
    // Refuzul era însă răspunsul greșit la problema aia: Bianca a cerut de trei
    // ori să se poată șterge, iar un buton care nu face nimic e mai rău decât
    // unul care face și ce ține de urmările lui.
    //
    // Deci se face schimbarea ÎNTREAGĂ: pleacă cutia, pleacă bucățile, pleacă
    // declarația `core` — și rolul trece din `core` în `mid`, fiindcă un mijloc
    // fără nucleu e chiar ce a rămas. Fără rândul ăla, modulul n-ar mai putea fi
    // încărcat, iar ștergerea ar fi lăsat documentul într-o stare din care nu se
    // mai poate publica.
    delete plan.core_rect;
    delete plan.core_parts;
    d.core = null;
    if (d.role === "core") d.role = "mid";
    return d;
  } else if (ales.fel === "scaraParte") {
    // O RAMPĂ NU SE ȘTERGE SINGURĂ. O scară fără una din rampele ei nu mai urcă
    // nicăieri, iar un podest fără ce să lege n-are ce căuta pe planșă — deci
    // «șterge» pe o parte șterge SCARA, ca și cum ar fi fost aleasă întreagă.
    plan.furniture?.splice(ales.i, 1);
  } else plan[ales.fel2]!.splice(ales.i, 1);
  return d;
}

/** SPARGE O BUCATĂ DINTR-UN ZID: `[t0, t1]` pe axa LUI lungă pleacă, iar ce
 *  rămâne de o parte și de alta devin ZIDURI DE SINE STĂTĂTOARE.
 *
 *  Cerut de arhitect (20.10.2026): «add a way to make a break in a wall like
 *  remove a certain section of a wall and then the separate parts become
 *  individual walls. Like a reverse wall build tool that demolishes a portion».
 *
 *  BUCATA DINTÂI PĂSTREAZĂ `id`-ul, a doua primește unul nou. Așa golurile,
 *  axele și orice altceva care arată spre zidul de dinainte rămân valabile
 *  pentru jumătatea de la început, iar numai ce cade în a doua se remapează —
 *  mai puține legături rupte decât dacă amândouă ar fi fost ziduri noi.
 *
 *  GOLURILE DIN SPĂRTURĂ PLEACĂ CU EA: o ușă din bucata demolată n-are unde să
 *  stea. Cele din bucățile rămase trec la bucata în care le cade mijlocul,
 *  exact ca la tăierea de la apucare (v. `imparteZidulLaCamera`).
 *
 *  UN ZID CURB NU SE SPARGE: pe el „axa lungă" nu e o dreaptă, iar bucățile
 *  ar ieși din coardă, nu din arc. Se întoarce documentul neatins. */
export function demoleazaBucata(
  doc: TronsonDoc, i: number, t0: number, t1: number,
): TronsonDoc {
  const zid = doc.plan?.walls?.[i];
  if (!zid || zid.bulge) return doc;
  const o = osiaPeretelui(inelul(zid.poly));
  const lung = (1 - o.axa) as 0 | 1;
  const [s0, s1] = [Math.min(...o.span), Math.max(...o.span)];
  let a = Math.max(s0, Math.min(t0, t1));
  let b = Math.min(s1, Math.max(t0, t1));
  if (b - a <= 1e-6) return doc;                       // spărtură de nimic
  // CIOTURILE SE ÎNGHIT: dacă dintr-o parte ar rămâne o fărâmă, spărtura se
  // duce până la capăt. Altfel planul s-ar umple de pereți de doi centimetri.
  // `CIOT_MIN` e cel de la tăierea despărțiturilor, nu o cifră nouă: e aceeași
  // întrebare — cât de scurtă poate fi o bucată de zid înainte să nu mai fie
  // un zid, ci un pinten pe care nu-l zidește nimeni.
  if (a - s0 < CIOT_MIN) a = s0;
  if (s1 - b < CIOT_MIN) b = s1;
  const d = clona(doc);
  const lista = d.plan!.walls!;
  const ax = axaPeretelui(zid);
  const fix = ax[0][o.axa];
  const capat = (t: number): Pt => (o.axa === 0 ? [fix, t] : [t, fix]) as Pt;

  const bucati: [number, number][] = [];
  if (a > s0) bucati.push([s0, a]);
  if (b < s1) bucati.push([b, s1]);

  // TOT ZIDUL S-A DUS: pleacă din listă, cu golurile lui.
  if (!bucati.length) return sterge(d, { fel: "perete", i });

  const facute: { id: string; de: number; la: number }[] = [];
  bucati.forEach(([de, la], k) => {
    const w = k === 0 ? lista[i] : clona(zid);
    w.poly = poligonDinAx(capat(de), capat(la), w.thickness_m, 0);
    w.axis = [capat(de), capat(la)] as PereteDoc["axis"];
    if (k > 0) { w.id = idNou(lista.map((x) => x.id).filter(Boolean) as string[]);
                 lista.push(w); }
    facute.push({ id: w.id!, de, la });
  });

  // GOLURILE: cele din spărtură pleacă, restul trec la bucata lor.
  for (const fel of ["doors", "windows"] as const) {
    const g0 = d.plan![fel] ?? [];
    d.plan![fel] = g0.filter((g) => {
      if (g.wall !== zid.id) return true;
      const t = lung === 0 ? g.x.a : g.y.a;
      const gasit = facute.find((f) => t >= f.de - 1e-9 && t <= f.la + 1e-9);
      if (!gasit) return false;                        // cădea în spărtură
      g.wall = gasit.id;
      return true;
    });
  }
  return d;
}

/** DĂ MODULULUI UN NUCLEU — scara, lifturile și ghena prin care urcă oamenii.
 *
 *  Ștergerea lui exista de mult (v. `sterge` pe «nucleu»); nașterea, deloc. Un
 *  nucleu scos din greșeală, sau un modul care trebuie să-l preia după ce
 *  altul a fost șters, nu se mai puteau face în niciun fel: catalogul rămânea
 *  fără modulul care duce scara, iar asta nu se repară din desen — `core` e o
 *  DECLARAȚIE, nu un obiect pus din paletă (01.10.2026).
 *
 *  MĂSURILE SUNT CELE OBIȘNUITE, nu o ghicitoare: cele ale lui M3, singurul
 *  modul cu nucleu din catalog — scară de 1,80, două puțuri de 1,60, pereți de
 *  30. Cutia se așază pe axa coridorului, lipită de latura de jos a ei, și se
 *  mută pe urmă cu mâna ca orice nucleu (`mutaNucleul`, `redimNucleul`).
 *
 *  BUCĂȚILE NU SE PUN AICI, și nimic nu le reface la salvare: `core_parts`
 *  sunt ce pune omul în nucleu — un lift din paletă (`adaugaMobila`) sau o
 *  piesă trecută prin «Parte a nucleului» (`parteDinPiesa`). Cutia declară
 *  două lifturi, deci până le pune cineva, catalogul arată că lipsesc. */
export function faNucleu(doc: TronsonDoc): TronsonDoc {
  if (doc.core) return doc;
  const d = clona(doc);
  d.plan ??= { rooms: [], walls: [], doors: [], windows: [] };
  const STAIR = 1.8, LIFT = 1.6, ZID = 0.3, LIFTURI = 2;
  // Adâncimea cutiei: scara, puțurile și pereții dintre ele.
  const adanc = STAIR + LIFT + 3 * ZID;
  const front = Math.min(5.25, Math.max(2.4, d.front.nominal_m * 0.5));
  const env = anvelopa(d);
  const xs = env.map((q) => q[0]);
  const ys = env.map((q) => q[1]);
  const x0 = r3((Math.min(...xs) + Math.max(...xs)) / 2 - front / 2);
  // Pe axa coridorului: cutia urcă de la ea în jos, ca pe M3.
  const y1 = r3(Math.min(0, (Math.min(...ys) + Math.max(...ys)) / 2));
  d.core = { front_m: r3(front), stair_depth_m: STAIR, lift_depth_m: LIFT,
             lifts: LIFTURI, wall_t_m: ZID };
  d.plan.core_rect = [x0, r3(y1 - adanc), r3(x0 + front), y1];
  return d;
}

/** Dublează ce e selectat, decalat, ca să se vadă că sunt două. */
export function dubleaza(doc: TronsonDoc, ales: Ales): { doc: TronsonDoc; ales: Ales } {
  if (!ales) return { doc, ales };
  const d = clona(doc);
  const plan = d.plan!;
  const DEC = 0.3;
  if (ales.fel === "perete") {
    const gasite = ceStaPeZiduri(plan, [ales.i]);
    const copie = clona(plan.walls![ales.i]);
    const vechiId = plan.walls![ales.i].id;
    copie.id = idNou(plan.walls!.map((w) => w.id));
    // COPIA NU SE LIPEȘTE DE ORIGINAL.
    //
    // Doi pereți paraleli ale căror fețe se apropie sub `ROST_LIPIT` sunt
    // FOILE ACELUIAȘI perete (zidăria de închidere peste rezistență), deci se
    // mută împreună — v. `lipiteleLui`. Cu o decalare fixă de 30 cm, copia unui
    // zid de 30 ieșea fix lipită de el: trasă, ducea și originalul după ea.
    // Cerut de arhitect (26.10.2026): «când dublez un perete nu vreau să se
    // agațe din start de cei de lângă el, pentru că vreau să-l mut; așa cum e
    // acum mi-l mută și pe cel inițial».
    //
    // Pe axa pe care se MUTĂ peretele, copia pleacă deci cu grosimea lui plus
    // un rost întreg: atâta trebuie ca fețele să nu se mai vadă una pe alta.
    // Pe axa LUNGĂ rămâne decalarea obișnuită, care doar o scoate de sub
    // original ca să se vadă că sunt două.
    const o = osiaPeretelui(inelul(plan.walls![ales.i].poly));
    // DINCOLO DE MAGNET, nu doar dincolo de lipire. Cu rostul socotit din
    // `ROST_LIPIT`, copia unui zid de 30 ieșea la 4 cm de original — iar raza
    // de lipire a tragerii urcă la `SNAP_MAX` (6 cm), deci copia era trasă
    // ÎNAPOI pe fața originalului la prima mișcare: «când dublez un perete
    // tot se agață de cel inițial și nu mă lasă să-l mut» (26.10.2026, cu
    // poză — chiar cota «0,04» de pe ea). Rostul trebuie să fie mai mare
    // decât raza cea mai largă a magnetului, altfel copia nu se poate
    // desprinde la nicio mărire.
    const departe = Math.max(DEC, r3(o.grosime + SNAP_MAX + 0.01));
    const mutare: [number, number] =
      o.axa === 0 ? [departe, DEC] : [DEC, departe];
    copie.poly.forEach((q) => {
      q[0].a += mutare[0];
      q[1].a += mutare[1];
    });
    // ȘI AXA, NU DOAR POLIGONUL. Un zid poartă DOUĂ geometrii: `poly`, ce se
    // desenează, și `axis`, linia din care se deduc cadrul selecției, cotele
    // și lungimea. Mutat numai poligonul, copia se năștea cu axa ORIGINALULUI
    // — iar cadrul ales se face din `axis` (v. `Panza::cadruAles`), deci
    // mânerele de grosime ale copiei se desenau la fețele ORIGINALULUI. Ele
    // stau la 6 px în afara feței: la mărirea obișnuită asta e mai mult decât
    // rostul de 7 cm dintre cele două ziduri, deci banda de prindere cădea
    // chiar PESTE corpul copiei. Apăsată în mijloc, copia nu se mai muta — se
    // pornea o schimbare de GROSIME, iar ea o trântea înapoi peste original:
    // «când dublez un perete tot se agață de cel inițial și nu mă lasă să-l
    // mut» (26.10.2026). Calea care dublează mai multe deodată muta axa de la
    // bun început (v. `dubleazaAlesele`); asta e aceeași faptă, scrisă o
    // singură dată.
    if (copie.axis) {
      copie.axis = copie.axis.map(
        (q) => [q[0] + mutare[0], q[1] + mutare[1]]) as typeof copie.axis;
    }
    plan.walls!.push(copie);
    // CU GOLURILE LUI. Un zid dublat fără ele ar fi un zid orb, iar cine
    // dublează un perete cu o ușă în el vrea al doilea perete cu ușă — altfel
    // ar fi mai scurt să deseneze unul nou.
    copiazaCeStaPeZiduri(plan, gasite, mutare, { [vechiId]: copie.id });
    return { doc: d, ales: { fel: "perete", i: plan.walls!.length - 1 } };
  }
  if (ales.fel === "mobila") {
    // ANSAMBLUL SE DUBLEAZĂ ÎNTREG, cu un nume de grup NOU: cu cel vechi, cele
    // două mese ar fi fost un singur ansamblu de douăsprezece piese, și s-ar
    // fi mutat una pe alta. Alegerea cade pe copia piesei apăsate, ca la o
    // piesă singură.
    const cu = grupulLui(doc, ales.i);
    const g = cu.length > 1 ? grupNou(doc) : null;
    let ales2 = plan.furniture!.length;
    // COPIA SE NAȘTE ALĂTURI DE ORIGINAL, NU PESTE EL.
    //
    // Decalarea era 30 cm pe amândouă axele, orice ar fi fost copiat. Pe o
    // piesă mică merge; pe un PAT de 1,60 × 2,00 cu noptierele lui, copia
    // ieșea exact peste original — adică născută încurcată în el: «patul
    // dublat încă se agață de noptierele patului inițial (...) inițial, până
    // mut obiectul dublat, nu ar trebui agățat de nimic» (27.10.2026).
    //
    // Se măsoară ANSAMBLUL ÎNTREG, nu piesa apăsată: un pat se dublează cu
    // noptierele lui, iar o copie care sare doar cât patul tot ar cădea peste
    // noptiera de alături.
    //
    // PE LATURA SCURTĂ, ca să nu plece departe: copia apare lipită de original
    // pe partea unde ansamblul e mai îngust, la un rost de `DEC`. Pe cealaltă
    // axă nu se mișcă deloc — altfel ar ieși pe diagonală, iar diagonala n-o
    // cere nimeni.
    const colturile = (f: PiesaDoc): Pt[] => {
      const rad = ((f.rot_deg ?? 0) * Math.PI) / 180;
      const co = Math.cos(rad), si = Math.sin(rad);
      const hw = (f.w_m ?? 0) / 2, hh = (f.h_m ?? 0) / 2;
      return ([[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]] as const).map(
        ([u, v]) => [val(f.x) + u * co - v * si,
                     val(f.y) + u * si + v * co] as Pt);
    };
    const cutiaLor = (js: readonly number[], dx = 0, dy = 0) => {
      const q = js.flatMap((j) => colturile(plan.furniture![j]));
      return { x0: Math.min(...q.map((p) => p[0])) + dx,
               x1: Math.max(...q.map((p) => p[0])) + dx,
               y0: Math.min(...q.map((p) => p[1])) + dy,
               y1: Math.max(...q.map((p) => p[1])) + dy };
    };
    const a0 = cutiaLor(cu);
    const latime = a0.x1 - a0.x0;
    const adanc = a0.y1 - a0.y0;
    // SE CAUTĂ UN LOC LIBER, nu se sare o distanță socotită.
    //
    // Prima scriere măsura doar ANSAMBLUL copiat și sărea cât el. Pe un pat
    // legat cu noptierele lui mergea — dar în documentele adevărate patul și
    // noptierele NU sunt un ansamblu (măsurat pe «23 IZOLAT LUXURY»: din 420
    // de piese, 276 n-au niciun grup; grupate sunt doar mesele cu scaune și
    // bucătăriile). Atunci saltul de-o lățime de pat cădea fix peste noptiera
    // de alături și peste patul vecin — chiar ce s-a reclamat: «patul dublat
    // încă se agață de noptierele patului inițial».
    //
    // Deci copia se așază unde NU ATINGE NIMIC: se încearcă pe rând locurile
    // de lângă original — întâi pe latura scurtă, ca să rămână aproape —, și
    // se ia primul liber. Cutiile se iau încadratoare (colțuri ROTITE): o
    // piesă oblică e astfel socotită puțin mai mare decât e, ceea ce aici e
    // partea bună — mai bine un rost în plus decât o suprapunere.
    const altele = (plan.furniture ?? [])
      .map((_f, j) => j).filter((j) => !cu.includes(j));
    const cutiiAltele = altele.map((j) => cutiaLor([j]));
    const seAting = (c: { x0: number; x1: number; y0: number; y1: number }) =>
      cutiiAltele.some((o) => c.x0 < o.x1 - 1e-9 && o.x0 < c.x1 - 1e-9
                           && c.y0 < o.y1 - 1e-9 && o.y0 < c.y1 - 1e-9);
    const pasX = r3(latime + DEC);
    const pasY = r3(adanc + DEC);
    // Întâi latura scurtă, în amândouă sensurile, apoi cea lungă; apoi mai
    // departe, pas cu pas. Opt pași sunt de-ajuns pe orice plan real; dacă nu
    // s-a găsit nimic, rămâne primul loc — mai bine lângă, decât nicăieri.
    const scurtaPeX = latime <= adanc;
    const dir: [number, number][] = scurtaPeX
      ? [[pasX, 0], [-pasX, 0], [0, pasY], [0, -pasY]]
      : [[0, pasY], [0, -pasY], [pasX, 0], [-pasX, 0]];
    let mut: [number, number] = dir[0];
    cautare:
    for (let k = 1; k <= 8; k++) {
      for (const [dx, dy] of dir) {
        const c: [number, number] = [r3(dx * k), r3(dy * k)];
        if (!seAting(cutiaLor(cu, c[0], c[1]))) { mut = c; break cautare; }
      }
    }
    for (const j of cu) {
      const copie = clona(plan.furniture![j]);
      copie.x = { ...copie.x, a: r3(copie.x.a + mut[0]) };
      copie.y = { ...copie.y, a: r3(copie.y.a + mut[1]) };
      // O SCARĂ ÎȘI DUCE RAMPELE. Ele stau în coordonate de PLAN, nu în cadrul
      // piesei (v. `PiesaDoc.scara`), deci mutarea cutiei nu le clintește:
      // copia ar fi ieșit desenată exact peste original, cu o cutie mutată
      // degeaba alături. Cerut de arhitect (21.10.2026): «scările ar trebui să
      // poată fi dublate și oglindite ca obiectele».
      if (copie.scara) duScara(copie.scara, mut);
      if (g) copie.grup = g;
      if (j === ales.i) ales2 = plan.furniture!.length;
      plan.furniture!.push(copie);
    }
    return { doc: d, ales: { fel: "mobila", i: ales2 } };
  }
  if (ales.fel === "aparat") {
    const copie = clona(plan.electrical![ales.i]);
    // COPIA NU MOȘTENEȘTE LEGĂTURA: două prize ale aceleiași noptiere ar fi
    // recalculate amândouă în același punct, deci una ar dispărea sub cealaltă.
    delete copie.pentru;
    delete copie.parte;
    copie.x = { ...copie.x, a: r3(copie.x.a + DEC) };
    copie.y = { ...copie.y, a: r3(copie.y.a + DEC) };
    // A box's copy goes along its wall instead: the diagonal step would take
    // it off the face it is drawn into.
    if (copie.kind === "doza") {
      const o = plan.electrical![ales.i];
      const r = ((o.rot_deg ?? 0) * Math.PI) / 180;
      const s = lipitDeZid(d, [o.x.a - Math.sin(r) * DEC, o.y.a + Math.cos(r) * DEC],
                           "doza");
      copie.x = { ...copie.x, a: s.p[0] };
      copie.y = { ...copie.y, a: s.p[1] };
      if (s.rot) copie.rot_deg = s.rot; else delete copie.rot_deg;
    }
    // A strip's copy continues it along its face, 5 cm past its end, cut at
    // the face's stop; a copy that would be under 0.10 m is not made.
    if (copie.kind === "banda_led") {
      const f = fataBenzii(doc, ales.i);
      const o = plan.electrical![ales.i];
      if (!f) return { doc, ales };
      const sg = f.L < 0 ? -1 : 1;
      const t0 = f.L + sg * 0.05;
      const loc = Math.min(Math.abs(f.L), sg > 0 ? f.dreapta - t0 : t0 - f.stanga);
      if (loc < LUNGIME_MINIMA_BANDA - 1e-9) return { doc, ales };
      copie.x = { ...copie.x, a: r3(o.x.a + t0 * f.u[0]) };
      copie.y = { ...copie.y, a: r3(o.y.a + t0 * f.u[1]) };
      copie.length_m = r3(sg * loc);
    }
    plan.electrical!.push(copie);
    return { doc: d, ales: { fel: "aparat", i: plan.electrical!.length - 1 } };
  }
  if (ales.fel === "camera") {
    // CU TOT CE ȚINE DE EA. O cameră dublată singură ar fi un poligon fără
    // ziduri, adică exact ce șterge derivarea la prima salvare.
    const contur = inelul(plan.rooms[ales.i].poly);
    const { ziduri, mobila } = alECamerei(plan, contur);
    const gasite = ceStaPeZiduri(plan, ziduri);
    const copie = clona(plan.rooms[ales.i]);
    copie.poly.forEach((q) => { q[0].a = r3(q[0].a + DEC); q[1].a = r3(q[1].a + DEC); });
    plan.rooms.push(copie);
    /** Zidul vechi -> zidul copiat. Golurile își scriu gazda pe nume, deci fără
     *  harta asta ușile copiei ar arăta spre pereții originalului: le-ai muta pe
     *  unele și s-ar tăia în celelalte. */
    const idNou_: Record<string, string> = {};
    for (const j of ziduri) {
      const w = clona(plan.walls![j]);
      idNou_[plan.walls![j].id] = w.id = idNou(plan.walls!.map((x) => x.id));
      w.poly.forEach((q) => { q[0].a = r3(q[0].a + DEC); q[1].a = r3(q[1].a + DEC); });
      if (w.axis) w.axis = w.axis.map((q) => [r3(q[0] + DEC), r3(q[1] + DEC)]) as [Pt, Pt];
      plan.walls!.push(w);
    }
    copiazaCeStaPeZiduri(plan, gasite, [DEC, DEC], idNou_);
    for (const j of mobila) {
      const f = clona(plan.furniture![j]);
      f.x = { ...f.x, a: r3(f.x.a + DEC) };
      f.y = { ...f.y, a: r3(f.y.a + DEC) };
      plan.furniture!.push(f);
    }
    // ȘI FINISAJUL EI. O cameră dublată care s-ar întoarce la implicitul
    // felului n-ar mai fi o copie: ai muta-o alături și ai vedea alt parchet.
    const fin = (plan.finishes ?? []).find((f) => f.room === ales.i);
    if (fin) {
      plan.finishes = [...(plan.finishes ?? []),
                       { ...fin, room: plan.rooms.length - 1 }];
    }
    return { doc: d, ales: { fel: "camera", i: plan.rooms.length - 1 } };
  }
  if (ales.fel === "anvelopa") return { doc, ales };   // nu se dublează
  if (ales.fel === "nucleu") {
    // Un modul are cel mult un nucleu (`doc.core` e unul singur, nu o listă):
    // al doilea n-ar avea unde să fie scris.
    return { doc, ales };
  }
  if (ales.fel === "nucleuParte") {
    // O parte se dublează ca orice altceva — al doilea lift e chiar felul de
    // schimbare pentru care cineva deschide nucleul.
    const parte = plan.core_parts?.[ales.i];
    if (!parte) return { doc, ales };
    const copie = {
      ...clona(parte),
      poly: parte.poly.map((q) => [r3(q[0] + DEC), r3(q[1] + DEC)]),
    };
    if (copie.scara) duScara(copie.scara, [DEC, DEC]);
    plan.core_parts!.push(copie);
    return { doc: d, ales: { fel: "nucleuParte", i: plan.core_parts!.length - 1 } };
  }
  // O axă nu se dublează: ea se deduce din ziduri, iar două pe același loc ar
  // fi aceeași axă.
  if (ales.fel !== "gol") return { doc, ales };
  const lista = plan[ales.fel2]!;
  const copie = clona(lista[ales.i]);
  copie.x.a += DEC;
  lista.push(copie);
  return { doc: d, ales: { ...ales, i: lista.length - 1 } };
}

/** `W12` — primul id liber din seria peretelui. */
export function idNou(existente: readonly string[]): string {
  const luate = new Set(existente);
  for (let n = 1; n < 9999; n++) if (!luate.has(`W${n}`)) return `W${n}`;
  return `W${existente.length + 1}`;
}

// --------------------------------------------------------- pereți curbi ---

/** CURBURA NU ARE PLAFON (23.09.2026, Stefan: «de ce ne trebuie o limită?»).
 *
 *  `bulge = 1` e un semicerc; peste, arcul trece de semicerc (la 2,5 are
 *  aproape 270°), iar un cerc întreg nu se scrie cu un singur `bulge`, ci din
 *  doi pereți — ca în DWG. Plafonul de ±1 de dinainte ascundea o greșeală a
 *  lui `arcDinAx`, care punea centrul de partea greșită a coardei: arcul ieșea
 *  ca o buclă care nu mai ajungea în capătul lui. Cu centrul pus corect,
 *  vârful arcului stă sub cursor, deci arcul crește doar cât e tras. Rămâne o
 *  singură cerință: o cifră FINITĂ. */
function bulgeFinit(bulge: number): number {
  return Number.isFinite(bulge) ? bulge : 0;
}

export const r3 = (v: number) => Math.round(v * 1000) / 1000;

/** Poligonul unui perete, din axul lui, grosime și curbură. Cele două fețe sunt
 *  același arc offsetat cu ±t/2, puse cap la cap — v. `arcDinAx` pentru semnul
 *  offsetului, care e și cel pe care straturile numesc stânga și dreapta. */
export function poligonDinAx(a: Pt, b: Pt, grosime: number,
                             bulge: number): VarfAfin[] {
  const fataPlus = arcDinAx(a, b, bulge, grosime / 2);
  const fataMinus = arcDinAx(a, b, bulge, -grosime / 2).reverse();
  return [...fataPlus, ...fataMinus].map((p) =>
    [{ a: r3(p[0]) }, { a: r3(p[1]) }] as VarfAfin);
}

/** Axul unui perete drept, dedus din dreptunghiul lui: mijloacele laturilor
 *  scurte. Din el pornește prima curbare a unui zid transcris, care n-are `axis`
 *  fiindcă a fost desenat drept. */
export function axulPeretelui(ring: readonly Pt[]): [Pt, Pt] {
  const o = osiaPeretelui(ring);
  const mij = (o.fete[0] + o.fete[1]) / 2;
  return o.lungPeX
    ? [[o.span[0], mij], [o.span[1], mij]]
    : [[mij, o.span[0]], [mij, o.span[1]]];
}

/** CURBEAZĂ UN PERETE. `bulge` în convenția DXF; `0` îl face drept la loc.
 *
 *  Rescrie `poly` — ce citește toată lumea — ȘI perechea `axis`/`bulge`, care
 *  spune din ce a fost făcut. Se scriu de același gest, fiindcă despărțite ar
 *  însemna un desen și o descriere a lui care nu se potrivesc, și nimic n-ar
 *  întreba care are dreptate.
 *
 *  Camerele din jur îl urmează fiindcă se DERIVĂ din ziduri la fiecare
 *  schimbare (`Masa`, `camere.ts`): conturul lor lângă arc iese o linie frântă
 *  pe fața lui.
 */
export function curbeazaPeretele(doc: TronsonDoc, i: number,
                                 bulge: number): TronsonDoc {
  const d = clona(doc);
  const zid = d.plan?.walls?.[i];
  if (!zid) return doc;
  const ax = (zid.axis as [Pt, Pt] | undefined)
    ?? axulPeretelui(inelul(zid.poly));
  const b = bulgeFinit(bulge);
  zid.poly = poligonDinAx(ax[0], ax[1], zid.thickness_m, b);
  // AXUL RĂMÂNE SCRIS ȘI CÂND PERETELE E DREPT. El e coloana grafului: nodurile
  // se strâng din capete de ax, iar un perete care și l-ar pierde la fiecare
  // îndreptare ar ieși și din graf. `bulge` însă dispare la zero — un
  // «e curb, cu curbura zero» i-ar cere cititorului să se întrebe.
  zid.axis = ax;
  if (Math.abs(b) < 1e-9) delete zid.bulge;
  else zid.bulge = b;
  return d;
}

/** Un perete nou, ca dreptunghi între două puncte. */
/** FORMELE DE CAMERĂ care se pot trage dintr-o mișcare.
 *
 *  Un modul se editează pornind de la unul existent, dar nu întotdeauna: uneori
 *  trebuie desenată o cameră de la zero, alături, ca să se vadă cum ar arăta.
 *  Trei forme acoperă aproape tot ce se desenează de mână — dreptunghiul, L-ul
 *  (o cameră cu un colț mâncat de casa scării) și T-ul (o cameră cu un gât).
 *  Restul se obține din ele, trăgând de pereți.
 */
export const FORME_CAMERA = {
  camera: "dreptunghi",
  cameraL: "L",
  cameraT: "T",
} as const;

export type FormaCamera = keyof typeof FORME_CAMERA;

/** Conturul formei, în dreptunghiul tras de la `a` la `b`. */
export function conturCamerei(a: Pt, b: Pt, forma: FormaCamera): Pt[] {
  const x0 = Math.min(a[0], b[0]);
  const y0 = Math.min(a[1], b[1]);
  const x1 = Math.max(a[0], b[0]);
  const y1 = Math.max(a[1], b[1]);
  const mx = (x0 + x1) / 2;
  const my = (y0 + y1) / 2;
  if (forma === "cameraL") {
    // Colțul din dreapta-jos lipsește: se trage dintr-un dreptunghi, ca în
    // Homestyler, iar care colț lipsește se hotărăște mutând pereții după.
    return [[x0, y0], [x1, y0], [x1, my], [mx, my], [mx, y1], [x0, y1]];
  }
  if (forma === "cameraT") {
    const g = (x1 - x0) / 6;               // jumătatea gâtului
    return [[x0, y0], [x1, y0], [x1, my], [mx + g, my], [mx + g, y1],
            [mx - g, y1], [mx - g, my], [x0, my]];
  }
  return [[x0, y0], [x1, y0], [x1, y1], [x0, y1]];
}

/** Pune o cameră întreagă: conturul ei ca pereți, și camera dinăuntru.
 *
 *  PEREȚII PE CONTUR, CAMERA TOT PE EL. În catalog, camerele și pereții sunt
 *  două transcrieri ale aceleiași planșe și nu se ating la milimetru — v. nota
 *  din capul fișierului. O cameră desenată aici respectă convenția: poligonul
 *  ei e conturul, iar zidurile stau călare pe el. Derivarea de la salvare
 *  potrivește oricum camera cu spațiul dintre ziduri.
 */
/** Camera dintr-un contur desenat din colț în colț. */
export function adaugaCameraContur(
  doc: TronsonDoc, contur: Pt[], grosime = 0.1,
): { doc: TronsonDoc; ales: Ales } | null {
  return contur.length >= 3 ? zideste(doc, contur, grosime) : null;
}

/** O ZONĂ DESENATĂ PESTE O CAMERĂ — fără ziduri în jurul ei.
 *
 *  Toate celelalte camere se nasc ZIDITE: conturul tras devine pereți, iar
 *  camera e spațiul dintre ei. O zonă de desfumare nu e așa. Ea e un triunghi
 *  sau un trapez desenat ÎN casa scării ori în holul liftului — o bucată din
 *  camera-gazdă, marcată pentru evacuarea fumului —, deci:
 *
 *    • n-are pereți ai ei. Ziduri în jurul ei ar tăia holul în două și ar
 *      închide lifturile într-o cutie care nu există pe planșă;
 *    • se SUPRAPUNE peste camera în care e trasă, și asta e normal. De-aia nu
 *      se adună nici la suprafața utilă (v. `Desen::PESTE_ALTA_CAMERA`).
 *
 *  Cerut de arhitect (14.10.2026): un fel de a pune o zonă din paletă. */
// ------------------------------------------------------------- cotele ---
//
// COTELE PLANȘEI SE SOCOTESC, nu se scriu: ele măsoară geometria desenată, la
// fiecare desenare (v. `cote::cotelePlanului`). De-aia nu se pot șterge — nu
// sunt în document ca să fie scoase din el. Arhitectul cere amândouă lucrurile
// care lipsesc (15.10.2026): să poată PUNE o cotă de la un punct la altul, și
// să poată ȘTERGE orice cotă, inclusiv pe cele venite din desen.
//
// Puse: se scriu în `plan.dims`. Șterse: cele de mână se scot de-acolo, iar
// cele socotite se trec pe lista stinselor, pe cheia lor — altfel s-ar naște
// din nou la următoarea desenare.

/** CÂT SE DESENEAZĂ DINTR-O AXĂ, pe numele ei.
 *
 *  Aspect, nu proiect: axa rămâne pe aceeași poziție, cu aceiași pereți pe ea.
 *  Cerut de arhitect (15.10.2026): «să putem micșora aspectul axelor, adică să
 *  pot să selectez o axă și s-o fac mai scurtă sau mai lungă doar la aspect —
 *  funcționalitatea rămâne la fel». Se poate și din mână (apucătoarele de la
 *  capete) și din panou (cifra), fiindcă a cerut amândouă. */
export function lungimeaAxei(
  doc: TronsonDoc, nume: string, jos: number, sus: number,
): TronsonDoc {
  if (!(sus - jos > 0.05)) return doc;      // sub 5 cm nu mai e o linie
  const d = clona(doc);
  const plan = d.plan!;
  const restul = (plan.axes_len ?? []).filter((x) => x.nume !== nume);
  plan.axes_len = [...restul,
    { nume, jos: r3(jos), sus: r3(sus) }];
  return d;
}

/** ARE ZIDUL ĂSTA CAPETELE LĂSATE LIBERE? */
export function capeteLibere(doc: TronsonDoc, i: number): boolean {
  const id = doc.plan?.walls?.[i]?.id;
  return !!id && (doc.plan?.capete_libere ?? []).includes(id);
}

/** LASĂ (sau leagă la loc) CAPETELE UNUI ZID.
 *
 *  Portița de mână cerută de arhitect (27.10.2026). Conturul camerei se
 *  oprește cu 6 cm înainte de capătul unui zid care iese în gol, fiindcă la
 *  derivare zidurile se scad LUNGITE — v. `camere::lungit`. Cu capetele
 *  lăsate libere, camera ajunge până în zid.
 *
 *  CÂMPUL NU RĂMÂNE GOL ÎN DOCUMENT, ca la axele blocate: legat ultimul zid,
 *  lista se scoate cu totul, altfel ar intra în fiecare salvare și ar schimba
 *  amprenta documentului degeaba. */
export function lasaCapeteleLibere(
  doc: TronsonDoc, i: number, liber: boolean,
): TronsonDoc {
  const id = doc.plan?.walls?.[i]?.id;
  if (!id) return doc;
  const acum = doc.plan?.capete_libere ?? [];
  const are = acum.includes(id);
  if (are === liber) return doc;
  const d = clona(doc);
  const noi = liber ? [...acum, id] : acum.filter((x) => x !== id);
  if (noi.length) d.plan!.capete_libere = noi;
  else delete d.plan!.capete_libere;
  return d;
}

/** Lasă axa să se întindă iar cât o întind pereții ei. */
export function lungimeaAxeiLibera(doc: TronsonDoc, nume: string): TronsonDoc {
  const restul = (doc.plan?.axes_len ?? []).filter((x) => x.nume !== nume);
  if (restul.length === (doc.plan?.axes_len ?? []).length) return doc;
  const d = clona(doc);
  d.plan!.axes_len = restul;
  return d;
}

/** PUNE O COTĂ de la un punct la altul. */
export function adaugaCota(doc: TronsonDoc, a: Pt, b: Pt): TronsonDoc {
  // sub un centimetru nu e o măsură, e un clic scăpat
  if (Math.hypot(b[0] - a[0], b[1] - a[1]) < 0.01) return doc;
  const d = clona(doc);
  const plan = d.plan!;
  plan.dims = [...(plan.dims ?? []),
    { a: [r3(a[0]), r3(a[1])], b: [r3(b[0]), r3(b[1])] }];
  return d;
}

/** STINGE O COTĂ, oricare ar fi ea.
 *
 *  Pusă de mână, se scoate din document. Socotită, se trece pe lista stinselor:
 *  ea se naște din geometrie, deci singurul fel de a o face să tacă e să ții
 *  minte că a fost stinsă. */
export function stingeCota(doc: TronsonDoc, c: { a: Pt; b: Pt }): TronsonDoc {
  return stingeCheiaCotei(doc, cheiaCotei(c));
}

/** STINGE COTA CU CHEIA ASTA. Drumul prin care trece o cotă ALEASĂ: ea se ține
 *  pe cheie, nu pe obiect, fiindcă o cotă socotită se naște la fiecare
 *  desenare (v. `Ales`). */
export function stingeCheiaCotei(doc: TronsonDoc, cheie: string): TronsonDoc {
  const d = clona(doc);
  const plan = d.plan!;
  const inainte = (plan.dims ?? []).length;
  plan.dims = (plan.dims ?? []).filter(
    (x) => cheiaCotei({ a: [x.a[0], x.a[1]], b: [x.b[0], x.b[1]] }) !== cheie);
  const eraDeMana = plan.dims.length !== inainte;
  if (!plan.dims.length) delete plan.dims;
  // AMÂNDOUĂ, NU UNA SAU ALTA.
  //
  // Se întorcea aici dacă fusese una de mână, pe presupunerea că o măsură e
  // ori scrisă, ori socotită. PE UN PLAN IMPORTAT NU E AȘA: importul scrie
  // cote în document, iar planșa socotește una peste ele, pe ACEEAȘI față —
  // deci cu ACEEAȘI cheie. Ștearsă cea scrisă, cea socotită rămânea desenată
  // în locul ei, și din afară se citea «×»-ul nu face nimic. A doua apăsare o
  // stingea, fiindcă atunci lista era goală și cheia ajungea unde trebuie.
  //
  // MĂSURAT PE CATALOG: din 3764 de cote, două nu se stingeau — chiar perechea
  // asta, pe «CAPAT ATIPIC 2-3 CAMERE», o cotă de import și geamăna ei
  // socotită, amândouă cu cheia `195,1240|815,1240`.
  const stinse = new Set(plan.dims_stinse ?? []);
  if (!eraDeMana && stinse.has(cheie)) return doc;
  stinse.add(cheie);
  plan.dims_stinse = [...stinse];
  return d;
}

/** CÂT DE DEPARTE de fața măsurată poate sta o cheie veche ca s-o mai
 *  recunoaștem drept a ei. Depărtările liniei sunt de ordinul unui metru la
 *  măririle obișnuite; peste doi metri nu mai e aceeași cotă. */
const MUTARE_CHEIE_MAX = 2.0;

/** MUTĂ CHEILE VECHI pe noua socoteală, o dată, la deschidere.
 *
 *  Cheile scrise până pe 15.10.2026 erau capetele LINIEI DESENATE, deci purtau
 *  în ele mărirea la care fusese ștearsă cota (v. `cheiaCotei`). Pe TESTDESF
 *  erau patru, și trei dintre ele erau aceeași cotă de 39,85 ștearsă de trei
 *  ori, la trei măriri: `2418`, `2421`, `2432` — fața măsurată e la `2335`.
 *  Lăsate așa, n-ar mai prinde nimic: cotele șterse ieri s-ar întoarce toate,
 *  iar omul ar trebui să le șteargă din nou.
 *
 *  SE MUTĂ DOAR CE E FĂRĂ DUBIU: aceeași lungime (±1,5 cm), aceeași direcție,
 *  același mijloc în lungul ei, și o singură cotă de pe planșă care să
 *  răspundă. Două fețe paralele la un metru una de alta, la fel de lungi, ar
 *  fi amândouă candidate — atunci cheia rămâne cum e, și cota se șterge din
 *  nou cu mâna. Mai bine o ștergere pierdută decât una pusă pe altă cotă. */
export function cuCoteleStinseMutate(doc: TronsonDoc): TronsonDoc {
  const vechi = doc.plan?.dims_stinse;
  if (!vechi?.length) return doc;
  const cote = cotelePlanului(deriva(doc), 46, []);
  const acum = new Set(cote.map(cheiaCotei));
  if (vechi.every((c) => acum.has(c))) return doc;

  const citeste = (cheie: string): [Pt, Pt] | null => {
    const parti = cheie.split("|");
    if (parti.length !== 2) return null;
    const p = parti.map((x) => x.split(",").map(Number));
    if (p.some((q) => q.length !== 2 || q.some((n) => !Number.isFinite(n)))) {
      return null;
    }
    return [[p[0][0] / 100, p[0][1] / 100], [p[1][0] / 100, p[1][1] / 100]];
  };

  let schimbat = false;
  const noi = vechi.map((cheie) => {
    if (acum.has(cheie)) return cheie;
    const seg = citeste(cheie);
    if (!seg) return cheie;
    const L = Math.hypot(seg[1][0] - seg[0][0], seg[1][1] - seg[0][1]);
    if (!(L > 0)) return cheie;
    const mij: Pt = [(seg[0][0] + seg[1][0]) / 2, (seg[0][1] + seg[1][1]) / 2];
    // SE CAUTĂ ȘI PE FAȚA MĂSURATĂ, ȘI PE LINIA DESENATĂ. Cheia veche era o
    // linie desenată, iar fața pe care cade cheia nouă poate fi CEALALTĂ din
    // pereche: cota de 39,85 se scrie în dreapta, la x≈24,2, dar cheia ei e
    // fața din stânga, la x=0. Măsurate una de alta, cele două sunt la 24 m
    // depărtare — nicio toleranță cinstită nu le-ar fi apropiat.
    const aproape = (a: Pt, b: Pt) => {
      const L2 = Math.hypot(b[0] - a[0], b[1] - a[1]);
      if (!(L2 > 0) || Math.abs(L2 - L) > 0.015) return false;
      const u: Pt = [(b[0] - a[0]) / L2, (b[1] - a[1]) / L2];
      const dx = mij[0] - (a[0] + b[0]) / 2;
      const dy = mij[1] - (a[1] + b[1]) / 2;
      return Math.abs(dx * u[0] + dy * u[1]) < 0.02
          && Math.abs(-dx * u[1] + dy * u[0]) < MUTARE_CHEIE_MAX;
    };
    const potrivite = cote.filter((c) => {
      const m = c.masurat;
      return (m ? aproape(m[0], m[1]) : false) || aproape(c.a, c.b);
    });
    if (potrivite.length !== 1) return cheie;
    schimbat = true;
    return cheiaCotei(potrivite[0]);
  });
  if (!schimbat) return doc;
  const d = clona(doc);
  d.plan!.dims_stinse = [...new Set(noi)];
  return d;
}

/** Everything a module goes through on opening, before `Editor` takes its
 *  baseline. Each step only tidies, so the module must open saved: a step left
 *  to run after the baseline would mark it «nesalvat» with nobody having
 *  touched it. `Masa::deschide` repeats the key migration for its other
 *  callers, which is a no-op here. */
export function gataDeDeschis(doc: TronsonDoc): TronsonDoc {
  return cuCoteleStinseMutate(cuCutiileScarilor(cuAxeScrise(cuLuriDesfacute(doc))));
}

/** The stored box of every drawn stair, refitted to what is drawn. Boxes
 *  written before the box took in the landing are short of it, and the
 *  generator builds the stair's core from the box. A stair with an angle or a
 *  flip keeps its box (`inJurulCutiei`). A module that needs nothing comes back
 *  as the same object. */
function cuCutiileScarilor(doc: TronsonDoc): TronsonDoc {
  const piese = doc.plan?.furniture ?? [];
  const veche = (i: number) => {
    const f = piese[i];
    const c = f.scara?.rampe?.length && !inJurulCutiei(f) ? cutiaScarii(f.scara!) : null;
    return !!c && (f.x.a !== r3(c.cx) || f.y.a !== r3(c.cy)
                   || f.w_m !== r3(c.w) || f.h_m !== r3(c.h));
  };
  const de = piese.map((_, i) => i).filter(veche);
  if (!de.length) return doc;
  const d = clona(doc);
  for (const i of de) refaCutiaScarii(d, i);
  return d;
}

/** APRINDE la loc o cotă stinsă. */
export function aprindeCota(doc: TronsonDoc, cheie: string): TronsonDoc {
  const stinse = (doc.plan?.dims_stinse ?? []).filter((x) => x !== cheie);
  if (stinse.length === (doc.plan?.dims_stinse ?? []).length) return doc;
  const d = clona(doc);
  d.plan!.dims_stinse = stinse;
  return d;
}

// ------------------------------------------------- întinderea unei zone ---
//
// O BUCATĂ DE PLAN, MUTATĂ CU ZIDURILE EI CU TOT: tragi o fereastră peste ea
// și o duci unde vrei. Ce e cuprins ÎNTREG pleacă întreg; ce e doar TĂIAT de
// fereastră se ÎNTINDE după ea — capătul dinăuntru pleacă, celălalt rămâne, și
// zidul se lungește. Planul nu se rupe: bucata mutată rămâne legată de rest.
//
// REGULA E PE VÂRFURI, nu pe obiecte, și de-aici iese totul singur: un zid cu
// toate colțurile înăuntru se mută, unul cu două din patru se întinde, o cameră
// la fel — ea urmează zidurile ei. N-are nevoie de un caz scris pentru fiecare
// fel de obiect.
//
// A FOST ȘI DESPRINSĂ, o jumătate de oră: pe TESTDESF întinderea ieșise o claie
// de diagonale și părea că geometria o ia razna. Nu geometria era de vină, ci
// DESENUL ei — fiecare zid întins se arăta cu chihlimbariu aprins și umplut,
// iar fațadele traversează toată planșa, deci se aprindeau toate. Cerut înapoi
// cu desenul potolit: «it should move the connected walls as well, it shouldn't
// fully separate it… keep only the selected portion highlighted and the rest of
// the attached walls normal» (15.10.2026). V. `editor/Panza` pentru desen.
//
// CE NU FACE: nu reface camerele după ziduri și nu repară nimic. «Se mută exact
// cum ai selectat, și după, dacă s-a stricat ceva, fixăm de mână» — hotărât așa
// (15.10.2026), fiindcă o reparare automată ar muta și lucruri pe care omul
// le-a lăsat anume pe loc.

/** Fereastra de întindere, în metri. */
export type Fereastra = { x0: number; y0: number; x1: number; y1: number };

const inFereastra = (p: Pt, f: Fereastra) =>
  p[0] >= f.x0 && p[0] <= f.x1 && p[1] >= f.y0 && p[1] <= f.y1;

/** Un vârf afin, mutat: deplasarea se adună la partea CONSTANTĂ, ca orice
 *  mutare absolută — partea proporțională (`k`) rămâne a ei, altfel un zid
 *  legat de frontul modulului s-ar rupe de el. */
const varfMutat = (v: VarfAfin, dep: Pt): VarfAfin =>
  [{ ...v[0], a: r3(v[0].a + dep[0]) }, { ...v[1], a: r3(v[1].a + dep[1]) }];

/** What a window may take, by view layer (`editor/layers`): only what is on
 *  a layer at 100%. `true` takes everything; `false` leaves the devices out,
 *  as the old single electrics layer did. */
export type FiltruFereastra = {
  /** Walls, their openings and the rooms that follow them. */
  pereti: boolean;
  piesa: (kind: string) => boolean;
  aparat: (a: { kind: string; devices?: string[] }) => boolean;
};

/** CE PRINDE FEREASTRA. Scos separat de mutare fiindcă pânza are nevoie de el
 *  la fiecare mișcare de mouse, ca să arate ce pleacă, fără să atingă
 *  documentul. */
export function ceCuprinde(doc: TronsonDoc, f: Fereastra,
                           /* The layer that is switched off is not in the
                              window, and the one function the gestures share
                              is where that is decided. */
                           withDevices: boolean | FiltruFereastra = true) {
  const plan = doc.plan;
  const filtru: FiltruFereastra = typeof withDevices === "object" ? withDevices
    : { pereti: true, piesa: () => true, aparat: () => withDevices };
  const ziduriIntregi: number[] = [];
  /** Zidurile pe care fereastra doar le TAIE. NU se mută și nu se întind — se
   *  țin numai ca să se poată arăta stins pe desen ce rămâne pe loc, acolo pe
   *  unde bucata se rupe de restul planului. */
  const ziduriTaiate: number[] = [];
  if (filtru.pereti) (plan?.walls ?? []).forEach((w, i) => {
    const ring = inelul(w.poly ?? []);
    if (ring.length < 2) return;
    const cate = ring.filter((p) => inFereastra(p, f)).length;
    if (cate === ring.length) ziduriIntregi.push(i);
    else if (cate > 0) ziduriTaiate.push(i);
  });
  // CAMERA URMEAZĂ ZIDURILE EI, deci se socotește tot pe vârfuri: prinsă pe
  // jumătate, latura dinăuntru pleacă și camera se întinde odată cu peretele
  // care o mărginește. Asta și vrea cine trage o fațadă în afară.
  const camere: number[] = [];
  if (filtru.pereti) (plan?.rooms ?? []).forEach((c, i) => {
    if (inelul(c.poly ?? []).some((p) => inFereastra(p, f))) camere.push(i);
  });
  // GOLURILE MERG CU ZIDUL LOR. Cerut explicit: «ușile și ferestrele ar trebui
  // să meargă cu peretele pe care erau atașate, n-are sens să rămână singure»
  // (15.10.2026). Deci un gol pleacă fie fiindcă e el însuși în fereastră, fie
  // fiindcă zidul lui s-a mutat întreg de sub el.
  const ziduriMutate = new Set(
    ziduriIntregi.map((i) => (plan?.walls ?? [])[i]?.id).filter(Boolean));
  const goluri: { fel: "doors" | "windows"; i: number }[] = [];
  for (const fel of ["doors", "windows"] as const) {
    if (filtru.pereti) (plan?.[fel] ?? []).forEach((g, i) => {
      const p: Pt = [valoarea(g.x), valoarea(g.y)];
      if (inFereastra(p, f) || (g.wall && ziduriMutate.has(g.wall))) {
        goluri.push({ fel, i });
      }
    });
  }
  const piese: number[] = [];
  (plan?.furniture ?? []).forEach((m, i) => {
    if (inFereastra([valoarea(m.x), valoarea(m.y)], f) && filtru.piesa(m.kind)) piese.push(i);
  });
  const electrice: number[] = [];
  (plan?.electrical ?? []).forEach(
    (a: { kind: string; devices?: string[]; x: { a: number }; y: { a: number } }, i: number) => {
      if (inFereastra([valoarea(a.x), valoarea(a.y)], f) && filtru.aparat(a)) electrice.push(i);
    });
  return { ziduriIntregi, ziduriTaiate, camere, goluri, piese, electrice };
}

const valoarea = (z: { a: number; k?: number } | number): number =>
  typeof z === "number" ? z : z.a;

// ------------------------------------------------- bucata decupată și lipită ---
//
// COPIAZĂ ȘI LIPEȘTE O PORȚIUNE DE PLAN, cerut pe 19.10.2026: «When we select a
// portion with shift S we should be able to copy it and paste with ctrl c and
// ctrl v (or command for Mac), and when placing a selected portion we should be
// able to flip it or rotate from buttons and keys».
//
// Gestul de întindere (Shift+S) alege deja o bucată și știe exact ce a prins
// (`ceCuprinde`); de-aici încolo e un al doilea verb pe aceeași selecție.

/** O BUCATĂ DE PLAN, scoasă din document și ținută în mână.
 *
 *  COORDONATELE SUNT RELATIVE LA CENTRUL FERESTREI, nu absolute: bucata se
 *  lipește unde e cursorul, poate de mai multe ori și poate în alt modul, iar
 *  una scrisă în coordonatele locului de unde a fost luată ar fi trebuit
 *  translatată la fiecare din cele trei. Relativ, lipirea e o adunare.
 *
 *  VÂRFURILE SE TURTESC LA CIFRE. În document un vârf poate fi AFIN — o parte
 *  constantă plus una proporțională cu frontul modulului (v. `Afin`) —, ca o
 *  fațadă să se lungească odată cu el. Copia nu moștenește legătura asta,
 *  dinadins: proporția e scrisă pe o axă a modulului, iar bucata se poate
 *  lipi rotită cu 90°, unde „proporțional cu frontul pe x” nu mai înseamnă
 *  nimic. Copia e geometrie așezată de mână; cine vrea un zid legat de front
 *  îl leagă pe urmă, din panou. */
export type Bucata = {
  walls: PereteDoc[];
  rooms: CameraDoc[];
  doors: GolDoc[];
  windows: GolDoc[];
  furniture: PiesaDoc[];
  electrical: ElectricDoc[];
};

/** EVERY COORDINATE OF A PORTION, through one point function.
 *
 *  Copy, rotate, mirror and paste all go through this one list of fields, so
 *  a field is added once and none is left behind. A wall's axis is on it
 *  because the editor rebuilds a wall from its axis: an axis left at the old
 *  place sends the wall back there on the next thickness change. Coordinates
 *  come out flat (`{ a }`, no `k`) and to the millimetre, as a portion carries
 *  them; orientation (angles, hinges, flips, ring order, curvature, layers)
 *  is each caller's own. */
export function mutaPuncteleBucatii(
  b: Bucata, pct: (x: number, y: number) => [number, number],
): Bucata {
  const p = (x: number, y: number): [number, number] => {
    const [u, v] = pct(x, y);
    return [r3(u), r3(v)];
  };
  const varf = (v: VarfAfin): VarfAfin => {
    const [x, y] = p(valoarea(v[0]), valoarea(v[1]));
    return [{ a: x }, { a: y }];
  };
  const pozitie = (o: { x: Afin; y: Afin }) => {
    const [x, y] = p(valoarea(o.x), valoarea(o.y));
    o.x = { a: x }; o.y = { a: y };
  };
  const n = clona(b);
  for (const w of n.walls) {
    w.poly = (w.poly ?? []).map(varf);
    if (w.axis) w.axis = [p(w.axis[0][0], w.axis[0][1]), p(w.axis[1][0], w.axis[1][1])];
  }
  for (const c of n.rooms) c.poly = (c.poly ?? []).map(varf);
  for (const o of [...n.doors, ...n.windows]) pozitie(o);
  for (const m of n.furniture) {
    pozitie(m);
    // SCARA DESENATĂ își poartă rampele în coordonatele planului, nu în cutia
    // ei (v. `PiesaDoc.scara`): lăsate, ar fi rămas pe vechea direcție, iar
    // scara s-ar fi desenat de-a curmezișul cutiei ei.
    if (m.scara) {
      m.scara = { ...m.scara,
                  rampe: m.scara.rampe.map((r) => ({
                    de: p(r.de[0], r.de[1]), la: p(r.la[0], r.la[1]) })),
                  ...(m.scara.podeste ? { podeste: m.scara.podeste.map((q) => ({
                    ...q, contur: q.contur.map((z) => p(z[0], z[1])) })) } : {}) };
    }
  }
  for (const a of n.electrical) pozitie(a);
  return n;
}

/** DECUPEAZĂ ce prinde fereastra, fără să atingă documentul.
 *
 *  Ce pleacă e CHIAR ce arată gestul că e ales (`ceCuprinde`), ca Delete-ul
 *  de pe aceeași selecție: zidurile cuprinse ÎNTREGI, camerele atinse,
 *  golurile lor, mobila și aparatele dinăuntru. Zidul pe care fereastra doar
 *  îl TAIE nu se copiază — o jumătate de zid n-ar fi un zid, ar fi o linie. */
export function copiazaCuprinsul(doc: TronsonDoc, f: Fereastra,
                                 withDevices: boolean | FiltruFereastra = true): Bucata | null {
  const plan = doc.plan;
  if (!plan) return null;
  const ce = ceCuprinde(doc, f, withDevices);
  const cx = (f.x0 + f.x1) / 2, cy = (f.y0 + f.y1) / 2;
  const ziduri = ce.ziduriIntregi;
  if (!ziduri.length && !ce.camere.length && !ce.goluri.length
      && !ce.piese.length && !ce.electrice.length) return null;
  const b: Bucata = {
    walls: ziduri.map((i) => clona(plan.walls![i])),
    rooms: ce.camere.map((i) => clona(plan.rooms![i])),
    doors: [], windows: [],
    furniture: ce.piese.map((i) => clona(plan.furniture![i])),
    electrical: ce.electrice.map((i) => clona(plan.electrical![i])),
  };
  for (const fel of ["doors", "windows"] as const) {
    b[fel] = ce.goluri.filter((g) => g.fel === fel).map((g) => clona(plan[fel]![g.i]));
  }
  // APARATELE ÎȘI PIERD LEGĂTURA CU PIESA pe care o urmăreau (`pentru` e un
  // INDICE în lista modulului, iar în bucată lista e alta). S-ar fi putut
  // renumerota, dar ar fi fost o legătură care supraviețuiește doar dacă piesa
  // a intrat și ea în fereastră — adică uneori. Ruptă de tot, regula e una
  // singură și se citește: priza lipită stă unde a fost pusă.
  for (const a of b.electrical) { delete a.pentru; delete a.parte; }
  // GOLUL AL CĂRUI ZID N-A INTRAT ÎN BUCATĂ rămâne fără gazdă. Nu se aruncă:
  // `ceCuprinde` îl dă numai dacă e el însuși în fereastră sau dacă zidul lui
  // pleacă întreg, deci cazul e golul desenat liber. `wall` se stinge ca să nu
  // arate spre un zid din alt document.
  const idn = new Set(b.walls.map((w) => w.id).filter(Boolean));
  for (const fel of ["doors", "windows"] as const) {
    for (const o of b[fel]) if (o.wall && !idn.has(o.wall)) o.wall = null;
  }
  return mutaPuncteleBucatii(b, (x, y) => [x - cx, y - cy]);
}

/** Cât de mare e bucata, pe axele planului — pentru desenul ei sub cursor. */
export function cadrulBucatii(b: Bucata): { w: number; h: number } {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  const pune = (x: number, y: number) => {
    x0 = Math.min(x0, x); y0 = Math.min(y0, y);
    x1 = Math.max(x1, x); y1 = Math.max(y1, y);
  };
  for (const w of b.walls) for (const v of w.poly ?? []) pune(valoarea(v[0]), valoarea(v[1]));
  for (const c of b.rooms) for (const v of c.poly ?? []) pune(valoarea(v[0]), valoarea(v[1]));
  for (const m of b.furniture) pune(valoarea(m.x), valoarea(m.y));
  return Number.isFinite(x0) ? { w: x1 - x0, h: y1 - y0 } : { w: 0, h: 0 };
}

/** ROTEȘTE BUCATA în jurul centrului ei (adică al originii: v. `Bucata`).
 *
 *  Numai sferturi de tur. Un unghi oarecare ar fi cerut ca zidurile să se
 *  retraseze din ax, iar camerele să-și refacă poligoanele; la 90° totul e o
 *  permutare de coordonate, deci geometria iese exactă, fără rotunjiri care
 *  să desfacă nodurile dintre ziduri. Asta și cere gestul: butoane și taste,
 *  nu un unghi scris. */
export function rotesteBucata(b: Bucata, grade: number): Bucata {
  const g = ((Math.round(grade / 90) * 90) % 360 + 360) % 360;
  if (!g) return b;
  const rad = (g * Math.PI) / 180;
  const co = Math.round(Math.cos(rad)), si = Math.round(Math.sin(rad));
  const n = mutaPuncteleBucatii(b, (x, y) => [x * co - y * si, x * si + y * co]);
  for (const lista of [n.doors, n.windows] as const) {
    for (const o of lista) {
      if (o.rot_deg != null) o.rot_deg = r3(in360(o.rot_deg + g));
    }
  }
  for (const m of n.furniture) m.rot_deg = r3(in360((m.rot_deg ?? 0) + g));
  for (const a of n.electrical) {
    if (a.rot_deg != null) a.rot_deg = r3(in360(a.rot_deg + g));
  }
  return n;
}

/** OGLINDEȘTE BUCATA pe axa dată, tot în jurul centrului ei.
 *
 *  ORDINEA VÂRFURILOR SE ÎNTOARCE la poligoane: oglindirea schimbă sensul de
 *  parcurgere, iar un inel ajuns invers e un poligon cu arie negativă — îl
 *  citesc greșit și aria, și derivarea camerelor. Curbura (`bulge`) își
 *  schimbă semnul din același motiv: arcul se umflă de partea cealaltă. */
export function oglindesteBucata(b: Bucata, axa: Axa): Bucata {
  const n = mutaPuncteleBucatii(b, (x, y) => (axa === "h" ? [-x, y] : [x, -y]));
  for (const w of n.walls) {
    w.poly = (w.poly ?? []).reverse();
    if (w.bulge) w.bulge = -w.bulge;
    // the axis keeps its direction, and a mirror swaps its left and right:
    // the layers, counted from left to right, swap with them
    if (w.layers?.length) w.layers = [...w.layers].reverse();
  }
  for (const c of n.rooms) c.poly = (c.poly ?? []).reverse();
  // GOLURILE N-AU STEAGURI DE OGLINDIRE, au MÂNĂ: `swing` spune de ce parte a
  // zidului baleiază foaia, `hinge` în care toc stă balamaua (v. `GolDoc`).
  // Oglindit, un gol își schimbă AMÂNDOUĂ: direcția lui se întoarce — de asta
  // se ocupă `unghiOglindit`, aceeași socoteală ca la un gol oglindit singur —,
  // iar balamaua trece în celălalt toc, fiindcă stânga ușii devine dreapta ei.
  // `swing` rămâne: foaia bate în continuare în aceeași cameră, care s-a
  // oglindit și ea odată cu bucata.
  for (const lista of [n.doors, n.windows] as const) {
    for (const o of lista) {
      o.rot_deg = unghiOglindit(o.rot_deg ?? 0, axa);
      if (o.hinge === "end") delete o.hinge; else o.hinge = "end";
    }
  }
  for (const m of n.furniture) {
    const h = mainaOglindita(m.rot_deg ?? 0, m.flip_x === true,
                             m.flip_y === true, axa);
    m.rot_deg = h.rot;
    if (h.oglX) m.flip_x = true; else delete m.flip_x;
    if (h.oglY) m.flip_y = true; else delete m.flip_y;
  }
  for (const a of n.electrical) oglindesteAparatul(a, axa);
  return n;
}

/** LIPEȘTE BUCATA cu centrul ei în `c`.
 *
 *  ID-URILE ZIDURILOR SE REFAC, iar golurile sunt trimise spre cele noi: cu
 *  cele vechi, copia și originalul ar fi purtat același `id`, iar o ușă a
 *  copiei s-ar fi socotit pe zidul originalului (și invers — `ceStaPeZiduri`
 *  caută după `id`). Aceeași grijă ca la `dubleaza` pe un perete.
 *
 *  ANSAMBLURILE DE MOBILĂ PRIMESC NUME NOI, din același motiv ca la dublare:
 *  altfel masa lipită și cea copiată ar fi fost un singur ansamblu și s-ar fi
 *  mutat una pe alta. */
export function lipesteBucata(
  doc: TronsonDoc, b: Bucata, c: Pt,
): { doc: TronsonDoc; ales: Ales } {
  const d = clona(doc);
  const plan = (d.plan = d.plan ?? ({} as NonNullable<TronsonDoc["plan"]>));
  const [dx, dy] = c;
  b = mutaPuncteleBucatii(b, (x, y) => [x + dx, y + dy]);
  plan.walls = plan.walls ?? [];
  const gazdeNoi: Record<string, string> = {};
  const idEle = () => plan.walls!.map((w) => w.id).filter(Boolean) as string[];
  for (const w0 of b.walls) {
    const w = clona(w0);
    const vechi = w.id;
    w.id = idNou(idEle());
    if (vechi) gazdeNoi[vechi] = w.id;
    plan.walls.push(w);
  }
  plan.rooms = plan.rooms ?? [];
  for (const c0 of b.rooms) {
    plan.rooms.push(clona(c0));
  }
  for (const fel of ["doors", "windows"] as const) {
    plan[fel] = plan[fel] ?? [];
    for (const o0 of b[fel]) {
      const o = clona(o0);
      if (o.wall && gazdeNoi[o.wall]) o.wall = gazdeNoi[o.wall];
      plan[fel]!.push(o);
    }
  }
  plan.furniture = plan.furniture ?? [];
  const intai = plan.furniture.length;
  const grupuriNoi: Record<string, string> = {};
  for (const m0 of b.furniture) {
    const m = clona(m0);
    if (m.grup) {
      if (!grupuriNoi[m.grup]) grupuriNoi[m.grup] = grupNou(d);
      m.grup = grupuriNoi[m.grup];
    }
    plan.furniture.push(m);
  }
  if (b.electrical.length) {
    plan.electrical = plan.electrical ?? [];
    for (const a0 of b.electrical) plan.electrical.push(clona(a0));
  }
  // ALEGEREA CADE PE CEVA DIN CE S-A LIPIT, ca după orice gest care pune ceva:
  // primul zid, altfel prima piesă. Fără ea, bucata lipită ar fi rămas
  // nealeasă, iar ⌘Z ar fi fost singurul fel de a mai ajunge la ea.
  const ales: Ales = plan.walls.length > (plan.walls.length - b.walls.length)
    ? { fel: "perete", i: plan.walls.length - b.walls.length }
    : b.furniture.length ? { fel: "mobila", i: intai } : null;
  return { doc: d, ales };
}

/** ȘTERGE ce cuprinde fereastra.
 *
 *  Cerut odată cu gestul (15.10.2026): «when we have that section selected
 *  with S, if we press on the delete key we should be able to delete those
 *  elements».
 *
 *  SE ȘTERGE CE E APRINS, adică și zidurile pe care fereastra doar le TAIE —
 *  ele sunt alese, se văd alese, iar un Delete care lasă în urmă tocmai ce
 *  ardea pe ecran ar fi mai greu de înțeles decât unul care ia tot.
 *
 *  GOLURILE ORFANE PLEACĂ ȘI ELE: o ușă al cărei perete-gazdă s-a șters n-are
 *  pe ce sta, iar lăsată în document ar ieși desenată în aer. */
export function stergeCuprinsul(doc: TronsonDoc, f: Fereastra,
                                withDevices: boolean | FiltruFereastra = true): TronsonDoc {
  const ce = ceCuprinde(doc, f, withDevices);
  // CE E ALES, adică numai ce e cuprins întreg: zidul tăiat de fereastră nu se
  // mută, deci n-are de ce să se și șteargă. O singură regulă de ales, pentru
  // amândouă faptele.
  const ziduri = new Set(ce.ziduriIntregi);
  if (!ziduri.size && !ce.camere.length && !ce.goluri.length
      && !ce.piese.length && !ce.electrice.length) return doc;
  const d = clona(doc);
  const plan = d.plan!;
  plan.walls = (plan.walls ?? []).filter((_, i) => !ziduri.has(i));
  const camere = new Set(ce.camere);
  plan.rooms = (plan.rooms ?? []).filter((_, i) => !camere.has(i));
  // GOLURILE: cele alese, și atât. Un gol al unui zid șters e DEJA printre
  // ele — `ceCuprinde` duce după zid golurile zidurilor cuprinse întregi, iar
  // de șters se șterg chiar numai alea. (A existat aici și o a doua pază, pe
  // `wall`, de pe vremea când ștergerea lua și zidurile tăiate de fereastră.)
  for (const fel of ["doors", "windows"] as const) {
    const alese = new Set(ce.goluri.filter((g) => g.fel === fel).map((g) => g.i));
    plan[fel] = (plan[fel] ?? []).filter((_g, i) => !alese.has(i));
  }
  const piese = new Set(ce.piese);
  plan.furniture = (plan.furniture ?? []).filter((_, i) => !piese.has(i));
  const el = new Set(ce.electrice);
  if (plan.electrical) {
    plan.electrical = plan.electrical.filter((_, i: number) => !el.has(i));
  }
  return d;
}

/** ÎNTINDE ce cuprinde fereastra, cu `dep`. */
export function mutaCuprinsul(
  doc: TronsonDoc, f: Fereastra, dep: Pt, withDevices: boolean | FiltruFereastra = true,
): TronsonDoc {
  if (!dep[0] && !dep[1]) return doc;
  const d = clona(doc);
  const plan = d.plan;
  if (!plan) return doc;
  // Faded walls stay where they are, and so do the rooms that follow them.
  const cuPereti = typeof withDevices !== "object" || withDevices.pereti;

  // ZIDURILE ȘI CAMERELE, PE VÂRFURI: fiecare colț dinăuntru pleacă, iar ce
  // iese din fereastră rămâne — așa se întinde zidul tăiat în loc să se rupă.
  if (cuPereti) for (const w of plan.walls ?? []) {
    w.poly = (w.poly ?? []).map(
      (v) => (inFereastra([valoarea(v[0]), valoarea(v[1])], f)
        ? varfMutat(v, dep) : v));
    // ȘI AXUL, cu aceeași regulă. Era lăsat pe loc, iar de-aici tot ce s-a
    // reclamat după un ⇧S (22.10.2026): «pentru peretele selectat punctul de
    // care trag nu mai este la capătul peretelui… și rămâne agățat de peretele
    // de sus chiar dacă l-am mutat mai departe».
    //
    // Axul nu e un desen, e CE ȘTIE EDITORUL despre zid: din el ies mânerele
    // de capăt (v. `Panza`, gestul `capatPerete`), nodurile grafului
    // (`nodurile`) și lungirea vecinilor (`lungesteVecinii`). Rămas în urmă,
    // zidul se vedea mutat dar se purta ca și cum ar fi stat pe loc — apucat
    // de unde era înainte, și încă lipit de vecinul pe care îl părăsise.
    // `mutaPereteleSingur` îl duce de mult, la fel; numai întinderea îl uitase.
    //
    // CAPETELE SE CERN UNUL CÂTE UNUL, ca vârfurile poligonului, nu adunând
    // `dep` la amândouă: o întindere mută DOAR ce e în fereastră, deci un zid
    // prins pe jumătate se lungește, iar celălalt capăt al lui trebuie să
    // rămână exact unde era. Și se păstrează astfel ORDINEA capetelor, care
    // recalculate din poligon s-ar fi putut întoarce pe dos (v. `nodurile`,
    // despre zidurile aproape pătrate).
    if (w.axis) {
      w.axis = (w.axis as [Pt, Pt]).map(
        (q) => (inFereastra([q[0], q[1]], f)
          ? [r3(q[0] + dep[0]), r3(q[1] + dep[1])] as Pt : q)) as [Pt, Pt];
    }
  }
  if (cuPereti) for (const c of plan.rooms ?? []) {
    c.poly = (c.poly ?? []).map(
      (v) => (inFereastra([valoarea(v[0]), valoarea(v[1])], f)
        ? varfMutat(v, dep) : v));
  }
  // ȘI NUCLEUL, cu aceeași regulă. Era lăsat pe loc, iar pe un modul care își
  // ține lifturile ACOLO — M5 are două și NICIO piesă de lift, M3 are și-și —
  // o întindere peste casa scării muta zidurile din jur și lăsa puțurile unde
  // erau. Arătat de arhitect (22.10.2026): «am folosit shift+s ca să mut
  // ascensorul cu pereții».
  //
  // DOUĂ LOCURI, fiindcă formatul are două: `core_rect` e cutia nucleului
  // ([x0,y0,x1,y1]), iar `core_parts` sunt bucățile lui — scara, puțurile,
  // ghena —, fiecare cu poligonul ei în coordonate SIMPLE, nu afine. Mutat
  // numai unul din ele, desenul s-ar fi rupt de cutie.
  //
  // CUTIA SE MUTĂ PE COLȚURI, ca orice altceva prins de întindere: colțul
  // dinăuntrul ferestrei pleacă, cel dinafară stă. Pe un nucleu cuprins întreg
  // ies amândouă, deci cutia se duce întreagă; pe unul prins pe jumătate ea se
  // întinde, exact ca un zid tăiat de fereastră.
  if (plan.core_rect && plan.core_rect.length === 4) {
    const [x0, y0, x1, y1] = plan.core_rect;
    const colt = (x: number, y: number): [number, number] =>
      (inFereastra([x, y], f) ? [r3(x + dep[0]), r3(y + dep[1])] : [x, y]);
    const [a0, b0] = colt(x0, y0);
    const [a1, b1] = colt(x1, y1);
    plan.core_rect = [a0, b0, a1, b1];
  }
  for (const b of plan.core_parts ?? []) {
    // A STAIR PART DOES NOT STRETCH: its treads are counted. It moves when the
    // window holds all of it, and stays when it holds only part of it.
    if (b.scara) {
      if ((b.poly ?? []).every((q) => inFereastra([q[0], q[1]], f))) {
        b.poly = b.poly.map((q) => [r3(q[0] + dep[0]), r3(q[1] + dep[1])]);
        duScara(b.scara, dep);
      }
      continue;
    }
    b.poly = (b.poly ?? []).map(
      (q) => (inFereastra([q[0], q[1]], f)
        ? [r3(q[0] + dep[0]), r3(q[1] + dep[1])] as [number, number] : q));
  }

  const ce = ceCuprinde(doc, f, withDevices);
  for (const g of ce.goluri) {
    const o = (plan[g.fel] ?? [])[g.i];
    if (!o) continue;
    o.x = { ...o.x, a: r3(o.x.a + dep[0]) };
    o.y = { ...o.y, a: r3(o.y.a + dep[1]) };
  }
  for (const i of ce.piese) {
    const m = (plan.furniture ?? [])[i];
    if (!m) continue;
    m.x = { ...m.x, a: r3(m.x.a + dep[0]) };
    m.y = { ...m.y, a: r3(m.y.a + dep[1]) };
    // o scară trasă își poartă rampele și podestele în coordonate de plan
    if (m.scara) duScara(m.scara, dep);
  }
  for (const i of ce.electrice) {
    const a = (plan.electrical ?? [])[i];
    if (!a) continue;
    a.x = { ...a.x, a: r3(a.x.a + dep[0]) };
    a.y = { ...a.y, a: r3(a.y.a + dep[1]) };
  }
  return d;
}

/** O ZONĂ DESENATĂ, fără să se suprapună peste nicio cameră.
 *
 *  CAMERELE NU SE SUPRAPUN — cerut explicit (14.10.2026: «why is the zona de
 *  desfumare over the spatiu tehnic? It looks confusing. The rooms should not
 *  overlap»). Zona a fost întâi o însemnare PESTE camera-gazdă, și aia era
 *  greșeala: două camere pe același loc înseamnă două nume scrise în același
 *  punct, două arii pentru aceeași podea și un desen pe care nu-l poți citi.
 *
 *  Deci zona își face LOC, nu se așază deasupra:
 *
 *    • dacă acoperă aproape toată camera (v. `ZONA_INGHITE`), nu se adaugă
 *      nimic — camera aceea DEVINE zonă. Cine trage zona peste tot spațiul
 *      tehnic spune «ăsta e zona», nu «mai fac una la fel peste el»;
 *    • dacă acoperă o bucată, bucata se SCADE din gazdă și devine zonă. Cele
 *      două se ating, ca orice două camere vecine;
 *    • unde nu e nicio cameră, zona e pur și simplu o cameră nouă.
 *
 *  Zona nu are pereți ai ei nici acum: ziduri în jurul ei ar tăia holul în
 *  două și ar închide lifturile într-o cutie care nu există pe planșă. */
/** UN POLIGON CU GAURĂ, scris ca UN SINGUR INEL — prin «gaura de cheie».
 *
 *  O cameră e un singur inel (`CameraDoc.poly`), n-are cum să poarte o gaură.
 *  Numai că scăderea unei zone trase STRICT ÎNĂUNTRUL gazdei — un duș în
 *  mijlocul băii, care nu atinge niciun perete — dă tocmai asta: inel exterior
 *  plus o gaură. `adaugaZona` lua din scădere numai inelul exterior, deci în
 *  cazul ăla gazda rămânea ÎNTREAGĂ, peste zonă: două camere pe aceeași podea
 *  și aria numărată de două ori — exact ce refuză restul fișierului
 *  («camerele nu se suprapun», 14.10.2026). Un duș tras în colț, lipit de două
 *  laturi, dădea o scădere în L și mergea; unul tras în mijloc, nu. Arătat
 *  indirect de arhitect (22.10.2026), cerând dușul ca o cameră a lui.
 *
 *  GAURA DE CHEIE e felul obișnuit de a scrie una ca alta: inelul exterior se
 *  deschide în punctul cel mai apropiat de gaură, se parcurge gaura întreagă,
 *  și se închide înapoi pe unde s-a intrat. Coridorul are lățime ZERO, deci nu
 *  ia nicio arie, iar formula lui Gauss dă chiar exterior − gaură fiindcă
 *  `polygon-clipping` întoarce gaura cu sensul invers inelului ei exterior.
 *  Desenul o umple la fel, cu orice regulă de umplere: cele două treceri prin
 *  coridor se anulează. */
export function inelCuGaura(pol: readonly (readonly Pt[])[]): Pt[] {
  const fara = (r: readonly Pt[]) => {
    const q = r.slice();
    if (q.length > 1 && q[0][0] === q[q.length - 1][0]
        && q[0][1] === q[q.length - 1][1]) q.pop();
    return q as Pt[];
  };
  /** Aria CU SEMN — din ea se citește în ce sens e parcurs inelul. */
  const semnat = (r: readonly Pt[]) => r.reduce((t, q, i) => {
    const n = r[(i + 1) % r.length];
    return t + q[0] * n[1] - n[0] * q[1];
  }, 0) / 2;
  let out = fara(pol[0]);
  for (const g of pol.slice(1)) {
    let h = fara(g);
    if (h.length < 3 || out.length < 3) continue;
    // GAURA SE PARCURGE ÎN SENS INVERS inelului exterior, altfel aria ei s-ar
    // ADUNA în loc să se scadă. Se MĂSOARĂ, nu se presupune: ce sens întoarce
    // biblioteca de tăiat pentru găuri nu e o însușire pe care să ne bizuim.
    if (Math.sign(semnat(h)) === Math.sign(semnat(out))) h = h.slice().reverse();
    let bi = 0, bj = 0, cel = Infinity;
    for (let i = 0; i < out.length; i++) {
      for (let j = 0; j < h.length; j++) {
        const d = (out[i][0] - h[j][0]) ** 2 + (out[i][1] - h[j][1]) ** 2;
        if (d < cel) { cel = d; bi = i; bj = j; }
      }
    }
    out = [...out.slice(0, bi + 1), ...h.slice(bj), ...h.slice(0, bj + 1),
           ...out.slice(bi)];
  }
  // ÎNCHIS LA LOC, cu primul punct repetat la capăt: așa vin inelele din
  // biblioteca de tăiat, iar cine ne cheamă taie ultimul punct ca să le
  // deschidă (`cel.slice(0, -1)`). Întors deschis, el ar fi retezat un vârf
  // ADEVĂRAT — și chiar asta a și făcut: baia ieșea 3,97 m² în loc de 9,62.
  return [...out, out[0]];
}

export function adaugaZona(
  doc: TronsonDoc, contur: Pt[], kind = "desfumare",
): { doc: TronsonDoc; ales: Ales } | null {
  if (contur.length < 3) return null;
  const aria = (p: readonly Pt[]) => Math.abs(p.reduce((s, q, i) => {
    const r = p[(i + 1) % p.length];
    return s + q[0] * r[1] - r[0] * q[1];
  }, 0)) / 2;
  if (aria(contur) < 0.5) return null;      // un clic scăpat, nu o zonă

  const d = clona(doc);
  const plan = d.plan!;
  const nou: Pt[][] = [contur.map((q) => [q[0], q[1]] as Pt)];

  // GAZDELE: camerele peste care cade zona.
  for (let i = plan.rooms.length - 1; i >= 0; i--) {
    // `inelul` citește poligonul oricum ar fi scris — afin sau brut — ca peste
    // tot în fișier. Citit cu `v[0].a`, un document scris cu perechi simple
    // dădea vârfuri nedefinite, iar biblioteca de tăiat refuza geometria.
    const gazda = inelul(plan.rooms[i].poly ?? []);
    if (gazda.length < 3) continue;
    const comun = taie().intersection(
      [inchide(gazda)] as unknown as PoligonTaiat,
      [inchide(nou[0])] as unknown as PoligonTaiat);
    const peste = comun.reduce(
      (s, pol) => s + aria(pol[0] as unknown as Pt[]), 0);
    if (peste < 1e-6) continue;             // nu se ating

    // Bucata acoperită se SCADE din gazdă.
    const ramas = taie().difference(
      [inchide(gazda)] as unknown as PoligonTaiat,
      [inchide(nou[0])] as unknown as PoligonTaiat);
    // CU TOT CU GĂURI (v. `inelCuGaura`): o zonă trasă în mijlocul gazdei lasă
    // o gaură, iar luând numai inelul exterior gazda rămânea întreagă.
    const cel = ramas.map((pol) => inelCuGaura(pol as unknown as Pt[][]))
      .sort((a, b) => aria(b) - aria(a))[0];
    // DACĂ DIN GAZDĂ N-AR MAI RĂMÂNE O CAMERĂ, zona i-a luat locul: nu se mai
    // adaugă nimic, camera aceea DEVINE zonă. Ăsta e cazul din poză — zona
    // trasă peste tot spațiul tehnic — și tot el prinde și mâna care n-a
    // nimerit colțul la milimetru.
    //
    // Pragul e același 0,5 m² cu care se judecă peste tot «a fost un clic
    // scăpat, nu o cameră». A existat o vreme și un prag de PROCENT (peste 90%
    // din gazdă), scos fiindcă nicio probă nu-l putea deosebi de ăsta, iar în
    // singurul caz în care s-ar fi deosebit făcea rău: pe un hol de 20 m²
    // acoperit 92%, el ar fi aruncat o bucată de 1,6 m² de podea adevărată.
    if (!cel || aria(cel) < 0.5) {
      plan.rooms[i].kind = kind;
      delete plan.rooms[i].label;
      return { doc: d, ales: { fel: "camera", i } };
    }
    const fara = cel.slice(0, -1).length >= 3 ? cel.slice(0, -1) : cel;
    plan.rooms[i].poly = fara.map(
      (q) => [{ a: r3(q[0]) }, { a: r3(q[1]) }] as VarfAfin);
    plan.rooms[i].area_m2 = Math.round(aria(fara) * 100) / 100;
  }

  plan.rooms.push({
    kind, side: "A",
    area_m2: Math.round(aria(nou[0]) * 100) / 100,
    poly: nou[0].map((q) => [{ a: r3(q[0]) }, { a: r3(q[1]) }] as VarfAfin),
    // DESENATĂ CU MÂNA, deci nu se deduce din pereți — v. `camere::refacute`.
    // Fără semnul ăsta, camerele se refac din zidurile din jur la prima
    // geometrie atinsă, iar o zonă care n-are ziduri ale ei (un duș tras în
    // baie) dispărea la primul perete mișcat. Cerut de arhitect (22.10.2026):
    // «vreau să trasez o secțiune care nu e înconjurată de pereți, să-i setez
    // tipul, și să-mi apară perimetrul».
    desenata: true,
  } as never);
  return { doc: d, ales: { fel: "camera", i: plan.rooms.length - 1 } };
}

/** Inelul, închis — `polygon-clipping` vrea primul punct și la sfârșit. */
const inchide = (p: readonly Pt[]): Pt[] =>
  [...p.map((q) => [q[0], q[1]] as Pt), [p[0][0], p[0][1]] as Pt];

export function adaugaCamera(
  doc: TronsonDoc, a: Pt, b: Pt, forma: FormaCamera, grosime = 0.1,
): { doc: TronsonDoc; ales: Ales } | null {
  return zideste(doc, conturCamerei(a, b, forma), grosime);
}

function zideste(
  doc: TronsonDoc, contur: Pt[], grosime: number,
): { doc: TronsonDoc; ales: Ales } | null {
  const arie = Math.abs(contur.reduce((s2, p, i) => {
    const q = contur[(i + 1) % contur.length];
    return s2 + p[0] * q[1] - q[0] * p[1];
  }, 0)) / 2;
  // Sub o jumătate de metru pătrat n-a fost o cameră, a fost un clic scăpat.
  if (arie < 0.5) return null;

  let d = clona(doc);
  for (let i = 0; i < contur.length; i++) {
    const p0 = contur[i];
    const p1 = contur[(i + 1) % contur.length];
    // Prelungit cu jumătate de grosime la fiecare capăt, ca zidurile să se
    // întâlnească în colț și să nu rămână o crestătură de câțiva centimetri.
    const dx = p1[0] - p0[0];
    const dy = p1[1] - p0[1];
    const l = Math.hypot(dx, dy) || 1;
    const ex = (dx / l) * (grosime / 2);
    const ey = (dy / l) * (grosime / 2);
    d = adaugaPerete(d, [r3(p0[0] - ex), r3(p0[1] - ey)],
                        [r3(p1[0] + ex), r3(p1[1] + ey)], grosime).doc;
  }

  const plan = d.plan!;
  plan.rooms.push({
    // Ce e camera asta nu se poate ști din conturul tras: pornește de la felul
    // despre care nu se afirmă nimic (v. `feluri::FEL_IMPLICIT`) și se alege
    // din panou. «other» a stat aici o vreme și era mai rău decât o
    // necunoscută: nu e un fel din vocabularul canonic, deci pleca în document
    // un cuvânt pe care niciun motor nu-l cunoaște.
    kind: FEL_IMPLICIT, side: "A",
    area_m2: Math.round(arie * 100) / 100,
    poly: contur.map((p) => [{ a: r3(p[0]) }, { a: r3(p[1]) }] as VarfAfin),
  });
  return { doc: d, ales: { fel: "camera", i: plan.rooms.length - 1 } };
}

/** CE PORNEȘTE FIECARE UNEALTĂ DE ZID: felul, materialul, grosimea și
 *  înălțimea cu care se pune peretele.
 *
 *  Parapetul nu e altă unealtă decât peretele — e același gest cu alte
 *  implicite. De-aia stă aici, ca tabel, și nu ca o a doua funcție de desenat:
 *  o ramură paralelă ar fi trebuit să repete lipirea de vecini, axul scris,
 *  nodurile și poligonul, adică tot ce face un zid să fie un zid.
 *
 *  ÎNĂLȚIMEA PARAPETULUI e `H_PARAPET` (1,00 m), implicitul catalogului, nu cei
 *  0,90 m din STAS 6131. Norma cere 0,90 până la 15 m de la sol, 1,00 între 15
 *  și 40 și 1,10 peste — iar un ȘABLON nu știe la ce cotă ajunge etajul lui.
 *  Deci se pune valoarea care acoperă cazul obișnuit al unei bare de locuințe,
 *  se scrie în document (nu se lasă dedusă) și se poate schimba din panou. */
const IMPLICITELE_ZIDULUI: Record<string, {
  kind: string; material: string; grosime: number; inaltime?: number;
}> = {
  perete: { kind: "partition", material: "zidarie", grosime: 0.1 },
  parapet: { kind: "parapet", material: "zidarie", grosime: 0.15,
             inaltime: H_PARAPET },
};

export function adaugaPerete(
  doc: TronsonDoc, a: Pt, b: Pt, grosime?: number, bulge = 0,
  unealta: "perete" | "parapet" = "perete",
): { doc: TronsonDoc; ales: Ales } {
  const d = clona(doc);
  const plan = d.plan!;
  plan.walls = plan.walls ?? [];
  const fel = IMPLICITELE_ZIDULUI[unealta] ?? IMPLICITELE_ZIDULUI.perete;
  const gros = grosime ?? fel.grosime;
  // AXUL SE SCRIE de la început: un perete desenat aici e un perete de graf, iar
  // nodurile lui trebuie să se lipească de ale vecinilor din prima.
  plan.walls.push({
    id: idNou(plan.walls.map((w) => w.id)),
    kind: fel.kind, material: fel.material, thickness_m: gros,
    ...(fel.inaltime != null ? { height_m: fel.inaltime } : {}),
    axis: [[a[0], a[1]], [b[0], b[1]]],
    ...(Math.abs(bulge) > 1e-9 ? { bulge } : {}),
    poly: poligonDinAx(a, b, gros, bulge),
  });
  return { doc: d, ales: { fel: "perete", i: plan.walls.length - 1 } };
}

/** E DE BETON zidul `i` — adică unul în care nu se taie o ușă din editor?
 *
 *  `material` e singura distincție care se propagă în jos, la structură și la
 *  parcaj (v. `doc::PereteDoc`): un perete de beton e PORTANT. Un gol tăiat în
 *  el — ușă sau fereastră, e totuna pentru zid — nu e o decizie de
 *  arhitectură, e una de structură: cere buiandrug și
 *  bulbi la capete, și mută rigiditatea nivelului. Un editor de module care îl
 *  lasă să se întâmple dintr-un click scrie un plan pe care structura îl
 *  refuză mai târziu, la alt om și după altă muncă.
 *
 *  SE ÎNTREABĂ TOT SANDVIȘUL, nu foaia de sub cursor. Fațada catalogului e
 *  scrisă în două rânduri — o foaie de rezistență de 30 cm și una de zidărie de
 *  15 lipită pe ea (v. `lipiteleLui`) —, iar cursorul căzut pe zidărie găsește
 *  zidăria. Golul ar trece însă prin amândouă, deci întrebarea e dacă VREUNA
 *  din foi e de beton, nu dacă e de beton chiar foaia nimerită de mână. */
export function esteBeton(doc: TronsonDoc, i: number): boolean {
  const ziduri = doc.plan?.walls ?? [];
  if (!ziduri[i]) return false;
  return [...lipiteleLui(doc, i)].some((j) => ziduri[j]?.material === "beton");
}

/** UNDE AR STA UN GOL PUS AICI, și în ce zid.
 *
 *  Scoasă din `adaugaGol` ca să răspundă și înainte de apăsare: unealta desenează
 *  o fantomă sub cursor, iar fantoma trebuie să cadă EXACT unde va cădea golul.
 *  Calculate în două locuri, cele două poziții ar fi ajuns să difere, iar
 *  previzualizarea ar fi mințit — mai rău decât să nu existe.
 *
 *  Din același motiv întoarce și `beton`: refuzul trebuie să se vadă în fantomă
 *  ÎNAINTE de apăsare. O fantomă care spune «da» și o apăsare care nu face
 *  nimic e chiar editorul care înghite gesturi.
 */
export function pozitiaGolului(doc: TronsonDoc, p: Pt): {
  gazda: number; centru: Pt; rot: number; grosime: number; lungPeX: boolean;
  /** Zidul-gazdă duce încărcare, deci nu i se taie ușă — v. `esteBeton`. */
  beton: boolean;
} | null {
  const gazda = (doc.plan?.walls ?? []).findIndex((w) => {
    // UN ZID CURB e gazdă doar în grosimea lui: cutia lui de încadrare cuprinde
    // și camera din concavitate, iar un clic acolo i-ar fi pus golul în arc
    if (w.bulge && w.axis) {
      const [a, b] = w.axis as [Pt, Pt];
      const pe = pePeretele(a, b, w.bulge, p);
      return Math.hypot(p[0] - pe.punct[0], p[1] - pe.punct[1])
        <= w.thickness_m / 2 + 0.1;
    }
    const c = cadru(inelul(w.poly));
    return p[0] >= c.x - 0.1 && p[0] <= c.x + c.w + 0.1
        && p[1] >= c.y - 0.1 && p[1] <= c.y + c.h + 0.1;
  });
  if (gazda < 0) return null;
  const zid = doc.plan!.walls![gazda];
  if (zid.bulge && zid.axis) {
    // PE ARC: piciorul punctului pe axul curb, cu golul tangent la el. Capetele
    // opresc golul, ca fantoma să nu iasă din zid.
    const [a, b] = zid.axis as [Pt, Pt];
    const pe = pePeretele(a, b, zid.bulge, p);
    const rot = ((Math.round(pe.deg * 1000) / 1000) % 360 + 360) % 360;
    return {
      gazda,
      centru: [r3(pe.punct[0]), r3(pe.punct[1])] as Pt,
      rot,
      grosime: zid.thickness_m,
      lungPeX: Math.abs(Math.cos((rot * Math.PI) / 180)) >= Math.SQRT1_2,
      beton: esteBeton(doc, gazda),
    };
  }
  const o = osiaPeretelui(inelul(zid.poly));
  // pe MEDIANA peretelui, nu unde s-a apăsat: un gol stă în mijlocul grosimii
  const mediana = (o.fete[0] + o.fete[1]) / 2;
  // și în lungul lui, oprit între capete, ca fantoma să nu iasă din zid
  const jos = Math.min(...o.span);
  const sus = Math.max(...o.span);
  const dus = Math.min(sus, Math.max(jos, o.axa === 1 ? p[0] : p[1]));
  return {
    gazda,
    centru: (o.axa === 1 ? [dus, mediana] : [mediana, dus]) as Pt,
    rot: o.lungPeX ? 0 : 90,
    grosime: o.grosime,
    lungPeX: o.lungPeX,
     beton: esteBeton(doc, gazda),
  };
}

/** Un gol nou pe peretele de sub punctul dat. `null` când nu e niciun perete
 *  acolo: o ușă care nu stă într-un zid e o ușă desenată în aer, iar catalogul
 *  o refuză (`names wall …, which is not here`).
 *
 *  ȘI `null` CÂND E BETON SUB EL — v. `esteBeton`. Refuzul e al ORICĂRUI GOL,
 *  nu doar al ușii: o fereastră taie același perete portant, cu aceleași
 *  urmări — buiandrug, bulbi, rigiditatea nivelului —, iar o interdicție care
 *  ține numai pentru jumătate din unelte nu e o regulă, e o ciudățenie de
 *  ținut minte. Golurile care CHIAR stau în fațada de beton sunt cele venite
 *  din planșă, unde traveea a fost gândită cu ele; ele intră prin import, nu
 *  prin unealta asta, și nu se ating.
 *
 *  Refuzul e MUT aici, fiindcă nu el e locul unde se spune: omul îl află din
 *  fantoma roșie de sub cursor, dinainte să apese (v. `Panza`). */
/** E GOLUL ĂSTA RĂTĂCIT PRIN CAMERĂ, fără niciun zid sub el? */
export function golRatacit(
  doc: TronsonDoc, fel: "doors" | "windows", i: number,
): boolean {
  const g = doc.plan?.[fel]?.[i];
  if (!g) return false;
  const ziduri = doc.plan?.walls ?? [];
  // GAZDA SCRISĂ E ADEVĂRUL, nu geometria de sub mijlocul golului.
  //
  // Prima variantă întreba `pozitiaGolului` — și dădea «rătăcit» pentru ușile
  // BUNE din catalog: zidul e TĂIAT la fiecare gol, deci sub mijlocul unei uși
  // chiar nu e niciun zid, ci golul dintre două cioturi. Aleasă, orice ușă din
  // planșă ar fi sărit din loc. Ce spune dacă o ușă e a cuiva e `wall`.
  if (g.wall && ziduri.some((w) => w.id === g.wall)) return false;
  // Fără gazdă scrisă, se mai dă o șansă geometriei: o ușă care stă totuși pe
  // linia unui zid e la locul ei, doar nedeclarată.
  const c: Pt = [g.x.a, g.y.a];
  for (const w of ziduri) {
    const [a, b] = axaPeretelui(w);
    const vx = b[0] - a[0], vy = b[1] - a[1];
    const L2 = vx * vx + vy * vy;
    const t = L2 > 1e-12
      ? Math.max(0, Math.min(1, ((c[0] - a[0]) * vx + (c[1] - a[1]) * vy) / L2))
      : 0;
    const d = Math.hypot(c[0] - (a[0] + vx * t), c[1] - (a[1] + vy * t));
    if (d <= w.thickness_m / 2 + LIPIRE) return false;
  }
  return true;
}

/** LIPEȘTE UN GOL RĂTĂCIT DE CEL MAI APROPIAT ZID.
 *
 *  O ușă nu se poate PUNE în mijlocul camerei — `adaugaGol` refuză, fiindcă
 *  `pozitiaGolului` nu găsește gazdă. Dar una poate AJUNGE acolo pe alte
 *  drumuri: citită așa dintr-o planșă la import, sau lăsată în urmă de zidul pe
 *  care stătea, mutat de sub ea. Atunci desenul arată o ușă plutind, iar arcul
 *  ei nu taie nimic.
 *
 *  Se caută zidul cu axul cel mai apropiat de mijlocul golului, se coboară
 *  perpendiculara pe el, iar golul se reașază acolo — cu rotația și gazda
 *  zidului. De mutat pe lungul lui se mută mai departe ca orice gol
 *  (v. `schimbaGolul`, `mutaGol` din pânză).
 *
 *  Se cheamă la ALEGERE, nu la desenare: o reparație tăcută, făcută la fiecare
 *  randare, ar muta ușa sub mâna omului fără ca el să fi cerut ceva. Aleasă,
 *  el o vede sărind la perete și poate să anuleze. */
export function lipesteGolulDeZid(
  doc: TronsonDoc, fel: "doors" | "windows", i: number,
): TronsonDoc {
  const g = doc.plan?.[fel]?.[i];
  const ziduri = doc.plan?.walls ?? [];
  if (!g || !ziduri.length) return doc;
  const c: Pt = [g.x.a, g.y.a];
  if (!golRatacit(doc, fel, i)) return doc;          // e deja al unui zid

  let celMaiAproape: Pt | null = null;
  let dist = Infinity;
  for (const w of ziduri) {
    const [a, b] = axaPeretelui(w);
    const vx = b[0] - a[0], vy = b[1] - a[1];
    const L2 = vx * vx + vy * vy;
    // Piciorul perpendicularei, ținut între capete: un gol lipit de prelungirea
    // zidului ar fi ieșit din el.
    const t = L2 > 1e-12
      ? Math.max(0, Math.min(1, ((c[0] - a[0]) * vx + (c[1] - a[1]) * vy) / L2))
      : 0;
    const q: Pt = [a[0] + vx * t, a[1] + vy * t];
    const d = Math.hypot(c[0] - q[0], c[1] - q[1]);
    if (d < dist) { dist = d; celMaiAproape = q; }
  }
  if (!celMaiAproape) return doc;

  const unde = pozitiaGolului(doc, celMaiAproape);
  if (!unde) return doc;
  const d = clona(doc);
  const gol = d.plan![fel]![i];
  gol.x = { ...gol.x, a: r3(unde.centru[0]) };
  gol.y = { ...gol.y, a: r3(unde.centru[1]) };
  gol.rot_deg = unde.rot;
  gol.wall = d.plan!.walls![unde.gazda].id;
  return d;
}

/** MUTĂ UN GOL ORIUNDE ÎN PLAN, dar îl AȘAZĂ TOT PE UN ZID.
 *
 *  Cerut de arhitect (20.10.2026): «add the functionality for a door so that
 *  we can move it like we do wall portions like we can click and move it
 *  anywhere and also control it from the arrows. I want to be able to move it
 *  anywhere not just on walls but still have the restriction to not press
 *  click outside a wall and have it snap to walls when it gets close».
 *
 *  Până acum golul aluneca doar PE LUNGUL zidului lui — deci ca să-l muți pe
 *  alt perete trebuia să-l ștergi și să pui altul. Acum mâna îl poartă liber
 *  prin plan, iar el cade pe peretele cel mai apropiat.
 *
 *  DACĂ NU E NICIUN ZID ÎN RAZĂ, NU SE MUTĂ NIMIC. Asta e chiar cererea — «not
 *  to press click outside a wall» —, și e și singurul răspuns cinstit: un gol
 *  în mijlocul camerei n-ar tăia nimic, iar arcul lui s-ar desena în gol.
 *  Documentul se întoarce neatins, deci gestul nu intră în istoric.
 *
 *  ÎNĂUNTRUL ZIDULUI, nu peste capătul lui: `pozitiaGolului` ține golul între
 *  marginile peretelui, cu `MARGINE_LA_COLT` la fiecare capăt — un gol lipit
 *  de colț nu mai e un gol, e un colț lipsă. */
export function mutaGolulLiber(
  doc: TronsonDoc, fel: "doors" | "windows", i: number, spre: Pt,
  raza = 1.5,
): TronsonDoc {
  const g = doc.plan?.[fel]?.[i];
  if (!g) return doc;
  // ÎNTÂI SE CAUTĂ ZIDUL CEL MAI APROPIAT, pe axele lor — nu se cere ca mâna
  // să fie CHIAR în perete.
  //
  // `pozitiaGolului` singur nu ajunge: el așază golul pe zidul de SUB punct,
  // deci lipirea s-ar fi făcut doar cu degetul chiar pe zidărie, iar «snap to
  // walls when it gets close» n-ar fi existat. Căutarea e aceeași ca la un gol
  // rătăcit (v. `lipesteGolulDeZid`), ca să nu fie două socoteli.
  let peZid: Pt | null = null;
  let dist = Infinity;
  for (const w of doc.plan?.walls ?? []) {
    const [a, b] = axaPeretelui(w);
    const vx = b[0] - a[0], vy = b[1] - a[1];
    const L2 = vx * vx + vy * vy;
    const t = L2 > 1e-12
      ? Math.max(0, Math.min(1, ((spre[0] - a[0]) * vx + (spre[1] - a[1]) * vy) / L2))
      : 0;
    const q: Pt = [a[0] + vx * t, a[1] + vy * t];
    const dd = Math.hypot(spre[0] - q[0], spre[1] - q[1]);
    if (dd < dist) { dist = dd; peZid = q; }
  }
  // Fără niciun zid în rază nu se mută nimic — «not to press click outside a
  // wall». Altfel golul ar sări peste toată camera la cel mai apropiat perete.
  if (!peZid || dist > raza) return doc;
  const unde = pozitiaGolului(doc, peZid);
  if (!unde) return doc;
  const d = clona(doc);
  const gol = d.plan![fel]![i];
  if (Math.abs(gol.x.a - unde.centru[0]) < 1e-6
      && Math.abs(gol.y.a - unde.centru[1]) < 1e-6) return doc;
  gol.x = { ...gol.x, a: r3(unde.centru[0]) };
  gol.y = { ...gol.y, a: r3(unde.centru[1]) };
  gol.rot_deg = unde.rot;
  gol.wall = d.plan!.walls![unde.gazda].id;
  return d;
}

/** TRAGEREA UNUI CAPĂT DE GOL: capătul apucat pleacă, celălalt rămâne pe loc.
 *
 *  ==========================================================================
 *  Aceeași regulă ca la mobilă (`redimMobila`) și la zid, dar pe O SINGURĂ
 *  AXĂ: un gol stă ÎN zid, deci singura lui mărime care se poate trage pe
 *  planșă e lățimea, în lungul peretelui. Înălțimea și parapetul rămân în
 *  panou — ele nu se văd în plan, iar un mâner pentru o cifră care nu se
 *  vede ar fi un mâner care minte.
 *
 *  CELE DOUĂ SCRIERI MERG ÎMPREUNĂ. `schimbaGolul` ia lățimea și o DEPLASARE
 *  relativă; ca să stea celălalt capăt pe loc, centrul trebuie să se mute cu
 *  jumătate din cât a crescut lățimea. Deplasarea se socotește din lățimea
 *  CHIAR SCRISĂ, nu din cea cerută: lățimea are un prag de jos (lumina minimă
 *  plus tocuri), iar sub el cererea e ridicată tăcut — socotind din cerere,
 *  golul ar aluneca în zid exact când e strâns la minim.
 *
 *  Pasul e `PAS_MARIME`, cei 5 cm ai pânzei (19.10.2026).
 *  ========================================================================== */
export function redimGolul(
  doc: TronsonDoc, fel: "doors" | "windows", i: number,
  capat: -1 | 1, la: Pt,
): TronsonDoc {
  const g = doc.plan?.[fel]?.[i];
  if (!g) return doc;
  const w0 = g.w_m ?? 0;
  const rad = ((g.rot_deg ?? 0) * Math.PI) / 180;
  const cos = Math.cos(rad), sin = Math.sin(rad);
  // Punctul de sub deget, proiectat pe axa golului, față de mijlocul lui.
  const u = (la[0] - g.x.a) * cos + (la[1] - g.y.a) * sin;
  // LĂȚIMEA E DE LA CAPĂTUL FIX PÂNĂ LA DEGET, nu dublul distanței până la
  // mijloc: capătul opus stă la `−capat · w/2` de centru, deci lățimea cerută
  // e `capat·u + w/2`. Dublând distanța până la mijloc, golul creștea cu de
  // două ori cât s-a tras — și tocmai asta a prins proba, care ține CAPĂTUL
  // CELĂLALT, nu lățimea. Aceeași formă ca la mobilă (`redimMobila`).
  let cerut = Math.round((capat * u + w0 / 2) / PAS_MARIME) * PAS_MARIME;
  if (cerut <= 0) return doc;
  // NU DINCOLO DE ZIDUL LUI. Un gol mai lat decât peretele în care stă nu mai
  // e un gol: zidul rămâne desenat pe sub partea ieșită, fiindcă acolo chiar e
  // zid — «if I increase a window it should decrease the wall we extend into,
  // it shouldn't be visible underneath» (19.10.2026). Panoul avea un plafon
  // (4 m), tragerea n-avea niciunul.
  //
  // Se măsoară pe AXUL zidului gazdă, de la capătul care STĂ: celălalt n-are
  // voie să treacă de capătul axului, minus o palmă de zid care trebuie să
  // rămână — un gol lipit de colț desface nodul.
  const zid = doc.plan?.walls?.find((q) => q.id === g.wall);
  if (zid && !zid.bulge) {
    const ax = axaPeretelui(zid);
    const L = Math.hypot(ax[1][0] - ax[0][0], ax[1][1] - ax[0][1]);
    if (L > 1e-9) {
      // Capătul fix al golului, în lungul axului (0 … L).
      const t0 = ((g.x.a - ax[0][0]) * (ax[1][0] - ax[0][0])
                + (g.y.a - ax[0][1]) * (ax[1][1] - ax[0][1])) / L;
      const fix = t0 - (capat * w0) / 2;
      const loc = capat > 0 ? L - MARGINE_LA_COLT - fix : fix - MARGINE_LA_COLT;
      cerut = Math.min(cerut, Math.floor(loc / PAS_MARIME) * PAS_MARIME);
      if (cerut <= 0) return doc;
    }
  }
  // ÎNTÂI LĂȚIMEA, ca să se afle cât a intrat de fapt în document.
  const cuLatime = schimbaGolul(doc, fel, i, { latime: cerut });
  const scris = cuLatime.plan?.[fel]?.[i]?.w_m ?? w0;
  if (Math.abs(scris - w0) < 1e-9) return doc;
  return schimbaGolul(cuLatime, fel, i, { deplasare: (capat * (scris - w0)) / 2 });
}

export function adaugaGol(
  doc: TronsonDoc, fel: "doors" | "windows", p: Pt, latime: number,
  model?: ModelUsa,
): { doc: TronsonDoc; ales: Ales } | null {
  const unde = pozitiaGolului(doc, p);
  if (!unde) return null;
  if (unde.beton) return null;
  const d = clona(doc);
  const lista = (d.plan![fel] = d.plan![fel] ?? []);
  const r = (v: number) => Math.round(v * 1000) / 1000;
  lista.push({
    x: { a: r(unde.centru[0]) },
    y: { a: r(unde.centru[1]) },
    w_m: latime, rot_deg: unde.rot,
    wall: d.plan!.walls![unde.gazda].id,
    ...(fel === "windows" ? { parapet_m: 0.9 } : {}),
    // MODELUL SE SCRIE DOAR CÂND NU E IMPLICITUL: o ușă simplă rămâne fără
    // `model`, ca cele din catalog, iar documentul nu se umple de afirmații
    // despre ce oricum se presupune.
    ...(fel === "doors" && model && model !== "single" ? { model } : {}),
  });
  return { doc: d, ales: { fel: "gol", fel2: fel, i: lista.length - 1 } };
}

/** DESFACE MOBILAREA DEDUSĂ A UNEI CAMERE ÎN PIESE ADEVĂRATE.
 *
 *  PROBLEMA: mobila pe care o vezi într-un modul din catalog nu există.
 *
 *  Biblioteca desenează mobilarea după FELUL camerei — un pat cu noptiere în
 *  dormitor, o canapea cu fotolii în living — și o desenează ca pe niște linii
 *  într-un strat care nu prinde clickuri. Se putea muta numai ce pusese cineva
 *  de mână, adică nimic într-un modul venit din planșă, unde toată mobila e
 *  dedusă. Cătălin a apăsat pe o canapea, n-a mișcat nimic, și a întrebat pe
 *  bună dreptate «nu putem muta mobilier?».
 *
 *  SOLUȚIA E CEA CARE SE MAI FOLOSEȘTE O DATĂ AICI: se scrie la prima atingere.
 *  Axul unui perete se scrie tot așa (v. `mutaCapatUnic`) — deducția ține până
 *  pune cineva mâna pe lucru, iar din clipa aia documentul e cel care spune
 *  adevărul. Camera atinsă își varsă mobilarea în `plan.furniture`, exact acolo
 *  unde era desenată; desenul nu se schimbă cu nimic, dar de-acum e apucabilă.
 *
 *  SE DESFACE CAMERA ÎNTREAGĂ, nu piesa apăsată. Așezarea e o compoziție:
 *  canapeaua stă unde stă fiindcă acolo se termină fotoliile. Scoasă singură,
 *  restul ar rămâne dedus și s-ar reașeza sub ea la prima redimensionare a
 *  camerei — două mobilări peste aceeași cameră.
 *
 *  Întoarce și indicele piesei celei mai apropiate de punctul atins, ca gestul
 *  să continue pe ea fără un al doilea click. */
export function desfaMobilierul(
  doc: TronsonDoc, camera: number, langa?: Pt,
): { doc: TronsonDoc; ales: Ales } | null {
  const plan = deriva(doc);
  const c = plan.camere[camera];
  if (!c) return null;
  const k = cadruCamerei(c.inel as number[][]);
  if (!k) return null;
  const asezare = asezareaCamerei(c.kind, k.w, k.h);
  if (!asezare.length) return null;

  // Din cadrul camerei în planul modulului: aceeași transformare pe care o pune
  // desenul (`translate(cx cy) rotate(deg)`), ca piesele scrise să cadă exact
  // peste cele desenate.
  const rad = (k.deg * Math.PI) / 180;
  const co = Math.cos(rad);
  const si = Math.sin(rad);
  const d = clona(doc);
  const lista = (d.plan!.furniture = d.plan!.furniture ?? []);
  const primul = lista.length;
  for (const a of asezare) {
    lista.push({
      kind: a.kind,
      x: { a: r3(k.cx + a.x * co - a.y * si) },
      y: { a: r3(k.cy + a.x * si + a.y * co) },
      w_m: r3(a.w), h_m: r3(a.h),
      rot_deg: r3((((k.deg + a.rot) % 360) + 360) % 360),
    });
  }

  // Piesa de sub deget, ca gestul să continue pe ea.
  let ales = primul;
  if (langa) {
    let cea = Infinity;
    for (let j = primul; j < lista.length; j++) {
      const dd = Math.hypot(lista[j].x.a - langa[0], lista[j].y.a - langa[1]);
      if (dd < cea) { cea = dd; ales = j; }
    }
  }
  return { doc: d, ales: { fel: "mobila", i: ales } };
}

// ------------------------------------------------- nucleul, pe bucăți ---
//
// SCARA ȘI LIFTURILE SUNT DATE, NU DESEN, și asta e tot ce le lipsea ca să
// poată fi editate. Stau în `plan.core_parts` — o listă de `{kind, poly}`, cu
// poligoane simple, neafine — iar pe M3 sunt chiar trei: o scară și două
// puțuri. Erau desenate într-un strat surd, fără selecție, fără apucătoare și
// fără panou: „pare ceva desenat și nu se poate modifica" era exact așa.
//
// SE EDITEAZĂ PE LOC, NU SE MUTĂ ÎN MOBILIER. Tentația era să le trec în
// `plan.furniture`, unde există deja mutare, rotire, redimensionare și
// ștergere — dar mobilierul e ADNOTARE: motoarele nu-l citesc. `core_parts` îl
// citește `from_tronsoane.py` ca să pună scara adevărată în clădirea generată,
// deci un lift devenit mobilier ar dispărea din tot ce se calculează, iar
// modulul ar rămâne cu un gol prin care nu urcă nimeni. Deci rămân unde sunt și
// capătă gesturile acolo — mutare, ștergere, dublare, felul și mărimile scrise
// în panou. MĂRIMILE NU SE TRAG: un puț de lift are 1,60 m fiindcă atâta are
// cabina, iar o scară are treapta calculată; v. nota de la `schimbaMobila`.
//
// CE LE ȚINE ÎN FRÂU rămâne catalogul: o parte trebuie să stea în `core_rect`
// (lărgit cu o grosime de perete) și două părți n-au voie să se calce. Editorul
// nu împiedică nimic din toate astea — le arată la publicare, prin lista de
// probleme, unde se văd cu numele lor.

/** Dreptunghiul unei părți de nucleu: colțul din stânga-sus și mărimile. */
export function cadrulPartii(poly: readonly number[][]) {
  const xs = poly.map((q) => q[0]);
  const ys = poly.map((q) => q[1]);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}

const dreptunghi = (x: number, y: number, w: number, h: number): number[][] =>
  [[r3(x), r3(y)], [r3(x + w), r3(y)], [r3(x + w), r3(y + h)], [r3(x), r3(y + h)]];

/** The pieces the switch «Parte a nucleului» takes, as the palette names them. */
const PIESE_DE_NUCLEU: readonly string[] = ["lift", "ghena", "scara", "scaraU"];

/** A piece's box on the plan axes, as placed: a stair's from its flights
 *  (what the drawing shows), anything else from its measures and rotation. */
function cutiaPentruNucleu(f: PiesaDoc) {
  const c = f.scara?.rampe?.length ? cutiaScarii(f.scara) : null;
  if (c) return { x0: c.cx - c.w / 2, x1: c.cx + c.w / 2,
                  y0: c.cy - c.h / 2, y1: c.cy + c.h / 2 };
  return cutiaPiesei(f);
}

/** MAKES FURNITURE PIECE `i` COUNT, where it stands (OB-39).
 *
 *  On a module with a core the catalogue and the building read only
 *  `core_parts`, so a lift, chute or stair has to be a part to count. When a
 *  part of the piece's kind already lies under more than half of the piece's
 *  box, the piece is that part's copy (the catalogue templates carry both):
 *  it is removed and the part chosen, so one lift stays one record. Otherwise
 *  the piece becomes a part, its box, since parts carry no rotation; a U
 *  stair is a `scara` part, and a stair keeps its flights, one drawn from its
 *  size getting them first. A module without a core is returned untouched:
 *  there the catalogue counts lift and stair rooms, not parts. */
export function parteDinPiesa(
  doc: TronsonDoc, i: number,
): { doc: TronsonDoc; ales: Ales } {
  const f0 = doc.plan?.furniture?.[i];
  if (!f0 || !doc.core || !PIESE_DE_NUCLEU.includes(f0.kind)) {
    return { doc, ales: f0 ? { fel: "mobila", i } : null };
  }
  const src = promoveazaScara(doc, i, doc.storey_height_m ?? H_NIVEL);
  const f = src.plan!.furniture![i];
  const b = cutiaPentruNucleu(f);
  const kind = f.kind === "scaraU" ? "scara" : f.kind;
  const sub = (doc.plan!.core_parts ?? []).findIndex((p) => {
    if (p.kind !== kind) return false;
    const c = cadrulPartii(p.poly);
    const du = Math.min(b.x1, c.x + c.w) - Math.max(b.x0, c.x);
    const dv = Math.min(b.y1, c.y + c.h) - Math.max(b.y0, c.y);
    return du > 0 && dv > 0 && du * dv > 0.5 * (b.x1 - b.x0) * (b.y1 - b.y0);
  });
  // a copy lying on a part that has no flights hands its own over, with their
  // box: the switch never drops a stair's flights
  const preia = sub >= 0 && !!f.scara && !doc.plan!.core_parts![sub].scara;
  const d = clona(sub >= 0 && !preia ? doc : src);
  const plan = d.plan!;
  plan.furniture!.splice(i, 1);
  legaturiFaraPiesa(plan, i);
  if (preia) {
    plan.core_parts![sub] = { ...plan.core_parts![sub],
                              poly: dreptunghi(b.x0, b.y0, b.x1 - b.x0, b.y1 - b.y0),
                              scara: clona(f.scara!) };
  }
  if (sub >= 0) return { doc: d, ales: { fel: "nucleuParte", i: sub } };
  const parti = (plan.core_parts = plan.core_parts ?? []);
  parti.push({
    kind, poly: dreptunghi(b.x0, b.y0, b.x1 - b.x0, b.y1 - b.y0),
    ...(f.scara ? { scara: clona(f.scara) } : {}),
  });
  return { doc: d, ales: { fel: "nucleuParte", i: parti.length - 1 } };
}

/** TURNS CORE PART `i` BACK INTO A FURNITURE PIECE, at its box's centre with
 *  its width and depth. Parts carry no rotation, so the piece comes back
 *  axis-aligned; a stair keeps its flights and goes on drawing from them. */
export function piesaDinParte(
  doc: TronsonDoc, i: number,
): { doc: TronsonDoc; ales: Ales } {
  const p = doc.plan?.core_parts?.[i];
  if (!p || !["lift", "ghena", "scara"].includes(p.kind)) {
    return { doc, ales: p ? { fel: "nucleuParte", i } : null };
  }
  const c = cadrulPartii(p.poly);
  const d = clona(doc);
  const plan = d.plan!;
  plan.core_parts!.splice(i, 1);
  const lista = (plan.furniture = plan.furniture ?? []);
  lista.push({
    kind: p.kind === "scara" && p.scara?.rampe?.length
      ? felulScarii(p.scara.rampe) : p.kind,
    x: { a: r3(c.x + c.w / 2) }, y: { a: r3(c.y + c.h / 2) },
    w_m: r3(c.w), h_m: r3(c.h), rot_deg: 0,
    ...(p.scara ? { scara: clona(p.scara) } : {}),
  });
  return { doc: d, ales: { fel: "mobila", i: lista.length - 1 } };
}

/** Mută o parte a nucleului — o scară, un puț de lift. */
export function mutaParteaNucleului(
  doc: TronsonDoc, i: number, dep: Pt,
): TronsonDoc {
  const parte = doc.plan?.core_parts?.[i];
  if (!parte || (!dep[0] && !dep[1])) return doc;
  const d = clona(doc);
  d.plan!.core_parts![i].poly = parte.poly.map(
    (q) => [r3(q[0] + dep[0]), r3(q[1] + dep[1])]);
  // a stair part's flights move with it
  const sc = d.plan!.core_parts![i].scara;
  if (sc) duScara(sc, dep);
  return d;
}

/** Schimbă mărimile sau felul unei părți.
 *
 *  Mărimile se scriu ținând COLȚUL din stânga-sus: o parte care s-ar umfla din
 *  centru ar intra în vecina ei, iar catalogul refuză două părți care se calcă. */
export function schimbaParteaNucleului(
  doc: TronsonDoc, i: number,
  ce: { kind?: string; w?: number; h?: number },
): TronsonDoc {
  const parte = doc.plan?.core_parts?.[i];
  if (!parte) return doc;
  const d = clona(doc);
  const p = d.plan!.core_parts![i];
  if (ce.kind != null) p.kind = ce.kind;
  if (ce.w != null || ce.h != null) {
    const c = cadrulPartii(parte.poly);
    p.poly = dreptunghi(c.x, c.y,
                        Math.max(0.3, ce.w ?? c.w), Math.max(0.3, ce.h ?? c.h));
  }
  return d;
}

/** CE SE CROIEȘTE CU MÂNA, și ce are măsuri date. Pe FEL, nu pe fel de obiect:
 *  aceleași nume se folosesc și pentru părțile nucleului (`core_parts`), și
 *  pentru piesele din `furniture`, iar întrebarea e aceeași în amândouă.
 *
 *  Scările și puțul de lift NU: «lifturile au dimensiuni fixe, așa vin din
 *  fabrică, și scările sunt calculate cu anumite dimensiuni; le putem ajusta
 *  manual, dar niciodată automat din cauza mutării unui alt element». Un lift
 *  de 1,57 m ieșit din mână nu e o cabină care există, deci măsurile lor se
 *  SCRIU în panou, unde cifra se vede înainte să intre în document (v. nota de
 *  la `schimbaMobila`). `scaraU` intră aici lângă `scara`: e tot o scară, cu
 *  treapta calculată — lipsa ei din listă ar fi lăsat-o trasă de mână.
 *
 *  Restul — ghena, sasul, holul, bazinul, boxele — se croiesc pe loc, fiindcă
 *  n-au catalog: o ghenă e cât o cer tuburile care urcă prin ea, nu cât o dă
 *  un furnizor. De-aia a fost cerută trasă de colțuri. */
const PARTI_CU_MASURI_DATE: readonly string[] = ["scara", "scaraU", "lift"];
export const parteaSeCroieste = (kind: string): boolean =>
  !PARTI_CU_MASURI_DATE.includes(kind);

/** CROIEȘTE O PARTE A NUCLEULUI, trasă de un colț.
 *
 *  Muchia OPUSĂ celei apucate rămâne pe loc — aceeași regulă ca la cutie și la
 *  cameră (v. `Apucare`). Scrisul din panou ține colțul din stânga-sus
 *  (`schimbaParteaNucleului`); aici ține colțul de care NU tragi, fiindcă ce
 *  vede omul e colțul din mână, iar celălalt trebuie să stea.
 *
 *  CUTIA ÎL CUPRINDE, ȘI PRIN `redimNucleul`, nu scriind `core_rect` de-a
 *  dreptul: catalogul cere partea în cutie, iar cutia lărgită își duce și
 *  camerele din jur. Scris aici, `core_rect` ar fi crescut peste o cameră care
 *  n-a aflat, și planul ar fi ieșit cu nucleul călcând living-ul.
 *
 *  Numai părțile fără catalog — v. `parteaSeCroieste`. Chemat pe o scară sau pe
 *  un lift întoarce documentul neatins: altfel panoul ar fi singura cale spre
 *  măsurile lor, dar mâna le-ar fi putut ocoli. */
export function redimParteaNucleului(
  doc: TronsonDoc, i: number, apucare: Apucare, la: Pt,
): TronsonDoc {
  const parte = doc.plan?.core_parts?.[i];
  if (!parte || !parteaSeCroieste(parte.kind)) return doc;
  const c = cadrulPartii(parte.poly);
  // 30 cm, cât și la măsurile scrise: sub atât n-ar fi un spațiu, ar fi o dungă.
  const MIC = 0.3;
  const [x0, y0, x1, y1] = [c.x, c.y, c.x + c.w, c.y + c.h];
  const nouX = apucare.sx < 0 ? Math.min(la[0], x1 - MIC)
                              : Math.max(la[0], x0 + MIC);
  const nouY = apucare.sy < 0 ? Math.min(la[1], y1 - MIC)
                              : Math.max(la[1], y0 + MIC);
  const nx0 = apucare.sx < 0 ? r3(nouX) : x0;
  const nx1 = apucare.sx > 0 ? r3(nouX) : x1;
  const ny0 = apucare.sy < 0 ? r3(nouY) : y0;
  const ny1 = apucare.sy > 0 ? r3(nouY) : y1;
  if (nx0 === x0 && nx1 === x1 && ny0 === y0 && ny1 === y1) return doc;

  let d = clona(doc);
  d.plan!.core_parts![i].poly = dreptunghi(nx0, ny0, nx1 - nx0, ny1 - ny0);

  // CUTIA, lărgită numai pe laturile chiar depășite, fiecare prin gestul ei.
  const rect = d.plan?.core_rect;
  if (rect?.length === 4) {
    const [bx0, by0, bx1, by1] = rect.map(Number);
    if (nx0 < bx0) d = redimNucleul(d, { sx: -1, sy: 0 }, [nx0, 0]);
    if (nx1 > bx1) d = redimNucleul(d, { sx: 1, sy: 0 }, [nx1, 0]);
    if (ny0 < by0) d = redimNucleul(d, { sx: 0, sy: -1 }, [0, ny0]);
    if (ny1 > by1) d = redimNucleul(d, { sx: 0, sy: 1 }, [0, ny1]);
  }
  return d;
}

/** CE S-A APUCAT DINTR-UN DREPTUNGHI: un colț sau o latură.
 *
 *  Semnele spun ce muchie pleacă după mână: `-1` cea de jos a axei, `+1` cea de
 *  sus, `0` niciuna. Un colț are amândouă semnele nenule (`{sx: 1, sy: 1}` e
 *  colțul dreapta-jos al desenului), o latură are unul singur (`{sx: 1, sy: 0}`
 *  e latura din dreapta). Muchia OPUSĂ celei apucate rămâne mereu pe loc — asta
 *  e toată regula redimensionării.
 *
 *  Se trag numai CUTIA NUCLEULUI, camera și zidul. Piesele — mobilă, aparate,
 *  scara și puțurile de lift — nu se mai redimensionează din mână deloc; v.
 *  nota de la `schimbaMobila`. */
export type Apucare = { sx: -1 | 0 | 1; sy: -1 | 0 | 1 };

/** Cele patru colțuri, în ordinea vârfurilor dreptunghiului: 0 stânga-sus,
 *  1 dreapta-sus, 2 dreapta-jos, 3 stânga-jos. */
export const COLTURI: readonly Apucare[] = [
  { sx: -1, sy: -1 }, { sx: 1, sy: -1 }, { sx: 1, sy: 1 }, { sx: -1, sy: 1 },
];

/** Cele patru laturi: sus, dreapta, jos, stânga. */
export const LATURI: readonly Apucare[] = [
  { sx: 0, sy: -1 }, { sx: 1, sy: 0 }, { sx: 0, sy: 1 }, { sx: -1, sy: 0 },
];

/** Mută CUTIA nucleului. Numai cutia.
 *
 *  ÎNTÂI DUCEA ȘI BUCĂȚILE, fiindcă sunt conținutul ei — și fiindcă catalogul
 *  cere o parte să stea în cutie. Dar legate așa, scara, lifturile și cutia
 *  erau un singur bloc care se mișca dintr-o bucată: «e conectat la scări și la
 *  lifturi; ar trebui să fie separat, ca să putem șterge/muta camera și
 *  obiectele separat».
 *
 *  Deci sunt separate. Cutia spune cât loc ține nucleul; scara și puțurile sunt
 *  obiecte în el, fiecare cu mutarea, mărimea și ștergerea lui. Cine le duce
 *  unele fără altele va afla la publicare — catalogul cere partea în cutie, și o
 *  spune pe nume —, iar asta e mai bine decât un bloc pe care nu-l poți desface. */
export function mutaNucleul(doc: TronsonDoc, dep: Pt): TronsonDoc {
  const rect = doc.plan?.core_rect;
  if (!rect || rect.length !== 4 || (!dep[0] && !dep[1])) return doc;
  const d = clona(doc);
  d.plan!.core_rect = [r3(rect[0] + dep[0]), r3(rect[1] + dep[1]),
                       r3(rect[2] + dep[0]), r3(rect[3] + dep[1])];
  return d;
}

// ----------------------------------------------------------- întoarcerile ---
//
// CELE PATRU ÎNTOARCERI — un sfert de tur într-o parte, un sfert în cealaltă,
// oglindirea pe orizontală, oglindirea pe verticală — SUNT ALE ORICĂRUI OBIECT
// ALES, și stau toate în bara lui.
//
// CE SE APĂRĂ E REGULA, nu comenzile: cine a învățat că o piesă de mobilier se
// întoarce din bara care-i apare peste ea caută același lucru pe un zid, pe o
// ușă, pe scară. O comandă găsită o dată în bară, o dată în panou și o dată
// nicăieri nu-l învață unde e, îl învață că «uneori se poate».
//
// DECI BUTOANELE SUNT MEREU ACOLO, în aceeași ordine, și pe obiectele pe care
// întoarcerea n-are ce face sunt STINSE, cu motivul scris în tooltip. Un buton
// stins care spune de ce e mai bun decât un buton lipsă: el răspunde la
// întrebarea pusă («se poate roti camera?»), pe când lipsa lui o lasă
// deschisă. Ce poate fiecare fel spune `intoarcerile`, dintr-un loc — altfel
// bara, tastele și panoul ar fi ajuns să răspundă altfel la aceeași întrebare.

/** Cutia nucleului, ca ax-aliniată: colțul mic și cel mare. */
const cutiaNucleului = (rect: readonly number[]): { c: Pt; w: number; h: number } => ({
  c: [(rect[0] + rect[2]) / 2, (rect[1] + rect[3]) / 2],
  w: Math.abs(rect[2] - rect[0]),
  h: Math.abs(rect[3] - rect[1]),
});

/** Un sfert de tur? Cutia nucleului și părțile din ea se rotesc DOAR așa.
 *
 *  Nu din comoditate: `core_rect` e un dreptunghi cu laturile pe axe, iar o
 *  parte se scrie tot ca dreptunghi (v. `cadrulPartii`, care citește cutia
 *  minimă). Rotite cu 15°, amândouă ar fi citite mai departe ca dreptunghiuri
 *  ax-aliniate — adică s-ar UMFLA la fiecare apăsare, fără ca nimeni să fi
 *  cerut o scară mai mare. Un sfert de tur le duce exact în ele însele. */
const sfertDeTur = (grade: number) => in360(grade) % 90 === 0 && in360(grade) % 180 !== 0;

/** ROTEȘTE NUCLEUL — cutia și părțile din ea, în jurul centrului cutiei.
 *
 *  Cutia își schimbă laturile între ele, iar scara și puțurile se rotesc cu ea:
 *  un nucleu rotit e același nucleu așezat de-a curmezișul, nu o cutie întoarsă
 *  peste niște piese rămase cum erau.
 *
 *  POATE IEȘI DIN MODUL, și n-o împiedicăm aici: o cutie de 2,4 × 5,8 m
 *  întoarsă nu mai încape în banda unui tronson îngust. Catalogul o spune pe
 *  nume la publicare (partea trebuie să stea în `core_rect`, iar nucleul în
 *  modul), și acolo e locul — v. nota de deasupra lui `cadrulPartii`. */
export function rotesteNucleul(doc: TronsonDoc, grade: number): TronsonDoc {
  const rect = doc.plan?.core_rect;
  if (!rect || rect.length !== 4 || !sfertDeTur(grade)) return doc;
  const { c, w, h } = cutiaNucleului(rect);
  const rot = rotitorul(c, grade);
  const d = clona(doc);
  d.plan!.core_rect = [r3(c[0] - h / 2), r3(c[1] - w / 2),
                       r3(c[0] + h / 2), r3(c[1] + w / 2)];
  d.plan!.core_parts = (d.plan!.core_parts ?? []).map((p) => ({
    ...p, poly: p.poly.map((q) => rot([q[0], q[1]])),
    ...(p.scara ? { scara: scaraPrin(p.scara, rot) } : {}),
  }));
  return d;
}

/** A stair part's flights and written landings through the same point map as
 *  its outline, so a turned or mirrored core keeps each stair on its box. */
function scaraPrin<S extends NonNullable<PiesaDoc["scara"]>>(sc: S, f: (q: Pt) => Pt): S {
  const q = (p: readonly [number, number]) => f([p[0], p[1]]) as [number, number];
  return { ...sc,
           rampe: sc.rampe.map((r) => ({ ...r, de: q(r.de), la: q(r.la) })),
           ...(sc.podeste ? { podeste: sc.podeste.map((x) => ({ ...x, contur: x.contur.map(q) })) } : {}) };
}

/** OGLINDEȘTE NUCLEUL: părțile lui, în jurul centrului cutiei.
 *
 *  CUTIA NU SE ATINGE — un dreptunghi ax-aliniat oglindit în jurul propriului
 *  centru e tot el. Ce se schimbă e ce e ÎN ea: scara trece de partea cealaltă
 *  a puțurilor, iar asta e chiar întrebarea pentru care cineva deschide
 *  nucleul. */
export function oglindesteNucleul(doc: TronsonDoc, axa: Axa): TronsonDoc {
  const rect = doc.plan?.core_rect;
  if (!rect || rect.length !== 4 || !doc.plan?.core_parts?.length) return doc;
  const ogl = oglinditorul(cutiaNucleului(rect).c, axa);
  const d = clona(doc);
  d.plan!.core_parts = d.plan!.core_parts!.map((p) => ({
    ...p, poly: p.poly.map((q) => ogl([q[0], q[1]])),
    ...(p.scara ? { scara: scaraPrin(p.scara, ogl) } : {}),
  }));
  return d;
}

/** ROTEȘTE O PARTE A NUCLEULUI în jurul centrului ei: un sfert de tur îi
 *  schimbă laturile între ele. Scara lată cât cutia devine scara adâncă cât
 *  cutia — v. `sfertDeTur` pentru de ce numai atât. */
export function rotesteParteaNucleului(
  doc: TronsonDoc, i: number, grade: number,
): TronsonDoc {
  const parte = doc.plan?.core_parts?.[i];
  if (!parte || !sfertDeTur(grade)) return doc;
  const c = cadrulPartii(parte.poly);
  const d = clona(doc);
  d.plan!.core_parts![i].poly = dreptunghi(
    c.x + (c.w - c.h) / 2, c.y + (c.h - c.w) / 2, c.h, c.w);
  // a stair part's flights turn with it, about the same centre
  const sc = d.plan!.core_parts![i].scara;
  if (sc) {
    const r = rotitorul([c.x + c.w / 2, c.y + c.h / 2], grade);
    for (const x of sc.rampe) {
      x.de = r(x.de) as [number, number];
      x.la = r(x.la) as [number, number];
    }
    for (const x of sc.podeste ?? []) {
      x.contur = x.contur.map((q) => r(q) as [number, number]);
    }
  }
  return d;
}

/** SEMNUL DE DESCHIDERE al unei uși, așa cum se VEDE acum: cel scris, sau cel
 *  dedus din camerele de lângă (v. `doc::spreCamera`). Se citește din planul
 *  derivat fiindcă acolo e singurul loc unde deducția a fost deja făcută. */
function semnulUsii(doc: TronsonDoc, i: number): 1 | -1 {
  const g = deriva(doc).goluri.find((x) => x.fel === "usa" && x.idx === i);
  if (!g?.spre) return 1;
  const ux = (g.b[0] - g.a[0]) / (g.latime || 1);
  const uy = (g.b[1] - g.a[1]) / (g.latime || 1);
  return (g.spre[0] - g.c[0]) * -uy + (g.spre[1] - g.c[1]) * ux < 0 ? -1 : 1;
}

/** MERGE GOLUL DE-A LUNGUL AXEI PE CARE SE OGLINDEȘTE? Un gol stă în zidul
 *  lui, deci are o singură direcție, iar oglinda ori i-o urmează, ori o taie —
 *  și de asta atârnă care din cele două jumătăți ale sensului se întoarce. */
const golulPeAxa = (deg: number, axa: Axa): boolean => {
  const rad = (deg * Math.PI) / 180;
  return axa === "h"
    ? Math.abs(Math.cos(rad)) >= Math.abs(Math.sin(rad))
    : Math.abs(Math.sin(rad)) > Math.abs(Math.cos(rad));
};

/** OGLINDEȘTE UN GOL — adică, pe o ușă, una din cele două jumătăți ale
 *  sensului ei, după CUM CADE OGLINDA peste zid.
 *
 *  Un gol nu se poate oglindi ca un obiect liber: el e tăiat în perete, iar
 *  perechea lui de simetrie e tot în perete. Ce se poate oglindi e felul în
 *  care se deschide foaia — și asta chiar sunt două oglinzi, nu una:
 *
 *    * de-a LUNGUL zidului — balamaua trece dintr-un toc în celălalt
 *      (`oglindesteUsa`);
 *    * de-a CURMEZIȘUL lui — foaia baleiază în cealaltă cameră
 *      (`intoarceUsa`).
 *
 *  Care e «H» și care e «V» se citește din direcția zidului, nu se alege: pe un
 *  zid orizontal, oglinda pe orizontală merge pe lungul lui, deci mută
 *  balamaua; pe unul vertical, aceeași apăsare o taie, deci întoarce sensul.
 *  Așa butonul face mereu ce arată desenul că face.
 *
 *  FEREASTRA NU ARE NICIUNA: n-are foaie cu balama scrisă în document, deci
 *  oglindită iese tot ea. V. `intoarcerile`, care stinge butonul cu motivul. */
export function oglindesteGolul(
  doc: TronsonDoc, fel: "doors" | "windows", i: number, axa: Axa,
): TronsonDoc {
  const g = doc.plan?.[fel]?.[i];
  if (!g || fel !== "doors") return doc;
  return golulPeAxa(g.rot_deg ?? 0, axa)
    ? oglindesteUsa(doc, i)
    : intoarceUsa(doc, i, semnulUsii(doc, i));
}

/** CE POATE FACE OBIECTUL ALES din cele patru întoarceri.
 *
 *  `deCeNu` e cheia de traducere a motivului, sau `null` când se poate; `nume`
 *  e comanda pe care apăsarea o face de fapt, când ea are un nume al ei (ușa);
 *  `aprins` e starea steagului pe care butonul l-ar bascula — pe o piesă
 *  aproape simetrică e singurul semn că e deja întoarsă. */
export type Intoarcere = {
  deCeNu: string | null;
  nume?: string;
  aprins?: boolean;
};

const NU_SE_POATE = (deCeNu: string): Intoarcere => ({ deCeNu });
const SE_POATE: Intoarcere = { deCeNu: null };

/** Oglindirile unei piese sau ale unui aparat: se pot mereu, iar steagul
 *  aprins e cel pe care apăsarea l-ar bascula (v. `mainaOglindita`). */
function oglindirileMainii(
  deg: number, oglX: boolean, oglY: boolean,
): { oglH: Intoarcere; oglV: Intoarcere } {
  const stare = (axa: Axa): Intoarcere => {
    const m = mainaOglindita(deg, oglX, oglY, axa);
    return { deCeNu: null, aprins: m.oglX !== oglX ? oglX : oglY };
  };
  return { oglH: stare("h"), oglV: stare("v") };
}

export function intoarcerile(doc: TronsonDoc, ales: Ales): {
  rotire: Intoarcere; oglH: Intoarcere; oglV: Intoarcere;
} {
  const plan = doc.plan;
  const toate = (m: Intoarcere) => ({ rotire: m, oglH: m, oglV: m });
  switch (ales?.fel) {
    case "mobila": {
      const f = plan?.furniture?.[ales.i];
      if (!f) return toate(NU_SE_POATE("editor.intoarceri.nimic"));
      return { rotire: SE_POATE,
               ...oglindirileMainii(f.rot_deg ?? 0, f.flip_x === true,
                                    f.flip_y === true) };
    }
    case "aparat": {
      const a = plan?.electrical?.[ales.i];
      if (!a) return toate(NU_SE_POATE("editor.intoarceri.nimic"));
      if (a.kind === "banda_led") return toate(NU_SE_POATE("editor.intoarceri.banda"));
      // A box's wall decides how it stands; only its row order can reverse.
      if (a.kind === "doza") {
        const doza = NU_SE_POATE("editor.intoarceri.doza");
        return { rotire: doza, oglV: doza,
                 oglH: { deCeNu: null, aprins: a.flip_x === true } };
      }
      // A footed symbol stands in the room, so it does not turn over; only a
      // switch has a hand to mirror. A dragged one still turns: nothing else
      // would bring it round to its new wall.
      const fel = aparatul(a.kind);
      if (fel?.picior) {
        return { rotire: SE_POATE, oglV: NU_SE_POATE("editor.intoarceri.simbol_pe_zid"),
                 oglH: fel.rol === "comanda"
                   ? { deCeNu: null, aprins: a.flip_x === true }
                   : NU_SE_POATE(fel.grupa === "Prize" ? "editor.intoarceri.priza_fara_mana"
                     : "editor.intoarceri.simetric") };
      }
      return { rotire: SE_POATE,
               ...oglindirileMainii(a.rot_deg ?? 0, a.flip_x === true,
                                    a.flip_y === true) };
    }
    case "perete":
      return { rotire: SE_POATE, oglH: SE_POATE, oglV: SE_POATE };
    case "gol": {
      const rotire = NU_SE_POATE("editor.intoarceri.gol_rotire");
      if (ales.fel2 !== "doors") {
        const f = NU_SE_POATE("editor.intoarceri.fereastra");
        return { rotire, oglH: f, oglV: f };
      }
      const deg = plan?.doors?.[ales.i]?.rot_deg ?? 0;
      const nume = (axa: Axa): Intoarcere => ({
        deCeNu: null,
        nume: golulPeAxa(deg, axa)
          ? "editor.oglindeste_balamaua" : "editor.intoarce_sensul",
      });
      return { rotire, oglH: nume("h"), oglV: nume("v") };
    }
    case "nucleu": {
      const are = (plan?.core_parts ?? []).length > 0;
      const ogl = are ? SE_POATE : NU_SE_POATE("editor.intoarceri.nucleu_gol");
      return { rotire: SE_POATE, oglH: ogl, oglV: ogl };
    }
    case "nucleuParte": {
      const p = NU_SE_POATE("editor.intoarceri.parte");
      return { rotire: SE_POATE, oglH: p, oglV: p };
    }
    // O PARTE DE SCARĂ ÎNTOARCE SCARA ÎNTREAGĂ — o rampă oglindită singură n-ar
    // mai fi legată de celelalte. Oglindirea se face pe GEOMETRIE, nu pe
    // steagurile `oglX`/`oglY` (v. `oglindesteScara`), deci se poate oricând.
    //
    // ROTIREA A FOST STINSĂ O ZI, cu motivul la vedere, fiindcă un unghi scris
    // pe piesă chiar n-ar fi făcut nimic. Cerută pe nume (21.10.2026), s-a
    // făcut tot pe rampe, ca oglindirea — deci se poate și ea.
    case "scaraParte":
      return { rotire: SE_POATE, oglH: SE_POATE, oglV: SE_POATE };
    case "camera":
      return toate(NU_SE_POATE("editor.intoarceri.camera"));
    case "anvelopa":
      return toate(NU_SE_POATE("editor.intoarceri.anvelopa"));
    default:
      return toate(NU_SE_POATE("editor.intoarceri.nimic"));
  }
}

/** ROTEȘTE CE E ALES, cu `grade` — o singură poartă pentru toate felurile, ca
 *  `mutaObiectul` pentru mutare. Ce nu se rotește rămâne neatins; `intoarcerile`
 *  spune dinainte care sunt alea și de ce. */
export function rotesteObiectul(
  doc: TronsonDoc, ales: Ales, grade: number,
): TronsonDoc {
  if (!ales || !grade) return doc;
  switch (ales.fel) {
    case "mobila": {
      // O SCARĂ SE ROTEȘTE PE GEOMETRIE, nu scriindu-i un unghi — v.
      // `rotesteScara`, și nota geamănă din `oglindesteObiectul`.
      if (doc.plan?.furniture?.[ales.i]?.scara?.rampe?.length) {
        return rotesteScara(doc, ales.i, grade);
      }
      // ANSAMBLUL SE ÎNVÂRTE ÎN JURUL LUI, nu fiecare piesă în jurul ei:
      // scaunele trebuie să rămână la masă. `rotesteGrupul` întoarce
      // documentul neatins pentru o piesă singură, deci ramura de dedesubt
      // rămâne calea obișnuită.
      const g = rotesteGrupul(doc, ales.i, grade);
      if (g !== doc) return g;
      return schimbaMobila(doc, ales.i,
        { rot: (doc.plan?.furniture?.[ales.i]?.rot_deg ?? 0) + grade });
    }
    case "aparat":
      return schimbaAparat(doc, ales.i,
        { rot: (doc.plan?.electrical?.[ales.i]?.rot_deg ?? 0) + grade });
    case "perete": return rotestePeretele(doc, ales.i, grade);
    case "nucleu": return rotesteNucleul(doc, grade);
    case "nucleuParte": return rotesteParteaNucleului(doc, ales.i, grade);
    default: return doc;
  }
}

/** OGLINDEȘTE CE E ALES, pe axa dată — cealaltă jumătate a porții de mai sus.
 *
 *  Unghiul unei piese se trimite mai departe DOAR dacă s-a schimbat: scris,
 *  `schimbaAparat` rupe legătura aparatului cu piesa care-l justifică (o rotire
 *  e o poziție nouă, v. acolo), iar o oglindire nu mută nimic — priza noptierei
 *  rămâne priza noptierei și după ce simbolul e întors. */
export function oglindesteObiectul(
  doc: TronsonDoc, ales: Ales, axa: Axa,
): TronsonDoc {
  if (!ales) return doc;
  switch (ales.fel) {
    case "mobila":
    case "aparat": {
      const mobila = ales.fel === "mobila";
      // O SCARĂ SE OGLINDEȘTE PE GEOMETRIE, nu pe steaguri. Desenul ei iese din
      // RAMPE, care stau în coordonate de plan, iar `oglX`/`oglY` nu le ating —
      // scara ar fi rămas neclintită, cu un steag pus degeaba pe piesă. (Tot
      // de-aia `rot_deg` e ținut 0 pe scări, v. `schimbaScaraParte`.)
      if (mobila && doc.plan?.furniture?.[ales.i]?.scara?.rampe?.length) {
        return oglindesteScara(doc, ales.i, axa);
      }
      // Ansamblul se oglindește întreg, din același motiv ca la rotire.
      if (mobila) {
        const g = oglindesteGrupul(doc, ales.i, axa);
        if (g !== doc) return g;
      }
      const o = mobila ? doc.plan?.furniture?.[ales.i]
        : doc.plan?.electrical?.[ales.i];
      if (!o) return doc;
      if (!mobila && o.kind === "banda_led") return doc;
      // A box has no hand to mirror, whatever its wall: the flag is its row order.
      if (!mobila && o.kind === "doza") {
        return axa === "h"
          ? schimbaAparat(doc, ales.i, { oglX: o.flip_x !== true }) : doc;
      }
      if (!mobila && aparatul(o.kind)?.picior) {
        return axa === "h" && aparatul(o.kind)?.rol === "comanda"
          ? schimbaAparat(doc, ales.i, { oglX: o.flip_x !== true }) : doc;
      }
      const deg = o.rot_deg ?? 0;
      const m = mainaOglindita(deg, o.flip_x === true, o.flip_y === true, axa);
      const ce = { oglX: m.oglX, oglY: m.oglY,
                   ...(m.rot === r3(in360(deg)) ? {} : { rot: m.rot }) };
      return mobila ? schimbaMobila(doc, ales.i, ce)
        : schimbaAparat(doc, ales.i, ce);
    }
    case "perete": return oglindestePeretele(doc, ales.i, axa);
    case "gol": return oglindesteGolul(doc, ales.fel2, ales.i, axa);
    case "nucleu": return oglindesteNucleul(doc, axa);
    default: return doc;
  }
}

// ------------------------------------------------------------------ axele ---
//
// AXELE SUNT LINIILE PE CARE STAU ZIDURILE, citite din desen.
//
// Cerute de Cătălin: «aș vrea să definim niște axe de care putem să tragem,
// astfel încât atunci când dăm clic pe ax și tragem de el să se modifice toți
// pereții / camerele din linia aia». Adică gestul pe care îl are orice planșă
// de trasare: A, B, C pe verticală, 1, 2, 3 pe orizontală.
//
// NU SE DECLARĂ, SE CITESC. Un câmp `axes` în document ar fi a doua părere
// despre unde stau zidurile, și s-ar despărți de prima la primul perete mutat.
// Axele sunt deci o VEDERE peste pereți: fiecare linie pe care se aliniază
// zidurile e un ax, iar tras, el mută tot ce stă pe ea — prin `mutaPeretele`,
// același drum ca tragerea unui zid, cu camerele și golurile care vin după.

/** Un ax al planșei: o linie pe care stau unul sau mai mulți pereți. */
export type Ax = {
  /** `0` — ax vertical, se mută pe x (literele A, B, C). `1` — orizontal (1, 2, 3).
   *
   *  PE UN AX OBLIC rămâne felul lui cel mai apropiat, ca tot ce citește doar
   *  «încotro bate» să meargă mai departe neschimbat; ce contează cu adevărat
   *  pentru el e `n`. */
  axa: 0 | 1;
  /** NORMALA UNITARĂ a axei — încotro se mută ea. Lipsă = ortogonală, adică
   *  `(1,0)` pentru `axa 0` și `(0,1)` pentru `axa 1`.
   *
   *  Pereții nu stau toți în cruce: o aripă rotită, un colț tăiat, o fațadă pe
   *  bisectoare. Până acum axa unui zid se hotăra din DREPTUNGHIUL LUI DE
   *  ÎNCADRARE — care latură e mai lungă —, iar pentru un zid la 30° cutia e
   *  aproape pătrată: felul ieșea la întâmplare, zidul cădea într-o grupă cu
   *  care n-avea nimic de-a face, și linia lui roșie nu se desena niciodată.
   *  Cerut 13.10.2026: «trebuie să avem axe pe diferite unghiuri… să putem
   *  trage de ele». */
  n?: Pt;
  /** Unde stă, pe normala lui: `p · n` e același pentru toate punctele axei.
   *  Pe o axă ortogonală e chiar `x` sau `y`, ca înainte. */
  valoare: number;
  /** Cât ține, pe cealaltă axă — cât să se deseneze, nu mai mult. */
  jos: number;
  sus: number;
  /** `jos`/`sus` vin dintr-o SCURTARE CERUTĂ (`plan.axes_len`), nu din pereți.
   *  V. `intindereaAxei`: fără ea, o axă ortogonală se desenează pe toată
   *  planșa, deci nu e totuna de unde îi vin capetele. */
  scurtat?: boolean;
  /** Numele de pe planșă: litere pe verticală, cifre pe orizontală. */
  nume: string;
  /** Pereții care stau pe el. */
  ziduri: number[];
};

/** Cât de aproape trebuie să fie axele a doi pereți ca să fie ACELAȘI ax. */
const LIPIRE_AX = 0.06;

/** AXELE UNUI MODUL, citite din pereții lui.
 *
 *  Se ia axul fiecărui perete și se strâng cele care cad pe aceeași linie. Un
 *  ax cu un singur perete scurt n-are ce căuta pe planșă — ar fi zgomot, iar
 *  Cătălin a cerut anume «să nu încurce lizibilitatea schemei» —, deci se
 *  păstrează doar liniile care chiar organizează desenul: cele cu mai mulți
 *  pereți, sau cu unul lung.
 *
 *  DOUĂ LINII LIPITE SUNT O SINGURĂ AXĂ. Fațada e scrisă în două foi — una de
 *  rezistență și una de zidărie lipită pe ea —, iar axele lor cad la 22,5 cm
 *  una de alta: pe M5 ieșeau A și B cu rost 0,000 între corpuri, adică două
 *  linii pentru un perete. Cerut de Stefan (17.09): «axul A și B nu e nevoie
 *  de ambele, pereții sunt lipiți, putem păstra doar unul». Se strâng deci
 *  grupele ale căror CORPURI se ating (v. `ROST_LIPIT`), iar linia rămasă e a
 *  foii care cântărește mai mult — lungime × grosime —, adică a celei de
 *  rezistență. Măsurat pe M5: 29 de axe devin 22.
 *
 *  Nu se strâng și cele doar APROPIATE: între C și D ale lui M5 sunt 30 cm de
 *  gol, iar acolo chiar sunt doi pereți cu ceva între ei. Pragul e atingerea,
 *  nu o distanță aleasă.
 *
 *  Numele urmează convenția de trasare: LITERE pe verticală, de la stânga la
 *  dreapta; CIFRE pe orizontală, de sus în jos. */
/** Unghiul normalei, rotunjit la zecime de grad — cheia unei direcții oblice. */
const unghiulNormalei = (n: Pt): number =>
  Math.round(((Math.atan2(n[1], n[0]) * 180) / Math.PI) * 10) / 10;

/** Normala unei axe, cu implicitul ortogonal. */
export const normalaAxei = (a: { axa: 0 | 1; n?: Pt }): Pt =>
  a.n ?? (a.axa === 0 ? [1, 0] : [0, 1]);

/** PUNCTUL DE PE AXĂ la parametrul `t` — inversa lui `peAxa`.
 *
 *  PE O AXĂ ORTOGONALĂ `t` E CHIAR `x` SAU `y`, fiindcă așa îl scrie `axele`:
 *  `jos`/`sus` ies acolo din `min`/`max` pe coordonata cealaltă. Socoteala cu
 *  normala dădea, pentru axele ORIZONTALE, `u = (-1, 0)` — adică `-x` —, deci
 *  apucătoarele capetelor cădeau oglindit față de linia lor, de partea
 *  cealaltă a originii. Pe desen se vedea ca o axă care nu se lasă scurtată:
 *  trăgeai de un pătrat aflat în altă parte decât linia (15.10.2026). */
export function punctulAxei(
  a: { axa: 0 | 1; n?: Pt; valoare: number }, t: number,
): Pt {
  if (!a.n) return a.axa === 0 ? [a.valoare, t] : [t, a.valoare];
  const u: Pt = [-a.n[1], a.n[0]];                   // de-a lungul axei
  return [a.n[0] * a.valoare + u[0] * t,
          a.n[1] * a.valoare + u[1] * t];
}

/** CÂT DE DEPARTE, DE-A LUNGUL AXEI, stă un punct — inversa lui `punctulAxei`. */
export function peAxa(a: { axa: 0 | 1; n?: Pt }, p: Pt): number {
  if (!a.n) return a.axa === 0 ? p[1] : p[0];
  return -p[0] * a.n[1] + p[1] * a.n[0];
}

/** Cele două capete ale liniei unei axe, pe întinderea ei. */
export function capeteleAxei(a: Ax): [Pt, Pt] {
  return [punctulAxei(a, a.jos), punctulAxei(a, a.sus)];
}

/** CÂT SE DESENEAZĂ dintr-o axă, în parametrul ei de-a lungul.
 *
 *  Cât o întind PEREȚII EI, sau cât s-a cerut la scurtare. Axele ortogonale se
 *  trăgeau dintr-o margine a planșei în cealaltă, ca o linie de trasare pe
 *  toată coala; pe un modul cu o aripă rotită asta ducea bula cu numele la
 *  colțul planșei, departe de zidurile despre care vorbește. Cerut
 *  (15.10.2026): «NOT BEFORE THE EDGE, NOT ON THE DRAWING. Right under where
 *  the wall ends».
 *
 *  Dintr-un loc, fiindcă LINIA, BULELE CU NUMELE ȘI APUCĂTOARELE trebuie să
 *  cadă pe aceleași două capete. Când linia se trăgea din cadrul planșei iar
 *  apucătoarele din `jos`/`sus`, trasul de un capăt scria `axes_len` fără ca
 *  linia să se clintească: «the axes still don't work» (15.10.2026). */
/** CUTIA A TOT CE SE DESENEAZĂ — ziduri, camere, anvelopă.
 *
 *  NU E `plan.cadru`. Pe TESTDESF cadrul se oprește la y=37,15, dar un zid
 *  urcă până la 39,6: o axă dusă până la marginea cadrului își lăsa bula cu
 *  numele chiar peste zid. Axele se trag până afară din TOT desenul. */
export function cutiaDesenului(
  plan: { pereti: { inel: Pt[] }[]; camere: { inel: Pt[] }[]; anvelopa: Pt[] },
): { x: number; y: number; w: number; h: number } {
  let x0 = Infinity; let y0 = Infinity; let x1 = -Infinity; let y1 = -Infinity;
  const ia = (inel: readonly Pt[]) => {
    for (const q of inel) {
      if (q[0] < x0) x0 = q[0];
      if (q[0] > x1) x1 = q[0];
      if (q[1] < y0) y0 = q[1];
      if (q[1] > y1) y1 = q[1];
    }
  };
  plan.pereti.forEach((w) => ia(w.inel));
  plan.camere.forEach((r) => ia(r.inel));
  ia(plan.anvelopa);
  if (!Number.isFinite(x0)) return { x: 0, y: 0, w: 0, h: 0 };
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

export function intindereaAxei(
  a: Ax, c: { x: number; y: number; w: number; h: number },
): [number, number] {
  if (a.scurtat) return [a.jos, a.sus];
  // UNDE TAIE LINIA AXEI CUTIA PLANȘEI. Pe cele ortogonale iese exact ce ieșea
  // înainte — dintr-o margine în cealaltă. Cele OBLICE se opreau însă la
  // zidurile lor, adică în mijlocul desenului, și-și lăsau bula cu numele
  // peste camere: pe un modul cu aripă rotită, douăzeci de litere căzute peste
  // plan. Duse și ele până la marginea planșei, numele ies afară, lângă
  // celelalte.
  // DIRECȚIA, LUATĂ CHIAR DIN `punctulAxei`, nu socotită a doua oară din
  // normală: pe axele ortogonale cele două convenții nu sunt aceeași, iar
  // `[-n[1], n[0]]` dădea `-x` pentru orizontale — tăierea ieșea pe dos
  // (`t = [-23.35, 0]`) și bulele cădeau în camere.
  const o = punctulAxei(a, 0);
  const q = punctulAxei(a, 1);
  const u: Pt = [q[0] - o[0], q[1] - o[1]];
  let t0 = -Infinity;
  let t1 = Infinity;
  const taie = (p0: number, d: number, min: number, max: number) => {
    if (Math.abs(d) < 1e-9) return;                  // paralelă cu marginea
    const ta = (min - p0) / d;
    const tb = (max - p0) / d;
    t0 = Math.max(t0, Math.min(ta, tb));
    t1 = Math.min(t1, Math.max(ta, tb));
  };
  taie(o[0], u[0], c.x, c.x + c.w);
  taie(o[1], u[1], c.y, c.y + c.h);
  if (!Number.isFinite(t0) || !Number.isFinite(t1) || !(t1 - t0 > 0.05)) {
    return [a.jos, a.sus];
  }
  return [t0, t1];
}

/** Cât de departe de ortogonal trebuie să bată un zid ca să-și ceară axa lui.
 *  Sub atât e o fațadă strâmbă din transcriere, nu o aripă rotită — iar ea se
 *  îndreaptă (v. `indreaptaPeretele`), nu-și capătă un ax propriu. */
const OBLIC_GRADE = 4;

/** Cât de aproape ca unghi sunt doi pereți ca să fie pe ACEEAȘI direcție. */
const LIPIRE_UNGHI = 3;

export function axele(doc: TronsonDoc): Ax[] {
  const ziduri = doc.plan?.walls ?? [];
  if (!ziduri.length) return [];
  /** Sub atâta, un perete singur nu face un ax. */
  const LUNG = 2.0;

  /** Grupa, cu ce-i trebuie strângerii: corpul ei și cât cântărește. */
  type Grupa = Ax & { corp: [number, number]; greutate: number };
  const grupe: Grupa[] = [];
  ziduri.forEach((w, j) => {
    // UN ZID CURB NU E PE NICIO AXĂ: axele sunt linii drepte, iar cutia unui
    // arc ar fi pus o axă prin mijlocul camerei din concavitate
    if (w.bulge) return;
    const ax = axaPeretelui(w);
    // ZIDUL OBLIC ÎȘI FACE GRUPA LUI, pe direcția lui.
    //
    // Pe drumul de jos, felul axei iese din dreptunghiul de încadrare — care
    // latură e mai lungă. Pentru un zid la 30° cutia e aproape pătrată, deci
    // felul ieșea la întâmplare și zidul se lipea de o grupă cu care n-are
    // nimic de-a face. Aici se strâng după UNGHI și după depărtarea pe normala
    // lor, adică după ce înseamnă chiar o axă de trasare.
    const grd = ((((Math.atan2(ax[1][1] - ax[0][1], ax[1][0] - ax[0][0])
                   * 180) / Math.PI) % 180) + 180) % 180;
    const dinAx = Math.min(Math.abs(grd), Math.abs(grd - 90), Math.abs(grd - 180));
    if (dinAx > OBLIC_GRADE) {
      const L = Math.hypot(ax[1][0] - ax[0][0], ax[1][1] - ax[0][1]) || 1;
      const u: Pt = [(ax[1][0] - ax[0][0]) / L, (ax[1][1] - ax[0][1]) / L];
      // normala, mereu de aceeași parte, ca două ziduri paralele desenate în
      // sensuri opuse să cadă în aceeași grupă
      let n: Pt = [u[1], -u[0]];
      if (n[0] < 0 || (Math.abs(n[0]) < 1e-9 && n[1] < 0)) n = [-n[0], -n[1]];
      const uu: Pt = [-n[1], n[0]];
      const mij: Pt = [(ax[0][0] + ax[1][0]) / 2, (ax[0][1] + ax[1][1]) / 2];
      const val = mij[0] * n[0] + mij[1] * n[1];
      const t0 = ax[0][0] * uu[0] + ax[0][1] * uu[1];
      const t1 = ax[1][0] * uu[0] + ax[1][1] * uu[1];
      const o2 = osiaPeretelui(inelul(w.poly));
      const g = grupe.find((x) => x.n
        && Math.abs(x.n[0] * n[1] - x.n[1] * n[0]) < Math.sin((LIPIRE_UNGHI * Math.PI) / 180)
        && x.n[0] * n[0] + x.n[1] * n[1] > 0
        && Math.abs(x.valoare - val) <= LIPIRE_AX);
      const jos2 = Math.min(t0, t1);
      const sus2 = Math.max(t0, t1);
      if (g) {
        g.jos = Math.min(g.jos, jos2);
        g.sus = Math.max(g.sus, sus2);
        g.greutate += L * o2.grosime;
        g.ziduri.push(j);
      } else {
        grupe.push({ axa: Math.abs(n[0]) >= Math.abs(n[1]) ? 0 : 1, n, valoare: val,
                     jos: jos2, sus: sus2, nume: "", ziduri: [j],
                     corp: [val, val], greutate: L * o2.grosime });
      }
      return;
    }
    const o = osiaPeretelui(inelul(w.poly));
    const valoare = (ax[0][o.axa] + ax[1][o.axa]) / 2;
    const alta = (1 - o.axa) as 0 | 1;
    const jos = Math.min(ax[0][alta], ax[1][alta]);
    const sus = Math.max(ax[0][alta], ax[1][alta]);
    const corp: [number, number] = [Math.min(...o.fete), Math.max(...o.fete)];
    const greutate = o.lungime * o.grosime;
    const gasit = grupe.find((g) => g.axa === o.axa
      && Math.abs(g.valoare - valoare) <= LIPIRE_AX);
    if (gasit) {
      gasit.jos = Math.min(gasit.jos, jos);
      gasit.sus = Math.max(gasit.sus, sus);
      gasit.corp = [Math.min(gasit.corp[0], corp[0]),
                    Math.max(gasit.corp[1], corp[1])];
      gasit.greutate += greutate;
      gasit.ziduri.push(j);
    } else {
      grupe.push({ axa: o.axa, valoare, jos, sus, nume: "", ziduri: [j],
                   corp, greutate });
    }
  });

  // GRUPELE LIPITE SE TOPESC ÎNTR-UNA. Se merge de la stânga la dreapta pe
  // fiecare direcție: vecina care atinge corpul celei dinainte intră în ea, iar
  // linia păstrată e a celei mai grele. Așa se strânge și un sandviș de trei
  // foi, dintr-o singură trecere.
  const stranse: Grupa[] = [];
  // GRUPELE OBLICE NU SE TOPESC cu nimic: ele s-au strâns deja după unghi,
  // iar «corpul» lor e un punct pe normală, nu o fâșie de fețe.
  for (const g of grupe) if (g.n) stranse.push(g);
  for (const axa of [0, 1] as const) {
    const ale = grupe.filter((g) => !g.n && g.axa === axa)
      .sort((a, b) => a.corp[0] - b.corp[0]);
    for (const g of ale) {
      const ultima = stranse[stranse.length - 1];
      if (ultima && !ultima.n && ultima.axa === axa
          && g.corp[0] - ultima.corp[1] <= ROST_LIPIT) {
        ultima.jos = Math.min(ultima.jos, g.jos);
        ultima.sus = Math.max(ultima.sus, g.sus);
        ultima.corp = [Math.min(ultima.corp[0], g.corp[0]),
                       Math.max(ultima.corp[1], g.corp[1])];
        ultima.ziduri.push(...g.ziduri);
        if (g.greutate > ultima.greutate) ultima.valoare = g.valoare;
        ultima.greutate += g.greutate;
        continue;
      }
      stranse.push(g);
    }
  }

  // HOTĂRÂRILE OMULUI, peste deducție — v. `plan.axes`.
  //
  // Se aplică AICI, între strângere și numerotare, fiindcă numele și cotele
  // dintre axe ies din mulțimea de mai jos, în ordine: o axă scoasă sau
  // adusă reface singură și literele, și cifrele, și distanțele. Nimic nu
  // ține minte că «axa C» a fost cândva a treia.
  const alese = doc.plan?.axes ?? [];
  const peLoc = (a: { axa: 0 | 1; valoare: number }, g: { axa: 0 | 1; valoare: number }) =>
    a.axa === g.axa && Math.abs(a.valoare - g.valoare) <= LIPIRE_AX;

  const deduse = stranse.filter((g) => g.ziduri.length > 1 || g.sus - g.jos >= LUNG);

  // ADUSE: pozițiile cerute pe care deducția nu le dă. Dacă acolo chiar e o
  // grupă (un zid prea scurt ca să-și merite axa), se ia ea, cu zidurile și
  // întinderea ei; altfel axa se trage peste tot cadrul, fiindcă nu se
  // sprijină pe nimic.
  const totul = stranse.length
    ? {
        jos0: Math.min(...stranse.map((g) => g.jos)),
        sus0: Math.max(...stranse.map((g) => g.sus)),
      }
    : { jos0: 0, sus0: 0 };
  const aduse: Grupa[] = [];
  for (const a of alese) {
    if (a.ascuns) continue;
    if (deduse.some((g) => peLoc(a, g))) continue;
    const slaba = stranse.find((g) => peLoc(a, g));
    if (slaba) { aduse.push(slaba); continue; }
    aduse.push({
      axa: a.axa, valoare: a.valoare, nume: "", ziduri: [],
      jos: totul.jos0, sus: totul.sus0,
      corp: [a.valoare, a.valoare], greutate: 0,
    });
  }

  const tari = [...deduse, ...aduse].filter(
    (g) => !alese.some((a) => a.ascuns && peLoc(a, g)));
  // LITERE pe verticală (de la stânga), CIFRE pe orizontală (de sus).
  //
  // AXELE OBLICE CONTINUĂ SERIA DE LITERE (cerut 13.10.2026). Fiecare direcție
  // oblică se numerotează pe rând, de-a lungul normalei ei, iar literele curg
  // mai departe de unde s-au oprit cele verticale: A, B, C pe verticală, apoi
  // D, E pe aripa rotită. Așa se citește dintr-o privire că D și E sunt tot
  // axe de trasare, nu alt fel de linie, și nu se ciocnesc cu cifrele.
  const litere = tari.filter((g) => !g.n && g.axa === 0)
    .sort((a, b) => a.valoare - b.valoare);
  const cifre = tari.filter((g) => !g.n && g.axa === 1)
    .sort((a, b) => a.valoare - b.valoare);
  // Direcțiile oblice, în ordinea unghiului lor, ca să fie mereu aceeași.
  const oblice = tari.filter((g) => !!g.n);
  const directii = [...new Set(oblice.map((g) => unghiulNormalei(g.n!)))]
    .sort((x, y) => x - y);
  const inSir: Grupa[] = [];
  for (const d of directii) {
    inSir.push(...oblice.filter((g) => unghiulNormalei(g.n!) === d)
      .sort((a, b) => a.valoare - b.valoare));
  }
  [...litere, ...inSir].forEach((g, k) => {
    g.nume = k < 26 ? String.fromCharCode(65 + k) : `A${k - 25}`;
  });
  cifre.forEach((g, k) => { g.nume = String(k + 1); });
  // SCURTAREA DE ASPECT, la sfârșit: după ce numele sunt date, fiindcă pe nume
  // se ține minte (v. `plan.axes_len`). Nu atinge nici `valoare`, nici
  // zidurile — axa rămâne aceeași linie, doar desenată mai scurt.
  const lungimi = new Map(
    (doc.plan?.axes_len ?? []).map((x) => [x.nume, x]));
  const cuLungime = (a: Ax): Ax => {
    const L = lungimi.get(a.nume);
    return L ? { ...a, jos: L.jos, sus: L.sus, scurtat: true } : a;
  };
  return [...litere, ...inSir, ...cifre].map(cuLungime);
}

/** SCOATE O AXĂ DIN TRASARE — zidurile ei rămân.
 *
 *  O axă ștearsă e o hotărâre de trasare, nu o ștergere de pereți: pe planșă
 *  nu se mai cotează pe ea, dar zidurile care o făceau sunt tot acolo. De-aia
 *  se scrie o abatere (`plan.axes`, `ascuns`), nu se atinge geometria.
 *
 *  Literele, cifrele și cotele dintre axe se refac singure la următorul desen
 *  (v. `axele`). */
export function stergeAxul(doc: TronsonDoc, ax: Ax): TronsonDoc {
  const d = clona(doc);
  if (!d.plan) return doc;
  const altele = (d.plan.axes ?? []).filter(
    (a) => !(a.axa === ax.axa && Math.abs(a.valoare - ax.valoare) <= LIPIRE_AX));
  d.plan.axes = [...altele, { axa: ax.axa, valoare: r3(ax.valoare), ascuns: true }];
  return d;
}

/** ADUCE O AXĂ pe linia unui perete.
 *
 *  Pe axul lui, nu pe fața lui: axa de trasare trece prin mijlocul zidului, ca
 *  toate celelalte. Dacă acolo era una stinsă, se reaprinde în loc să se scrie
 *  a doua abatere pe aceeași poziție. */
export function adaugaAxul(doc: TronsonDoc, perete: number): TronsonDoc {
  const w = doc.plan?.walls?.[perete];
  if (!w || w.bulge) return doc;
  const o = osiaPeretelui(inelul(w.poly));
  const ax = axaPeretelui(w);
  const valoare = r3((ax[0][o.axa] + ax[1][o.axa]) / 2);
  const d = clona(doc);
  if (!d.plan) return doc;
  const altele = (d.plan.axes ?? []).filter(
    (a) => !(a.axa === o.axa && Math.abs(a.valoare - valoare) <= LIPIRE_AX));
  d.plan.axes = [...altele, { axa: o.axa, valoare }];
  return d;
}

/** Are peretele ăsta deja o axă? Ecranul o întreabă ca să nu ofere «adaugă
 *  axă» acolo unde una există deja. */
export function areAxa(doc: TronsonDoc, perete: number): boolean {
  const w = doc.plan?.walls?.[perete];
  if (!w || w.bulge) return false;
  const o = osiaPeretelui(inelul(w.poly));
  const ax = axaPeretelui(w);
  const valoare = (ax[0][o.axa] + ax[1][o.axa]) / 2;
  return axele(doc).some((g) => g.axa === o.axa
    && Math.abs(g.valoare - valoare) <= LIPIRE_AX);
}

/** TRAGE UN AX: mută tot ce stă pe linia lui.
 *
 *  Tot axul pleacă PRINTR-O SINGURĂ chemare a lui `mutaPeretele`, cu grupul
 *  spus pe față — deci cu runurile tuturor pereților lui, cu camerele de pe
 *  amândouă fețele, cu golurile și cu prizele.
 *
 *  O CHEMARE, NU CÂTE UNA PE PERETE, și asta e miezul. Fiecare chemare mută
 *  și capetele vecinilor prinși în noduri de zidul ei (v. `duCapat`): un ax
 *  cu două foi — fațada de rezistență și zidăria lipită pe ea, de-acum o
 *  singură axă (v. `axele`) — ar fi tras de două ori capătul aceluiași perete
 *  perpendicular, iar acela s-ar fi lungit cu 2δ. Aceeași socoteală o făcea și
 *  o linie cu mai multe bucăți pentru camera sprijinită pe două dintre ele.
 *  Cu grupul strâns dinainte, fețele se adună o dată și se mută o dată. */
/** CÂT E ÎNTRE DOUĂ AXE VECINE, pe fiecare familie.
 *
 *  Cerut de Olivia: «să se vadă distanța dintre axuri ca o cotă». Se dă pe
 *  perechi de axe ALĂTURATE, nu de la prima la fiecare: așa se citește travee
 *  cu travee, cum se cotează un plan de trasare. Literele și cifrele se
 *  socotesc separat — n-are sens o măsură de la «A» la «2». */
export function distanteleAxelor(axe: readonly Ax[]): {
  axa: 0 | 1; de: number; la: number; valoare: number;
}[] {
  const out: { axa: 0 | 1; de: number; la: number; valoare: number }[] = [];
  for (const axa of [0, 1] as const) {
    const ale = axe.filter((x) => x.axa === axa)
      .map((x) => x.valoare)
      .sort((p1, p2) => p1 - p2);
    for (let i = 1; i < ale.length; i++) {
      const v = r3(ale[i] - ale[i - 1]);
      // Două axe care cad una peste alta n-au ce cota între ele.
      if (v > 1e-6) out.push({ axa, de: ale[i - 1], la: ale[i], valoare: v });
    }
  }
  return out;
}

/** E blocată axa asta? V. `plan.axes_blocate`. */
export function axaBlocata(doc: TronsonDoc, nume: string): boolean {
  return (doc.plan?.axes_blocate ?? []).includes(nume);
}

/** BLOCHEAZĂ sau DEBLOCHEAZĂ o axă, pe nume. */
export function blocheazaAxul(
  doc: TronsonDoc, nume: string, blocat: boolean,
): TronsonDoc {
  if (axaBlocata(doc, nume) === blocat) return doc;
  const d = clona(doc);
  const plan = d.plan!;
  const restul = (plan.axes_blocate ?? []).filter((x) => x !== nume);
  plan.axes_blocate = blocat ? [...restul, nume] : restul;
  if (!plan.axes_blocate.length) delete plan.axes_blocate;
  return d;
}

export function mutaAxul(doc: TronsonDoc, ax: Ax, delta: number): TronsonDoc {
  if (!delta || !ax.ziduri.length) return doc;
  // O AXĂ BLOCATĂ NU SE MUTĂ, oricine ar cere-o. Regula stă AICI, nu în
  // gesturi: pânza mută o axă din două locuri — trasă de linia ei și împinsă
  // din tastatură (v. `Panza`) —, iar o pază pusă în unul din ele ar fi lăsat
  // celălalt să treacă. Tot pe-aici intră și orice gest viitor.
  if (axaBlocata(doc, ax.nume)) return doc;
  const grup = new Set<number>();
  for (const j of ax.ziduri) for (const k of runulLiniei(doc, j)) grup.add(k);
  // PE NORMALA EI. `mutaPeretele` adună deplasarea la o coordonată, deci știe
  // numai de x și de y; o axă oblică se mută cu un VECTOR, pe normala ei.
  if (ax.n) {
    return mutaPereteleSingur(doc, ax.ziduri[0],
                              [ax.n[0] * delta, ax.n[1] * delta], { grup });
  }
  return cuInsemnareaMutata(mutaPeretele(doc, ax.ziduri[0], delta, { grup }),
                            ax, delta);
}

/** The `plan.axes` note on the moved axis goes with it.
 *
 *  An added axis is a written position that `axele` dresses with the walls of
 *  the weak group under it. Moving only the walls left the note where it was:
 *  the rebuilt axis had no walls, and the guard in `mutaAxul` refused every
 *  later move (sheet row 67, 27.10.2026). Hidden notes are kept as they are:
 *  a hidden note on this axis is not a thing the user can select and move.
 *
 *  Orthogonal axes only: a note is an x or a y, while an oblique axis measures
 *  along its normal, so the two cannot be matched; oblique axes keep the old
 *  behaviour (see the caller).
 *
 *  The note is dropped, not moved, when the walls land in another axis: faces
 *  within `ROST_LIPIT` of a heavier wall merge into its group (see `axele`),
 *  and a note left next to that axis would show as an axis with no walls,
 *  which is the very state this helper exists to prevent. */
function cuInsemnareaMutata(doc: TronsonDoc, ax: Ax, delta: number): TronsonDoc {
  const peEa = (a: { axa: 0 | 1; valoare: number; ascuns?: boolean }) =>
    !a.ascuns && a.axa === ax.axa && Math.abs(a.valoare - ax.valoare) <= LIPIRE_AX;
  if (!(doc.plan?.axes ?? []).some(peEa)) return doc;
  const d = clona(doc);
  if (!d.plan) return doc;
  const nou = r3(ax.valoare + delta);
  const mutate = new Set<object>();
  d.plan.axes = (d.plan.axes ?? []).map((a) => {
    if (!peEa(a)) return a;
    const b = { ...a, valoare: nou };
    mutate.add(b);
    return b;
  });
  const acolo = axele(d).find(
    (g) => g.axa === ax.axa && Math.abs(g.valoare - nou) <= LIPIRE_AX);
  if (!acolo?.ziduri.length) {
    d.plan.axes = d.plan.axes.filter((a) => !mutate.has(a));
  }
  return d;
}

// --------------------------------------------- conturul, adică amprenta ---
//
// CONTURUL SE POATE TRAGE DE LATURĂ ȘI DE COLȚ, nu doar scrie ca două cifre.
//
// Până aici forma nu era geometrie: se CALCULA din `front.nominal_m` ×
// `band_depth_m`, sau din `measured_leg_a_m` și `notch_m` pe un colț. Bun pentru
// ce vine din planșă — acolo conturul chiar e un dreptunghi sau un pătrat cu
// crestătură — și neputincios pentru orice altceva: «ar trebui să pot edita și
// forma, nu doar aria; să selectez o latură și s-o trag sus/jos, sau un colț».
//
// Două numere nu descriu șapte laturi. Deci conturul devine un POLIGON scris în
// document (`plan.outline`), iar cifrele se mută ODATĂ CU EL: frontul e pasul la
// care începe modulul următor, deci o latură trasă cu o jumătate de metru în
// afară îl duce cu o jumătate de metru, nu la lățimea conturului.
//
// DE CE DIFERENȚA ȘI NU MĂRIMEA. Conturul nu e totuna cu pasul, și pe catalogul
// transcris chiar nu e: zidul de cusătură, 0,30, e desenat în AMÂNDOI vecinii —
// în afara feței de jos, la [-0,30, 0], și înăuntru la capătul de sus — fiindcă
// două module alăturate trebuie să-l aibă în același loc, ca unirea să-l facă
// unul singur (v. `from_tronsoane.py`). Un contur care cuprinde tot ce e desenat
// în modul e deci cu 0,30 mai lat decât pasul, iar o regulă pe mărime ar fi
// mutat pasul acolo la prima atingere: fiecare cusătură cu două ziduri lipite în
// loc de unul, și fiecare bară cu 0,30 mai lungă de fiecare modul.
//
// SE SCRIE LA PRIMA ATINGERE, ca axul unui perete și ca mobila dedusă: până
// atunci deducția din cifre e adevărată și e mai bună (se întinde), iar din
// clipa în care cineva trage de o latură, poligonul e cel care spune adevărul.

/** Modulul, cu CONTURUL SCRIS. Fără efect dacă îl are deja. */
export function cuConturScris(doc: TronsonDoc): TronsonDoc {
  if (!doc.plan || (doc.plan.outline?.length ?? 0) >= 3) return doc;
  const d = clona(doc);
  d.plan!.outline = anvelopa(doc).map((q) => [r3(q[0]), r3(q[1])] as Pt);
  return d;
}

/** Cât ține conturul pe x — pe asta se mișcă frontul. */
function intindereaX(ring: readonly Pt[]): number {
  const xs = ring.map((q) => q[0]);
  return r3(Math.max(...xs) - Math.min(...xs));
}

/** Cifrele conturului, mutate cu CÂT s-a mutat conturul.
 *
 *  `reper` e documentul de DINAINTE de gest: din el se ia atât conturul de la
 *  care se măsoară diferența, cât și cifrele pe care le mută. Așa regula e și
 *  idempotentă — chemată de două ori în același gest, cu același reper, dă
 *  același număr, nu îl adună de două ori.
 *
 *  `front.nominal_m` e PASUL la care începe modulul următor, nu lățimea
 *  desenului: pe catalogul transcris conturul e cu 0,30 mai lat decât pasul,
 *  fiindcă zidul de cusătură e desenat în amândoi vecinii (v. nota de sus). Se
 *  ține și întinderea declarată în jurul lui, fiindcă un nominal în afara
 *  propriului interval e o stare pe care catalogul nu o acceptă.
 *
 *  `band_depth_m` NU se rescrie: e o cifră a catalogului, comună tuturor
 *  modulelor, iar un contur strâmb nu e un motiv să se schimbe pentru toți. Dacă
 *  forma nu se mai potrivește cu banda, catalogul o spune la publicare. */
function cifreleDinContur(d: TronsonDoc, reper: TronsonDoc): void {
  const o = d.plan?.outline;
  if (!o?.length) return;
  const dF = r3(intindereaX(o) - intindereaX(anvelopa(reper)));
  const F = r3(reper.front.nominal_m + dF);
  d.front = {
    ...reper.front,
    nominal_m: F,
    min_m: Math.min(reper.front.min_m, F),
    max_m: Math.max(reper.front.max_m, F),
  };
  if (d.front.min_m !== d.front.max_m) delete d.front.rigid;
  // Pe colț, brațul se lungește cu aceeași diferență: e latura pătratului.
  if (d.role.startsWith("corner") && d.plan!.measured_leg_a_m != null
      && reper.plan?.measured_leg_a_m != null) {
    d.plan!.measured_leg_a_m = r3(reper.plan.measured_leg_a_m + dF);
  }
}

/** MUTĂ UN COLȚ al conturului, liber în plan. */
export function mutaColtulConturului(
  doc: TronsonDoc, i: number, la: Pt,
): TronsonDoc {
  const cu = cuConturScris(doc);
  const o = cu.plan?.outline;
  if (!o || i < 0 || i >= o.length) return doc;
  const d = cu === doc ? clona(doc) : cu;
  d.plan!.outline![i] = [r3(la[0]), r3(la[1])];
  cifreleDinContur(d, doc);
  return d;
}

/** MUTĂ O LATURĂ a conturului, perpendicular pe ea.
 *
 *  Latura `i` merge de la vârful `i` la `i+1`. Se mută amândouă capetele ei, pe
 *  normala laturii: o latură trasă oblic ar fi de fapt două colțuri trase, iar
 *  pentru asta există mânerele de colț. */
export function mutaLaturaConturului(
  doc: TronsonDoc, i: number, dep: Pt,
): TronsonDoc {
  const cu = cuConturScris(doc);
  const o = cu.plan?.outline;
  if (!o || i < 0 || i >= o.length) return doc;
  const a = o[i];
  const b = o[(i + 1) % o.length];
  const vx = b[0] - a[0];
  const vy = b[1] - a[1];
  const L = Math.hypot(vx, vy);
  if (L < 1e-9) return doc;
  // Cât din deplasare e pe normala laturii — restul se aruncă.
  const nx = -vy / L;
  const ny = vx / L;
  const t = dep[0] * nx + dep[1] * ny;
  if (Math.abs(t) < 1e-9) return doc;
  const d = cu === doc ? clona(doc) : cu;
  const out = d.plan!.outline!;
  const j = (i + 1) % out.length;
  out[i] = [r3(a[0] + nx * t), r3(a[1] + ny * t)];
  out[j] = [r3(b[0] + nx * t), r3(b[1] + ny * t)];
  cifreleDinContur(d, doc);
  return d;
}

/** Taie un colț în două — un contur cu mai multe laturi decât avea.
 *
 *  Fără ea, forma nu poate decât să se strâmbe: un dreptunghi rămâne cu patru
 *  laturi oricât ai trage de ele. Se pune un vârf pe mijlocul laturii, iar de
 *  acolo încolo se trage ca oricare altul. */
export function taieLaturaConturului(doc: TronsonDoc, i: number): TronsonDoc {
  const cu = cuConturScris(doc);
  const o = cu.plan?.outline;
  if (!o || i < 0 || i >= o.length) return doc;
  const a = o[i];
  const b = o[(i + 1) % o.length];
  const d = cu === doc ? clona(doc) : cu;
  d.plan!.outline!.splice(i + 1, 0,
                          [r3((a[0] + b[0]) / 2), r3((a[1] + b[1]) / 2)]);
  return d;
}

/** CONTURUL URMEAZĂ ZIDURILE.
 *
 *  Cerut anume: «când redimensionez, să se redimensioneze și conturul
 *  tronsonului automat». Până aici conturul era o declarație pe care numai
 *  mâna o schimba: trăgeai fațada cu jumătate de metru în afară și modulul
 *  rămânea desenat pe vechiul dreptunghi, cu zidul ieșit din el, iar
 *  nepotrivirea se auzea abia la publicare.
 *
 *  REGULA E O DIFERENȚĂ, NU O POZIȚIE. Se compară documentul de la începutul
 *  gestului (`doc0`) cu cel de acum: fiecare latură a conturului își cunoaște
 *  zidurile — cele paralele cu ea, care ating linia ei și se suprapun cu
 *  întinderea ei —, iar dacă zidurile alea s-au mutat pe normala laturii, latura
 *  se mută cu ele, cu aceeași distanță. Trasă în afară, fațada duce conturul
 *  după ea; trasă înăuntru, îl strânge.
 *
 *  DE CE NU «latura stă pe fața zidului». Fiindcă în catalog conturul NU stă
 *  pe fețe: la cusătura dintre două module zidul e împărțit — pe M1 stă între
 *  −0,154 și 0,146, iar conturul trece prin el, la 0. O regulă pe poziție ar fi
 *  «corectat» fiecare modul neatins la prima mișcare de mouse; una pe
 *  diferență lasă neatins tot ce nu s-a mișcat. Deci un modul neatins își
 *  păstrează conturul dedus din cifre, care se întinde (v. `cuConturScris`).
 *
 *  Pe un colț, crestătura are laturile ei și zidurile ei, deci intră în
 *  aceeași regulă fără ramură în plus. O latură fără ziduri, sau ale cărei
 *  ziduri au fost șterse, rămâne unde e. Cifrele conturului (frontul) se
 *  recitesc din poligon. */
export function conturUrmeazaZidurile(doc0: TronsonDoc, doc: TronsonDoc): TronsonDoc {
  if (doc0 === doc) return doc;
  const w0 = doc0.plan?.walls ?? [];
  const w1 = new Map((doc.plan?.walls ?? []).map((w) => [w.id, w]));
  if (!w0.length || !w1.size) return doc;
  const env0 = anvelopa(doc0);
  const env = anvelopa(doc);
  if (env.length < 3 || env.length !== env0.length) return doc;
  // IDEMPOTENT: conturul nou se socotește din conturul DE LA ÎNCEPUT plus
  // deplasarea zidurilor, nu din conturul de acum plus deplasarea. Cât ține
  // gestul funcția e chemată la fiecare mișcare și încă o dată la ridicarea
  // degetului, pe un document al cărui contur a fost deja mutat — adunat
  // peste el, frontul sărea de două ori cât zidul («sare cumva foarte mult»).
  /** Cât de departe de linia laturii poate sta o față a zidului ca zidul să
   *  fie «al laturii»: jumătate din cel mai gros zid al catalogului, plus
   *  ceva — un zid de cusătură străpuns de contur intră, un zid din camera de
   *  alături nu. */
  const ATINGE = 0.3;
  const SUPRAPUNERE = 0.25;
  const nou: Pt[] = env0.map((q) => [q[0], q[1]] as Pt);
  let schimbat = false;
  for (let i = 0; i < env0.length; i++) {
    const a = env0[i];
    const b = env0[(i + 1) % env0.length];
    const dx = Math.abs(b[0] - a[0]);
    const dy = Math.abs(b[1] - a[1]);
    if (dx < LIPIRE && dy < LIPIRE) continue;
    if (Math.min(dx, dy) > LIPIRE) continue;          // oblică: n-o ține niciun zid
    const axa: 0 | 1 = dx < dy ? 0 : 1;
    const alta = (1 - axa) as 0 | 1;
    const valoare = (a[axa] + b[axa]) / 2;
    const jos = Math.min(a[alta], b[alta]);
    const sus = Math.max(a[alta], b[alta]);
    const deltas: number[] = [];
    for (const w of w0) {
      // PARAPETUL NU ȚINE NICIO LATURĂ A CONTURULUI, oricât de aproape ar
      // trece de ea. Conturul e amprenta modulului, iar din el se recalculează
      // `front.nominal_m` și `band_depth_m` (v. `cifreleDinContur`) — cifre pe
      // care planul de situație le citește ca pas al barei și ca adâncime a
      // benzii, aceeași pentru toată colecția. Întoarcerea laterală a unei
      // logii stă în bandă, lipită de capătul modulului și suprapusă peste el
      // cu 1,30 m: fără regula asta, trasă cu 20 cm, ar fi dus frontul
      // modulului cu 20 cm și, de acolo, tot ce se așază după el.
      if (w.kind === "parapet") continue;
      const o = osiaPeretelui(inelul(w.poly));
      if (o.axa !== axa) continue;
      if (Math.min(...o.fete.map((f) => Math.abs(f - valoare))) > ATINGE) continue;
      const s0 = Math.min(...o.span);
      const s1 = Math.max(...o.span);
      if (Math.min(s1, sus) - Math.max(s0, jos) < SUPRAPUNERE) continue;
      const acum = w1.get(w.id);
      if (!acum) continue;
      const o1 = osiaPeretelui(inelul(acum.poly));
      if (o1.axa !== axa) continue;
      deltas.push((o1.fete[0] + o1.fete[1]) / 2 - (o.fete[0] + o.fete[1]) / 2);
    }
    if (!deltas.length) continue;
    // Mediana: dacă un zid de pe latură a fost mutat singur (glisat), iar
    // restul au rămas, latura rămâne cu restul.
    deltas.sort((p, q) => p - q);
    const delta = deltas[Math.floor(deltas.length / 2)];
    if (Math.abs(delta) < 0.005) continue;
    nou[i][axa] = r3(nou[i][axa] + delta);
    nou[(i + 1) % env.length][axa] = r3(nou[(i + 1) % env.length][axa] + delta);
    schimbat = true;
  }
  if (!schimbat) {
    // Nimic de mutat. Dacă conturul de acum e cel de la început, documentul
    // rămâne; dacă fusese mutat de o chemare anterioară, se întoarce la el.
    const laFel = env.every((q, i) => Math.abs(q[0] - env0[i][0]) < 1e-9
                                      && Math.abs(q[1] - env0[i][1]) < 1e-9);
    if (laFel) return doc;
    const d = clona(doc);
    if (doc0.plan?.outline) d.plan!.outline = doc0.plan.outline.map((q) => [q[0], q[1]] as Pt);
    else delete d.plan!.outline;
    // ȘI CIFRELE se întorc: conturul s-a întors la cel de la începutul
    // gestului, iar frontul e al lui, nu al conturului dintre timp.
    d.front = { ...doc0.front };
    if (doc0.plan?.measured_leg_a_m != null) {
      d.plan!.measured_leg_a_m = doc0.plan.measured_leg_a_m;
    }
    return d;
  }
  const d = clona(doc);
  d.plan!.outline = nou.map((q) => [r3(q[0]), r3(q[1])] as Pt);
  cifreleDinContur(d, doc0);
  return d;
}

/** CONTURUL CUPRINDE CE S-A DESENAT PE MARGINE.
 *
 *  Cerut odată cu tronsonul născut de la zero: «după ce pui camere și pereți pe
 *  marginea tronsonului, să înceapă să se mute marginea tronsonului după camere
 *  și pereți». Un modul nou pleacă de la forma tipică a categoriei lui — un
 *  dreptunghi cât banda, un L —, iar prima cameră desenată aproape niciodată nu
 *  cade exact în ea. Fără regula asta, conturul ar fi rămas cifra de pornire
 *  până când cineva l-ar fi tras de latură cu mâna, iar desenul ar fi ieșit din
 *  el.
 *
 *  `conturUrmeazaZidurile` NU ACOPERĂ CAZUL ĂSTA, și nu din scăpare: ea măsoară
 *  cât S-A MUTAT un zid, deci are nevoie de zidul ăla și în documentul de
 *  dinaintea gestului. Un zid abia desenat n-are cu ce fi comparat. Aici se
 *  măsoară altceva — cât iese el DINCOLO de latură — și se mută latura ca să-l
 *  cuprindă.
 *
 *  NUMAI ÎN AFARĂ, ȘI NUMAI ZIDURILE NOI. Două îngrădiri, amândouă cu motiv:
 *
 *    * numai în afară, fiindcă o regulă care și strânge ar mușca din amprenta
 *      declarată la fiecare zid desenat mai înăuntru — conturul spune cât loc
 *      ține modulul pe bară, nu unde s-a ajuns cu desenul;
 *    * numai zidurile NOI, fiindcă în catalog zidul de cusătură (0,30) e desenat
 *      călare pe capătul modulului, cu jumătate în afara conturului. O regulă pe
 *      toate zidurile ar fi lărgit fiecare modul transcris cu 0,15 la prima
 *      atingere — adică fiecare bară, cu atât de fiecare modul.
 *
 *  ȘI UN ZID CĂLARE PE LINIE NU MIȘCĂ NIMIC. Cine trage o fațadă chiar pe latura
 *  conturului o desenează pe linie, nu dincolo de ea, iar jumătatea care iese
 *  n-are ce spune despre amprentă — e chiar convenția cusăturii. Pragul e
 *  `PE_LINIE`, jumătate din cel mai gros zid al catalogului: sub el zidul e AL
 *  laturii, peste el e dincolo de ea și latura îl ia înăuntru întreg.
 *
 *  PARAPEȚII NU CONTEAZĂ, ca și la `conturUrmeazaZidurile` și din același motiv:
 *  un balcon în consolă stă, prin definiție, în afara amprentei. */
export function conturCuprindeZidurileNoi(
  doc0: TronsonDoc, doc: TronsonDoc,
): TronsonDoc {
  if (doc0 === doc) return doc;
  return conturCuprindeBalcoaneleNoi(doc0, zidurileNoiInContur(doc0, doc));
}

/** Semnătura unei camere: poligonul ei, rotunjit. Camerele n-au id, iar un
 *  balcon nou se recunoaște după ce nu era în documentul de dinainte. */
const semnaturaCamerei = (poly: VarfAfin[]) =>
  inelul(poly).map((q) => `${r3(q[0])},${r3(q[1])}`).join(";");

/** Inelul fără vârful de închidere și fără vârfurile care stau pe linia
 *  vecinilor — ce lasă în urmă o unire de dreptunghiuri. */
function faraVarfuriInutile(inel: readonly Pt[]): Pt[] {
  let q = inel.map((p) => [r3(p[0]), r3(p[1])] as Pt);
  if (q.length > 1 && q[0][0] === q.at(-1)![0] && q[0][1] === q.at(-1)![1]) q.pop();
  for (let schimbat = true; schimbat && q.length > 3;) {
    schimbat = false;
    for (let i = 0; i < q.length; i++) {
      const a = q[(i + q.length - 1) % q.length];
      const b = q[i];
      const c = q[(i + 1) % q.length];
      const x = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
      if (Math.abs(x) < 1e-6) { q = q.filter((_, j) => j !== i); schimbat = true; break; }
    }
  }
  return q;
}

/** UN BALCON ÎN CONSOLĂ NOU LĂRGEȘTE CONTURUL cu dala lui — și numai cu ea.
 *
 *  Cerut de Stefan (23.09): «când adaug balconul în afara tronsonului trebuia
 *  să îmi mărească perimetrul exterior». Până atunci parapeții erau scoși
 *  anume din lărgire, iar balconul rămânea desenat în aer, lângă un contur
 *  care nu știa de el.
 *
 *  NU SE MUTĂ LATURA ÎNTREAGĂ, ca la un zid nou (v. `zidurileNoiInContur`):
 *  un balcon de 2 m pe o fațadă de 6 m ar fi împins toată fațada cu 1,55 m și
 *  ar fi pus în amprentă 6 m² de aer pe lângă el. Se UNEȘTE conturul cu dala:
 *  iese o umflătură cât balconul, cu colțurile lui.
 *
 *  BALCONUL RĂMÂNE ÎN CONSOLĂ. Felul e scris în document (`outdoor_kind`) la
 *  punere, dinaintea lărgirii, iar nimic nu-l mai deduce din contur când e
 *  scris — deci nu devine logie și nu intră în suprafața construită. Dacă dala
 *  iese pe lângă capătul modulului, frontul crește cu ea (v.
 *  `cifreleDinContur`), cum a cerut Stefan. */
function conturCuprindeBalcoaneleNoi(doc0: TronsonDoc, doc: TronsonDoc): TronsonDoc {
  const stiute = new Set((doc0.plan?.rooms ?? []).map((r) => semnaturaCamerei(r.poly)));
  const dale = (doc.plan?.rooms ?? [])
    .filter((r) => r.kind === "balcony" && r.outdoor_kind !== "loggia"
                   && !stiute.has(semnaturaCamerei(r.poly)))
    .map((r) => inelul(r.poly));
  if (!dale.length) return doc;
  const env = anvelopa(doc);
  if (env.length < 3) return doc;
  const inchis = (q: readonly Pt[]) => [...q.map((p) => [p[0], p[1]] as [number, number]),
                                        [q[0][0], q[0][1]] as [number, number]];
  const unit = taie().union([inchis(env)], ...dale.map((q) => [inchis(q)]));
  // O dală care nu atinge conturul ar da două bucăți: amprenta nu se rupe în
  // două pentru un balcon lăsat în aer, deci se lasă cum era.
  if (unit.length !== 1) return doc;
  const nou = faraVarfuriInutile(unit[0][0] as Pt[]);
  if (nou.length < 3) return doc;
  const d = clona(cuConturScris(doc));
  d.plan!.outline = nou;
  cifreleDinContur(d, doc0);
  return d;
}

/** ZIDURILE NOI DUC LATURA după ele — v. `conturCuprindeZidurileNoi`. */
function zidurileNoiInContur(
  doc0: TronsonDoc, doc: TronsonDoc,
): TronsonDoc {
  const stiute = new Set((doc0.plan?.walls ?? []).map((w) => w.id));
  const noi = (doc.plan?.walls ?? [])
    .filter((w) => !stiute.has(w.id) && w.kind !== "parapet")
    .map((w) => cadru(inelul(w.poly)));
  if (!noi.length) return doc;
  const env = anvelopa(doc);
  if (env.length < 3) return doc;

  /** Cât poate ieși un zid dincolo de latură fără s-o miște: jumătate din cel
   *  mai gros zid al catalogului (0,30), plus un milimetru de răbdare pentru
   *  rotunjiri. */
  const PE_LINIE = 0.151;
  /** Cât trebuie să se suprapună zidul cu latura ca să fie al ei — aceeași
   *  măsură ca în `conturUrmeazaZidurile`. */
  const SUPRAPUNERE = 0.25;

  // SENSUL INELULUI, ca normala să iasă în afară și nu înăuntru. Anvelopa dedusă
  // din cifre e trigonometrică, dar `plan.outline` e desenat de mână și poate fi
  // scris în oricare sens; aria cu semn e singurul lucru care o spune.
  let s2 = 0;
  for (let i = 0; i < env.length; i++) {
    const a = env[i];
    const b = env[(i + 1) % env.length];
    s2 += a[0] * b[1] - b[0] * a[1];
  }
  const sens = s2 >= 0 ? 1 : -1;

  const nou: Pt[] = env.map((q) => [q[0], q[1]] as Pt);
  let schimbat = false;
  for (let i = 0; i < env.length; i++) {
    const a = env[i];
    const b = env[(i + 1) % env.length];
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    if (Math.abs(dx) < LIPIRE && Math.abs(dy) < LIPIRE) continue;
    if (Math.min(Math.abs(dx), Math.abs(dy)) > LIPIRE) continue;   // oblică
    /** Axa pe care se mută latura: o latură verticală se mută pe x. */
    const axa: 0 | 1 = Math.abs(dx) < Math.abs(dy) ? 0 : 1;
    const alta = (1 - axa) as 0 | 1;
    /** Încotro e afară, pe axa aia: normala exterioară a inelului. */
    const afara = Math.sign(axa === 0 ? sens * dy : -sens * dx);
    if (!afara) continue;
    const valoare = (a[axa] + b[axa]) / 2;
    const jos = Math.min(a[alta], b[alta]);
    const sus = Math.max(a[alta], b[alta]);
    let iese = 0;
    for (const c of noi) {
      const c0 = axa === 0 ? c.x : c.y;
      const c1 = c0 + (axa === 0 ? c.w : c.h);
      const s0 = alta === 0 ? c.x : c.y;
      const s1 = s0 + (alta === 0 ? c.w : c.h);
      if (Math.min(s1, sus) - Math.max(s0, jos) < SUPRAPUNERE) continue;
      // Cât iese fața cea mai depărtată a zidului dincolo de linia laturii.
      iese = Math.max(iese, afara > 0 ? c1 - valoare : valoare - c0);
    }
    if (iese <= PE_LINIE) continue;
    nou[i][axa] = r3(nou[i][axa] + afara * iese);
    nou[(i + 1) % env.length][axa] = r3(nou[(i + 1) % env.length][axa]
                                        + afara * iese);
    schimbat = true;
  }
  if (!schimbat) return doc;
  const d = clona(cuConturScris(doc));
  d.plan!.outline = nou.map((q) => [r3(q[0]), r3(q[1])] as Pt);
  cifreleDinContur(d, doc0);
  return d;
}

/** Scoate un colț. Sub patru vârfuri nu mai e un contur, deci se refuză. */
export function stergeColtulConturului(doc: TronsonDoc, i: number): TronsonDoc {
  const o = doc.plan?.outline;
  if (!o || o.length <= 3 || i < 0 || i >= o.length) return doc;
  const d = clona(doc);
  d.plan!.outline!.splice(i, 1);
  cifreleDinContur(d, doc);
  return d;
}


//
// CONTURUL E DOAR O FORMĂ, ȘI NIMIC DINĂUNTRU NU-L URMEAZĂ.
//
// Prima variantă îl trata ca pe o margine care împinge: frontul scris muta
// capătul modulului, cu zidul de cusătură și cu camerele de pe el, iar banda
// împingea în afară tot ce era dincolo de coridor. Ieșise complicat și greșit —
// „redimensionarea conturului n-ar trebui să schimbe nimic din tronson; e doar
// forma pe care era menit să o acopere".
//
// Deci conturul e o DECLARAȚIE despre cât loc ține modulul, nu un gest asupra
// geometriei. Se schimbă singur. Dacă după aceea ce e desenat nu mai umple
// forma — sau iese din ea — cine spune asta e catalogul, la publicare: are
// probele lui pentru acoperire (`rooms and walls cover only …%`) și pentru
// cusătură (`front = ultima cameră + 0,30`). Ele sunt locul unde nepotrivirea
// trebuie auzită, nu mâna care trage de margine.

/** Scrie FRONTUL modulului — pasul la care începe următorul.
 *
 *  Nu mută nimic din tronson. Întinderea declarată se lărgește dacă cifra iese
 *  din ea, fiindcă un nominal în afara propriului interval e o stare pe care
 *  nici catalogul nu o acceptă; iar un modul întins nu mai poate fi `rigid`,
 *  care cere min == max. */
export function schimbaFrontul(doc: TronsonDoc, nou: number): TronsonDoc {
  const f = doc.front;
  if (!f) return doc;
  const cerut = r3(Math.max(0.5, nou));
  if (Math.abs(cerut - f.nominal_m) < 1e-6) return doc;
  const d = clona(doc);
  d.front = {
    ...f,
    nominal_m: cerut,
    min_m: Math.min(f.min_m, cerut),
    max_m: Math.max(f.max_m, cerut),
  };
  if (d.front.min_m !== d.front.max_m) delete d.front.rigid;
  return d;
}

/** Scrie ADÂNCIMEA BENZII. Nu mută nimic din tronson.
 *
 *  E o cifră a CATALOGULUI — 15,30 pe toate șapte, ca modulele să se lege într-o
 *  bară (`test_every_module_declares_one_band_depth`). Se poate scrie totuși,
 *  altfel latura lungă a conturului ar rămâne singura pe care mâna nu o atinge.
 *  Schimbată, modulul nu se mai potrivește cu celelalte — și asta se aude la
 *  publicare. */
export function schimbaBanda(doc: TronsonDoc, nou: number): TronsonDoc {
  const cerut = Math.max(2, r3(nou));
  if (!doc.band_depth_m || Math.abs(cerut - doc.band_depth_m) < 1e-6) return doc;
  const d = clona(doc);
  d.band_depth_m = cerut;
  return d;
}

/** Scrie BRAȚUL unui modul de colț — latura pătratului pe care îl ține.
 *
 *  Conturul colțului nu e un dreptunghi: e un pătrat din care lipsește
 *  crestătura din colțul intrând (v. `anvelopa`). Latura vine din
 *  `measured_leg_a_m`, iar crestătura din `notch_m`; amândouă se scriu aici, tot
 *  fără să atingă nimic dinăuntru. */
export function schimbaBratul(
  doc: TronsonDoc, ce: { brat?: number; crestatura?: number },
): TronsonDoc {
  const plan = doc.plan;
  if (!plan || !doc.role.startsWith("corner")) return doc;
  const d = clona(doc);
  if (ce.brat != null) {
    d.plan!.measured_leg_a_m = Math.max(2, r3(ce.brat));
    // Brațul e și frontul, pe un colț: e aceeași latură măsurată.
    d.front = { ...d.front, nominal_m: d.plan!.measured_leg_a_m,
                min_m: Math.min(d.front.min_m, d.plan!.measured_leg_a_m),
                max_m: Math.max(d.front.max_m, d.plan!.measured_leg_a_m) };
    if (d.front.min_m !== d.front.max_m) delete d.front.rigid;
  }
  if (ce.crestatura != null) {
    d.plan!.notch_m = Math.max(0, r3(ce.crestatura));
  }
  return d;
}

/** REDIMENSIONEAZĂ NUCLEUL, împingând camerele din jurul lui.
 *
 *  «Nu putem micșora sau mări spațiul alb; putem doar să facem camera mai mare
 *  peste el.» Cutia nucleului se putea muta, dar nu și strânge sau lărgi — deci
 *  golul dintre camere era o mărime pe care n-o hotăra nimeni.
 *
 *  MUCHIA TRASĂ E O LINIE, deci pleacă prin `mutaLinie`, aceeași cale pe care
 *  merge de mult tragerea unui zid: camera care se sprijină pe ea o urmează. Așa
 *  ies amândouă sensurile dintr-un singur gest — nucleul strâns lasă camera să
 *  crească în loc, nucleul lărgit o împinge înapoi — și nu rămâne, între ele, o
 *  fâșie care nu e a nimănui.
 *
 *  PĂRȚILE NU SE ÎNTIND CU CUTIA. Un puț de lift are 1,60 m fiindcă atâta are
 *  cabina, nu fiindcă atâta a rămas; scalat odată cu cutia, ar fi ieșit un lift
 *  de măsuri inventate. Dacă nu mai încap, catalogul o spune la publicare. */
export function redimNucleul(
  doc: TronsonDoc, apucare: Apucare, la: Pt,
): TronsonDoc {
  const rect = doc.plan?.core_rect;
  if (!rect || rect.length !== 4) return doc;
  const [x0, y0, x1, y1] = rect.map(Number);
  const MIC = 0.6;

  // Muchiile care se mută: numai cele apucate. O latură trasă mută una singură,
  // un colț pe amândouă — v. `Apucare`.
  const peX = apucare.sx < 0 ? "jos" : "sus";
  const peY = apucare.sy < 0 ? "jos" : "sus";
  const nouX = peX === "jos" ? Math.min(la[0], x1 - MIC) : Math.max(la[0], x0 + MIC);
  const nouY = peY === "jos" ? Math.min(la[1], y1 - MIC) : Math.max(la[1], y0 + MIC);
  const dx = apucare.sx ? r3(nouX - (peX === "jos" ? x0 : x1)) : 0;
  const dy = apucare.sy ? r3(nouY - (peY === "jos" ? y0 : y1)) : 0;
  if (!dx && !dy) return doc;

  let d = doc;
  // Camerele de pe fiecare muchie mutată o urmează. Întinderea e cea a cutiei
  // pe cealaltă axă: exact bucata de linie pe care o ține nucleul.
  if (dx) {
    d = mutaLinie(d, { axa: 0, valoare: peX === "jos" ? x0 : x1,
                       span: [y0, y1], delta: dx, doarCamere: true });
  }
  if (dy) {
    d = mutaLinie(d, { axa: 1, valoare: peY === "jos" ? y0 : y1,
                       span: [x0, x1], delta: dy, doarCamere: true });
  }
  const rez = clona(d);
  rez.plan!.core_rect = [
    r3(dx && peX === "jos" ? nouX : x0), r3(dy && peY === "jos" ? nouY : y0),
    r3(dx && peX === "sus" ? nouX : x1), r3(dy && peY === "sus" ? nouY : y1),
  ];
  return rez;
}

/** Scrie de-a dreptul mărimile cutiei, ținând colțul din stânga-sus. */
export function schimbaNucleul(
  doc: TronsonDoc, ce: { w?: number; h?: number },
): TronsonDoc {
  const rect = doc.plan?.core_rect;
  if (!rect || rect.length !== 4) return doc;
  const [x0, y0, x1, y1] = rect.map(Number);
  const la: Pt = [ce.w != null ? x0 + Math.max(0.6, ce.w) : x1,
                  ce.h != null ? y0 + Math.max(0.6, ce.h) : y1];
  return redimNucleul(doc, { sx: 1, sy: 1 }, la);
}

/** MODULUL, CU MOBILA DEDUSĂ SCRISĂ ÎN DOCUMENT.
 *
 *  DE CE SE FACE LA DESCHIDERE, ȘI NU LA ATINGERE. Prima variantă desfăcea
 *  camera abia când puneai degetul pe o canapea. Suna bine și era greșită:
 *  desenul dedus rămânea pe planșă lângă piesele scrise, deci apucai patul,
 *  trăgeai de el, iar în cameră rămânea patul DESENAT — părea că ai scos o
 *  copie. Mai rău, zona de apucat a desenului rămânea și ea acolo, așa că a
 *  doua atingere scria încă o mobilare peste prima: în captura Biancăi se văd
 *  trei paturi scoase din aceeași cameră, iar camera neatinsă.
 *
 *  Deci editorul nu mai lucrează deloc pe mobilă dedusă. La deschidere, ce se
 *  vedea ca desen se scrie în document — exact ca axele pereților
 *  (`cuAxeScrise`), din același motiv: deducția ține până pune cineva mâna pe
 *  lucru, iar editorul ESTE mâna. Din clipa aia planșa arată `plan.furniture`
 *  și numai el, deci ce vezi e ce se mută.
 *
 *  SE SCRIE O SINGURĂ DATĂ, ȘI SE DECIDE PE CAMERĂ: o cameră care are deja
 *  vreo piesă înăuntru nu se atinge. Regula a fost întâi a documentului întreg
 *  — «are vreo piesă? atunci nimic» — și era prea largă: pe VM-ul Biancăi,
 *  ciorna M9 avea nouă piese puse de mână, iar cu regula aia TOATE celelalte
 *  camere ale ei ar fi rămas goale în editor, deși până atunci își arătau
 *  mobilarea dedusă.
 *
 *  Pe cameră iese și întoarcerea bine: un pat șters lasă în urmă noptierele,
 *  deci camera tot «are piese» și nu se remobilează. Abia o cameră golită de
 *  tot își primește înapoi mobilarea dedusă la următoarea deschidere — ceea ce
 *  e chiar înțelesul ei: documentul nu spune nimic despre camera asta, deci
 *  uite ce încape.
 *
 *  NU SE MAI CHEAMĂ LA DESCHIDEREA EDITORULUI (23.09.2026): un modul se
 *  deschide cu mobila din catalog și numai cu ea. Rămâne ca unealtă a
 *  probelor, care își fac din ea module mobilate. */
export function cuMobilaScrisa(doc: TronsonDoc): TronsonDoc {
  const plan = doc.plan;
  if (!plan?.rooms?.length) return doc;

  let d = doc;
  plan.rooms.forEach((r, i) => {
    if (!MOBILATE_PLAN.has(r.kind)) return;
    const contur = inelul(r.poly);
    const areDeja = (d.plan!.furniture ?? []).some(
      (f) => inPoligon([f.x.a, f.y.a], contur));
    if (areDeja) return;
    // Așezarea NOUĂ, cu regulile ei (v. `design/mobilare`); cea veche rămâne
    // pentru vizualizatorul de colecție, care doar desenează.
    d = mobileazaCamera(d, i);
  });
  return d;
}

/** Pune o PIESĂ de mobilier acolo unde s-a apăsat.
 *
 *  Mărimile vin din bibliotecă (`PIESE_MOBILIER`), care le ține odată cu
 *  desenul: o canapea are 2.10 x 0.90 și acolo scrie, nu în editor. */
/** Pune o scară gata făcută, cu primul nas de treaptă în punctul apăsat. */
/** THE FLIGHTS OF A PRESET STAIR, from its start and the way it climbs.
 *
 *  `dir` is the unit vector the first flight climbs along; the preset's shape
 *  is built along +x and turned onto it. `oglinda` mirrors the shape across
 *  the first flight, so a U placed against a wall opens away from the wall
 *  instead of into it (the gesture decides, v. `Panza`). The default, +x and
 *  no mirror, is the stair the palette placed with one click.
 *
 *  `primaRampa` is the riser count the drag gave the first flight, as the
 *  custom stair is drawn (Tudor, 28.10.2026: the preset "needs to have the
 *  same move functionality as the custom stair"). On one flight it is the
 *  whole count; on more, the storey's remaining risers go to the flights
 *  after it. */
export function rampelePresetului(
  cheie: string, p: Pt, hNivel: number, dir: Pt = [1, 0], oglinda = false,
  primaRampa?: number,
): { rampe: { de: Pt; la: Pt }[]; latime: number; treapta: number } | null {
  // R15: one flight takes 16 risers, so on a storey that needs more the
  // straight stair can only be built with a landing
  const fel = cheie === "dreapta" && lungimileScarii(hNivel).contratrepte > TREPTE_MAX
    ? "dreapta_podest" : cheie;
  const pre = PRESETURI_SCARA.find((x) => x.cheie === fel);
  if (!pre) return null;
  // CÂTE TREPTE, DIN NIVEL. Presetul poartă forma; numărul îl dă înălțimea de
  // urcat, ca la orice scară (v. `PRESETURI_SCARA`).
  // The comfortable count, not the steepest one: riser within R07 and the
  // pair inside Blondel's band, so a new stair is not born red
  // (2.80 → 16 × 17.5 cm, tread 28 cm).
  // The tread is a whole centimetre, the nearest to the band's centre among
  // those the storey allows (`lungimileScarii`): on 3.00 the formula gives
  // 29.7 cm, a figure nobody builds (Tudor, 28.10.2026: "only full
  // centimeters"); 30 is taken. On 2.80 it stays 28.
  const bun = lungimileScarii(hNivel, pre.rampe);
  const treapta = treaptaNivelului(hNivel);
  const per = !primaRampa ? imparteContratrepte(bun.contratrepte, pre.rampe)
    : pre.rampe === 1 ? [primaRampa]
    : [primaRampa, ...imparteContratrepte(
        Math.max(pre.rampe - 1, bun.contratrepte - primaRampa), pre.rampe - 1)];
  const lung = lungimeDinContratrepte(per[0], treapta);
  const rampe: { de: Pt; la: Pt }[] = [{ de: [0, 0], la: [lung, 0] }];
  pre.laturi.forEach((lat, i) => {
    rampe.push(rampaUrmatoare(rampe[i] as never, lat,
                              // PODESTUL DE COLȚ E PĂTRAT: a doua rampă pleacă
                              // la o jumătate de lățime dincolo de capătul
                              // celei dintâi, nu la o lățime întreagă ca la U.
                              // Between parallel flights the eye is F39's
                              // 20 cm (`OCHI_MIN`), so a U from the palette
                              // is not born red; it was 10 cm (28.10.2026).
                              pre.sensuri?.[i] ? pre.latime / 2 : pre.latime + OCHI_MIN,
                              per[i + 1] ?? per[0], treapta,
                              pre.sensuri?.[i], pre.latime) as never);
  });
  // the shape, built along +x, turned onto `dir`: x along it, y across it
  const L = Math.hypot(dir[0], dir[1]) || 1;
  const u: Pt = [dir[0] / L, dir[1] / L];
  const n: Pt = oglinda ? [u[1], -u[0]] : [-u[1], u[0]];
  const pune = (q: Pt): Pt =>
    [r3(p[0] + q[0] * u[0] + q[1] * n[0]), r3(p[1] + q[0] * u[1] + q[1] * n[1])];
  return { rampe: rampe.map((r) => ({ de: pune(r.de), la: pune(r.la) })),
           latime: pre.latime, treapta };
}

export function adaugaScaraPreset(
  doc: TronsonDoc, cheie: string, p: Pt,
  hNivel = doc.storey_height_m ?? H_NIVEL, dir: Pt = [1, 0], oglinda = false,
  primaRampa?: number,
): { doc: TronsonDoc; ales: Ales } | null {
  const pr = rampelePresetului(cheie, p, hNivel, dir, oglinda, primaRampa);
  if (!pr) return null;
  const { rampe, treapta } = pr;
  return adaugaScaraDesenata(doc, rampe, felulScarii(rampe as never),
                             pr.latime, treapta);
}

/** CAPĂTUL PE CARE CREȘTE RAMPA `k` dintr-o scară de `n` rampe: cel care NU dă
 *  într-un podest.
 *
 *  DE CE NU MEREU ÎNAINTE. Lungimea unei rampe nu e o măsură de sine stătătoare,
 *  e trepte × adâncime (v. `schimbaScara`), deci scrise treptele, rampa trebuie
 *  să se lungească de undeva. Întinsă mereu din capătul de pornire, rampa
 *  DINTÂI își împingea capătul de sus în podest: podestul se desenează chiar
 *  între capătul uneia și începutul celeilalte (`ScaraDesen::podest`), deci se
 *  strângea la fiecare treaptă adăugată. Cerut de arhitect (21.10.2026):
 *  treptele cresc pe capătul care nu dă în podest.
 *
 *  O RAMPĂ SINGURĂ n-are podest de nicio parte: crește înainte, cum a fost
 *  trasă. PRIMA crește înapoi, fiindcă podestul ei e la capătul de sus. ULTIMA
 *  crește înainte. UNA DIN MIJLOC are podest de amândouă părțile și n-are
 *  încotro: crește înainte și ÎMPINGE ce vine după ea (v. `impingeDupa`). */
function capatulLiber(n: number, k: number): "de" | "la" {
  return n > 1 && k === 0 ? "de" : "la";
}

/** ÎNTINDE RAMPA la `n` contratrepte, crescând pe capătul `capat`.
 *
 *  Celălalt capăt STĂ: el e cel lipit de podest, iar podestul se deduce din
 *  capetele rampelor, deci mișcat ar fi mutat podestul. */
function intindeRampa(r: { de: [number, number]; la: [number, number] },
                      n: number, treapta: number,
                      capat: "de" | "la"): void {
  const lung = lungimeDinContratrepte(n, treapta);
  const dx = r.la[0] - r.de[0], dy = r.la[1] - r.de[1];
  const L = Math.hypot(dx, dy) || 1;
  const ux = dx / L, uy = dy / L;
  if (capat === "la") r.la = [r3(r.de[0] + ux * lung), r3(r.de[1] + uy * lung)];
  else r.de = [r3(r.la[0] - ux * lung), r3(r.la[1] - uy * lung)];
}

/** DUCE CU `dep` tot ce vine DUPĂ rampa `k` — rampele și podestele scrise.
 *
 *  PROPAGAREA. O rampă cu podest de amândouă părțile nu se poate lungi fără să
 *  împingă: ce vine după ea se duce cu tot atât, ca depărtările dintre rampe —
 *  adică podestele — să rămână cum erau. Cerut de arhitect (21.10.2026):
 *  creșterea împinge într-o parte și se duce prin tot ce e legat.
 *
 *  ȘI PODESTUL `k` SE DUCE, nu doar cele de după: el stă între capătul rampei
 *  `k`, care s-a mutat, și începutul rampei `k+1`, care se mută acum — deci
 *  îmbinarea întreagă pleacă cu `dep`. Numai forma SCRISĂ are nevoie de asta;
 *  una dedusă iese singură din rampele mutate. */
function impingeDupa(sc: {
  rampe: { de: [number, number]; la: [number, number] }[];
  podeste?: { k: number; contur: [number, number][] }[];
}, k: number, dep: Pt): void {
  if (!dep[0] && !dep[1]) return;
  const q = (p: readonly [number, number]): [number, number] =>
    [r3(p[0] + dep[0]), r3(p[1] + dep[1])];
  for (let j = k + 1; j < sc.rampe.length; j++) {
    sc.rampe[j].de = q(sc.rampe[j].de);
    sc.rampe[j].la = q(sc.rampe[j].la);
  }
  for (const x of sc.podeste ?? []) if (x.k >= k) x.contur = x.contur.map(q);
}

/** SCRIE CONTRATREPTELE fiecărei rampe — fiecare crescând pe capătul ei liber,
 *  împingând ce vine după ea.
 *
 *  ȚINE DEPĂRTĂRILE DINTRE RAMPE, adică podestele, fără să le socotească
 *  dinainte: rampa care s-a lungit împinge tot ce vine după cu exact cât s-a
 *  mutat capătul ei, deci îmbinările rămân cum erau. Mersul e ÎNAINTE, rampă cu
 *  rampă, ca împinsul uneia să prindă și rampele pe care le-a lungit deja. */
function intindeScara(sc: {
  rampe: { de: [number, number]; la: [number, number] }[];
  podeste?: { k: number; contur: [number, number][] }[];
}, per: readonly number[], treapta: number): void {
  sc.rampe.forEach((r, k) => {
    const capat = capatulLiber(sc.rampe.length, k);
    const la0: Pt = [r.la[0], r.la[1]];
    intindeRampa(r, per[k] ?? per[0], treapta, capat);
    if (capat === "la") {
      impingeDupa(sc, k, [r.la[0] - la0[0], r.la[1] - la0[1]]);
    }
  });
}

/** Cât de aproape de aliniere se lipește o rampă trasă, în metri.
 *
 *  12 cm: la mărirea de lucru sunt câțiva pixeli — destul cât să nu trebuiască
 *  nimerit la milimetru, prea puțin cât să tragi o rampă anume strâmbă și să
 *  ți-o îndrepte editorul. */
export const LIPIRE_RAMPA = 0.12;

/** MUTĂ O RAMPĂ a scării — liber, dar lipindu-se când podestul iese drept.
 *
 *  Rampa se mută ÎNTREAGĂ (nu i se schimbă nici lungimea, nici direcția), deci
 *  rămâne cum era față de vecină — paralelă dacă era paralelă, cotită dacă era
 *  cotită; ce se poate strica prin mutare e ALINIEREA
 *  capetelor. Când capătul rampei mutate și cel al vecinei cad pe aceeași
 *  linie, podestul dintre ele iese un dreptunghi curat; altfel iese un
 *  patrulater strâmb (v. `ScaraDesen::podest`). De-aia se lipește acolo: e
 *  singura poziție pe care cineva o țintește.
 *
 *  Lipirea se măsoară DE-A LUNGUL rampelor — pe cealaltă direcție mutarea e
 *  liberă, fiindcă depărtarea dintre rampe e tocmai ce se alege mutând. */
export function mutaRampaScarii(
  doc: TronsonDoc, i: number, k: number, dep: Pt,
): TronsonDoc {
  const f = doc.plan?.furniture?.[i];
  if (!f?.scara?.rampe[k] || (!dep[0] && !dep[1])) return doc;
  const d = clona(doc);
  const sc = d.plan!.furniture![i].scara!;
  const r = sc.rampe[k];
  const muta = (q: [number, number], v: Pt): [number, number] =>
    [r3(q[0] + v[0]), r3(q[1] + v[1])];
  r.de = muta(r.de, dep);
  r.la = muta(r.la, dep);

  // LIPIREA: cât de departe de aliniere a ajuns, măsurat pe direcția rampei.
  const vecina = sc.rampe[k - 1] ?? sc.rampe[k + 1];
  if (vecina) {
    const dx = r.la[0] - r.de[0], dy = r.la[1] - r.de[1];
    const L = Math.hypot(dx, dy) || 1;
    const ux = dx / L, uy = dy / L;
    // capetele care se întâlnesc pe podest: sfârșitul uneia, începutul celeilalte
    const alMeu = sc.rampe[k - 1] ? r.de : r.la;
    const alEi = sc.rampe[k - 1] ? vecina.la : vecina.de;
    // ȘI LA UN SFERT DE TUR, tot asta se țintește: abaterea se măsoară pe
    // direcția rampei MUTATE, iar dusă la zero ea aduce începutul rampei a
    // doua în dreptul capătului celei dintâi — adică fix colțul pătrat. (Am
    // încercat s-o opresc pe scările cotite, bănuind că le strică; n-am putut
    // arăta niciun caz în care o face, iar fără caz nu se adaugă cod.)
    const abatere = (alMeu[0] - alEi[0]) * ux + (alMeu[1] - alEi[1]) * uy;
    if (Math.abs(abatere) <= LIPIRE_RAMPA) {
      const v: Pt = [-ux * abatere, -uy * abatere];
      r.de = muta(r.de, v);
      r.la = muta(r.la, v);
    }
  }

  const cutie = cutiaScarii(sc);
  if (cutie) {
    const g = d.plan!.furniture![i];
    g.x = { ...g.x, a: r3(cutie.cx) };
    g.y = { ...g.y, a: r3(cutie.cy) };
    g.w_m = r3(cutie.w);
    g.h_m = r3(cutie.h);
  }
  return d;
}

/** DUCE RAMPELE ȘI PODESTELE cu `dep`, pe o scară dintr-un document clonat.
 *
 *  O scară trasă își poartă rampele și podestele în coordonate de PLAN (v.
 *  `PiesaDoc.scara`), nu în cadrul piesei: mutată piesa, ele nu se mută singure
 *  cu ea. Contururile SCRISE ale podestelor merg cu rampele — un podest citit
 *  din planșă sau desenat de mână are forma lui scrisă (`conturulPodestului`),
 *  iar lăsată pe loc ea ar rămâne în urmă și scara s-ar rupe în două.
 *
 *  UN FAPT, UN LOC: aceeași mutare o face fereastra care duce o bucată de plan
 *  și gestul care apucă scara de o parte a ei (`mutaScaraIntreaga`). */
function duScara(sc: {
  rampe: { de: [number, number]; la: [number, number] }[];
  podeste?: { k: number; contur: [number, number][] }[];
}, dep: Pt): void {
  const q = (p: readonly [number, number]): [number, number] =>
    [r3(p[0] + dep[0]), r3(p[1] + dep[1])];
  for (const r of sc.rampe) { r.de = q(r.de); r.la = q(r.la); }
  for (const x of sc.podeste ?? []) x.contur = x.contur.map(q);
}

/** ROTEȘTE SCARA, în jurul centrului cutiei ei.
 *
 *  PE RAMPE, ca oglindirea și din același motiv: desenul scării iese din ele,
 *  iar `rot_deg` e ținut 0 pe o scară desenată (v. `schimbaScaraParte`) — un
 *  unghi scris pe piesă n-ar fi citit de nimeni. Contururile SCRISE ale
 *  podestelor se rotesc odată cu rampele, altfel podestul citit din planșă ar
 *  rămâne pe vechea direcție.
 *
 *  Cerut de arhitect (21.10.2026), după ce butonul fusese stins o zi cu
 *  motivul la vedere: «adaugă și rotirea pentru scară». */
function rotesteScara(doc: TronsonDoc, i: number, grade: number): TronsonDoc {
  const sc0 = doc.plan?.furniture?.[i]?.scara;
  if (!sc0?.rampe?.length || !grade) return doc;
  const cutie = cutiaScarii(sc0);
  if (!cutie) return doc;
  const d = clona(doc);
  const sc = d.plan!.furniture![i].scara!;
  const r = rotitorul([cutie.cx, cutie.cy], grade);
  for (const x of sc.rampe) {
    x.de = r(x.de) as [number, number];
    x.la = r(x.la) as [number, number];
  }
  for (const x of sc.podeste ?? []) {
    x.contur = x.contur.map((q) => r(q) as [number, number]);
  }
  refaCutiaScarii(d, i);
  return d;
}

/** OGLINDEȘTE SCARA, în jurul centrului cutiei ei.
 *
 *  PE RAMPE, nu pe steaguri — v. nota din `oglindesteObiectul`. Contururile
 *  SCRISE ale podestelor se oglindesc odată cu ele, ca la mutare (`duScara`):
 *  lăsate pe loc, podestul citit din planșă ar fi rămas de partea cealaltă.
 *
 *  CAPETELE NU SE SCHIMBĂ ÎNTRE ELE: `de` rămâne `de`, deci scara urcă tot
 *  dinspre unde urca — oglindită, nu întoarsă. Săgeata de urcare se desenează
 *  din ele, iar o scară care și-ar fi schimbat sensul la o oglindire ar fi
 *  răspuns la altceva decât s-a cerut. */
function oglindesteScara(doc: TronsonDoc, i: number, axa: Axa): TronsonDoc {
  const sc0 = doc.plan?.furniture?.[i]?.scara;
  if (!sc0?.rampe?.length) return doc;
  const cutie = cutiaScarii(sc0);
  if (!cutie) return doc;
  const d = clona(doc);
  const sc = d.plan!.furniture![i].scara!;
  const o = oglinditorul([cutie.cx, cutie.cy], axa);
  for (const r of sc.rampe) {
    r.de = o(r.de) as [number, number];
    r.la = o(r.la) as [number, number];
  }
  for (const x of sc.podeste ?? []) {
    x.contur = x.contur.map((q) => o(q) as [number, number]);
  }
  refaCutiaScarii(d, i);
  return d;
}

/** MUTĂ SCARA ÎNTREAGĂ, apucată de ORICARE parte a ei.
 *
 *  O parte de scară n-are poziție de sine stătătoare. Podestul e chiar locul
 *  dintre două rampe (`ScaraDesen::podest`): mutat singur, el nu se mută, se
 *  DEZLIPEȘTE — de-aia `mutaPodestul` trebuia să-i scrie un contur, iar scrisul
 *  îl rupea pe veci de rampele care îl definesc. Iar o rampă fără celelalte nu
 *  e o scară, e o bucată de drum. Cerut de arhitect (21.10.2026): apucată de
 *  orice parte, scara se mută ca un corp.
 *
 *  DEPĂRTAREA DINTRE RAMPE NU SE MAI ALEGE TRĂGÂND O RAMPĂ, ci lățind podestul
 *  dintre ele (v. `redimPodestul`): întinderea podestului e mărginită chiar de
 *  rampe, deci împinse de el ele rămân legate de ce vede omul pe ecran.
 *
 *  FĂRĂ LIPIRE. `mutaRampaScarii` se lipea pe alinierea capetelor, fiindcă
 *  mutând o rampă singură tocmai alinierea se putea strica; mutată întreagă,
 *  geometria dinăuntru e rigidă — abaterea nu se schimbă, deci n-ar avea ce
 *  îndrepta.
 *
 *  Cutia se reface din rampe, ca la orice schimbare de scară. */
export function mutaScaraIntreaga(
  doc: TronsonDoc, i: number, dep: Pt,
): TronsonDoc {
  const f = doc.plan?.furniture?.[i];
  if (!f?.scara?.rampe?.length || (!dep[0] && !dep[1])) return doc;
  const d = clona(doc);
  const g = d.plan!.furniture![i];
  duScara(g.scara!, dep);
  const cutie = cutiaScarii(g.scara!);
  if (cutie) {
    g.x = { ...g.x, a: r3(cutie.cx) };
    g.y = { ...g.y, a: r3(cutie.cy) };
    g.w_m = r3(cutie.w);
    g.h_m = r3(cutie.h);
  }
  return d;
}

/** SCHIMBĂ O BUCATĂ DIN SCARA DESENATĂ.
 *
 *  O RAMPĂ se spune în trepte și în adâncimea lor: amândouă îi schimbă
 *  LUNGIMEA, fiindcă lungimea unei rampe nu e o măsură de sine stătătoare, e
 *  trepte × adâncime. Rampa crește pe capătul care NU dă într-un podest — v.
 *  `capatulLiber` —, iar una din mijlocul lanțului, care n-are niciunul, crește
 *  înainte și împinge ce vine după ea (`impingeDupa`).
 *
 *  UN PODEST se spune în adâncime, și atât: lățimea lui o dau rampele pe care
 *  le leagă (v. `ScaraDesen::podest`).
 *
 *  `pas` (înălțimea contratreptei) e al SCĂRII, nu al unei rampe: toate treptele
 *  unei scări urcă la fel. De-aia se scrie o dată, oricare parte ar fi aleasă.
 *
 *  Cutia piesei se reface din rampe la fiecare schimbare: ea e consecința lor. */
/** O RAMPĂ ÎN PLUS pe o scară deja pusă, pe o latură și într-un sens.
 *
 *  DE CE. Până acum o scară căpăta rampe o singură dată: la tragerea ei cu
 *  unealta, sau din presetul paletei (`adaugaScaraPreset`, singurul loc din tot
 *  editorul unde se face `rampe.push`). Pe una ADUSĂ DIN IMPORT nu exista
 *  niciun drum: panoul îi schimba treptele, adâncimea lor, lățimea rampei,
 *  podestul și contratreapta — niciodată câte rampe are și niciodată încotro
 *  merge una. Deci o scară importată cu două rampe în prelungire nu putea fi
 *  făcută să cotească de nicăieri; trebuia ștearsă și redesenată. Cerut,
 *  textual: «run-landing-then stair run in any direction of the landing not
 *  just down or straight and then repeat … so the importer can recognize that
 *  stair» (07.10.2026).
 *
 *  CUTIA SE REFACE DIN RAMPE, ca peste tot (v. `schimbaScaraParte`).
 */
export function adaugaRampaScarii(
  doc: TronsonDoc, i: number, latura: Latura, sens?: Latura,
): TronsonDoc {
  const f = doc.plan?.furniture?.[i];
  if (!f?.scara?.rampe?.length) return doc;
  const d = clona(doc);
  const sc = d.plan!.furniture![i].scara!;
  const treapta = sc.treapta_m ?? TREAPTA_SCARA;
  const ultima = sc.rampe[sc.rampe.length - 1];
  const n = contratrepteDinLungime(
    Math.hypot(ultima.la[0] - ultima.de[0], ultima.la[1] - ultima.de[1]),
    treapta);
  // Podestul de colț e pătrat; cel de întoarcere ține cât o rampă e de lată.
  const cotit = sens != null && sens !== sensulImplicit(ultima, latura);
  sc.rampe.push(rampaUrmatoare(
    ultima, latura, cotit ? sc.latime_m / 2 : sc.latime_m + 0.1, n, treapta,
    sens, sc.latime_m) as never);
  refaCutiaScarii(d, i);
  return d;
}

/** SENSUL UNEI RAMPE DEJA PUSE, schimbat — rampele de după ea o urmează.
 *
 *  O scară e un drum: dacă rampa a doua cotește, a treia pleacă de pe podestul
 *  ei, nu de unde era înainte. Deci se reașază tot ce vine după.
 */
export function intoarceRampaScarii(
  doc: TronsonDoc, i: number, k: number, sens: Latura,
): TronsonDoc {
  const f = doc.plan?.furniture?.[i];
  if (!f?.scara?.rampe?.[k] || k === 0) return doc;
  const d = clona(doc);
  const sc = d.plan!.furniture![i].scara!;
  const treapta = sc.treapta_m ?? TREAPTA_SCARA;
  for (let j = k; j < sc.rampe.length; j++) {
    const ant = sc.rampe[j - 1];
    const r = sc.rampe[j];
    const n = contratrepteDinLungime(
      Math.hypot(r.la[0] - r.de[0], r.la[1] - r.de[1]), treapta);
    // Latura rămâne cea de până acum — se schimbă SENSUL, nu unde stă rampa;
    // numai la cea atinsă se ia sensul cerut, celelalte îl păstrează pe al lor.
    const laturaJ = laturaDinVector(r.de[0] - ant.la[0], r.de[1] - ant.la[1]);
    const sensJ = j === k
      ? sens
      : laturaDinVector(r.la[0] - r.de[0], r.la[1] - r.de[1]);
    const cotit = sensJ !== sensulImplicit(ant, laturaJ);
    sc.rampe[j] = rampaUrmatoare(
      ant, laturaJ, cotit ? sc.latime_m / 2 : sc.latime_m + 0.1, n, treapta,
      sensJ, sc.latime_m) as never;
  }
  refaCutiaScarii(d, i);
  return d;
}

/** A piece with an angle or a flip is drawn around its box's centre, so
 *  refitting that box would move the stair itself, not just its dashed outline. */
function inJurulCutiei(f: PiesaDoc): boolean {
  return !!f.rot_deg || !!f.flip_x || !!f.flip_y;
}

/** Cutia piesei, refăcută din rampele ei. Aceeași socoteală la capătul
 *  fiecărei schimbări de scară — un singur loc, ca să nu se despartă. */
function refaCutiaScarii(d: TronsonDoc, i: number): void {
  const sc = d.plan!.furniture![i].scara!;
  const cutie = cutiaScarii(sc);
  if (!cutie) return;
  const p = d.plan!.furniture![i];
  p.x = { ...p.x, a: r3(cutie.cx) };
  p.y = { ...p.y, a: r3(cutie.cy) };
  p.w_m = r3(cutie.w);
  p.h_m = r3(cutie.h);
  p.kind = felulScarii(sc.rampe);
}


export function schimbaScaraParte(
  doc: TronsonDoc, i: number, parte: "rampa" | "podest", k: number,
  ce: { trepte?: number; treapta?: number; adanc?: number; pas?: number;
        lung?: number; lat?: number;
        /** ÎNAPOI LA DREPTUNGHI: șterge forma desenată de mână a podestului,
         *  ca el să se deducă iar din rampe. Singura cale înapoi — o formă cu
         *  șase laturi nu se poate spune din două cifre. */
        forma?: "dreptunghi" },
  hNivel = H_NIVEL,
): TronsonDoc {
  const f = doc.plan?.furniture?.[i];
  if (!f?.scara) return doc;
  const d = clona(doc);
  const sc = d.plan!.furniture![i].scara!;
  // ADÂNCIMEA DE DINAINTE, luată înainte s-o rescriem: treptele fiecărei rampe
  // se numără cu ea. Numărate cu cea nouă, o rampă „pierdea" trepte tocmai
  // fiindcă i se adânciseră — adâncimea treptei trebuie să schimbe LUNGIMEA
  // rampei, nu câte trepte are.
  const veche = sc.treapta_m ?? TREAPTA_SCARA;
  // BLONDEL, ca în panoul piesei (v. `schimbaScara`): scrisă una dintre cele
  // două măsuri ale treptei, cealaltă iese din formulă, iar numărul de
  // contratrepte se potrivește ca scara să urce tot nivelul. AMÂNDOUĂ SUNT ALE
  // SCĂRII ÎNTREGI, nu ale rampei apucate: două rampe cu trepte de adâncimi
  // diferite n-ar fi o scară.
  const prinFormula = (ce.pas != null && ce.pas > 0)
    || (ce.treapta != null && ce.treapta > 0);
  if (prinFormula) {
    const blon = potrivesteBlondel(
      { pas: ce.pas, treapta: ce.treapta }, hNivel,
      { pas: sc.pas_m, treapta: veche });
    const noua = r3(blon.treapta);
    sc.treapta_m = noua;
    // v. `schimbaScara`: se scrie pasul REZOLVAT, nu cel tastat.
    sc.pas_m = r3(blon.pas);
    // CONTRATREPTELE SE NUMĂRĂ ÎNTÂI, toate, cu adâncimea VECHE: numărate pe
    // rând, o rampă deja întinsă s-ar fi numărat cu cea nouă.
    intindeScara(sc, contratreptePeRampe(
      scaraDinRampe(sc.rampe, hNivel, veche).perRampa, blon.contratrepte), noua);
  }
  // LĂȚIMEA RAMPEI e a scării întregi — două rampe de lățimi diferite n-ar fi o
  // scară —, deci se schimbă de oriunde ar fi cerută, și TRAGE RAMPELE DUPĂ EA:
  // depărtarea dintre ele se măsoară între liniile de urcare, iar podestul se
  // reface din ele (v. `ScaraDesen::podest`).
  if (ce.lat != null && ce.lat > 0) sc.latime_m = r3(ce.lat);

  if (parte === "podest") {
    // FORMA DESENATĂ, ștearsă: podestul se deduce iar din rampe, iar cele două
    // cifre de mai jos au din nou ce să descrie.
    if (ce.forma === "dreptunghi") {
      const rest = (sc.podeste ?? []).filter((x) => x.k !== k);
      if (rest.length) sc.podeste = rest;
      else delete sc.podeste;
    }
    // ADÂNCIMEA PODESTULUI — cât ține el dincolo de linia de întoarcere.
    if (ce.adanc != null && ce.adanc > 0) sc.podest_m = r3(ce.adanc);
    // LUNGIMEA PODESTULUI — cât ține de la o rampă la cealaltă. Nu e o măsură a
    // lui: e DEPĂRTAREA DINTRE RAMPE, văzută de pe podest. De-aia scrisă aici
    // MUTĂ rampa a doua, în loc să întindă un dreptunghi peste ea.
    if (ce.lung != null && ce.lung > 0) {
      const a = sc.rampe[k], b = sc.rampe[k + 1];
      if (a && b) {
        const dx = a.la[0] - a.de[0], dy = a.la[1] - a.de[1];
        const L = Math.hypot(dx, dy) || 1;
        const ux = dx / L, uy = dy / L;
        const nx = -uy, ny = ux;
        // SENSUL RAMPEI A DOUA SE PĂSTREAZĂ, oricare ar fi el.
        //
        // Aici scria `b.la = de − u_A * lung2`, adică rampa a doua era pusă cu
        // forța invers-paralelă pe prima. Pe o scară în U aia chiar e direcția
        // ei; pe una COTITĂ, nu — iar o lungime de podest scrisă în panou
        // întorcea a doua rampă înapoi pe axa celei dintâi, adică desfăcea
        // sfertul de tur tocmai desenat. Se ia direcția pe care O ARE.
        const bx = b.la[0] - b.de[0], by = b.la[1] - b.de[1];
        const Lb = Math.hypot(bx, by) || 1;
        const vx = bx / Lb, vy = by / Lb;
        const cotit = Math.abs(ux * vx + uy * vy) < 0.866;
        let de: [number, number];
        if (cotit) {
          // LA UN SFERT DE TUR, «lungimea podestului» înseamnă cât ține colțul
          // PE DIRECȚIA PRIMEI RAMPE — adică la cât dincolo de capătul ei
          // pleacă linia de urcare a celei de-a doua. Măsurată pe normală ar fi
          // fost mereu zero, fiindcă a doua rampă pleacă chiar din dreptul ei.
          const d = Math.max(sc.latime_m / 2, ce.lung);
          de = [r3(a.la[0] + ux * d), r3(a.la[1] + uy * d)];
        } else {
          const dist = Math.max(sc.latime_m, ce.lung) - sc.latime_m;
          // de care parte stătea până acum — nu se întoarce scara pe dos
          const semn = ((b.de[0] - a.la[0]) * nx + (b.de[1] - a.la[1]) * ny) < 0
            ? -1 : 1;
          de = [r3(a.la[0] + nx * dist * semn), r3(a.la[1] + ny * dist * semn)];
        }
        b.de = de;
        b.la = [r3(de[0] + vx * Lb), r3(de[1] + vy * Lb)];
      }
    }
  } else if (ce.trepte != null) {
    const r = sc.rampe[k];
    if (r) {
      // NUMAI RAMPA ASTA, pe capătul ei liber — și, dacă e una din mijloc, cu
      // tot ce vine după ea (v. `capatulLiber`, `impingeDupa`).
      const capat = capatulLiber(sc.rampe.length, k);
      const la0: Pt = [r.la[0], r.la[1]];
      intindeRampa(r, Math.max(1, Math.round(ce.trepte)),
                   sc.treapta_m ?? TREAPTA_SCARA, capat);
      if (capat === "la") {
        impingeDupa(sc, k, [r.la[0] - la0[0], r.la[1] - la0[1]]);
      }
    }
  }
  const cutie = cutiaScarii(sc);
  if (cutie) {
    const g = d.plan!.furniture![i];
    g.x = { ...g.x, a: r3(cutie.cx) };
    g.y = { ...g.y, a: r3(cutie.cy) };
    g.w_m = r3(cutie.w);
    g.h_m = r3(cutie.h);
  }
  return d;
}

/** SCRIE SCARA TRASĂ RAMPĂ CU RAMPĂ.
 *
 *  `rampe` sunt liniile de urcare, în ordinea tragerii — chiar gestul omului.
 *  Din ele ies podestele, numărul de trepte al fiecărei rampe și CUTIA piesei;
 *  cutia se scrie fiindcă editorul apucă, mută și rotește piese dreptunghiulare,
 *  dar nu ea e scara (v. `plan/scara.ts`).
 *
 *  `null` = n-a ieșit nicio rampă cu lungime, adică au fost apăsări scăpate. */
/** O SCARĂ PUSĂ CA PIESĂ DEVINE UNA DESENATĂ, cu rampele ei.
 *
 *  DE CE. Tot ce știe editorul să spună despre o scară — câte contratrepte are
 *  fiecare rampă, cât de adâncă e treapta, cât urcă una — e scris pe RAMPE
 *  (`PiesaDoc.scara`). O scară pusă din paletă până la preseturi, și mai ales
 *  una ADUSĂ DIN IMPORT, e o cutie fără rampe, deci panoul n-avea ce-i arăta
 *  și n-avea ce-i scrie: «we should be able to set step count and height for
 *  all stairs (even default)» (06.10.2026).
 *
 *  ȘI NU UN AL DOILEA MODEL DE SCARĂ. A da cutiei câmpurile ei de trepte ar fi
 *  însemnat două feluri de a spune același lucru, care se pot despărți; cutia
 *  se face O DATĂ în rampe (`rampeleDinCutie`) și de acolo încolo e o scară ca
 *  oricare alta — se apucă o rampă, se trage de ea, se mută podestul.
 *
 *  LA PRIMA SCRIERE, nu la prima atingere: cât nu i-a cerut nimeni nimic, o
 *  scară adusă din import rămâne cum a venit. */
export function promoveazaScara(
  doc: TronsonDoc, i: number, hNivel = H_NIVEL,
): TronsonDoc {
  const f = doc.plan?.furniture?.[i];
  if (!f || (f.kind !== "scara" && f.kind !== "scaraU") || f.scara) return doc;
  // Mărimile de catalog când piesa n-a scris-o pe a ei — aceleași implicite pe
  // care le pune `doc::deriva` (:1601), ca să nu iasă două cutii diferite.
  const fel = felulPiesei(f.kind);
  const { rampe, latime, treapta } = rampeleDinCutie(
    f.kind, [f.x.a, f.y.a], f.w_m ?? fel?.w ?? 1.6, f.h_m ?? fel?.h ?? 1.0,
    f.rot_deg ?? 0, hNivel);
  const cutie = cutiaScarii({ rampe, latime_m: latime });
  if (!cutie) return doc;
  const d = clona(doc);
  const p = d.plan!.furniture![i];
  // ROTIREA S-A COBORÂT ÎN CAPETELE RAMPELOR: ele sunt în coordonatele
  // planului, nu în cadrul piesei (v. `PiesaDoc.scara`), deci piesa nu mai are
  // ce roti — altfel scara s-ar roti de două ori.
  p.rot_deg = 0;
  p.x = { ...p.x, a: r3(cutie.cx) };
  p.y = { ...p.y, a: r3(cutie.cy) };
  p.w_m = r3(cutie.w);
  p.h_m = r3(cutie.h);
  p.kind = felulScarii(rampe);
  p.scara = {
    rampe: rampe.map((r) => ({
      de: [r3(r.de[0]), r3(r.de[1])] as [number, number],
      la: [r3(r.la[0]), r3(r.la[1])] as [number, number],
    })),
    latime_m: r3(latime),
    treapta_m: r3(treapta),
  };
  return d;
}

/** TREPTELE ȘI PASUL UNEI SCĂRI ÎNTREGI — oricum a fost pusă.
 *
 *  `schimbaScaraParte` spune aceleași lucruri despre o RAMPĂ anume, apucată pe
 *  planșă; asta le spune despre scară, din panoul piesei, și merge și pe
 *  cutiile care n-au fost încă desenate (le promovează întâi).
 *
 *  PATRU MĂRIMI, DOUĂ LEGĂTURI. Numărul de contratrepte, adâncimea treptei,
 *  lungimea rampei și înălțimea contratreptei se leagă prin
 *  `lungime = (n − 1) × adâncime` și `nivel = n × pas`. Omul scrie numărul și
 *  adâncimea; lungimea rampei iese din ele (rampele se întind), iar pasul iese
 *  din nivel — afară de cazul în care îl scrie el, și atunci scara urcă la altă
 *  cotă decât tavanul, ceea ce e o hotărâre, nu o greșeală (v. `PiesaDoc.scara`
 *  `pas_m`). */
export function schimbaScara(
  doc: TronsonDoc, i: number,
  ce: { contratrepte?: number; treapta?: number; pas?: number | null;
        lat?: number },
  hNivel = H_NIVEL,
): TronsonDoc {
  let d = promoveazaScara(doc, i, hNivel);
  const f = d.plan?.furniture?.[i];
  if (!f?.scara) return doc;
  if (d === doc) d = clona(doc);
  const sc = d.plan!.furniture![i].scara!;
  // ADÂNCIMEA DE DINAINTE, luată ÎNAINTE s-o rescriem — aceeași capcană pe
  // care o descrie `schimbaScaraParte`: treptele rampelor de acum se numără cu
  // adâncimea cu care au fost puse. Numărate cu cea nouă, o scară căreia i se
  // scria 0,28 pe o rampă desenată lungă „creștea" de la 16 contratrepte la 54
  // — adâncimea trebuie să schimbe LUNGIMEA rampei, nu câte trepte are.
  const veche = sc.treapta_m ?? TREAPTA_SCARA;
  // BLONDEL: scrisă UNA dintre cele două măsuri ale treptei, cealaltă iese din
  // formulă, iar numărul de contratrepte se potrivește ca scara să urce tot
  // nivelul (v. `scara::potrivesteBlondel`).
  const prinFormula = (ce.pas != null && ce.pas > 0)
    || (ce.treapta != null && ce.treapta > 0);
  const blon = prinFormula
    ? potrivesteBlondel({ pas: ce.pas ?? undefined, treapta: ce.treapta },
                        hNivel, { pas: sc.pas_m, treapta: veche })
    : null;
  const treapta = blon ? r3(blon.treapta)
    : (ce.treapta != null && ce.treapta > 0 ? r3(ce.treapta) : veche);
  if (ce.lat != null && ce.lat > 0) sc.latime_m = r3(ce.lat);
  // PASUL SCRIS E CEL REZOLVAT, nu cel tastat. Contratreptele sunt un număr
  // ÎNTREG și scara trebuie să ajungă la planșeu, deci din cifra tastată iese
  // mai întâi NUMĂRUL de contratrepte, iar înălțimea iese din el: cerând 16,5
  // pe un nivel de 2,80 ies 17 trepte de 16,47. Se scrie valoarea adevărată, ca
  // panoul să arate scara, nu dorința.
  //
  // `null` ȘTERGE pasul scris, ca până acum: scara se întoarce la cel care iese
  // din nivel.
  if (ce.pas === null) delete sc.pas_m;
  else if (blon) sc.pas_m = r3(blon.pas);
  const nou = ce.contratrepte ?? blon?.contratrepte;
  if ((nou != null && nou > 0) || (ce.treapta != null && ce.treapta > 0)) {
    const acum = scaraDinRampe(sc.rampe, hNivel, veche);
    const per = nou != null && nou > 0
      ? contratreptePeRampe(acum.perRampa, nou) : acum.perRampa;
    // PODESTUL MERGE CU RAMPA, nu rămâne în urma ei: depărtarea dintre rampe —
    // adică podestul, care se desenează chiar între capătul uneia și începutul
    // celeilalte (`ScaraDesen::podest`) — rămâne cum era, iar rampa dintâi
    // crește ÎNAPOI, ca să nu-și împingă capătul în el (v. `intindeScara`).
    intindeScara(sc, per, treapta);
    // TREAPTA DERIVATĂ SE ȘI SCRIE, nu doar se folosește. Scrisă contratreapta,
    // treapta iese din formulă (v. `potrivesteBlondel`); nescrisă în document,
    // geometria se întindea cu cifra NOUĂ în timp ce `treapta_m` rămânea cea
    // VECHE — deci panoul arăta altceva decât desenul, iar următoarea socoteală
    // pornea de la cifra veche. Găsit socotind îndreptarea catalogului: o scară
    // ieșea cu 2h + l = 71,8 după ce fusese „adusă la normă".
    if (blon || (ce.treapta != null && ce.treapta > 0)) sc.treapta_m = treapta;
  }
  const cutie = cutiaScarii(sc);
  if (cutie) {
    const p = d.plan!.furniture![i];
    p.x = { ...p.x, a: r3(cutie.cx) };
    p.y = { ...p.y, a: r3(cutie.cy) };
    p.w_m = r3(cutie.w);
    p.h_m = r3(cutie.h);
  }
  return d;
}

/** THE RUN LENGTH OF A STAIR, typed: the riser count comes out of it.
 *
 *  The panel's generic length wrote the piece's box and left the flights
 *  where they were, so the drawing no longer matched its box and the stair's
 *  own numbers never moved (tested 27.10.2026: length 5 on a 15-riser stair
 *  changed nothing but the dashed box). A stair's length is its flights'
 *  treads laid end to end. The storey fixes the riser count, so the typed
 *  length can only choose the tread, a whole centimetre inside the band and
 *  the comfort range, and the run becomes the nearest the rules allow
 *  (`scara::lungimeaApropiata`): 2.00 or 4.10 on 2.80 give 4.20, 5.00 gives
 *  4.35. Before, the count was chosen from the length and the length kept
 *  (tested 28.10.2026): 2.496 made 13 risers of 21.5 cm, red on three rows,
 *  and 4.10 a run of 4.095 on 27.3 mm treads, figures nobody builds.
 *
 *  The tread is written and the riser height is left to the storey: a
 *  riser height written by an earlier tread or riser edit (`pas_m`) is
 *  dropped, because the count is now the storey's and `hNivel / n` is the
 *  only riser that climbs it. Written instead (as a first version did), it
 *  outlived a count typed afterwards: 17 risers kept 17.5 cm, 2.975 m on a
 *  2.80 storey, and the riser row stayed quiet (tested 28.10.2026). The
 *  flights are stretched with the same tread, so panel and drawing agree to
 *  the millimetre. */
export function schimbaLungimeaScarii(
  doc: TronsonDoc, i: number, lungime: number, hNivel = H_NIVEL,
): TronsonDoc {
  if (!(lungime > 0)) return doc;
  let d = promoveazaScara(doc, i, hNivel);
  const f = d.plan?.furniture?.[i];
  if (!f?.scara) return doc;
  if (d === doc) d = clona(doc);
  const sc = d.plan!.furniture![i].scara!;
  const blon = lungimeaApropiata(lungime, hNivel, sc.rampe.length);
  const treapta = blon.treapta;
  sc.treapta_m = treapta;
  delete sc.pas_m;
  intindeScara(sc, imparteContratrepte(blon.contratrepte, sc.rampe.length), treapta);
  refaCutiaScarii(d, i);
  return d;
}

/** THE TOTAL LENGTH OF A STAIR AS DRAWN, landings included. The storey fixes
 *  the flights, so a landing takes the difference. A figure too small stops at
 *  the smallest legal landing (NP 063 2.2.1.3.c, the width of the flight); only
 *  a written landing shape refuses, or a shape the lever cannot lengthen.
 *
 *  The panel's length was the run, the treads laid end to end, a figure the
 *  drawing of a U stair never shows: 3.92 m on a shape 3.21 m long. The
 *  architect dimensions the shape, so the typed figure is
 *  the shape's extent along the first flight (`intinderileScarii`), and what
 *  gives is the one elastic part the rules leave: the landing.
 *
 *  By form. One flight: the run and the length are the same figure, so this
 *  is `schimbaLungimeaScarii`. A U-turn landing that ends the shape:
 *  `podest_m` is solved so that the shape meets the figure, by bisection,
 *  because the extent never shrinks as the depth grows. Every other stair: a
 *  landing slides along the first flight by the difference, the first one
 *  unless another reaches the figure better (`mutaUnPodest`). A refusal, or a figure already met,
 *  returns the document it was given, a box piece unpromoted, so nothing is
 *  written. */
export function schimbaLungimeaTotala(
  doc: TronsonDoc, i: number, lungime: number, hNivel = H_NIVEL,
): TronsonDoc {
  if (!(lungime > 0)) return doc;
  const d = promoveazaScara(doc, i, hNivel);
  const sc0 = d.plan?.furniture?.[i]?.scara;
  if (!sc0) return doc;
  if (sc0.rampe.length === 1) return schimbaLungimeaScarii(d, i, lungime, hNivel);
  if (sc0.podeste?.some((x) => x.contur.length >= 3)) return doc;
  const acum = intinderileScarii(sc0);
  if (!acum) return doc;
  if (podestulLungeste(sc0)) {
    const lungimeaLa = (podest_m: number) =>
      intinderileScarii({ ...sc0, podest_m })!.length;
    const tinta = Math.max(lungime, lungimeaLa(sc0.latime_m));
    let jos = sc0.latime_m, sus = 30;
    if (Math.abs(tinta - acum.length) < 1e-6 || tinta > lungimeaLa(sus)) return doc;
    for (let n = 0; n < 48; n++) {
      const mijloc = (jos + sus) / 2;
      if (lungimeaLa(mijloc) < tinta) jos = mijloc; else sus = mijloc;
    }
    return schimbaScaraParte(d, i, "podest", 0, { adanc: (jos + sus) / 2 }, hNivel);
  }
  const [a] = sc0.rampe;
  const L = Math.hypot(a.la[0] - a.de[0], a.la[1] - a.de[1]) || 1;
  return mutaUnPodest(d, doc, i, lungime,
                      [(a.la[0] - a.de[0]) / L, (a.la[1] - a.de[1]) / L],
                      (sc) => intinderileScarii(sc)!.length,
                      sc0.rampe.slice(1).map((_, k) => k));
}

/** THE TOTAL WIDTH OF A STAIR, typed: the box's depth, from the outer edge of
 *  one flight to the outer edge of the other.
 *
 *  A stair has two widths and they measure different things (sheet row 20,
 *  21.10.2026; decided with Tudor 27.10.2026): the flight's width, and the
 *  whole stair's. On a single flight they are the same number, so the total
 *  simply writes the flight. With two flights the total is two flights plus
 *  the gap between them, and writing it moves the second flight so that the
 *  gap changes while the flights keep their width: the same lever as the
 *  landing's length (`schimbaScaraParte`, `lung`), which is that distance seen
 *  from the landing. The flights never overlap: the total is held at two
 *  flights at least. Any other turning shape moves at a landing instead
 *  (`mutaUnPodest`). */
export function schimbaLatimeaTotala(
  doc: TronsonDoc, i: number, latime: number, hNivel = H_NIVEL,
): TronsonDoc {
  if (!(latime > 0)) return doc;
  const d = promoveazaScara(doc, i, hNivel);
  const sc = d.plan?.furniture?.[i]?.scara;
  if (!sc) return doc;
  if (formaScarii(sc.rampe) === "dreapta") {
    return schimbaScara(d, i, { lat: latime }, hNivel);
  }
  if (uCuDouaRampe(sc)) {
    const lung = Math.max(2 * sc.latime_m, latime);
    return schimbaScaraParte(d, i, "podest", 0, { lung: r3(lung) }, hNivel);
  }
  if (sc.podeste?.some((x) => x.contur.length >= 3)) return doc;
  const [r] = sc.rampe;
  const L = Math.hypot(r.la[0] - r.de[0], r.la[1] - r.de[1]) || 1;
  return mutaUnPodest(d, doc, i, latime,
                      [-(r.la[1] - r.de[1]) / L, (r.la[0] - r.de[0]) / L],
                      (x) => intinderileScarii(x)!.width,
                      sc.rampe.slice(1).map((_, k) => k).reverse());
}

/** THE SIZE ROWS' LEVER: a landing slides along `axa`, carrying everything
 *  after it, and stops at the smallest legal landing (F39's gap on a U-turn, the
 *  flight's width in line, half a band past the flight's end at a corner). Of
 *  the landings in `ordine`, the one that comes nearest the figure wins, the
 *  first on a tie, because a shape's extreme can belong to any flight. A figure
 *  no landing brings nearer writes nothing. */
function mutaUnPodest(
  d: TronsonDoc, doc: TronsonDoc, i: number, tinta: number, axa: Pt,
  masura: (sc: NonNullable<PiesaDoc["scara"]>) => number, ordine: readonly number[],
): TronsonDoc {
  const sc = d.plan!.furniture![i].scara!;
  const pe = (p: readonly number[]) => p[0] * axa[0] + p[1] * axa[1];
  const acum = masura(sc);
  let bun = doc, rest = Math.abs(tinta - acum) - 1e-3;
  for (const k of ordine) {
    const a = sc.rampe[k], b = sc.rampe[k + 1];
    const dist = pe(b.de) - pe(a.la);
    const forma = formaScarii([a, b]);
    const pePrima = Math.abs(dist) < 1e-3;
    if (pePrima && forma === "u") continue;
    // a flight that starts on the axis stands on the side it runs to
    const latura = pePrima ? pe(b.la) - pe(b.de) : dist;
    if (Math.abs(latura) < 1e-3) continue;
    const prag = pePrima ? 0 : forma === "u" ? sc.latime_m + OCHI_MIN
      : forma === "dreapta" ? sc.latime_m : sc.latime_m / 2;
    const dep = Math.sign(latura) * Math.max(tinta - acum, prag - Math.abs(dist));
    const d2 = clona(d);
    const sc2 = d2.plan!.furniture![i].scara!;
    impingeDupa(sc2, k, [axa[0] * dep, axa[1] * dep]);
    const r = Math.abs(masura(sc2) - tinta);
    if (r < rest) {
      rest = r;
      refaCutiaScarii(d2, i);
      bun = d2;
    }
  }
  return bun;
}

/** A SINGLE FLIGHT SPLIT ON A LANDING, for a storey that needs more risers than
 *  one flight may take (R15): the risers shared evenly, the first half where it
 *  was, the second beyond a landing as deep as the flight is wide. */
export function adaugaPodest(
  doc: TronsonDoc, i: number, hNivel = H_NIVEL,
): TronsonDoc {
  let d = promoveazaScara(doc, i, hNivel);
  if (d.plan?.furniture?.[i]?.scara?.rampe.length !== 1) return doc;
  if (d === doc) d = clona(doc);
  const sc = d.plan!.furniture![i].scara!;
  const treapta = sc.treapta_m ?? TREAPTA_SCARA;
  const [n1, n2] = imparteContratrepte(
    scaraDinRampe(sc.rampe, hNivel, treapta).contratrepte, 2);
  const { de, la } = sc.rampe[0];
  const L = Math.hypot(la[0] - de[0], la[1] - de[1]) || 1;
  const la_m = (m: number): [number, number] =>
    [r3(de[0] + (la[0] - de[0]) / L * m), r3(de[1] + (la[1] - de[1]) / L * m)];
  const l1 = lungimeDinContratrepte(n1, treapta);
  const l2 = l1 + sc.latime_m;
  sc.rampe = [{ de, la: la_m(l1) },
              { de: la_m(l2), la: la_m(l2 + lungimeDinContratrepte(n2, treapta)) }];
  refaCutiaScarii(d, i);
  return d;
}

/** Is this the two-flight U that has a total width and a gap? A quarter
 *  turn, a straight stair with a landing or three flights have no such pair
 *  of levers: their "landing length" means something else to
 *  `schimbaScaraParte`, and moving one landing of three would push two
 *  flights into each other. There the flight's width is the flight's alone,
 *  and the total moves at a landing (`mutaUnPodest`). */
function uCuDouaRampe(sc: { rampe: { de: [number, number]; la: [number, number] }[] }): boolean {
  return sc.rampe.length === 2 && felulScarii(sc.rampe) === "scaraU";
}

/** THE FLIGHT'S WIDTH, typed: the total width holds while the gap allows.
 *
 *  Asked by the architect (sheet row 20, 21.10.2026): «dacă se modifică
 *  lățimea rampei atunci ar trebui să se păstreze lățimea totală a scării (L)
 *  și rampele se vor mări spre interior atât timp cât este destul spațiu;
 *  dacă nu este destul spațiu atunci se vor mări spre exterior și se va
 *  modifica și lățimea totală». "Enough space" is `OCHI_MIN`, the 20 cm the
 *  team's rules keep between flights (F39). With one flight there is no
 *  total to hold: the width is the flight's. */
export function schimbaLatimeaRampei(
  doc: TronsonDoc, i: number, lat: number, hNivel = H_NIVEL,
): TronsonDoc {
  if (!(lat > 0)) return doc;
  const d = promoveazaScara(doc, i, hNivel);
  const sc0 = d.plan?.furniture?.[i]?.scara;
  if (!sc0) return doc;
  if (!uCuDouaRampe(sc0)) return schimbaScara(d, i, { lat }, hNivel);
  const total = lungimeaPodestului(sc0, 0);
  // a millimetre of tolerance: a rotated stair's total is read off rounded
  // endpoints and can sit a hair under the threshold it is meant to meet
  const tine = total - 2 * lat >= OCHI_MIN - 5e-4;
  const lung = tine ? total : 2 * lat + OCHI_MIN;
  const d2 = schimbaScaraParte(d, i, "podest", 0, { lat, lung: r3(lung) }, hNivel);
  const dl = lat - sc0.latime_m;
  if (Math.abs(dl) < 1e-9) return d2;
  // THE FIRST FLIGHT'S OUTER EDGE STAYS, whichever branch was taken. A flight
  // widens about its own ascent line, so that edge would move out by half the
  // change while the second flight moved in by as much: the total held, but
  // the stair slid sideways by half the change; and past the threshold the
  // growth would be split between the two sides instead of landing on the
  // second flight's. So the whole stair moves back by that half, toward the
  // second flight: a held total is a held pair of edges, and growth goes where
  // "moves the second flight" says it goes.
  const d3 = clona(d2);
  const sc = d3.plan!.furniture![i].scara!;
  const a = sc.rampe[0], b = sc.rampe[1];
  const dx = a.la[0] - a.de[0], dy = a.la[1] - a.de[1];
  const L = Math.hypot(dx, dy) || 1;
  const nx = -dy / L, ny = dx / L;
  const semn = ((b.de[0] - a.la[0]) * nx + (b.de[1] - a.la[1]) * ny) < 0 ? -1 : 1;
  impingeDupa(sc, -1, [nx * semn * dl / 2, ny * semn * dl / 2]);
  refaCutiaScarii(d3, i);
  return d3;
}

export function adaugaScaraDesenata(
  doc: TronsonDoc, rampe: { de: Pt; la: Pt }[],
  felScara: "scara" | "scaraU", latime: number, treapta = TREAPTA_SCARA,
): { doc: TronsonDoc; ales: Ales } | null {
  const bune = rampe.filter(
    (r) => Math.hypot(r.la[0] - r.de[0], r.la[1] - r.de[1]) > 0.05);
  if (!bune.length) return null;
  const cutie = cutiaScarii({ rampe: bune, latime_m: latime });
  if (!cutie) return null;
  const d = clona(doc);
  const lista = (d.plan!.furniture = d.plan!.furniture ?? []);
  lista.push({
    kind: felScara,
    x: { a: r3(cutie.cx) }, y: { a: r3(cutie.cy) },
    w_m: r3(cutie.w), h_m: r3(cutie.h), rot_deg: 0,
    scara: {
      rampe: bune.map((r) => ({
        de: [r3(r.de[0]), r3(r.de[1])] as [number, number],
        la: [r3(r.la[0]), r3(r.la[1])] as [number, number],
      })),
      latime_m: r3(latime),
      // a tread other than the default is written, or the drawing would count
      // the flights with 0.28 and come out with another riser count
      ...(treapta !== TREAPTA_SCARA ? { treapta_m: treapta } : {}),
    },
  });
  return { doc: d, ales: { fel: "mobila", i: lista.length - 1 } };
}

export function adaugaMobila(
  doc: TronsonDoc, kind: string, p: Pt, masura?: { w: number; h: number },
  nucleu = true,
): { doc: TronsonDoc; ales: Ales } {
  // MASA CU SCAUNE NU E O PIESĂ, E UN ANSAMBLU: felul `masa_scaune` există
  // numai în paletă, iar în document se desface în blat și scaune legate prin
  // `grup`. V. `adaugaMasaCuScaune`.
  if (kind === FEL_MASA_CU_SCAUNE) return adaugaMasaCuScaune(doc, p, masura);
  const d = clona(doc);
  const lista = (d.plan!.furniture = d.plan!.furniture ?? []);
  const r = (v: number) => Math.round(v * 1000) / 1000;
  lista.push({ kind, x: { a: r(p[0]) }, y: { a: r(p[1]) },
               w_m: masura?.w ?? 1.6, h_m: masura?.h ?? 0.9, rot_deg: 0 });
  // on a module with a core a lift counts only as a core part: it is written
  // as one in the same change, so one undo takes the placement back (OB-39)
  if (nucleu && kind === "lift") return parteDinPiesa(d, lista.length - 1);
  return { doc: d, ales: { fel: "mobila", i: lista.length - 1 } };
}

/** FELUL DIN PALETĂ care pune masa CU scaunele ei.
 *
 *  Nu ajunge niciodată într-un document: `adaugaMobila` îl desface pe loc. E
 *  în catalog doar ca să aibă buton, nume și glif ca orice altă piesă — iar
 *  cine citește un document nu dă peste un fel pe care motoarele nu-l știu. */
export const FEL_MASA_CU_SCAUNE = "masa_scaune";

/** MASA DE PRÂNZ CU SCAUNELE EI, pusă dintr-o apăsare.
 *
 *  Cerut de arhitect (19.10.2026): «fa o opțiune de masa care vine cu toate
 *  scaunele deja ca o unitate. Când plasezi masa sa poți sa apeși pe ea si sa
 *  ai o opțiune, separa elemente si se detașează masa si scaunele».
 *
 *  SCAUNELE SE SCRIU ÎNAINTEA MESEI, și asta e chiar cererea „masa deasupra”:
 *  în SVG «deasupra» înseamnă «desenat mai târziu», iar blatul se desenează
 *  umplut cu alb (v. `arhMobilierPlan::R`), deci acoperă bucata de scaun
 *  trasă sub el. Fără ordinea asta s-ar fi văzut linia scaunului peste masă —
 *  exact ce s-a cerut scos. Nu se scrie `rang` pe blat: rangul e pentru
 *  hotărârile omului («Adu în față»), iar aici ordinea din listă spune destul,
 *  la fel ca la covorul de sub pat.
 *
 *  ALEGEREA CADE PE MASĂ, nu pe ultimul scaun: cine tocmai a pus ansamblul
 *  vrea panoul mesei, iar un click pe oricare piesă alege oricum tot grupul. */
export function adaugaMasaCuScaune(
  doc: TronsonDoc, p: Pt, masura?: { w: number; h: number },
): { doc: TronsonDoc; ales: Ales } {
  const cat = felulPiesei("masa");
  const w = masura?.w ?? cat?.w ?? 1.6;
  const h = masura?.h ?? cat?.h ?? 0.9;
  const d = clona(doc);
  const lista = (d.plan!.furniture = d.plan!.furniture ?? []);
  const g = grupNou(d);
  for (const s of scauneleMesei([p[0], p[1]], w, h)) {
    lista.push({ kind: s.kind, x: { a: r3(s.c[0]) }, y: { a: r3(s.c[1]) },
                 w_m: s.w, h_m: s.h, rot_deg: s.deg, grup: g });
  }
  lista.push({ kind: "masa", x: { a: r3(p[0]) }, y: { a: r3(p[1]) },
               w_m: r3(w), h_m: r3(h), rot_deg: 0, grup: g });
  return { doc: d, ales: { fel: "mobila", i: lista.length - 1 } };
}

/** Mută o piesă. Liber, nu pe o linie: mobila nu se sprijină pe nimic.
 *
 *  ȘI APARATELE EI CU EA. Priza de la capul patului nu e lângă pat fiindcă a
 *  nimerit acolo, e lângă el fiindcă asta e regula — deci se recalculează, nu
 *  se translatează: patul împins spre alt perete își duce prizele pe peretele
 *  ăla, nu la un metru în aer de vechiul. V. `aparateleUrmeazaMobila`. */
/** Cât de departe de pat mai e o noptieră A LUI, în metri. Noptiera stă lipită
 *  de pat, iar rostul desenat între ele e de ordinul centimetrilor
 *  (`finalizare.NOPTIERA_ROST_M` = 2 cm).
 *
 *  ZECE CENTIMETRI, nu un sfert de metru. Cerut de arhitect (23.10.2026):
 *  «noptierele se agață de pat prea repede. Dacă sunt la o distanță de 25 cm
 *  deja se mișcă cu patul. Aș vrea să micșorăm distanța, să se agațe dacă sunt
 *  mai aproape de 10 cm». Un sfert de metru fusese ales ca să lase loc
 *  rotunjirii și unei așezări de mână; în practică el înhăța și noptiere pe
 *  care omul le pusese DEOSEBIT de pat, iar atunci patul tras le lua cu el
 *  fără să fi cerut-o nimeni. Zece centimetri sunt tot de cinci ori rostul
 *  desenat, deci lipirea adevărată intră în continuare. */
const NOPTIERA_LANGA_PAT_M = 0.10;

/** NOPTIERELE UNUI PAT — cele lipite de el, pe care le duce după el.
 *
 *  Cerut de arhitect (13.10.2026): «when moving a bed should move it with the
 *  side tables too if it has any next to it». Așa se mută pe planșă: capătul
 *  patului e un ansamblu, nu trei piese care se nimeresc alături, iar mutat
 *  singur patul lăsa noptierele plutind în mijlocul camerei.
 *
 *  NUMAI CELE CARE SE ATING DE EL. Se măsoară pe cutiile lor, nu pe distanța
 *  dintre centre: o noptieră a patului de alături, într-un dormitor cu două
 *  paturi, e la fel de aproape de centru și n-are ce căuta în mutare. */
export function noptiereleLui(doc: TronsonDoc, i: number): number[] {
  const piese = doc.plan?.furniture ?? [];
  const pat = piese[i];
  if (!pat || (pat.kind !== "pat" && pat.kind !== "pat_o_persoana")) return [];
  const c = cutiaPiesei(pat);
  const out: number[] = [];
  for (let j = 0; j < piese.length; j++) {
    if (j === i || piese[j].kind !== "noptiera") continue;
    if (departareaCutiilor(c, cutiaPiesei(piese[j])) <= NOPTIERA_LANGA_PAT_M) {
      out.push(j);
    }
  }
  return out;
}

/** Cutia unei piese pe axele planului, cu rotirea ei luată în seamă.
 *
 *  Măsurile lipsă sunt ale CATALOGULUI: o piesă pusă la mărimea obișnuită nu-și
 *  scrie măsurile în document (v. `PiesaDoc.w_m`), iar socotite 0 noptiera ar fi
 *  ieșit un punct și nu s-ar fi atins niciodată de pat. */
function cutiaPiesei(f: PiesaDoc) {
  const cat = felulPiesei(f.kind);
  const w0 = f.w_m ?? cat?.w ?? 0.5;
  const h0 = f.h_m ?? cat?.h ?? 0.5;
  const a = ((f.rot_deg ?? 0) * Math.PI) / 180;
  const cs = Math.abs(Math.cos(a)), sn = Math.abs(Math.sin(a));
  const w = (w0 * cs + h0 * sn) / 2;
  const h = (w0 * sn + h0 * cs) / 2;
  return { x0: f.x.a - w, x1: f.x.a + w, y0: f.y.a - h, y1: f.y.a + h };
}

/** Cât spațiu rămâne între două cutii; 0 dacă se ating sau se suprapun. */
function departareaCutiilor(a: ReturnType<typeof cutiaPiesei>,
                            b: ReturnType<typeof cutiaPiesei>): number {
  const dx = Math.max(0, Math.max(a.x0 - b.x1, b.x0 - a.x1));
  const dy = Math.max(0, Math.max(a.y0 - b.y1, b.y0 - a.y1));
  return Math.hypot(dx, dy);
}

/** PASUL LA CARE SE LIPEȘTE O MĂRIME TRASĂ DE MÂNĂ. Același 5 cm ca al
 *  mutărilor de pe pânză (`Panza::PAS`): un plan tras din mână iese altfel cu
 *  piese de 1,847 m, iar apoi cineva citește 1,85 pe desen și 1,847 în
 *  document. Cerut anume pe 19.10.2026: «increase or decrease the size by 5 cm
 *  at a time». */
export const PAS_MARIME = 0.05;

/** Cât zid trebuie să rămână între un gol și colțul peretelui. 10 cm: sub atât
 *  nu mai e perete, e un spalet care nu se poate zidi, iar golul lipit de colț
 *  desface nodul cu vecinul. */
export const MARGINE_LA_COLT = 0.1;

/** TRAGEREA UNEI MUCHII A UNEI PIESE: muchia apucată pleacă, cea OPUSĂ rămâne
 *  pe loc — aceeași regulă ca la nucleu și la zid (v. `Apucare`).
 *
 *  ==========================================================================
 *  SE SOCOTEȘTE ÎN CADRUL PIESEI, nu în al lumii. O piesă rotită are laturile
 *  ei, iar «mai lat» înseamnă pe axa ei lungă, nu pe est-vest: punctul de sub
 *  deget se întoarce întâi în reperul piesei, acolo se schimbă semilățimile, și
 *  abia centrul rezultat se întoarce în lume. Fără asta, trasă de latura din
 *  dreapta, o canapea pusă la 30° ar crește oblic și s-ar și deplasa.
 *
 *  MĂRIMEA SE ROTUNJEȘTE LA `PAS_MARIME`, DAR CENTRUL NU: muchia opusă trebuie
 *  să rămână EXACT unde era, iar ea stă la jumătate de mărime de centru — deci
 *  centrul se mută cu jumătate din cât s-a schimbat mărimea, oricât ar fi aia.
 *  Rotunjind și centrul, piesa ar aluneca sub mână la fiecare treaptă.
 *
 *  DE CE SE POATE IARĂȘI TRAGE DE MOBILĂ. Mânerele au fost scoase pe
 *  `daa91840`, fiindcă bara de latură (7 px, adică 19 cm la 37 px/m) se întinde
 *  PESTE muchia piesei și acoperea rostul dintre ea și zidul de lângă: pe M3,
 *  liftul stă la 6,4 cm de fața lui W49, iar cu liftul ales nu exista un pixel
 *  din care zidul să mai poată fi apucat. Asta era o problemă de ZONĂ DE
 *  APUCAT, nu de pas, iar pasul de 5 cm n-o atinge. Se întorc deci numai pentru
 *  MOBILĂ — nu pentru lifturi și scări, care «au dimensiuni fixe, așa vin din
 *  fabrică» —, iar mânerele lor se desenează ÎNĂUNTRUL piesei (v. `Panza`), ca
 *  să nu mai treacă niciodată peste rostul de lângă ea.
 *  ========================================================================== */
/** MĂRIMEA SUB CARE UN FEL DE PIESĂ NU MAI ÎNSEAMNĂ NIMIC, în metri.
 *
 *  Numai felurile care chiar au o măsură de șantier; restul se opresc la pragul
 *  obișnuit de 10 cm (v. `redimMobila`). Tabelul e explicit, nu dedus dintr-un
 *  raport: ce e prea mic pentru un lavoar nu e aceeași întrebare ca ce e prea
 *  mic pentru o măsuță. */
const MINIMUL_PIESEI: Record<string, [number, number]> = {
  lavoar: [0.3, 0.2],
};

export function redimMobila(
  doc: TronsonDoc, i: number, apucare: Apucare, la: Pt,
): TronsonDoc {
  const f = doc.plan?.furniture?.[i];
  if (!f || (!apucare.sx && !apucare.sy)) return doc;
  // STRUCTURA CU MĂSURI DATE nu se trage, și oprirea stă AICI, nu numai în
  // pânză: o pază pusă doar în desen e una pe care o ocolește oricine cheamă
  // funcția — exact ce probează `model.test::un puț NU se redimensionează`.
  //
  // GHENA FACE EXCEPȚIE, fiindcă n-are catalog (v. `parteaSeCroieste`). Ea e
  // o PIESĂ, nu o parte de nucleu — în paleta «Structură», scrisă în
  // `plan.furniture` cu `structura: true` —, iar steagul ăla o scotea de sub
  // mână laolaltă cu lifturile: «it still doesn't let me modify the ghena»
  // (20.10.2026). Întrebarea nu e «e structură?», ci «are măsuri din fabrică?».
  if (felulPiesei(f.kind)?.structura && !parteaSeCroieste(f.kind)) return doc;
  const w0 = f.w_m ?? 0.6, h0 = f.h_m ?? 0.6;
  const rad = ((f.rot_deg ?? 0) * Math.PI) / 180;
  const cos = Math.cos(rad), sin = Math.sin(rad);
  // Punctul de sub deget, în reperul piesei.
  const dx = la[0] - f.x.a, dy = la[1] - f.y.a;
  const u = dx * cos + dy * sin;
  const v = -dx * sin + dy * cos;
  const rotunj = (x: number) => Math.round(x / PAS_MARIME) * PAS_MARIME;
  // CÂT DE MICĂ POATE AJUNGE PIESA. Pragul obișnuit e două trepte — 10 cm —,
  // adică «să mai fie ceva acolo». Un fel care are o măsură de ȘANTIER sub care
  // obiectul nu mai există și-o scrie în `MINIMUL_PIESEI`: cerut de arhitect
  // pentru lavoar (22.10.2026), «lavoarul [nu ar trebui să poată fi] mai mic de
  // 30×20 cm». Oprirea stă AICI, nu în desen: una pusă doar în desen e una pe
  // care o ocolește oricine cheamă funcția, iar panoul scrie tot prin ea.
  const [wMin, hMin] = MINIMUL_PIESEI[f.kind] ?? [PAS_MARIME * 2, PAS_MARIME * 2];
  let w = w0, h = h0;
  if (apucare.sx) w = Math.max(wMin, rotunj(w0 / 2 + apucare.sx * u));
  if (apucare.sy) h = Math.max(hMin, rotunj(h0 / 2 + apucare.sy * v));
  if (Math.abs(w - w0) < 1e-9 && Math.abs(h - h0) < 1e-9) return doc;
  // Centrul se mută cu jumătate din creștere, pe axele PIESEI, ca muchia opusă
  // să stea pe loc.
  const du = (apucare.sx * (w - w0)) / 2;
  const dv = (apucare.sy * (h - h0)) / 2;
  const d = schimbaMobila(doc, i, { w, h });
  const g = d.plan?.furniture?.[i];
  if (!g) return d;
  g.x = { ...g.x, a: r3(f.x.a + du * cos - dv * sin) };
  g.y = { ...g.y, a: r3(f.y.a + du * sin + dv * cos) };
  // Aparatele piesei o urmează: priza de la capul patului se socotește din
  // cadrul lui, iar cadrul tocmai s-a schimbat.
  urmeazaInLoc(d);
  return d;
}

/** MUTĂ MAI MULTE PIESE deodată, ca un corp — selecția multiplă.
 *
 *  UNIUNEA ÎNTÂI, MUTAREA PE URMĂ. `mutaMobila` chemată pe rând ar fi dus de
 *  două ori piesele care se află în ansamblul a două alese — un pat ales
 *  împreună cu noptiera lui, de pildă, fiindcă fiecare chemare își duce și
 *  grupul (`cePleacaCu`). Adunat întâi tot ce pleacă, fiecare piesă se mută o
 *  singură dată, oricâte alese ar cere-o.
 *
 *  CĂUTAT ÎNAINTE DE MUTARE, ca la una singură: după ea patul nu mai e lângă
 *  noptiere, deci nu s-ar mai recunoaște ca fiind ale lui. */
export function mutaMobilaMulte(
  doc: TronsonDoc, indici: readonly number[], dep: Pt,
): TronsonDoc {
  if (!indici.length || (!dep[0] && !dep[1])) return doc;
  const cu = new Set<number>();
  for (const i of indici) for (const k of cePleacaCu(doc, i)) cu.add(k);
  if (!cu.size) return doc;
  const d = clona(doc);
  for (const k of cu) {
    const g = d.plan?.furniture?.[k];
    if (!g) continue;
    g.x = { ...g.x, a: r3(g.x.a + dep[0]) };
    g.y = { ...g.y, a: r3(g.y.a + dep[1]) };
    if (g.scara) duScara(g.scara, dep);          // v. `mutaMobila`
  }
  urmeazaInLoc(d, [...cu]);
  return d;
}

export function mutaMobila(doc: TronsonDoc, i: number, dep: Pt): TronsonDoc {
  const f = doc.plan?.furniture?.[i];
  if (!f || (!dep[0] && !dep[1])) return doc;
  const d = clona(doc);
  // PATUL ÎȘI DUCE NOPTIERELE, iar ANSAMBLUL ÎȘI DUCE PIESELE. Căutate ÎNAINTE
  // de mutare: după ea patul nu mai e lângă ele, deci nu s-ar mai recunoaște
  // ca fiind ale lui.
  const cu = cePleacaCu(doc, i);
  for (const k of cu) {
    const g = d.plan!.furniture![k];
    g.x = { ...g.x, a: r3(g.x.a + dep[0]) };
    g.y = { ...g.y, a: r3(g.y.a + dep[1]) };
    // O SCARĂ ÎȘI DUCE RAMPELE, aici ca peste tot: ele stau în coordonate de
    // PLAN, deci mutată numai cutia, scara ar fi rămas desenată unde era.
    if (g.scara) duScara(g.scara, dep);
  }
  urmeazaInLoc(d, cu);
  return d;
}

// --------------------------------------------------- ansambluri de mobilă ---
//
// UN ANSAMBLU E O MULȚIME DE PIESE CU ACELAȘI `grup` (v. `PiesaDoc.grup`):
// masa cu scaunele ei, sau ce a legat omul cu «Unește elemente». Se mută, se
// rotește, se oglindește, se șterge și se dublează ÎNTREG, iar un click pe
// oricare piesă îl alege pe tot.
//
// NU E O PIESĂ COMPUSĂ. Fiecare scaun rămâne un rând în `plan.furniture`, cu
// felul, mărimea și unghiul lui — v. nota lungă de la `PiesaDoc.grup` pentru
// de ce s-a ales așa (și de ce desenul mesei nu-și mai face singur scaune).

/** PIESELE ANSAMBLULUI din care face parte `i` — ea inclusă, în ordinea din
 *  document. O piesă fără `grup` e singură în ansamblul ei. */
export function grupulLui(doc: TronsonDoc, i: number): number[] {
  const piese = doc.plan?.furniture ?? [];
  if (!piese[i]) return [];
  const g = piese[i].grup;
  if (!g) return [i];
  const out: number[] = [];
  piese.forEach((f, j) => { if (f.grup === g) out.push(j); });
  return out;
}

/** TOT CE PLEACĂ ÎMPREUNĂ CU PIESA `i`: ansamblul ei, plus noptierele lipite
 *  de fiecare pat din el. Două reguli diferite — una scrisă în document, alta
 *  citită din geometrie —, dar o singură listă pentru cine mută. */
function cePleacaCu(doc: TronsonDoc, i: number): number[] {
  const a = grupulLui(doc, i);
  const cu = new Set(a);
  for (const k of a) for (const n of noptiereleLui(doc, k)) cu.add(n);
  return [...cu];
}

/** Un nume de ansamblu care nu e luat. Ca `idNou`, și din același motiv: la
 *  dublare sau la lipire, copia nu are voie să cadă în ansamblul altcuiva. */
export function grupNou(doc: TronsonDoc): string {
  const luate = new Set(
    (doc.plan?.furniture ?? []).map((f) => f.grup).filter(Boolean));
  for (let n = 1; n < 9999; n++) if (!luate.has(`G${n}`)) return `G${n}`;
  return `G${luate.size + 1}`;
}

/** CUTIA ANSAMBLULUI, pe axele planului — centrul ei e punctul în jurul căruia
 *  se rotește și se oglindește. Centrul MESEI ar fi fost mai simplu de scris,
 *  dar ar fi cerut ca un ansamblu să aibă o piesă „principală”; cutia nu cere
 *  nimic și dă același lucru pe un ansamblu simetric. */
function cutiaGrupului(doc: TronsonDoc, indici: readonly number[]): Pt | null {
  const piese = doc.plan?.furniture ?? [];
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const i of indici) {
    const f = piese[i];
    if (!f) continue;
    const c = cutiaPiesei(f);
    x0 = Math.min(x0, c.x0); y0 = Math.min(y0, c.y0);
    x1 = Math.max(x1, c.x1); y1 = Math.max(y1, c.y1);
  }
  return Number.isFinite(x0) ? [(x0 + x1) / 2, (y0 + y1) / 2] : null;
}

/** UNEȘTE PIESELE DATE ÎNTR-UN ANSAMBLU.
 *
 *  Cerut de arhitect (19.10.2026): «sa ai si opțiunea de mulți select mobila
 *  si sa apeși unește elemente si sa devină un element pe care îl putem muta
 *  împreună si rămâne o unitate».
 *
 *  O PIESĂ CARE E DEJA ÎNTR-UN ANSAMBLU ÎL ADUCE PE TOT. Altfel unirea ar
 *  rupe ansamblul din care a fost luată: scaunul ales dintr-o masă ar pleca în
 *  ansamblul nou, iar masa ar rămâne cu cinci scaune și fără al șaselea. Cine
 *  vrea chiar scaunul ăla singur îl separă întâi. */
export function uneMobila(doc: TronsonDoc, indici: readonly number[]): TronsonDoc {
  const toate = new Set<number>();
  for (const i of indici) for (const j of grupulLui(doc, i)) toate.add(j);
  if (toate.size < 2) return doc;
  const d = clona(doc);
  const g = grupNou(doc);
  for (const i of toate) d.plan!.furniture![i].grup = g;
  return d;
}

/** DESFACE ANSAMBLUL din care face parte `i`: piesele rămân unde sunt, dar de
 *  acum fiecare se mută singură. Perechea lui «Unește elemente», și singurul
 *  fel de a ajunge la un scaun al mesei puse din paletă. */
export function separaMobila(doc: TronsonDoc, i: number): TronsonDoc {
  const g = doc.plan?.furniture?.[i]?.grup;
  if (!g) return doc;
  const d = clona(doc);
  for (const f of d.plan!.furniture!) if (f.grup === g) delete f.grup;
  return d;
}

/** ADU ÎN FAȚĂ / TRIMITE ÎN SPATE — v. `PiesaDoc.rang`.
 *
 *  Cerut de arhitect (19.10.2026): «Adauga opțiunea pentru mobilier ceva de
 *  genul "Bring forward" ca sa se vadă deasupra unei alte piese de mobilier.
 *  Dar si make background sau ceva sa se vadă ce e sub».
 *
 *  DE TOT ÎN FAȚĂ, NU CU UN PAS. Un pas cere să știi peste CINE treci, iar pe
 *  planșă piesele nu stau într-un teanc, ci împrăștiate: „cu unul mai sus” nu
 *  schimbă nimic vizibil decât dacă nimerește exact piesa de dedesubt, și
 *  atunci arhitectul apasă de cinci ori fără să vadă nimic între apăsări. Ce
 *  s-a cerut e să se VADĂ una peste alta, și asta se obține dintr-o apăsare.
 *
 *  ANSAMBLUL MERGE ÎNTREG: o masă adusă în față fără scaunele ei s-ar desface
 *  în desen, deși în document e tot o unitate. */
export function rangMobila(doc: TronsonDoc, i: number,
                           unde: "fata" | "spate"): TronsonDoc {
  return rangMobilaMulte(doc, [i], unde);
}

/** ACELAȘI LUCRU PE MAI MULTE PIESE — selecția multiplă, ca un corp.
 *
 *  TOATE ALESELE CAPĂTĂ ACELAȘI RANG, nu ranguri în șir. Pe planșă piesele nu
 *  stau într-un teanc, iar ce s-a cerut e să se VADĂ deasupra celorlalte, dintr-o
 *  apăsare (v. antetul): între ele ordinea nu e o întrebare pe care și-o pună
 *  cineva, iar un rang pe fiecare ar fi dat cinci apăsări de istoric pentru un
 *  singur gest.
 *
 *  MARGINEA SE SOCOTEȘTE EXCLUZÂND TOATE ALESELE, nu doar una: luate pe rând,
 *  prima mutată în față ar fi urcat marginea pe care o citește a doua, deci
 *  cele cinci piese ar fi ieșit pe cinci ranguri crescătoare, iar ultima — nu
 *  grupul — ar fi rămas deasupra.
 *
 *  Ranguri socotite pe cel EFECTIV al celorlalte piese — cel scris când există,
 *  altfel al felului —, fiindcă ăla e ce se vede pe ecran. */
/** ADU ÎN FAȚĂ / TRIMITE ÎN SPATE, pe ziduri.
 *
 *  Perechea lui `rangMobilaMulte`, și scrisă la fel: rangul nou e cu unu peste
 *  cel mai mare (sau sub cel mai mic) dintre zidurile RĂMASE, deci apăsat de
 *  două ori butonul nu mai are ce muta. Cerut de arhitect (26.10.2026): «nu văd
 *  un buton de adu în față pe pereți, și ar trebui să existe».
 *
 *  ZIDURILE SUNT DEJA PESTE MOBILĂ (v. `Desen`); asta alege ordinea ÎNTRE ele,
 *  acolo unde două se suprapun. */
export function rangPeretiMulti(
  doc: TronsonDoc, indici: readonly number[], unde: "fata" | "spate",
): TronsonDoc {
  const ziduri = doc.plan?.walls ?? [];
  const cu = new Set(indici.filter((i) => ziduri[i]));
  if (!cu.size) return doc;
  let min = Infinity, max = -Infinity;
  ziduri.forEach((w, j) => {
    if (cu.has(j)) return;
    const r = w.rang ?? 0;
    min = Math.min(min, r); max = Math.max(max, r);
  });
  if (!Number.isFinite(min)) return doc;           // numai alesele în plan
  const nou = unde === "fata" ? max + 1 : min - 1;
  const d = clona(doc);
  for (const j of cu) d.plan!.walls![j].rang = nou;
  return d;
}

export function rangMobilaMulte(
  doc: TronsonDoc, indici: readonly number[], unde: "fata" | "spate",
): TronsonDoc {
  const piese = doc.plan?.furniture ?? [];
  const cu = new Set<number>();
  for (const i of indici) {
    if (piese[i]) for (const j of grupulLui(doc, i)) cu.add(j);
  }
  if (!cu.size) return doc;
  let min = Infinity, max = -Infinity;
  piese.forEach((f, j) => {
    if (cu.has(j)) return;
    const r = f.rang ?? rangDesen(f.kind);
    min = Math.min(min, r); max = Math.max(max, r);
  });
  if (!Number.isFinite(min)) return doc;           // numai alesele în plan
  const nou = unde === "fata" ? max + 1 : min - 1;
  const d = clona(doc);
  for (const j of cu) d.plan!.furniture![j].rang = nou;
  return d;
}

/** ROTEȘTE ANSAMBLUL în jurul centrului cutiei lui: fiecare piesă își plimbă
 *  centrul pe cerc ȘI își adună gradele la unghiul propriu. Rotind numai
 *  unghiurile, scaunele ar privi în altă parte stând pe loc; rotind numai
 *  centrele, ar aluneca în jurul mesei cu spatele unde era. */
export function rotesteGrupul(doc: TronsonDoc, i: number,
                              grade: number): TronsonDoc {
  const cu = grupulLui(doc, i);
  if (cu.length < 2 || !grade) return doc;
  const c = cutiaGrupului(doc, cu);
  if (!c) return doc;
  const roteste = rotitorul(c, grade);
  const d = clona(doc);
  for (const j of cu) {
    const f = d.plan!.furniture![j];
    const [x, y] = roteste([f.x.a, f.y.a]);
    f.x = { ...f.x, a: x };
    f.y = { ...f.y, a: y };
    f.rot_deg = r3(in360((f.rot_deg ?? 0) + grade));
  }
  urmeazaInLoc(d, cu);
  return d;
}

/** OGLINDEȘTE ANSAMBLUL pe axa dată, tot în jurul centrului cutiei: centrele
 *  trec de partea cealaltă, iar fiecare piesă își întoarce simbolul cu aceeași
 *  socoteală ca una singură (v. `mainaOglindita`). */
export function oglindesteGrupul(doc: TronsonDoc, i: number,
                                 axa: Axa): TronsonDoc {
  const cu = grupulLui(doc, i);
  if (cu.length < 2) return doc;
  const c = cutiaGrupului(doc, cu);
  if (!c) return doc;
  const oglinda = oglinditorul(c, axa);
  const d = clona(doc);
  for (const j of cu) {
    const f = d.plan!.furniture![j];
    const [x, y] = oglinda([f.x.a, f.y.a]);
    f.x = { ...f.x, a: x };
    f.y = { ...f.y, a: y };
    const m = mainaOglindita(f.rot_deg ?? 0, f.flip_x === true,
                             f.flip_y === true, axa);
    f.rot_deg = m.rot;
    if (m.oglX) f.flip_x = true; else delete f.flip_x;
    if (m.oglY) f.flip_y = true; else delete f.flip_y;
  }
  urmeazaInLoc(d, cu);
  return d;
}

/** SCHIMBĂ FELUL UNEI CAMERE — «baie» în loc de «dormitor».
 *
 *  Felul nu se vede din formă (v. `camere.ts`), deci e singurul lucru despre
 *  o cameră care se poate spune doar de mână. Se scrie pe rândul ei din
 *  `plan.rooms`; derivarea camerelor din pereți îl păstrează pe suprapunere,
 *  deci rămâne al aceleiași camere și după ce se mută zidurile din jur.
 *  Finisajele, mobila și aparatele scrise în cameră rămân: cine le-a pus le-a
 *  pus pentru camera asta, nu pentru felul ei. */
export function schimbaCamera(doc: TronsonDoc, i: number,
                              ce: { kind?: string; label?: string },
): TronsonDoc {
  const r = doc.plan?.rooms?.[i];
  if (!r) return doc;
  // NUMELE SCRIS se schimbă singur, fără să atingă felul: e un cuvânt pe
  // planșă, nu o afirmație despre clădire. De-aia nu duce nimic după el —
  // nici mobila, nici declarația variantei — și de-aia se poate scrie și pe o
  // cameră al cărei fel rămâne neatins.
  if (ce.label !== undefined && ce.label !== r.label) {
    const d = clona(doc);
    const cam = d.plan!.rooms[i];
    if (ce.label) cam.label = ce.label.slice(0, 40);
    else delete cam.label;
    return ce.kind == null || ce.kind === r.kind
      ? d : schimbaCamera(d, i, { kind: ce.kind });
  }
  if (ce.kind == null || ce.kind === r.kind) return doc;
  const d = clona(doc);
  d.plan!.rooms[i].kind = ce.kind;
  // FELUL SCHIMBAT ÎN «BALCON» — SAU DIN EL — MIȘCĂ ȘI DECLARAȚIA VARIANTEI,
  // din același motiv ca la ștergere: locuința are afară ce spune desenul.
  if (ce.kind === "balcony" || r.kind === "balcony") {
    const cam = d.plan!.rooms[i];
    if (ce.kind === "balcony" && !cam.outdoor_kind) {
      cam.outdoor_kind = inPlic(centrul(inelul(cam.poly)), anvelopa(d))
        ? "loggia" : "balcony";
    }
    if (ce.kind !== "balcony") delete cam.outdoor_kind;
    declaraExteriorul(d, locuintaModulului(d, cam.side, cam.flat ?? 0),
                      ce.kind === "balcony" ? cam.outdoor_kind ?? "balcony" : null);
  }
  // MOBILA ȘI INSTALAȚIA RĂMÂN. Întâi plecau cu felul vechi — «patul dintr-o
  // baie e o greșeală» —, dar în practică felul se corectează des pe o cameră
  // deja amenajată (un «dormitor» care e de fapt birou), iar ștergerea lua cu
  // ea munca de mână. Cine vrea așezarea felului nou apasă «Mobilează camera»
  // și «Instalația camerei»: acolo e gestul care înlocuiește. Ștergerea mai
  // strica și legăturile `pentru` ale aparatelor, care arată mobila prin poziție.
  return d;
}

/** MOBILEAZĂ AUTOMAT camera `i`: scoate ce e în ea și pune așezarea regulilor
 *  (`design/mobilare`). Întoarce documentul neschimbat pentru un fel fără
 *  regulă. Un singur pas de istoric — cine nu vrea ce a ieșit apasă ⌘Z. */
export function mobileazaCamera(doc: TronsonDoc, i: number): TronsonDoc {
  const r = doc.plan?.rooms?.[i];
  if (!r || !MOBILABILE.has(r.kind)) return doc;
  const plan = deriva(doc);
  const cam = plan.camere[i];
  if (!cam) return doc;
  const noi = asezareaNoua(cam, plan.goluri);
  const d = clona(doc);
  const contur = inelul(r.poly);
  d.plan!.furniture = [
    ...(d.plan!.furniture ?? []).filter((f) => !inPoligon([f.x.a, f.y.a], contur)),
    ...noi,
  ];
  return d;
}

/** Cât de aproape de o muchie a camerei stă o piesă ca să fie PARCATĂ pe ea.
 *
 *  30 cm: un dulap lipit de zid lasă un rost de câțiva centimetri, iar o
 *  canapea trasă puțin în cameră tot a peretelui din spate e. Mai mult ar
 *  prinde și măsuța din fața ei, care nu ține de niciun zid. */
const PARCATA_PE_ZID = 0.3;

/** CÂT PLEACĂ O PIESĂ, pe o axă, când camera ei își schimbă marginile.
 *
 *  Muchia de care e parcată o duce cu ea. Nu e parcată pe niciuna — măsuța din
 *  mijloc — atunci pleacă cu muchia care s-a mutat: camera s-a strâns dinspre
 *  partea aia, deci tot ce e în ea vine încoace. Sunt parcate amândouă (o cadă
 *  cât camera de lată) e același răspuns. Mutate amândouă, se împarte după
 *  locul piesei între ele.
 *
 *  ȘI NIMENI NU RĂMÂNE ÎN CALEA ZIDULUI CARE VINE. Regulile de sus spun după
 *  CE MUCHIE merge piesa, și atât ajungea cât timp camera se lărgea: acolo
 *  peretele pleacă de lângă mobilă, deci nimic nu se lovește de nimic. Camera
 *  care se STRÂNGE cere și celălalt jumătate de răspuns — cerut anume: «când
 *  îl împing nu mai împinge mobila, merge doar dacă extind camera».
 *
 *  Două lucruri se vedeau, amândouă pe M1 cu livingul strâns cu 1,50 m: piesele
 *  parcate pe peretele rămas pe loc (canapeaua) stăteau cuminți sub zidul care
 *  venea peste ele, iar cele din mijloc, duse cu TOATĂ deplasarea, ieșeau prin
 *  fațada din capătul celălalt — trei piese afară din modul. Deci deplasarea se
 *  taie la cutia camerei DE ACUM: piesa nu rămâne în urma muchiei care vine
 *  (peretele o împinge înaintea lui, exact cât să scape de sub el) și nu trece
 *  de muchia cealaltă (se oprește lipită de ea).
 *
 *  CU IEȘIREA ÎN ZID PE CARE O AVEA. Se măsoară de la `max(min, p0)`, nu de la
 *  `min`: o piesă desenată un centimetru în tencuială — sau o cameră în L, a
 *  cărei cutie e mai mică decât camera — nu trebuie smucită înăuntru la fiecare
 *  zid mutat prin altă parte a planului. Piesa mai lată decât camera rămasă n-are
 *  unde încăpea: se lipește de muchia spre care mergea. */
function deplasareaInCamera(
  cutie0: [number, number], cutie1: [number, number],
  min: number, max: number,
): number {
  const [p0, p1] = cutie0;
  const [q0, q1] = cutie1;
  const d0 = q0 - p0;
  const d1 = q1 - p1;
  if (Math.abs(d0) <= 0.004 && Math.abs(d1) <= 0.004) return 0;
  const peJos = min - p0 <= PARCATA_PE_ZID;
  const peSus = p1 - max <= PARCATA_PE_ZID;
  const dupaMuchie = () => {
    if (peJos && !peSus) return d0;
    if (peSus && !peJos) return d1;
    if (Math.abs(d0) <= 0.004) return d1;
    if (Math.abs(d1) <= 0.004) return d0;
    const t = p1 > p0
      ? Math.min(1, Math.max(0, ((min + max) / 2 - p0) / (p1 - p0))) : 0;
    return d0 + (d1 - d0) * t;
  };
  const dep = dupaMuchie();
  // Cât ar trebui mutată piesa ca să stea fix pe o muchie sau pe cealaltă.
  const jos = q0 - Math.max(min, p0);
  const sus = q1 - Math.min(max, p1);
  if (jos <= sus) return Math.min(Math.max(dep, jos), sus);
  return Math.abs(dep - jos) <= Math.abs(dep - sus) ? jos : sus;
}

/** CÂT DE APROAPE DE UN ZID STĂ UN OBIECT CA SĂ PLECE CU EL: un metru și
 *  jumătate, măsurat de la conturul obiectului la conturul zidului.
 *
 *  Cerut anume, ca regulă unică: «toate obiectele la o distanță de maxim 1,5
 *  metri de perete să fie mutate cu peretele oricând se mută acel perete».
 *
 *  UN METRU ȘI JUMĂTATE E O ALEGERE, nu o măsură dedusă: e cât ține fâșia de
 *  mobilare de lângă un zid — un dulap de 60 cm cu ușa deschisă, o canapea cu
 *  măsuța ei, blatul cu scaunul tras de la el. Peste atât, obiectul stă în
 *  mijlocul camerei și nu mai e al niciunui perete. */
const LANGA_ZID = 1.5;

/** CÂT DE APROAPE STAU DOUĂ OBIECTE CA SĂ FIE UNUL SINGUR — o palmă.
 *
 *  Pragul contagiunii (v. `obiecteleUrmeazaZidurile`): sub atâta, două piese nu
 *  sunt vecine, ci bucăți ale aceluiași lucru — corpurile unui blat, patul cu
 *  noptierele lui, televizorul pe comoda lui. Citit din catalog, nu ales: v.
 *  măsurătoarea din antetul contagiunii. */
const LIPITE = 0.1;

/** APARATELE URMEAZĂ ZIDUL DE LÂNGĂ ELE, pe fiecare axă.
 *
 *  ==========================================================================
 *  MOBILA NU MAI PLEACĂ CU NICIUN ZID (12.10.2026, Bianca): «mobila să nu se
 *  mute când mutăm perete — stă pe loc, camera doar se face mai mare, și pe
 *  urmă mutăm noi mobila».
 *
 *  Întoarce regula cerută de Stefan pe 23.09.2026 («toate obiectele la o
 *  distanță de maxim 1,5 metri de perete să fie mutate cu peretele oricând se
 *  mută acel perete»), și o întoarce în tot: nu numai la peretele care se
 *  depărtează, ci și la cel care vine peste piesă. Întrebată anume, Bianca a
 *  ales varianta întreagă — o piesă peste care trece zidul rămâne unde e, și
 *  se mută cu mâna. Previzibilitatea e ce s-a cerut: mobila stă unde ai
 *  pus-o, oricâți pereți ai trage.
 *
 *  CE SE MUTĂ TOTUȘI ODATĂ CU ZIDUL, și de ce nu intră în regula de mai sus:
 *    · golurile (ușa, fereastra) sunt TĂIATE în zid și pleacă prin geometria
 *      lui;
 *    · aparatele de pe FAȚA zidului mutat (`ceStaPeZiduri` → `duCeAmGasit`):
 *      o doză îngropată în tencuială nu e un obiect așezat în cameră;
 *    · mobilarea DEDUSĂ a unei camere (cea nescrisă în `plan.furniture`) se
 *      redesenează din mărimea camerei — nu e o poziție pe care a pus-o
 *      cineva, ci un desen al camerei. Prima atingere o scrie
 *      (`desfaMobilierul`), și din clipa aia stă pe loc ca orice piesă.
 *
 *  Mai jos a rămas regula pentru APARATELE de sine stătătoare — priza, doza,
 *  întrerupătorul fără `pentru` —, care stau pe fața unui perete fără s-o
 *  scrie nicăieri: lăsate pe loc, ele ar rămâne în aer, în mijlocul camerei.
 *  ==========================================================================
 *
 *  PATRU ÎNTREBĂRI, ÎN ORDINEA ASTA, și fiecare răspunde doar unde tac cele
 *  dinaintea ei:
 *
 *    1. **ZIDUL DE LÂNGĂ** (`dupaZidulDeLanga`) — ce stă la cel mult
 *       `LANGA_ZID` de un zid mutat pleacă cu el. E regula cerută de Stefan:
 *       «toate obiectele la o distanță de maxim 1,5 metri de perete să fie
 *       mutate cu peretele oricând se mută acel perete».
 *    2. **CONTAGIUNEA** (`LIPITE`) — lipit de un obiect care pleacă, pleci și
 *       tu. Ea ține blatul întreg, oricât de lung.
 *    3. **CAMERA** (`dupaCamera`) — ce a rămas pe loc, dar stă într-o cameră pe
 *       care zidul o mișcă, pleacă odată cu ea. Un metru jumătate e mai puțin
 *       decât o odaie, iar fără pasul ăsta mobila din mijlocul livingului
 *       rămânea pe loc (v. nota de la `dupaCamera`: cifrele din producție).
 *    4. **CONTAGIUNEA ÎNCĂ O DATĂ**, pentru ce a pus în mișcare camera.
 *
 *  NICIUNA NU E O ANCORĂ SCRISĂ, și asta a fost cerința. Înainte, două lucruri
 *  țineau de o legătură scrisă undeva, nu de ce se vede pe planșă:
 *
 *    · MOBILA ținea NUMAI de camera ei, iar camera nu răspundea pentru ce cade
 *      în afara oricărui poligon — pe coridor, peste conturul modulului — și
 *      nici acolo unde camera se termină fără zid: o trecere liberă spre hol,
 *      un prag (`plan.thresholds`), un geam cât peretele. Restul trecea pe o a
 *      doua regulă, a celui mai apropiat zid paralel, unde câștiga zidul cel
 *      mai apropiat chiar dacă el stătea pe loc. Acum camera e a TREIA
 *      întrebare, nu singura, deci nu mai poate lăsa pe nimeni în urmă.
 *
 *    · APARATELE ELECTRICE aveau ancoră SCRISĂ — `ElectricDoc.pentru` — și se
 *      mutau numai prin piesa lor. Priza pusă cu mâna n-a avut niciodată
 *      `pentru`; iar cea care a avut îl PIERDE la prima tragere cu mâna
 *      (v. `mutaAparat`). Din clipa aia stătea pe fața unui perete fără să știe
 *      de el: peretele pleca, doza rămânea în aer, în mijlocul camerei.
 *
 *  Ancorarea scrisă rămâne — dar numai ca să AȘEZE, nu ca să mute: un aparat cu
 *  `pentru` se recalculează din piesa lui la sfârșit (`urmeazaInLoc`), fiindcă
 *  el nu are o poziție a lui, ci una dedusă. Unul fără `pentru` e un obiect ca
 *  oricare altul și intră în regula de distanță.
 *
 *  NU INTRĂ NUCLEUL (scara, liftul): e structură, nu obiect de cameră, și își
 *  are gesturile lui. Nu intră nici golurile — ușa și fereastra sunt TĂIATE în
 *  zid și pleacă odată cu el, prin geometria lui.
 *  ==========================================================================
 *
 *  Se compară documentul de la ÎNCEPUTUL gestului cu cel de acum (ca la
 *  `conturUrmeazaZidurile`), și tot pe cel de la început se măsoară distanța:
 *  după ce zidul a plecat cu un metru, obiectul nu mai e lângă el — dar era.
 *  Un obiect mutat chiar în gestul ăsta (tras cu mâna) nu e atins; asta face și
 *  ca a doua chemare, la ridicarea degetului, să nu-l mute încă o dată. */
export function obiecteleUrmeazaZidurile(doc0: TronsonDoc, doc: TronsonDoc): TronsonDoc {
  if (doc0 === doc) return doc;
  const ap0 = doc0.plan?.electrical ?? [];
  const ap = doc.plan?.electrical ?? [];
  // Listele trebuie să fie ALEAȘI ACEIAȘI în amândouă documentele: un obiect
  // adăugat sau șters în gestul ăsta schimbă indicii, iar perechile de mai jos
  // ar compara obiecte diferite între ele.
  const cuAparate = ap.length > 0 && ap.length === ap0.length;
  if (!cuAparate) return doc;
  const w1 = new Map((doc.plan?.walls ?? []).map((w) => [w.id, w]));
  /** Cutia unui inel, pe amândouă axele. */
  const cutiaInelului = (ring: readonly Pt[]): [number, number][] =>
    [0, 1].map((a) => {
      const v = ring.map((q) => q[a as 0 | 1]);
      return [Math.min(...v), Math.max(...v)] as [number, number];
    });
  /** Zidurile de la început, cu osia lor și cu deplasarea lor de-acum. */
  const ziduri = (doc0.plan?.walls ?? []).map((w) => {
    const o = osiaPeretelui(inelul(w.poly));
    const acum = w1.get(w.id);
    const o1 = acum ? osiaPeretelui(inelul(acum.poly)) : null;
    const delta = o1 && o1.axa === o.axa
      ? (o1.fete[0] + o1.fete[1]) / 2 - (o.fete[0] + o.fete[1]) / 2 : 0;
    // Și CUTIILE lui, de dinainte și de acum: gărzile de mai jos întreabă dacă
    // obiectul dă peste zid, iar asta se întreabă pe dreptunghi, nu pe fețe —
    // altfel un zid paralel cu mersul obiectului n-ar opri-o niciodată, deși el
    // intră în el pe la capăt (v. `taiatLaZiduri`).
    const c0 = cutiaInelului(inelul(w.poly));
    const c1 = acum ? cutiaInelului(inelul(acum.poly)) : null;
    // CÂT S-A MUTAT CUTIA, pe fiecare axă. `delta` de mai sus e mutarea
    // perpendiculară pe zid — cea care duce obiectele —, dar un zid poate veni
    // peste un obiect și altfel: lungit dintr-un capăt, sau pur și simplu scurt
    // și aproape pătrat, unde `osiaPeretelui` ghicește axa. Pe M5, un ciot de
    // 30×22 cm mutat cu o jumătate de metru ajungea fix peste aragaz, iar
    // garda, care întreba de `delta`, nu-l vedea deloc.
    const dz: [number, number] | null = c1
      ? [0, 1].map((a) => (c1[a][0] + c1[a][1]) / 2
                          - (c0[a][0] + c0[a][1]) / 2) as [number, number]
      : null;
    return { o, o1, delta, c0, c1, dz };
  });
  if (!ziduri.some((z) => Math.abs(z.delta) > 0.004
                          || (z.dz && (Math.abs(z.dz[0]) > 0.004
                                       || Math.abs(z.dz[1]) > 0.004)))) return doc;

  /** CÂT PLEACĂ O MUCHIE A CAMEREI: după zidul pe a cărui față stă.
   *
   *  Nu din `plan.rooms` al documentului de acum, deși acolo scrie deja ceva:
   *  `mutaLinie` duce doar vârfurile care cad chiar pe fața mutată, iar restul
   *  camerelor se refac abia la derivare (`cuCamereDerivate`), după noi.
   *  Măsurat pe M1, axul 2 tras în jos: din patru camere strânse, una singură
   *  își vedea vârfurile mutate aici — deci mobila celorlalte trei ar fi rămas
   *  pe loc. Zidurile, în schimb, sunt mutate de-a binelea, și muchia camerei
   *  ESTE fața unuia dintre ele. */
  const muchiaPleaca = (valoare: number, axa: 0 | 1, s0: number, s1: number) => {
    let cel = 0;
    // Cât trebuie să țină zidul din banda cerută ca să fie AL ei. O zecime de
    // metru ține la distanță zidul care doar atinge un colț — dar banda e acum
    // a unei piese, nu a camerei (v. `feliaCamerei`), iar o noptieră de 45 cm
    // așezată peste cusătura a două bucăți de fațadă n-ar mai fi găsit niciuna.
    const prag = Math.min(0.1, (s1 - s0) / 2);
    for (const z of ziduri) {
      if (z.o.axa !== axa || Math.abs(z.delta) <= 0.004) continue;
      const a0 = Math.min(...z.o.span);
      const a1 = Math.max(...z.o.span);
      if (Math.min(a1, s1) - Math.max(a0, s0) < prag) continue;
      if (!z.o.fete.some((ft) => Math.abs(ft - valoare) <= LIPIRE)) continue;
      if (Math.abs(z.delta) > Math.abs(cel)) cel = z.delta;
    }
    return cel;
  };
  /** FELIA CAMEREI DIN DREPTUL UNEI PIESE: cât drum are ea pe axa `a`, între
   *  pereții pe care i-ar atinge CHIAR EA.
   *
   *  CUTIA CAMEREI NU E CAMERA. Cerut de Stefan: «verifică și la camerele în L
   *  sau alte forme, că acolo cutia nu e camera». Măsurat pe M5, unde aproape
   *  toate camerele au între 5 și 9 colțuri: bucătăria în L, tăiată la cutie,
   *  își ducea blatul întreg 0,18 m² ÎN zidul care închide brațul scurt, la
   *  orice perete mutat cu jumătate de metru; pe M1 și M2, canapeaua unui living
   *  în L intra 0,62 m² în fațadă. Cutia trece peste pereții dinăuntrul ei, deci
   *  o muchie a ei nu e un perete al nimănui.
   *
   *  Deci muchiile nu se mai citesc o dată pe cameră, ci pentru fiecare piesă,
   *  pe BANDA pe care o ocupă ea pe axa cealaltă: coridorul în care s-ar plimba
   *  piesa. Pentru o cameră dreptunghiulară felia E cutia, deci nimic nu se
   *  schimbă acolo; pentru una în L, fiecare braț își ține mobila lui.
   *
   *  Se taie inelul pe trei linii — marginile benzii și mijlocul ei — și se ia
   *  ce au toate în comun: o piesă mai lată decât brațul în care stă se oprește
   *  la peretele îngust, nu la cel de dincolo de colț. O linie care cade în
   *  afara camerei — piesa desenată puțin în zid — nu spune nimic despre drumul
   *  ei și se lasă; dacă niciuna nu spune nimic, rămâne cutia, ca până acum. */
  const feliaCamerei = (
    inel: readonly Pt[], a: 0 | 1, s0: number, s1: number, c: number,
  ): [number, number] | null => {
    const alta = (1 - a) as 0 | 1;
    const eps = Math.min(0.01, (s1 - s0) / 4);
    let jos = -Infinity;
    let sus = Infinity;
    let gasit = false;
    for (const s of [s0 + eps, (s0 + s1) / 2, s1 - eps]) {
      const taieri: number[] = [];
      for (let k = 0; k < inel.length; k++) {
        const u = inel[k];
        const v = inel[(k + 1) % inel.length];
        // Latura orizontală (amândouă capetele de aceeași parte) nu taie linia.
        if ((u[alta] <= s) === (v[alta] <= s)) continue;
        const t = (s - u[alta]) / (v[alta] - u[alta]);
        taieri.push(u[a] + (v[a] - u[a]) * t);
      }
      if (taieri.length < 2) continue;
      taieri.sort((x, y) => x - y);
      for (let k = 0; k + 1 < taieri.length; k += 2) {
        if (taieri[k] - LIPIRE <= c && c <= taieri[k + 1] + LIPIRE) {
          jos = Math.max(jos, taieri[k]);
          sus = Math.min(sus, taieri[k + 1]);
          gasit = true;
          break;
        }
      }
    }
    return gasit && sus - jos > 0.05 ? [jos, sus] : null;
  };
  /** ZIDURILE CARE ÎNCHID O CAMERĂ: cele pe a căror față stă o muchie a ei.
   *
   *  Cerut de Stefan, 23.09.2026: «atunci când trag de pereți nu vreau să tragă
   *  mobila din alte camere, chiar dacă îndeplinește condiția». Un metru și
   *  jumătate trece lejer printr-un perete de 10–30 cm: cu `dupaZidulDeLanga`
   *  singur, dulapul de dincolo de compartimentare pleca și el cu fațada, deși
   *  zidul tras nu-i atinge camera deloc. Acum un obiect dintr-o cameră
   *  ascultă numai de zidurile camerei LUI. Iar dacă zidul tras duce după el
   *  un alt zid (colțul, sandvișul fațadei), acela e mutat de-a binelea și își
   *  duce mobila camerelor lui — prin aceeași regulă, nu printr-o excepție. */
  const PE_FATA = 0.05;
  const zidurileCamerei = (inel: readonly Pt[]): Set<number> => {
    const ale = new Set<number>();
    ziduri.forEach((z, j) => {
      const a = z.o.axa;
      const alta = (1 - a) as 0 | 1;
      const s0 = Math.min(...z.o.span);
      const s1 = Math.max(...z.o.span);
      for (let k = 0; k < inel.length; k++) {
        const u = inel[k];
        const v = inel[(k + 1) % inel.length];
        if (Math.abs(u[a] - v[a]) > PE_FATA) continue;
        const m = (u[a] + v[a]) / 2;
        if (!z.o.fete.some((ft) => Math.abs(ft - m) <= PE_FATA)) continue;
        const peste = Math.min(s1, Math.max(u[alta], v[alta]))
                      - Math.max(s0, Math.min(u[alta], v[alta]));
        if (peste > PE_FATA) { ale.add(j); return; }
      }
    });
    return ale;
  };
  const camere = (doc0.plan?.rooms ?? []).map((c) => {
    const inel = inelul(c.poly);
    return { inel, cutie: cutiaInelului(inel), ziduri: zidurileCamerei(inel) };
  });
  /** Sub atâta, o piesă nu e ÎN zid, ci lipită de el. Doi centimetri, fiindcă

  /** DISTANȚA DINTRE DOUĂ CUTII — zero când se ating sau se suprapun.
   *
   *  Pe amândouă axele deodată, nu doar pe cea a mutării: un obiect așezat
   *  dincolo de capătul unui zid scurt e departe de el chiar dacă stă fix pe
   *  linia lui. Fără asta, un ciot de perete dintr-un colț al modulului ar fi
   *  tras după el mobila de la celălalt capăt al aceleiași linii. */
  const departeDe = (a: number[][], b: [number, number][]): number => {
    const g = [0, 1].map((k) =>
      Math.max(0, Math.max(a[k][0], b[k][0]) - Math.min(a[k][1], b[k][1])));
    return Math.hypot(g[0], g[1]);
  };

  /** CÂT PLEACĂ UN OBIECT pe axa `axa`: cu zidul mutat cel mai apropiat de el.
   *
   *  Numai ziduri care CHIAR s-au mutat: unul care stă pe loc n-are ce să dea,
   *  iar dacă el ar fi luat în seamă doar fiindcă e mai aproape, obiectul ar
   *  rămâne în urmă exact în cazul din care a pornit toată povestea — o piesă
   *  prinsă între două ziduri, cu cel care pleacă al doilea ca apropiere.
   *
   *  Și numai ziduri lungi pe axa cealaltă (`o.axa === axa`): un zid se mută
   *  de-a curmezișul lui, deci el poate da o deplasare doar pe axa aia. Un
   *  obiect lângă un colț are câte un zid pe fiecare axă și pleacă pe amândouă,
   *  fiecare cu deplasarea zidului ei — adică pleacă cu colțul.
   *
     *  Cel mai apropiat, nu suma lor: două ziduri paralele care se apropie unul
   *  de altul cer lucruri contrare, iar între ele are dreptate cel de lângă
   *  care stă aparatul. */
  const dupaZidulDeLanga = (
    axa: 0 | 1, cut: number[][], ale: Set<number> | undefined,
  ): number => {
    let cel: { dist: number; delta: number } | null = null;
    for (const [j, z] of ziduri.entries()) {
      if (z.o.axa !== axa || Math.abs(z.delta) <= 0.004) continue;
      // Zidul altei camere nu-l duce, oricât de aproape ar fi (v.
      // `zidurileCamerei`). Obiectul din afara oricărei camere ascultă de toate.
      if (ale && !ale.has(j)) continue;
      const dist = departeDe(cut, z.c0);
      if (dist > LANGA_ZID) continue;
      if (!cel || dist < cel.dist) cel = { dist, delta: z.delta };
    }
    return cel ? cel.delta : 0;
  };

  /** CÂT PLEACĂ UN OBIECT pe axa `axa` DUPĂ CAMERA ÎN CARE STĂ — a doua cale,
   *  pentru ce e prea departe de orice zid ca să-l simtă.
   *
   *  ==========================================================================
   *  UN METRU JUMĂTATE E MAI PUȚIN DECÂT O CAMERĂ, și asta s-a văzut abia în
   *  producție. Stefan, 17.09.2026: «trag de pereți pe studio și nu se trag și
   *  obiectele de mobilier, electrocasnice etc. […] nici când trag de axuri nu
   *  îmi ia mobila».
   *
   *  Măsurat pe peretele de care trăgea el — W1 de pe M1, structural, 3,50 m —,
   *  cea mai apropiată piesă de mobilier stă la 2,25 m de el, iar următoarele
   *  la 2,75, 3,42, 3,46, 3,82, 4,15. Adică: nimic sub prag, deci nimic nu
   *  pleacă. Nu era o scăpare a socotelii, era pragul. Pe tot catalogul, un
   *  perete tras ducea median 3–6 piese din 41–93, iar 8–11 % din pereți nu
   *  mișcau NIMIC; pe M1, axa «5» ducea o singură piesă din 49.
   *
   *  Deci camera răspunde din nou — dar ALĂTURI de distanță, nu în locul ei, și
   *  asta e toată deosebirea față de cum era înainte. Reuniunea poate doar să
   *  miște mai mult, niciodată mai puțin: ce e lângă un zid pleacă fiindcă e
   *  lângă el, chiar dacă nu cade în nicio cameră — pe coridor, peste conturul
   *  modulului, într-o trecere liberă —, iar ce stă în mijlocul unei camere
   *  pleacă fiindcă pleacă și camera lui.
   *
   *  Distanța are ÎNTÂIETATE când amândouă au un răspuns: ea e cea locală, a
   *  obiectului lipit de zidul care chiar s-a mutat.
   *  ========================================================================== */
  const dupaCamera = (
    axa: 0 | 1, cut: number[][],
    camera: { inel: readonly Pt[]; cutie: [number, number][] },
  ): number => {
    const alta = (1 - axa) as 0 | 1;
    // Muchiile coridorului piesei, de dinainte și de acum.
    const [b0, b1] = cut[alta];
    const felie = feliaCamerei(camera.inel, axa, b0, b1,
                               (cut[axa][0] + cut[axa][1]) / 2)
      ?? camera.cutie[axa];
    // ÎNTÂI ZIDUL DIN DREPTUL PIESEI, APOI TOATĂ LINIA CAMEREI. În dreptul
    // piesei poate să nu fie zid deloc: pe M1, livingul se termină sus cu
    // 1,80 m de geam, iar canapeaua din fața lui n-avea pe cine să urmeze —
    // cu întrebarea pusă doar pe banda ei, rămânea pe loc când fațada pleca.
    // Linia camerei răspunde atunci în locul ei: e aceeași muchie, ținută de
    // aceiași pereți, doar că mai încolo.
    const [r0, r1] = camera.cutie[alta];
    const pleaca = (v: number) => muchiaPleaca(v, axa, b0, b1)
      || muchiaPleaca(v, axa, r0, r1);
    const acum: [number, number] = [
      felie[0] + pleaca(felie[0]),
      felie[1] + pleaca(felie[1]),
    ];
    return deplasareaInCamera(felie, acum, cut[axa][0], cut[axa][1]);
  };

  /** APARATELE CARE POT FI DUSE DE UN ZID, într-o singură listă.
   *
   *  Mobila NU mai e aici (v. antetul funcției): ea stă unde a pus-o omul,
   *  oricât s-ar muta pereții din jur. Au rămas aparatele de sine stătătoare,
   *  care sunt îngropate în fața unui zid fără s-o scrie nicăieri.
   *
   *  Aparatele cu `pentru` nu intră: n-au poziție proprie, ci una dedusă din
   *  piesa lor, și se recalculează la sfârșit (`urmeazaInLoc`). Puse aici, doza
   *  ar pleca de două ori — o dată cu regula asta, o dată cu piesa ei.
   *
   *  Aparatul mutat CHIAR ÎN GESTUL ĂSTA, tras cu mâna, nu intră nici el: e al
   *  mâinii, nu al zidului. Asta face și ca a doua chemare, la ridicarea
   *  degetului, să nu mute nimic încă o dată. */
  type Obiect = {
    i: number;
    cut: number[][];
    dep: Pt;
    /** Camera în care stă, din documentul de la ÎNCEPUT. Lipsă = pe coridor
     *  sau peste contur: atunci răspunde numai distanța. */
    camera: { inel: readonly Pt[]; cutie: [number, number][];
              ziduri: Set<number> } | undefined;
  };
  const lot: Obiect[] = [];
  ap0.forEach((a, i) => {
    if (a.pentru != null) return;
    const g = ap[i];
    if (Math.abs(g.x.a - a.x.a) > 1e-9 || Math.abs(g.y.a - a.y.a) > 1e-9) return;
    // Un aparat e un punct: n-are întindere pe planșă, doar un simbol.
    lot.push({ i, dep: [0, 0],
               camera: camere.find((c) => inPoligon([a.x.a, a.y.a], c.inel)),
               cut: [[a.x.a, a.x.a], [a.y.a, a.y.a]] });
  });
  if (!lot.length) return doc;

  // ÎNTÂI ZIDUL DE LÂNGĂ: fiecare obiect ia deplasarea zidului mutat de lângă el.
  for (const o of lot) {
    o.dep = [dupaZidulDeLanga(0, o.cut, o.camera?.ziduri),
             dupaZidulDeLanga(1, o.cut, o.camera?.ziduri)];
  }

  /** APOI CONTAGIUNEA: cine e lângă un obiect plecat pleacă și el, la fel.
   *
   *  ==========================================================================
   *  BLATUL NU SE RUPE ÎN DOUĂ. Fără pasul ăsta, măsurat pe M1 cu axul «1» tras
   *  1,30 m: din bucătărie plecau frigiderul (la 0,004 m de fațadă), corpul
   *  (0,704) și chiuveta (1,304), iar mașina de vase (1,904) și cea de spălat
   *  (2,504) rămâneau — de pe ACELAȘI blat continuu, așezat perpendicular pe
   *  fațada trasă. Frigiderul intra peste mașina de vase. La fel se despărțeau
   *  cele două noptiere ale aceluiași pat: una la 0,631, cealaltă la 2,781.
   *
   *  Nu e o greșeală a pragului, e forma întrebării: un blat e o singură
   *  mobilă din mai multe bucăți, iar distanța până la zid o are doar capătul
   *  lui. Ce ține bucățile împreună e că sunt lipite UNA DE ALTA.
   *
   *  Deci aceeași regulă, aplicată din obiect în obiect: LIPIT de un obiect
   *  care pleacă, pleci și tu, cu deplasarea lui. Se reia până nu se mai
   *  schimbă nimic — blatul pleacă întreg oricât de lung ar fi, fiindcă
   *  bucățile lui sunt lipite două câte două.
   *
   *  LIPIT, NU APROAPE, și pragul e citit din catalog, nu ales. Cel mai
   *  apropiat vecin al fiecărei piese, măsurat pe M1 și M5: bucățile unui blat
   *  stau la 0,000 m una de alta — toate, în toate bucătăriile —, patul și
   *  noptierele lui la 0,050 m, televizorul pe comoda lui la 0,000 m. Prima
   *  distanță care desparte lucruri cu adevărat deosebite e 0,269 m: canapeaua
   *  și măsuța din fața ei. Între 0,05 și 0,27 nu cade nimic, și în prăpastia
   *  aia stă `LIPITE`.
   *
   *  Cu `LANGA_ZID` în locul lui, contagiunea trecea prin toată camera: pe M1,
   *  comoda TV de pe peretele OPUS fațadei trase pleca și ea cu 0,40 m, iar
   *  dormitorul se muta întreg. O canapea și o măsuță nu sunt un obiect.
   *
   *  PE FIECARE AXĂ SEPARAT, ca și regula zidului: un obiect lângă un colț ia
   *  x de la un vecin și y de la altul, și pleacă cu colțul. Iar dintre vecinii
   *  plecați răspunde CEL MAI APROPIAT: două blaturi care se apropie unul de
   *  altul cer lucruri contrare, iar între ele are dreptate cel de lângă care
   *  stai.
   *
   *  NU E CAMERA ÎNAPOI PE ALTĂ UȘĂ. Camera trăgea după ea și ce n-avea nicio
   *  legătură cu zidul mutat, doar fiindcă era desenată în același poligon, și
   *  nu trăgea nimic din ce cădea în afara oricărei camere. Aici nu există
   *  poligoane: un obiect singur în mijlocul camerei, la peste 1,5 m de orice,
   *  rămâne pe loc. Iar un șir de obiecte lipite NU trece dintr-o cameră în
   *  alta (23.09): între două camere stă un zid, iar ce e dincolo de el ține de
   *  zidurile camerei lui, nu de vecinul lipit de peretele comun.
   *  ========================================================================== */
  const contagiunea = () => {
    let schimbat = true;
    while (schimbat) {
      schimbat = false;
      for (const o of lot) {
        for (const axa of [0, 1] as const) {
          if (Math.abs(o.dep[axa]) > 0.004) continue;
          let cel: { dist: number; dep: number } | null = null;
          for (const q of lot) {
            if (q === o || Math.abs(q.dep[axa]) <= 0.004) continue;
            // Nici lipit nu treci în altă cameră: între două camere e un zid.
            if (o.camera && q.camera && o.camera !== q.camera) continue;
            const dist = departeDe(o.cut, q.cut as [number, number][]);
            if (dist > LIPITE) continue;
            if (!cel || dist < cel.dist) cel = { dist, dep: q.dep[axa] };
          }
          if (cel) { o.dep[axa] = cel.dep; schimbat = true; }
        }
      }
    }
  };
  contagiunea();

  // ABIA ACUM CAMERA, și numai unde n-a răspuns nimeni — v. `dupaCamera`.
  //
  // DUPĂ CONTAGIUNE, NU ÎNAINTEA EI, și ordinea asta e chiar ce ține blatul
  // întreg. Camera răspunde pe muchiile ei, deci bucata de blat de la capătul
  // dinspre celălalt perete primea de la ea 1,150 m acolo unde zidul dăduse
  // 1,300 — același blat, două deplasări, exact ruptura pe care contagiunea o
  // repară. Lăsată să lucreze prima, ea leagă blatul de zidul lui, iar camera
  // nu mai are ce să contrazică: îi rămân piesele din mijlocul odăii, care n-au
  // pe nimeni lângă ele.
  for (const o of lot) {
    if (!o.camera) continue;
    for (const axa of [0, 1] as const) {
      if (Math.abs(o.dep[axa]) > 0.004) continue;
      const dl = dupaCamera(axa, o.cut, o.camera);
      if (Math.abs(dl) > 0.004) o.dep[axa] = dl;
    }
  }
  // Și încă o dată contagiunea, pentru ce a pus în mișcare camera.
  contagiunea();

  let d: TronsonDoc | null = null;
  for (const o of lot) {
    const dep: Pt = [o.dep[0], o.dep[1]];
    // NICIO GARDĂ DE «AFARĂ DIN ZID»: un aparat nu stă LÂNGĂ perete, stă PE
    // fața lui, în tencuială (v. `lipitDeZid`). Deplasarea zidului de lângă el
    // e exact ce-i trebuie: doza merge cu peretele în care e îngropată.
    if (Math.abs(dep[0]) <= 0.004) dep[0] = 0;
    if (Math.abs(dep[1]) <= 0.004) dep[1] = 0;
    if (!dep[0] && !dep[1]) continue;
    d = d ?? clona(doc);
    const g2 = d.plan!.electrical![o.i];
    g2.x = { ...g2.x, a: r3(g2.x.a + dep[0]) };
    g2.y = { ...g2.y, a: r3(g2.y.a + dep[1]) };
  }

  // ȘI APARATELE ANCORATE DUPĂ MOBILA LOR. Un zid tras duce mobila de lângă el;
  // aparatele acelei mobile se așază din nou pe conturul camerei — care tocmai
  // s-a schimbat și el. Fără rândul ăsta, priza patului ar rămâne pe linia
  // peretelui vechi, adică în mijlocul camerei lărgite.
  if (!d) return doc;
  urmeazaInLoc(d);
  return d;
}

/** Schimbă o piesă: unghi, mărime, fel, oglindire.
 *
 *  MĂRIMEA UNEI PIESE SE SCRIE, NU SE TRAGE — și asta e singura cale spre ea.
 *  Piesele aveau, ca nucleul și camera, mânere de redimensionare pe muchii și
 *  pe colțuri. Mânerul de latură e o bandă de 7 px, adică 19 cm la mărirea
 *  obișnuită de 37 px/m: mai lat decât rostul dintre o piesă și zidul de lângă
 *  ea, și mai lat decât zidul însuși. Pe M3, liftul stă la 6,4 cm de fața lui
 *  W49, iar cu liftul ales toată lățimea zidului răspundea cu `ew-resize` —
 *  măsurat cu `elementFromPoint`, pixel cu pixel, de la 3,30 la 3,50 nu exista
 *  un singur punct din care zidul să poată fi apucat. Cine voia să împingă
 *  peretele până la lift lățea liftul.
 *
 *  Iar asta nu e o nimerire proastă, e o măsură inventată: «lifturile au
 *  dimensiuni fixe, așa vin din fabrică, și scările sunt calculate cu anumite
 *  dimensiuni; le putem ajusta manual, dar niciodată automat din cauza mutării
 *  unui alt element». Un lift de 1,57 m ieșit din mână nu e o cabină care
 *  există. Deci mărimile se scriu în panou, unde cifra e văzută înainte să
 *  intre în document, iar planșa rămâne cu gesturile care mută: piesa, zidul,
 *  latura camerei. Se redimensionează prin tragere numai CAMERA și ZIDUL —
 *  singurele două care chiar se croiesc pe loc.
 *
 *  OGLINDIREA NU E O ROTIRE și de-aia e o comandă aparte: o bucătărie în L sau
 *  un colț de canapea au o mână, iar mâna cealaltă nu iese învârtind piesa —
 *  brațul scurt rămâne de aceeași parte oricât ai roti. Se dă ca STARE, nu ca
 *  basculare, ca două apăsări repezi pe același buton să nu depindă de câte
 *  dintre ele a apucat să le vadă documentul.
 *
 *  Steagul stins se ȘTERGE din document în loc să se scrie `false`: un câmp
 *  care spune «nu» e o a doua scriere a implicitului, și ar umple catalogul cu
 *  `flip_x: false` pe fiecare piesă pe care a apăsat cineva din greșeală. */
export function schimbaMobila(
  doc: TronsonDoc, i: number,
  ce: { rot?: number; w?: number; h?: number; inaltime?: number; kind?: string;
        oglX?: boolean; oglY?: boolean },
): TronsonDoc {
  const f = doc.plan?.furniture?.[i];
  if (!f) return doc;
  // A drawn stair turns on its flights, and `rot_deg` stays 0: see `rotesteScara`.
  if (ce.rot != null && f.scara?.rampe?.length) {
    return rotesteScara(doc, i, ce.rot - (f.rot_deg ?? 0));
  }
  // It mirrors on them too. A flag would only flip the drawing and leave the
  // landing's handles behind; `false` still clears one an older stair carries.
  if (f.scara?.rampe?.length && (ce.oglX || ce.oglY)) {
    return oglindesteScara(doc, i, ce.oglX ? "h" : "v");
  }
  const d = clona(doc);
  const g = d.plan!.furniture![i];
  // Unghiul se ține în [0, 360): altfel, rotit de destule ori, ar crește la
  // nesfârșit și panoul ar arăta 1080°.
  if (ce.rot != null) g.rot_deg = ((Math.round(ce.rot * 10) / 10) % 360 + 360) % 360;
  if (ce.w != null) g.w_m = r3(Math.max(0.1, ce.w));
  if (ce.h != null) g.h_m = r3(Math.max(0.1, ce.h));
  // ÎNĂLȚIMEA SE SCRIE DOAR CÂND SE ABATE de la cea de catalog: altfel fiecare
  // piesă atinsă ar căra în document o cifră care e oricum implicitul felului
  // ei, iar o schimbare a catalogului n-ar mai ajunge la ea.
  // A stair has no height of its own: it climbs the storey, and every stair
  // computation reads `storey_height_m` (see `schimbaScara`). A height written
  // here would only stretch the 3D box, so it is refused (27.10.2026).
  if (ce.inaltime != null && !g.scara && g.kind !== "scara" && g.kind !== "scaraU") {
    const v = r3(Math.max(0.01, ce.inaltime));
    const alFelului = felulPiesei(g.kind)?.inaltime;
    if (alFelului != null && Math.abs(v - alFelului) < 1e-6) delete g.height_m;
    else g.height_m = v;
  }
  if (ce.kind != null) g.kind = ce.kind;
  if (ce.oglX != null) { if (ce.oglX) g.flip_x = true; else delete g.flip_x; }
  if (ce.oglY != null) { if (ce.oglY) g.flip_y = true; else delete g.flip_y; }
  // ROTITĂ SAU OGLINDITĂ, PIESA ÎȘI DUCE APARATELE. «Stânga patului» e stânga
  // LUI, citită din unghiul lui: un pat întors cu 90° are alt perete la cap,
  // iar prizele trebuie să fie pe ăla.
  urmeazaInLoc(d, [i]);
  return d;
}

// ----------------------------------------------------- finisaje și electrice ---

/** FINISAJUL UNEI CAMERE, scris.
 *
 *  Se scrie doar ce s-a SCHIMBAT: o cameră căreia i se dă chiar implicitul
 *  felului ei nu primește rând, iar una care se întoarce la implicit îl pierde.
 *  Altfel lista ar crește cu fiecare click și documentul ar purta de douăzeci de
 *  ori «parchet la dormitor» — adevărat, dar deja scris în tabelul de implicite,
 *  deci o a doua copie care se desparte de prima la prima schimbare de convenție.
 */
export function pictaFinisaj(
  doc: TronsonDoc, camera: number, ce: { floor?: string; wall?: string },
): TronsonDoc {
  const d = clona(doc);
  const plan = d.plan!;
  const cam = plan.rooms?.[camera];
  if (!cam) return doc;
  const lista = plan.finishes ?? [];
  const i = lista.findIndex((f) => f.room === camera);
  const acum = i >= 0 ? { ...lista[i] } : { room: camera };
  if (ce.floor != null) acum.floor = ce.floor;
  if (ce.wall != null) acum.wall = ce.wall;
  // Implicitul nu se scrie: dacă alegerea e chiar el, câmpul se șterge.
  if (acum.floor === pardoseala(cam.kind)) delete acum.floor;
  if (acum.wall === pereteleCamerei(cam.kind)) delete acum.wall;
  const gol = acum.floor == null && acum.wall == null;
  const nou = gol ? lista.filter((f) => f.room !== camera)
    : i >= 0 ? lista.map((f, j) => (j === i ? acum : f))
      : [...lista, acum];
  if (nou.length) plan.finishes = nou; else delete plan.finishes;
  return d;
}

/** Un aparat electric nou, acolo unde s-a apăsat. */
export function adaugaAparat(
  doc: TronsonDoc, kind: string, p: Pt, rot = 0,
): { doc: TronsonDoc; ales: Ales } {
  const d = clona(doc);
  const lista = (d.plan!.electrical = d.plan!.electrical ?? []);
  lista.push({ kind, x: { a: r3(p[0]) }, y: { a: r3(p[1]) },
               ...(rot ? { rot_deg: r3(rot) } : {}) });
  return { doc: d, ales: { fel: "aparat", i: lista.length - 1 } };
}

/** A new LED strip (sheet row 114): its start on the face, the face's normal
 *  always written (a piece rotation turns only a written angle), its signed
 *  length along the face. */
export function adaugaBandaLed(
  doc: TronsonDoc, start: Pt, rot: number, lungime: number,
): { doc: TronsonDoc; ales: Ales } {
  const d = clona(doc);
  const lista = (d.plan!.electrical = d.plan!.electrical ?? []);
  lista.push({ kind: "banda_led", x: { a: r3(start[0]) }, y: { a: r3(start[1]) },
               rot_deg: r3(rot), length_m: r3(lungime) });
  return { doc: d, ales: { fel: "aparat", i: lista.length - 1 } };
}

/** Mută un aparat. Liber în plan: un aparat se lipește de zid când e PUS
 *  (v. `lipitDeZid`), nu cât e tras — altfel n-ar putea fi scos de pe un perete
 *  ca să fie dus pe altul. Doza e excepția: ea stă în zid, deci se mută numai
 *  de-a lungul lui (trasă, v. `lipesteDoza`). */
export function mutaAparat(doc: TronsonDoc, i: number, dep: Pt): TronsonDoc {
  const a = doc.plan?.electrical?.[i];
  if (a?.kind === "doza") dep = deALungulDozei(a, dep);
  if (a?.kind === "banda_led") dep = deALungulBenzii(doc, i, dep);
  if (!a || (!dep[0] && !dep[1])) return doc;
  const d = clona(doc);
  const g = d.plan!.electrical![i];
  g.x = { ...g.x, a: r3(g.x.a + dep[0]) };
  g.y = { ...g.y, a: r3(g.y.a + dep[1]) };
  // MÂNA RUPE LEGĂTURA. Cine trage priza în altă parte a spus unde o vrea; dacă
  // ar rămâne legată, prima atingere a canapelei i-ar aduce-o înapoi, și
  // editorul ar arăta ca și cum n-ar fi ascultat. Se pierde urmărirea, nu
  // aparatul — se poate lega la loc punând instalația camerei din nou.
  delete g.pentru;
  delete g.parte;
  return d;
}

/** The part of a step that runs along a box's wall. A box is drawn into the
 *  face of its wall and dimensioned along it; a step across would take it off
 *  the face, where it has neither. */
function deALungulDozei(a: ElectricDoc, dep: Pt): Pt {
  const r = ((a.rot_deg ?? 0) * Math.PI) / 180;
  const t: Pt = [-Math.sin(r), Math.cos(r)];
  const s = dep[0] * t[0] + dep[1] * t[1];
  return [r3(s * t[0]), r3(s * t[1])];
}

/** A strip's face: its direction `u` and the face's stops as signed distances
 *  from its start, unbounded off a face or on an oblique wall. */
function fataBenzii(doc: TronsonDoc, i: number):
    { u: Pt; c: Pt; L: number; stanga: number; dreapta: number } | null {
  const plan = deriva(doc);
  const e = plan.electrice.find((x) => x.idx === i);
  const a = doc.plan?.electrical?.[i];
  if (!e || !a) return null;
  const rad = (e.deg * Math.PI) / 180;
  const f = capeteleFetei(plan, e.c, e.deg);
  return { u: [Math.sin(rad), -Math.cos(rad)], c: e.c, L: a.length_m ?? 0,
           stanga: f?.stanga ?? -Infinity, dreapta: f?.dreapta ?? Infinity };
}

/** The part of a step that runs along a strip's face, kept so that both its
 *  ends stay inside the face's stops (a strip already past one is not pulled
 *  further out). */
function deALungulBenzii(doc: TronsonDoc, i: number, dep: Pt): Pt {
  const f = fataBenzii(doc, i);
  if (!f) return dep;
  const lo = Math.min(0, f.stanga - Math.min(0, f.L));
  const hi = Math.max(0, f.dreapta - Math.max(0, f.L));
  const s = Math.max(lo, Math.min(hi, dep[0] * f.u[0] + dep[1] * f.u[1]));
  return [r3(s * f.u[0]), r3(s * f.u[1])];
}

/** Moves one end of a strip to `t`, a signed distance along its face from its
 *  start, inside the face's stops and never closer than 0.10 m to the other
 *  end; the other end stays where it is. */
export function mutaCapatBanda(
  doc: TronsonDoc, i: number, capat: "start" | "end", t: number,
): TronsonDoc {
  const a = doc.plan?.electrical?.[i];
  const f = a?.kind === "banda_led" ? fataBenzii(doc, i) : null;
  if (!f) return doc;
  const sg = f.L < 0 ? -1 : 1;
  t = Math.max(Math.min(f.stanga, 0, f.L), Math.min(Math.max(f.dreapta, 0, f.L), t));
  const d = clona(doc);
  const g = d.plan!.electrical![i];
  if (capat === "end") {
    g.length_m = r3(sg * Math.max(LUNGIME_MINIMA_BANDA, sg * t));
  } else {
    const s = sg > 0 ? Math.min(t, f.L - LUNGIME_MINIMA_BANDA)
                     : Math.max(t, f.L + LUNGIME_MINIMA_BANDA);
    g.x = { ...g.x, a: r3(g.x.a + s * f.u[0]) };
    g.y = { ...g.y, a: r3(g.y.a + s * f.u[1]) };
    g.length_m = r3(f.L - s);
  }
  if (g.length_m === a!.length_m && g.x.a === a!.x.a && g.y.a === a!.y.a) return doc;
  delete g.pentru;
  delete g.parte;
  return d;
}

/** The shortest strip there is (sheet row 114). */
export const LUNGIME_MINIMA_BANDA = 0.1;

/** Drags a box: it lands where placing it at the pointer would put it, on the
 *  face of the nearest wall, so it slides along a wall and can be taken to
 *  another one. A drag is incremental (`Panza`), so moving it by the step and
 *  snapping would keep it on its first wall forever. */
export function lipesteDoza(doc: TronsonDoc, i: number, p: Pt): TronsonDoc {
  const a = doc.plan?.electrical?.[i];
  if (a?.kind !== "doza") return doc;
  const s = lipitDeZid(doc, p, "doza");
  const d = clona(doc);
  const g = d.plan!.electrical![i];
  g.x = { ...g.x, a: r3(s.p[0]) };
  g.y = { ...g.y, a: r3(s.p[1]) };
  if (s.rot) g.rot_deg = r3(s.rot); else delete g.rot_deg;
  delete g.pentru;
  delete g.parte;
  return d;
}

/** Schimbă un aparat: fel, înălțime de montaj, circuit, rotire. */
export function schimbaAparat(
  doc: TronsonDoc, i: number,
  ce: { kind?: string; h?: number | null; circuit?: string | null; rot?: number;
        oglX?: boolean; oglY?: boolean; index?: string },
): TronsonDoc {
  const a = doc.plan?.electrical?.[i];
  if (!a) return doc;
  const d = clona(doc);
  const g = d.plan!.electrical![i];
  if (ce.kind != null) g.kind = ce.kind;
  // A kind with no index (the master switch, the board) drops the old one: no
  // field is left in the panel to clear it, and the plan would keep printing it.
  if (ce.kind != null && !areIndice(ce.kind)) delete g.index;
  if (ce.index != null) {
    const x = indiceCurat(ce.index);
    if (x) g.index = x; else delete g.index;
  }
  // A box faces the way its wall does: it does not rotate.
  if (ce.rot != null && a.kind !== "doza" && a.kind !== "banda_led") {
    g.rot_deg = ((Math.round(ce.rot * 10) / 10) % 360 + 360) % 360;
    // Rotirea e tot o poziție: aparatul privește dinspre peretele lui, iar
    // urmărirea i-ar rescrie unghiul la prima mișcare a piesei. Felul,
    // înălțimea și circuitul nu sunt — alea rămân legate.
    delete g.pentru;
    delete g.parte;
  }
  // NULL ÎNSEAMNĂ «înapoi la implicit», nu «zero». O plafonieră care își pierde
  // înălțimea proprie urcă iar odată cu placa; una cu `h_m: 0` ar sta pe jos.
  if (ce.h === null) delete g.h_m; else if (ce.h != null) g.h_m = r3(Math.max(0, ce.h));
  if (ce.circuit === null) delete g.circuit;
  else if (ce.circuit != null) g.circuit = ce.circuit;
  // OGLINDIREA NU E O POZIȚIE, deci nu rupe legătura cu piesa care justifică
  // aparatul — ca rotirea de mai sus. Nu mută aparatul de nicăieri și nimic nu
  // i-o rescrie: urmărirea (`langaMobila`) recalculează centrul și unghiul unei
  // prize legate de o piesă, nu mâna simbolului. Deci o priză de noptieră poate
  // fi întoarsă și rămâne în continuare priza noptierei.
  //
  // Steagul stins se ȘTERGE, nu se scrie `false`, din același motiv ca la
  // mobilier: un câmp care spune «nu» e a doua scriere a implicitului.
  if (ce.oglX != null) { if (ce.oglX) g.flip_x = true; else delete g.flip_x; }
  if (ce.oglY != null) { if (ce.oglY) g.flip_y = true; else delete g.flip_y; }
  return d;
}

/** Adds a device to a mounting box, at the end of its row. Only sockets and
 *  switches go in a box; anything else, or a target that is not a box, leaves
 *  the document as it was. */
export function adaugaInDoza(doc: TronsonDoc, i: number, kind: string): TronsonDoc {
  const a = doc.plan?.electrical?.[i];
  if (a?.kind !== "doza" || !familiaInDoza(kind)) return doc;
  const d = clona(doc);
  const g = d.plan!.electrical![i];
  g.devices = [...(g.devices ?? []), kind];
  if (g.device_indices) g.device_indices = [...g.device_indices, ""];
  return d;
}

/** Takes the `j`-th device out of a box; the others keep their order. The
 *  last one out leaves the field unwritten, as an empty box is written. */
export function scoateDinDoza(doc: TronsonDoc, i: number, j: number): TronsonDoc {
  const a = doc.plan?.electrical?.[i];
  if (a?.kind !== "doza" || !a.devices || j < 0 || j >= a.devices.length) return doc;
  const d = clona(doc);
  const g = d.plan!.electrical![i];
  g.devices = g.devices!.filter((_, k) => k !== j);
  if (!g.devices.length) delete g.devices;
  if (g.device_indices) {
    g.device_indices = g.device_indices.filter((_, k) => k !== j);
    if (!g.device_indices.some(Boolean)) delete g.device_indices;
  }
  return d;
}

/** An index as written: trimmed, at most the schema's 8 characters. */
const indiceCurat = (s: string): string => s.trim().slice(0, 8);

/** Writes the index of a box's `j`-th device (sheet row 119); an all-empty
 *  list is not written. */
export function schimbaIndiceInDoza(doc: TronsonDoc, i: number, j: number,
                                    index: string): TronsonDoc {
  const a = doc.plan?.electrical?.[i];
  if (a?.kind !== "doza" || !a.devices || j < 0 || j >= a.devices.length) return doc;
  const d = clona(doc);
  const g = d.plan!.electrical![i];
  const lista = g.devices!.map((_, k) => g.device_indices?.[k] ?? "");
  lista[j] = indiceCurat(index);
  if (lista.some(Boolean)) g.device_indices = lista; else delete g.device_indices;
  return d;
}

/** LIPIT DE CEL MAI APROPIAT ZID, cu fața spre cameră.
 *
 *  Un aparat pus cu mâna cade unde a căzut cursorul — adică aproape niciodată
 *  chiar pe perete. Dar o priză în mijlocul camerei nu există: ea se montează
 *  într-o doză, iar doza e în zid. Deci la punere se caută zidul cel mai apropiat
 *  și aparatul se așază pe fața lui dinspre camera din care s-a apăsat.
 *
 *  PESTE `RAZA` NU SE LIPEȘTE. O plafonieră se pune chiar în mijlocul camerei, și
 *  acolo trebuie să rămână; trasă cu forța pe cel mai apropiat perete, ar fi
 *  ajuns o aplică fără să ceară nimeni asta.
 */
const RAZA_LIPIRE = 0.9;
export function lipitDeZid(
  doc: TronsonDoc, p: Pt, kind: string,
): { p: Pt; rot: number } {
  // Corpurile de tavan nu se lipesc: ele chiar stau în aer.
  if (kind === "plafoniera" || kind === "spot") return { p, rot: 0 };
  const pereti = doc.plan?.walls ?? [];
  const aproape: { q: Pt; d: number; n: Pt; gros: number; capat: boolean }[] = [];
  for (const w of pereti) {
    const [a, b] = axaPeretelui(w);
    const vx = b[0] - a[0];
    const vy = b[1] - a[1];
    const L2 = vx * vx + vy * vy;
    const t = L2 < 1e-12 ? 0
      : Math.max(0, Math.min(1, ((p[0] - a[0]) * vx + (p[1] - a[1]) * vy) / L2));
    const q: Pt = [a[0] + vx * t, a[1] + vy * t];
    const dd = Math.hypot(p[0] - q[0], p[1] - q[1]);
    const L = Math.hypot(vx, vy) || 1;
    if (dd <= RAZA_LIPIRE) {
      aproape.push({ q, d: dd, n: [-vy / L, vx / L], gros: w.thickness_m,
                     capat: t <= 0 || t >= 1 });
    }
  }
  if (!aproape.length) return { p, rot: 0 };
  const peFata = (cel: typeof aproape[number]) => {
    // Normala care arată ÎNCOTRO S-A APĂSAT: aparatul privește spre camera din
    // care a fost pus, nu spre cea de dincolo de zid.
    const semn = (p[0] - cel.q[0]) * cel.n[0] + (p[1] - cel.q[1]) * cel.n[1] >= 0
      ? 1 : -1;
    const n: Pt = [cel.n[0] * semn, cel.n[1] * semn];
    // PE FAȚA ZIDULUI, NU PE AXUL LUI. Axul e la mijlocul grosimii, adică în
    // miezul cărămizii: un aparat pus acolo e îngropat, iar în machetă chiar
    // dispare în perete. O priză se montează în doza din TENCUIALĂ, deci pe fața
    // dinspre camera din care a fost pusă — și aia e la o jumătate de grosime de
    // ax. Aceeași față pe care le așază și bagheta (v. `design/auto::peZid`), ca
    // un aparat pus de mână să nu stea altfel decât unul pus automat.
    const jum = cel.gros / 2;
    return { p: [r3(cel.q[0] + n[0] * jum), r3(cel.q[1] + n[1] * jum)] as Pt, n };
  };
  // A face up against another wall looks into solid masonry: where walls meet,
  // the nearest axis can be one whose face is buried, so the next one takes it.
  // A parapet stops at the sill: it hides no wall face behind it.
  const inele = pereti.filter((w) => w.kind !== "parapet").map((w) => inelul(w.poly));
  // The nearest FACE, the one the click is seen beside: by the axis, a thin
  // wall 10 cm away beat a thick one 2 cm away. But a wall the click lies
  // alongside, its face within a device's size, comes before one whose end is
  // merely near: in a room's corner the wall running on past it is often the
  // nearest, and its end would take the device out of the room clicked in
  // (QA OB-28, D4). Farther than that, the end wins: beside a door or a
  // passage it is the jamb the click was meant for, and the wall alongside
  // is the reveal across the opening or a wall 70 cm off.
  const fataLa = (x: typeof aproape[number]) => x.d - x.gros / 2;
  const alaturi = (x: typeof aproape[number]) => !x.capat && fataLa(x) <= 0.15;
  const fete = aproape.sort((x, y) => Number(alaturi(y)) - Number(alaturi(x))
      || fataLa(x) - fataLa(y))
    .map(peFata);
  const fata = fete.find(({ p: q, n }) => !inele.some(
    (inel) => inPlic([q[0] + n[0] * 0.03, q[1] + n[1] * 0.03], inel))) ?? fete[0];
  return { p: fata.p, rot: r3((Math.atan2(fata.n[1], fata.n[0]) * 180) / Math.PI) };
}

/** ZIDURILE CU MATERIALUL LOR — pentru ferirea aparatelor de beton.
 *
 *  Conturul, nu axul: aparatul stă pe FAȚA zidului, iar «pe ce perete e» se
 *  întreabă cu o distanță până la poligonul lui. Un zid curb răspunde atunci
 *  corect fără nicio socoteală de arc. */
export function zidurileCuMaterial(doc: TronsonDoc): ZidMaterial[] {
  return (doc.plan?.walls ?? []).map((w) => ({
    inel: inelul(w.poly), beton: w.material === "beton",
  }));
}

/** GOLURILE, ca segmente între tocuri — în ele nu se montează nimic. */
export function golurileDocumentului(doc: TronsonDoc): GolSimplu[] {
  const out: GolSimplu[] = [];
  for (const fel of ["doors", "windows"] as const) {
    for (const g of doc.plan?.[fel] ?? []) {
      const rad = ((g.rot_deg ?? 0) * Math.PI) / 180;
      const l = (g.w_m ?? 0.9) / 2;
      const c: Pt = [g.x.a, g.y.a];
      out.push({ a: [c[0] - Math.cos(rad) * l, c[1] - Math.sin(rad) * l],
                 b: [c[0] + Math.cos(rad) * l, c[1] + Math.sin(rad) * l] });
    }
  }
  return out;
}

/** APARATELE URMEAZĂ MOBILA, în timpul gestului.
 *
 *  ==========================================================================
 *  E ACEEAȘI REGULĂ, RULATĂ DIN NOU. Un aparat scris cu `pentru` și `parte`
 *  (v. `ElectricDoc`) nu are o poziție a lui: are o poziție care se CALCULEAZĂ
 *  din piesa lui, prin `asezareaLangaPiesa`. Aici se calculează iar — la
 *  fiecare milimetru cu care piesa e trasă pe pânză, fiindcă gestul de mutare
 *  trece prin `mutaMobila`, iar ea cheamă asta.
 *
 *  DE CE SE POATE LA FIECARE CADRU. Fiindcă nu se reia nimic din baghetă: nu se
 *  remobilează camera, nu se recalculează traseele, nu se atinge niciun aparat
 *  nelegat. Se mută aparatele UNEI piese — cel mult trei —, fiecare cu o
 *  proiecție pe conturul camerei ei. Restul desenului nici nu află.
 *
 *  Măsurat pe M5 — cel mai mare modul al catalogului, 65 KB, 97 de ziduri, 103
 *  aparate din care 53 legate: 1,28 ms pe cadru trăgând o piesă CU aparate,
 *  față de 1,37 ms trăgând una fără. Urmărirea se pierde în zgomotul copierii
 *  documentului, pe care mutarea o face oricum.
 *
 *  `care` sunt piesele atinse de gest; fără ea, toate. Traseele firelor se
 *  refac oricum singure la următoarea desenare: ele se deduc din poziții, deci
 *  nu se scriu și nu trebuie invalidate (v. nota din `design/electric`).
 *  ==========================================================================
 */
export function aparateleUrmeazaMobila(
  doc: TronsonDoc, care?: readonly number[],
): TronsonDoc {
  if (!deMutatDupaMobila(doc, care).length) return doc;
  const d = clona(doc);
  urmeazaInLoc(d, care);
  return d;
}

/** Aparatele legate de piesele cerute — partea care se poate întreba fără să
 *  se copieze documentul, ca un gest care n-are ce muta să nu-l copieze. */
function deMutatDupaMobila(
  doc: TronsonDoc, care?: readonly number[],
): { a: ElectricDoc; i: number }[] {
  const lista = doc.plan?.electrical;
  if (!lista?.length || !doc.plan?.furniture?.length) return [];
  const cerute = care ? new Set(care) : null;
  return lista
    .map((a, i) => ({ a, i }))
    .filter(({ a }) => a.pentru != null && a.parte != null
      && (!cerute || cerute.has(a.pentru)));
}

/** Aceeași urmărire, PE DOCUMENTUL PRIMIT. Gesturile de pe pânză își fac deja
 *  copia lor (mută o piesă, deci o scriu), iar o a doua copie pe cadru e, la
 *  un modul de 65 KB, chiar jumătate din timpul gestului. Rămâne privată: în
 *  afară nu se scrie în documente, se întorc altele. */
function urmeazaInLoc(d: TronsonDoc, care?: readonly number[]): void {
  const deMutat = deMutatDupaMobila(d, care);
  if (!deMutat.length) return;
  const mobila = d.plan!.furniture!;
  const ziduri = zidurileCuMaterial(d);
  const goluri = golurileDocumentului(d);
  const inele = (d.plan!.rooms ?? []).map((r) => inelul(r.poly));
  const mutate = new Set<number>();
  for (const { a, i } of deMutat) {
    const f = mobila[a.pentru!];
    if (!f) continue;
    const piesa = caPiesa(f);
    // CAMERA PIESEI, după centrul ei. O piesă trasă afară din orice cameră —
    // peste conturul modulului, cât ține gestul — nu-și duce aparatul nicăieri:
    // n-ar avea pe ce perete să-l pună, iar aparatul rămâne unde era, ceea ce e
    // și starea din care gestul se poate întoarce.
    const inel = inele.find((ring) => inPoligon(piesa.c, ring));
    if (!inel) continue;
    // NICIO POZIȚIE NOUĂ, NICIO MUTARE. Așezarea dă `null` când peretele din
    // spatele piesei, pe partea aparatului, e numai ușă sau fereastră — și
    // atunci aparatul rămâne unde era, ca și când piesa ar fi ieșit din cameră.
    // Nu se șterge: un gest e reversibil, iar trăgând piesa înapoi aparatul
    // trebuie să-și reia locul. Ce se pune și ce nu e decizia BAGHETEI, care
    // scrie o dată; urmărirea doar mută ce e deja scris.
    const r = asezareaLangaPiesa(piesa, a.parte!, inel, ziduri, goluri);
    if (!r) continue;
    const g = d.plan!.electrical![i];
    g.x = { ...g.x, a: r3(r.q[0]) };
    g.y = { ...g.y, a: r3(r.q[1]) };
    g.rot_deg = r3(r.deg);
    mutate.add(i);
  }
  if (!mutate.size) return;

  // DOUĂ APARATE NU STAU UNUL PESTE ALTUL, nici după ce piesa s-a mutat: o
  // canapea împinsă lângă comoda televizorului aduce priza ei peste priza de
  // semnal. Se împing doar cele care tocmai s-au mutat — restul sunt ori puse
  // de mână, ori ale altor piese, și n-au de ce să se dea la o parte.
  const desfacute = desfaSuprapunerile(d.plan!.electrical!.map((e, i) => ({
    q: [e.x.a, e.y.a] as Pt,
    deg: e.rot_deg ?? 0,
    d: aparatul(e.kind)?.d ?? 0.16,
    mobil: mutate.has(i),
  })), goluri);
  desfacute.forEach((x, i) => {
    if (!mutate.has(i)) return;
    const g = d.plan!.electrical![i];
    g.x = { ...g.x, a: r3(x.q[0]) };
    g.y = { ...g.y, a: r3(x.q[1]) };
  });
}

/** INSTALAȚIA UNEI CAMERE, pusă la loc — butonul de lângă «Mobilează camera».
 *
 *  Înlocuiește aparatele DIN CAMERA AIA și nu atinge nimic altundeva: e gestul
 *  cerut după ce s-a schimbat mobila sau felul camerei, iar reluarea baghetei
 *  pe tot modulul ar șterge ce a pus omul în celelalte douăzeci.
 *
 *  TABLOUL RĂMÂNE, oriunde ar fi. El nu e al camerei, e al apartamentului: îl
 *  pune bagheta în holul locuinței, iar rețeta unei camere nu-l cunoaște. Șters
 *  aici, toate firele modulului ar rămâne fără capăt (v. `design/electric`). */
export function puneInstalatiaCamerei(doc: TronsonDoc, i: number): TronsonDoc {
  const r = doc.plan?.rooms?.[i];
  if (!r) return doc;
  const plan = deriva(doc);
  const cam = plan.camere[i];
  if (!cam) return doc;
  const mobila = doc.plan!.furniture ?? [];
  const contur = inelul(r.poly);
  const ale = mobila.filter((f) => inPoligon([f.x.a, f.y.a], contur));
  const noi = instalatiaCamerei(plan, cam, ale, (f) => mobila.indexOf(f));
  const d = clona(doc);
  // CE SE ÎNLOCUIEȘTE: aparatele camerei — plus cele pe care rețeta ei le pune
  // ÎN AFARA ei. Întrerupătorul unei băi se montează dinadins pe holul de
  // dinaintea ei (zonarea nu-l lasă înăuntru, v. `design/auto`), deci nu e «al
  // camerei» după nicio măsură geometrică, dar e al rețetei ei: fără rândul de
  // mai jos, a doua apăsare pe buton ar lăsa două întrerupătoare unul peste
  // altul pe hol. Se recunoaște ca ACELAȘI aparat — același fel, la mai puțin
  // de o palmă de cel nou —, nu ca «orice e prin preajmă».
  const acelasi = (e: { kind: string; x: { a: number }; y: { a: number } }) =>
    noi.some((n) => n.kind === e.kind
      && Math.hypot(n.x.a - e.x.a, n.y.a - e.y.a) < 0.3);
  // A box stays too: it is placed and dimensioned by hand, not by a recipe.
  const deInlocuit = (e: { kind: string; x: { a: number }; y: { a: number };
                           rot_deg?: number }) =>
    e.kind !== "tablou" && e.kind !== "doza"
    && (aparatulEAlCamerei([e.x.a, e.y.a], e.rot_deg ?? 0, contur) || acelasi(e));
  d.plan!.electrical = [
    ...(d.plan!.electrical ?? []).filter((e) => !deInlocuit(e)),
    ...noi,
  ];
  return d;
}

/** UN APARAT NOU ÎN CAMERĂ, pus unde e loc — butonul «Adaugă» din panou.
 *
 *  DE CE NU ÎN MIJLOCUL CAMEREI. Fiindcă acolo n-are ce căuta: o priză se
 *  montează în zid. Deci se caută pe conturul camerei, din 10 în 10 cm, locul
 *  cel mai departe de aparatele care sunt deja acolo — ferit de goluri, iar
 *  betonul pierde la egalitate, ca peste tot. Pe un perete plin ochi nu mai
 *  există «departe», și atunci iese lângă ultimul: se vede că nu mai e loc, și
 *  se mută cu mâna.
 *
 *  Corpurile de tavan fac excepție fiindcă ele chiar stau în aer: plafoniera se
 *  pune în mijlocul camerei, unde stă orice plafonieră. */
export function adaugaAparatInCamera(
  doc: TronsonDoc, camera: number, kind: string,
): { doc: TronsonDoc; ales: Ales } {
  const r = doc.plan?.rooms?.[camera];
  if (!r) return { doc, ales: null };
  const inel = inelul(r.poly);
  const lista = doc.plan!.electrical ?? [];
  let unde: Pt;
  let rot = 0;
  if (kind === "plafoniera" || kind === "spot") {
    const xs = inel.map((p) => p[0]);
    const ys = inel.map((p) => p[1]);
    const c: Pt = [(Math.min(...xs) + Math.max(...xs)) / 2,
                   (Math.min(...ys) + Math.max(...ys)) / 2];
    unde = inPoligon(c, inel) ? c
      : [inel.reduce((a, p) => a + p[0], 0) / inel.length,
         inel.reduce((a, p) => a + p[1], 0) / inel.length];
  } else {
    const ziduri = zidurileCuMaterial(doc);
    const goluri = golurileDocumentului(doc);
    const ale = lista.filter(
      (e) => aparatulEAlCamerei([e.x.a, e.y.a], e.rot_deg ?? 0, inel));
    let cel: { q: Pt; deg: number } | null = null;
    let cea = -Infinity;
    for (let k = 0; k < inel.length; k++) {
      const a = inel[k];
      const b = inel[(k + 1) % inel.length];
      const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
      const n = Math.max(1, Math.round(L / 0.1));
      for (let j = 0; j <= n; j++) {
        const q: Pt = [a[0] + ((b[0] - a[0]) * j) / n, a[1] + ((b[1] - a[1]) * j) / n];
        if (inGol(q, goluri)) continue;
        let scor = ale.length
          ? Math.min(...ale.map((e) => Math.hypot(e.x.a - q[0], e.y.a - q[1])))
          : Math.min(...inel.map((v) => Math.hypot(v[0] - q[0], v[1] - q[1])));
        if (pePeretDeBeton(q, ziduri)) scor -= 1;
        if (scor <= cea) continue;
        cea = scor;
        const ux = (b[0] - a[0]) / (L || 1);
        const uy = (b[1] - a[1]) / (L || 1);
        const cand: Pt = [-uy, ux];
        const spre = inPoligon([q[0] + cand[0] * 0.12, q[1] + cand[1] * 0.12], inel)
          ? cand : ([-cand[0], -cand[1]] as Pt);
        cel = { q, deg: r3((Math.atan2(spre[1], spre[0]) * 180) / Math.PI) };
      }
    }
    unde = cel?.q ?? [inel[0][0], inel[0][1]];
    rot = cel?.deg ?? 0;
  }
  return adaugaAparat(doc, kind, [r3(unde[0]), r3(unde[1])], rot);
}

/** DEZLEAGĂ APARATUL DE PIESA LUI — rămâne unde e, dar nu mai e dus de ea.
 *
 *  Perechea butonului din panou: urmărirea e o ușurare cât timp o vrei, și o
 *  piedică din clipa în care nu. Legătura se face la loc punând instalația
 *  camerei din nou. */
export function dezleagaAparatul(doc: TronsonDoc, i: number): TronsonDoc {
  const a = doc.plan?.electrical?.[i];
  if (!a || a.pentru == null) return doc;
  const d = clona(doc);
  const g = d.plan!.electrical![i];
  delete g.pentru;
  delete g.parte;
  return d;
}

/** LEGĂTURILE, DUPĂ CE O PIESĂ A PLECAT DIN LISTĂ.
 *
 *  `pentru` e o POZIȚIE în `plan.furniture`, ca `FinisajDoc::room` în
 *  `plan.rooms`, deci o piesă ștearsă din mijloc ar muta toate legăturile de
 *  după ea cu un rând: priza patului ar ajunge «a dulapului» fără ca nimeni să
 *  fi atins nimic.
 *
 *  APARATUL RĂMÂNE, doar legătura se rupe. O priză desenată pe planșă se și
 *  execută: ștearsă odată cu canapeaua, ar dispărea dintr-un desen pe care
 *  cineva l-a citit deja. Rămasă nelegată, e chiar ce e — o priză pe perete. */
function legaturiFaraPiesa(plan: NonNullable<TronsonDoc["plan"]>, i: number) {
  for (const a of plan.electrical ?? []) {
    if (a.pentru == null) continue;
    if (a.pentru === i) { delete a.pentru; delete a.parte; }
    else if (a.pentru > i) a.pentru -= 1;
  }
}

/** AXELE ZIDURILOR, pentru drumul firelor. Cele scrise; pentru un zid fără
 *  `axis` (desenat de o versiune veche), deduse din dreptunghi. */
export function axeleZidurilor(doc: TronsonDoc): { a: Pt; b: Pt }[] {
  return (doc.plan?.walls ?? []).map((w) => {
    const [a, b] = axaPeretelui(w);
    return { a, b };
  });
}

// --------------------------------------------------------------- istoric ---

/** Teancul de anulări. Ține DOCUMENTE, nu operații inverse.
 *
 *  Un document de tronson e de zeci de KB și un gest îl atinge în câteva
 *  locuri; teancul de operații inverse ar fi mai mic, dar fiecare operație ar
 *  trebui să știe să se dea înapoi — și fiecare nouă unealtă ar aduce încă o
 *  inversă de scris și de greșit. La adâncimea asta (50 de pași × 60 KB = 3 MB
 *  în cel mai rău caz) copia întreagă e ieftină și nu poate fi greșită.
 */
export class Istoric {
  private inapoi: TronsonDoc[] = [];
  private inainte: TronsonDoc[] = [];
  private readonly adancime = 50;

  pune(doc: TronsonDoc) {
    this.inapoi.push(doc);
    if (this.inapoi.length > this.adancime) this.inapoi.shift();
    this.inainte = [];
  }

  anuleaza(curent: TronsonDoc): TronsonDoc | null {
    const d = this.inapoi.pop();
    if (!d) return null;
    this.inainte.push(curent);
    return d;
  }

  refa(curent: TronsonDoc): TronsonDoc | null {
    const d = this.inainte.pop();
    if (!d) return null;
    this.inapoi.push(curent);
    return d;
  }

  get poateAnula() { return this.inapoi.length > 0; }
  get poateReface() { return this.inainte.length > 0; }
}

// ------------------------------------------------------- spațiul exterior ---

/** BALCONUL NU E O PRIMITIVĂ NOUĂ, și de-aia secțiunea asta e scurtă.
 *
 *  Documentul are camere și pereți. Un balcon e o CAMERĂ EXTERIOARĂ închisă cu
 *  PARAPEȚI — exact ce e o cameră obișnuită închisă cu ziduri. Gestul e cel de
 *  cameră, uneltele sunt cele de zid, panoul e cel de cameră; ce se adaugă aici
 *  sunt cele trei lucruri pe care geometria nu le poate spune:
 *
 *    1. CARE LATURI SE ÎNCHID. Se închid cu parapet exact laturile pe care nu
 *       stă deja un zid. Aceeași regulă dă și balconul în consolă (trei laturi
 *       libere, a patra e fațada) și logia (o singură latură liberă, restul e
 *       scobitura din volum) — deci nu sunt două unelte, e una.
 *    2. CONSOLĂ SAU LOGIE. Din poziția față de anvelopă: afară e consolă,
 *       înăuntru e logie. Hotărăște o cifră — consola nu intră în suprafața
 *       construită, logia da —, deci se SCRIE în document (`outdoor_kind`), nu
 *       se lasă pe seama unui test geometric cu toleranța fiecărui consumator.
 *    3. AL CUI E. Un spațiu exterior aparține unei locuințe, iar `plan.outdoor`
 *       o cere ca indice. Se ia de la camera de dincolo de latura închisă —
 *       aceea din care se iese pe balcon.
 *
 *  CE NU SE FACE AICI: peticele din `plan.outdoor`. Ele sunt DERIVATE din
 *  desen — poligonul dalei, pereții care vin cu alegerea, zidurile pe care
 *  celelalte alegeri le scot — și se pun la salvare, pe server
 *  (`layout/tronson_edit.py`), unde stă deja tot ce e derivat. Un al doilea loc
 *  care știe să compună un petic ar fi exact divergența pe care catalogul e
 *  construit s-o evite.
 */

/** Cât de aproape trebuie să treacă un zid pe lângă o latură ca latura să fie
 *  ÎNCHISĂ de el: jumătate din cel mai gros zid al catalogului. Mai puțin ar
 *  pune un parapet peste fațadă; mai mult ar lăsa o latură deschisă lângă un
 *  zid care de fapt n-o atinge. */
const LATURA_INCHISA = 0.3;

/** Cât din latură trebuie să acopere zidul ca s-o închidă. Sub un sfert de
 *  metru e o atingere, nu o închidere. */
const ACOPERA_LATURA = 0.25;

/** Latura `a→b` e deja închisă de un zid? */
function laturaInchisa(doc: TronsonDoc, a: Pt, b: Pt): boolean {
  const dx = Math.abs(b[0] - a[0]);
  const dy = Math.abs(b[1] - a[1]);
  // Oblică: n-o ține niciun zid din catalogul de azi, deci se închide.
  if (Math.min(dx, dy) > LIPIRE) return false;
  const axa: 0 | 1 = dx < dy ? 0 : 1;
  const alta = (1 - axa) as 0 | 1;
  const valoare = (a[axa] + b[axa]) / 2;
  const jos = Math.min(a[alta], b[alta]);
  const sus = Math.max(a[alta], b[alta]);
  return (doc.plan?.walls ?? []).some((w) => {
    const o = osiaPeretelui(inelul(w.poly));
    if (o.axa !== axa) return false;
    if (Math.min(...o.fete.map((f) => Math.abs(f - valoare))) > LATURA_INCHISA) {
      return false;
    }
    const s0 = Math.min(...o.span);
    const s1 = Math.max(...o.span);
    return Math.min(s1, sus) - Math.max(s0, jos) >= ACOPERA_LATURA;
  });
}

/** CÂT DE DEPARTE DE FAȚADĂ POATE CĂDEA UN BALCON TRAS DE MÂNĂ și tot să se
 *  lipească de ea.
 *
 *  Trei sferturi de metru, și nu e o cifră de confort. Un balcon trebuie să
 *  atingă clădirea ca să existe — o dală care nu se sprijină pe nimic n-are unde
 *  fi pusă —, dar la scara ecranului fațada e o linie de un pixel: la 21 px/m,
 *  jumătate de metru e un centimetru de mouse. Fără lipire, gestul cădea pe
 *  tăcute (`adaugaBalcon` întorcea `null`) și fantoma dispărea la ridicarea
 *  degetului, ceea ce arată exact ca «am desenat un balcon și n-a apărut».
 *
 *  Se mută TOT dreptunghiul, nu se întinde latura: cine a tras 3 × 1,5 a spus cât
 *  vrea balconul, nu unde începe fațada. */
const LIPIRE_FATADA = 0.75;

/** Dreptunghiul se suprapune peste vreun zid? Pe cadre, nu pe poligoane: un
 *  zid E un dreptunghi subțire, iar întrebarea e «stă balconul peste el», nu
 *  «cu cât». Pragul de un centimetru lasă atingerea față-la-față să treacă. */
function pesteVreunZid(doc: TronsonDoc, ring: Pt[]): boolean {
  const c = cadru(ring);
  return (doc.plan?.walls ?? []).some((w) => {
    const z = cadru(inelul(w.poly));
    return Math.min(c.x + c.w, z.x + z.w) - Math.max(c.x, z.x) > 0.01
        && Math.min(c.y + c.h, z.y + z.h) - Math.max(c.y, z.y) > 0.01;
  });
}

/** Dreptunghiul, tras până se așază cu o latură pe fața unui zid.
 *
 *  DOUĂ REGULI, amândouă ieșite din câte un gest care ieșea prost:
 *
 *    * se lipește de fața dinspre care a fost DESENAT, nu de cea mai apropiată —
 *      altfel un balcon tras puțin peste fațadă sărea ÎN clădire, peste zidul de
 *      care trebuia să se sprijine;
 *    * dintre așezările posibile câștigă cea mai mică ce lasă balconul LIBER de
 *      ziduri, nu pur și simplu cea mai mică. Un balcon tras peste fațadă are
 *      fața interioară a compartimentării mai aproape (20 cm) decât muchia
 *      exterioară a anvelopei (35 cm), deci regula scurtă îl așeza flush cu
 *      interiorul și-l lăsa călare pe fațadă.
 *
 *  `null` = n-are de ce se lipi. */
function lipitDeFatada(doc: TronsonDoc, contur: Pt[]): Pt[] | null {
  const c = cadru(contur);
  const centru: Pt = [c.x + c.w / 2, c.y + c.h / 2];
  const candidati: { axa: 0 | 1; delta: number }[] = [];
  for (let i = 0; i < contur.length; i++) {
    const a = contur[i];
    const b = contur[(i + 1) % contur.length];
    const dx = Math.abs(b[0] - a[0]);
    const dy = Math.abs(b[1] - a[1]);
    if (Math.min(dx, dy) > LIPIRE) continue;
    const axa: 0 | 1 = dx < dy ? 0 : 1;
    const alta = (1 - axa) as 0 | 1;
    const v = (a[axa] + b[axa]) / 2;
    const jos = Math.min(a[alta], b[alta]);
    const sus = Math.max(a[alta], b[alta]);
    for (const w of doc.plan?.walls ?? []) {
      const o = osiaPeretelui(inelul(w.poly));
      if (o.axa !== axa) continue;
      const s0 = Math.min(...o.span);
      const s1 = Math.max(...o.span);
      if (Math.min(s1, sus) - Math.max(s0, jos) < ACOPERA_LATURA) continue;
      // Fața dinspre care s-a desenat: cea de care dreptunghiul stă mai
      // aproape pe partea lui.
      const fata = centru[axa] > v ? Math.max(...o.fete) : Math.min(...o.fete);
      const delta = fata - v;
      if (Math.abs(delta) > LIPIRE_FATADA) continue;
      candidati.push({ axa, delta });
    }
  }
  if (!candidati.length) return null;
  candidati.sort((p, q) => Math.abs(p.delta) - Math.abs(q.delta));
  const mutat = ({ axa, delta }: { axa: 0 | 1; delta: number }) =>
    contur.map((p) => (axa === 0 ? [r3(p[0] + delta), p[1]]
                                 : [p[0], r3(p[1] + delta)]) as Pt);
  for (const cand of candidati) {
    const ring = mutat(cand);
    if (!pesteVreunZid(doc, ring)) return ring;
  }
  // Niciuna nu-l lasă liber — între două ziduri, de pildă, unde chiar asta a
  // cerut omul (o logie scobită). Atunci câștigă deplasarea cea mai mică.
  return mutat(candidati[0]);
}

/** A câta locuință a modulului e `(latura, al câtelea de pe latură)`, în
 *  numărătoarea pe care o citesc variantele și `plan.outdoor`: întâi latura A,
 *  apoi B. Aceeași ordine ca `tronson_edit::recalculeaza`. */
export function locuintaModulului(doc: TronsonDoc, side: "A" | "B",
                                  peLatura = 0): number {
  const a = doc.sides?.A?.flats?.length ?? 1;
  return (side === "A" ? 0 : a) + peLatura;
}

/** CUI ÎI APARȚINE SPAȚIUL EXTERIOR de sub conturul dat: camera din spatele
 *  laturii lui închise.
 *
 *  Nu se poate citi din poziție singură. Pe o bară, `side` s-ar deduce din
 *  semnul lui y — dar pe un colț toate cele patru locuințe stau pe latura A, iar
 *  un balcon lipit de o bucătărie și unul lipit de livingul de alături ies la
 *  fel din geometrie. Deci se întreabă camera de dincolo de fațadă, cea din care
 *  se iese pe el. */
function stapanulExteriorului(doc: TronsonDoc, contur: Pt[]):
    { side: "A" | "B"; flat?: number } {
  const c = cadru(contur);
  const centru: Pt = [c.x + c.w / 2, c.y + c.h / 2];
  let cea: { side: "A" | "B"; flat?: number } | null = null;
  let cel = Infinity;
  for (const r of doc.plan?.rooms ?? []) {
    if (r.kind === "hol" || r.kind === "balcony") continue;
    const rc = cadru(inelul(r.poly));
    const d = Math.hypot(rc.x + rc.w / 2 - centru[0], rc.y + rc.h / 2 - centru[1]);
    if (d < cel) { cel = d; cea = { side: r.side, ...(r.flat != null ? { flat: r.flat } : {}) }; }
  }
  // FĂRĂ LATURA B ÎN DOCUMENT, totul e A. Semnul lui y e o regulă de bară (y = 0
  // e axa coridorului); pe un colț sau pe o casă izolată y e mereu pozitiv, iar
  // regula ar fi pus fiecare cameră pe o latură pe care documentul n-o declară.
  return cea ?? { side: doc.sides?.B ? (centru[1] > 0 ? "B" : "A") : "A" };
}

/** Ce declară varianta implicită despre spațiul exterior al locuinței `i`,
 *  schimbat. `null` = locuința rămâne fără nimic afară.
 *
 *  SE SCRIE DIN GEST, nu la salvare. `variants[].outdoor` e o DECLARAȚIE — ce
 *  are locuința —, iar cine desenează un balcon tocmai a declarat-o. Lăsată pe
 *  seama serverului, ar fi fost o recalculare care rescrie o declarație, adică
 *  exact ce `recalculeaza` refuză să facă cu ariile; iar lăsată nescrisă, ar fi
 *  picat la publicare cu un mesaj despre variante, pe care nimeni nu l-ar fi
 *  legat de zidul pe care tocmai l-a tras. */
function declaraExteriorul(d: TronsonDoc, locuinta: number,
                           fel: "balcony" | "loggia" | null): void {
  const variante = d.variants ?? [];
  const v = variante.find((x) => x.default) ?? variante[0];
  if (!v) return;
  const n = (d.sides?.A?.flats?.length ?? 0) + (d.sides?.B?.flats?.length ?? 0);
  const afara = [...(v.outdoor ?? [])];
  while (afara.length < n) afara.push("none");
  if (locuinta < 0 || locuinta >= afara.length) return;
  afara[locuinta] = fel ?? "none";
  v.outdoor = afara;
}

/** UN BALCON NOU, tras ca un dreptunghi.
 *
 *  `null` când dreptunghiul e prea mic — un clic scăpat nu e un balcon — sau
 *  când n-are nicio latură lipită de clădire: un balcon care nu atinge nimic e
 *  o dală care plutește, iar catalogul n-are unde s-o pună. */
/** SE VA PRINDE balconul tras acum de clădire? — întrebarea pusă CÂT ÎL TRAGI.
 *
 *  Un balcon care nu atinge nimic nu se pune (v. `adaugaBalcon`), iar până acum
 *  asta se afla abia la ridicarea degetului, când fantoma dispărea și nu se
 *  întâmpla nimic — «am desenat un balcon și n-a apărut». Cifra de lângă cursor
 *  o spune acum înainte, ca orice altă cotă vie. */
export function balconulSePrinde(doc: TronsonDoc, a: Pt, b: Pt): boolean {
  const tras = conturCamerei(a, b, "camera");
  if (arieAfisata(tras) < 0.5) return false;
  const contur = lipitDeFatada(doc, tras) ?? tras;
  return contur.some((p, i) => laturaInchisa(doc, p, contur[(i + 1) % contur.length]));
}

export function adaugaBalcon(
  doc: TronsonDoc, a: Pt, b: Pt,
): { doc: TronsonDoc; ales: Ales } | null {
  const tras = conturCamerei(a, b, "camera");
  const arie = arieAfisata(tras);
  if (arie < 0.5) return null;
  if (!doc.plan) return null;

  // SE LIPEȘTE DE FAȚADĂ — v. `LIPIRE_FATADA`. Se încearcă ÎNTOTDEAUNA, nu doar
  // când n-a nimerit: lipirea alege oricum deplasarea cea mai mică, deci un
  // dreptunghi deja așezat se mută cu zero. Cerută pentru cazul opus, cel tras
  // PESTE fațadă: acolo o latură cădea în toleranța de «închisă» și balconul
  // rămânea suprapus peste zidul pe care ar fi trebuit să se sprijine.
  const contur = lipitDeFatada(doc, tras) ?? tras;

  const laturi = contur.map((p, i) => [p, contur[(i + 1) % contur.length]] as [Pt, Pt]);
  const inchise = laturi.map(([p, q]) => laturaInchisa(doc, p, q));
  if (!inchise.some(Boolean)) return null;

  let d = clona(doc);
  const gros = IMPLICITELE_ZIDULUI.parapet.grosime;
  const lung = laturi.map(([p, q]) => Math.hypot(q[0] - p[0], q[1] - p[1]));
  // Câte laturi deschise are alături latura `i`: parapetul din față al unui
  // balcon în consolă are două, cei de pe lături câte una.
  const deschiseLanga = (i: number) =>
    [i + laturi.length - 1, i + 1].filter((j) => !inchise[j % laturi.length]).length;
  /** Al cui e colțul dintre laturile `i` și `j`: al celei cu mai mulți vecini
   *  deschiși — parapetul din față trece, lateralii se opresc în el —, apoi al
   *  celei mai lungi, apoi al primei. Hotărât o dată pe colț, ca amândoi să
   *  răspundă la fel. */
  const alLui = (i: number, j: number): boolean => {
    const di = deschiseLanga(i);
    const dj = deschiseLanga(j);
    if (di !== dj) return di > dj;
    if (Math.abs(lung[i] - lung[j]) > 1e-6) return lung[i] > lung[j];
    return i < j;
  };
  /** Cât se retrage capătul laturii `i` dinspre vecina `j`: nimic spre
   *  clădire sau când colțul e al lui, o grosime întreagă când e al vecinului. */
  const retragerea = (i: number, j: number): number =>
    inchise[j] || alLui(i, j) ? 0 : gros;
  for (let i = 0; i < laturi.length; i++) {
    if (inchise[i]) continue;
    const [p0, p1] = laturi[i];
    const dx = p1[0] - p0[0];
    const dy = p1[1] - p0[1];
    const L = Math.hypot(dx, dy) || 1;
    const ux = dx / L;
    const uy = dy / L;

    // PARAPETUL STĂ PE DALĂ, CU FAȚA PE MUCHIA EI — nu călare pe ea.
    //
    // Axul se așază înăuntru cu jumătate de grosime, deci fața dinspre afară
    // cade CHIAR pe latura balconului. Așa sunt transcrise și logiile din
    // catalog (M6, M8: dala de la y = −7,65, parapetul de la −7,65 la −7,50), și
    // nu e o chestiune de desen: tot editorul leagă o cameră de zidurile ei prin
    // FEȚE (v. `zidurilePeLatura`). Așezat cu axul pe muchie, parapetul avea
    // fețele la 7,5 cm de o parte și de alta a laturii, deci nu era al niciunei
    // laturi: balconul mutat pleca singur — camera se muta, parapeții rămâneau
    // pe loc, iar mobila de pe el se întorcea la ei la prima reașezare. Pe
    // deasupra, jumătate din parapet ieșea în consolă pe lângă dală.
    let nx = -uy;
    let ny = ux;
    const mij: Pt = [(p0[0] + p1[0]) / 2, (p0[1] + p1[1]) / 2];
    if (!inPoligon([mij[0] + nx * 0.01, mij[1] + ny * 0.01], contur)) {
      nx = -nx;
      ny = -ny;
    }
    // COLȚUL DINTRE DOI PARAPEȚI E AL UNUIA SINGUR. Unul trece până în colțul
    // dalei, celălalt se oprește pe fața lui dinăuntru — colțul închis pe care
    // îl face și peretele desenat de mână (v. `coltulLipit`): două
    // dreptunghiuri care se ating pe o muchie întreagă.
    //
    // A fost altfel: amândoi se trăgeau înapoi cu JUMĂTATE de grosime, ca
    // axele să se întâlnească în colț — dar un zid se termină unde i se
    // termină axul, deci în fiecare colț rămânea o crestătură de 7,5 × 7,5 cm
    // spre afară și, lângă ea, un pătrat la fel de mare acoperit de amândoi.
    //
    // Cel rezemat de clădire rămâne pe muchie: acolo parapetul trebuie să
    // atingă fațada, nu să se oprească la 7,5 cm de ea.
    const inainte = retragerea(i, (i + laturi.length - 1) % laturi.length);
    const dupa = retragerea(i, (i + 1) % laturi.length);
    const ix = (nx * gros) / 2;
    const iy = (ny * gros) / 2;
    d = adaugaPerete(d, [r3(p0[0] + ix + ux * inainte), r3(p0[1] + iy + uy * inainte)],
                        [r3(p1[0] + ix - ux * dupa), r3(p1[1] + iy - uy * dupa)],
                     undefined, 0, "parapet").doc;
  }

  const c = cadru(contur);
  const centru: Pt = [c.x + c.w / 2, c.y + c.h / 2];
  const fel: "balcony" | "loggia" = inPlic(centru, anvelopa(doc))
    ? "loggia" : "balcony";
  const stapan = stapanulExteriorului(doc, contur);
  const plan = d.plan!;
  plan.rooms.push({
    kind: "balcony", side: stapan.side,
    ...(stapan.flat != null ? { flat: stapan.flat } : {}),
    outdoor_kind: fel,
    area_m2: Math.round(arie * 100) / 100,
    poly: contur.map((p) => [{ a: r3(p[0]) }, { a: r3(p[1]) }] as VarfAfin),
  });
  declaraExteriorul(d, locuintaModulului(d, stapan.side, stapan.flat ?? 0), fel);
  return { doc: d, ales: { fel: "camera", i: plan.rooms.length - 1 } };
}

/** Consolă sau logie, schimbat din panou — și cu el declarația variantei.
 *
 *  NU MUTĂ GEOMETRIA. Cele două chiar diferă prin ea — o logie e scobită în
 *  volum, un balcon iese din el —, dar mutarea e a omului: trage dala înăuntru
 *  sau în afară și zidurile o urmează. Ce face câmpul ăsta e să spună CE a
 *  desenat, fiindcă din poligon se poate deduce, iar o deducție nu e o
 *  declarație: un balcon lipit de fațadă, la un centimetru înăuntru, ar fi
 *  ieșit logie și ar fi intrat în suprafața construită fără ca nimeni s-o
 *  ceară. */
export function schimbaExteriorul(doc: TronsonDoc, i: number,
                                  fel: "balcony" | "loggia"): TronsonDoc {
  const r = doc.plan?.rooms?.[i];
  if (!r || r.kind !== "balcony" || r.outdoor_kind === fel) return doc;
  const d = clona(doc);
  d.plan!.rooms[i].outdoor_kind = fel;
  const cam = d.plan!.rooms[i];
  declaraExteriorul(d, locuintaModulului(d, cam.side, cam.flat ?? 0), fel);
  return d;
}

// ------------------------------------------------ UN TRONSON DE LA ZERO ---
//
// PÂNĂ AICI UN MODUL NOU SE PUTEA NAȘTE DOAR DINTR-UNUL VECHI: deschideai M2,
// «Salvează ca nou», și de-acolo ștergeai ce nu-ți trebuia. Adică pentru un
// modul care n-are nimic din M2 aveai de șters cincizeci și trei de ziduri
// înainte de prima linie desenată de tine.
//
// CATEGORIA HOTĂRĂȘTE FORMA, și numai forma. Cele trei ale colecției — capăt,
// mijloc, colț — nu sunt o etichetă pusă pe un desen: spun UNDE poate sta
// modulul pe o bară, iar de-acolo vine amprenta lui. Un capăt și un mijloc sunt
// dreptunghiuri cât banda de adânci; un colț e L-ul în care se întâlnesc două
// bare, adică pătratul laturii lui din care lipsește crestătura dinăuntru
// (v. `doc::anvelopa`). De-aia alegerea categoriei e tot ce se cere la naștere.
//
// «GOL» ÎNSEAMNĂ FĂRĂ NIMIC DESENAT, nu fără cifre. Documentul pleacă cu
// adâncimea benzii a catalogului, cu frontul lui și cu laturile declarate —
// altfel n-ar fi un șablon de tronson, ar fi un poligon —, iar `plan` are
// listele goale. Probele catalogului pică pe el, toate, fiindcă un modul fără
// camere n-are arie utilă și nu acoperă nimic din amprentă; e starea adevărată
// a unui desen abia început, iar pastila de verificări o arată ca atare. Se
// salvează ca CIORNĂ, deci nicio generare nu-l vede până nu-l publică cineva.
//
// CIFRELE SUNT TIPICE, NU SFINTE. Frontul și latura colțului sunt numere
// rotunde din gama pe care o măsoară catalogul (fronturi între 6,10 și 13,50;
// latura lui M5, 17,55) — de unde se pornește, nu unde se ajunge: conturul se
// trage de colț și de latură din prima clipă.

/** Cele trei categorii ale colecției. Aceleași cuvinte pe care le derivă
 *  serverul din `role` (v. `api::Tronson::category`), fiindcă ele sunt ce vede
 *  omul — `role` are cinci valori, dintre care «core» e un mijloc cu nucleu. */
export type CategorieTronson = "capat" | "mijloc" | "colt" | "izolat";

/** ADÂNCIMEA BENZII a catalogului: 15,30 m pe toate cele șapte module.
 *
 *  E o cifră a COLECȚIEI, nu a modulului — module de adâncimi diferite nu pot
 *  sta pe aceeași bară, iar catalogul are o probă pentru asta
 *  (`test_every_module_declares_one_band_depth`). Un modul nou pornește deci de
 *  la ea, ca să se lege cu ce e deja acolo; dacă proiectul lucrează pe altă
 *  bandă, se schimbă din panou și catalogul spune la publicare ce nu se leagă. */
export const BANDA_CATALOG = 15.3;

/** Forma tipică a fiecărei categorii: rolul pe care-l scrie în document,
 *  frontul de pornire și laturile pe care declară locuințe.
 *
 *  Colțul are o singură latură — schema o cere («A corner module has no two
 *  sides in the ribbon sense, so it declares them all under A») — și e RIGID:
 *  un colț se așază, se oglindește și se rotește, nu se întinde, fiindcă barele
 *  pe care le leagă își poartă singure elasticitatea. */
const FORMA_TIPICA: Record<CategorieTronson, {
  role: TronsonDoc["role"]; front: number; laturi: ("A" | "B")[];
  rigid?: boolean;
}> = {
  capat: { role: "end", front: 10, laturi: ["A", "B"] },
  mijloc: { role: "mid", front: 12, laturi: ["A", "B"] },
  colt: { role: "corner-L", front: 24, laturi: ["A"], rigid: true },
  // IZOLATUL: o casă. O singură latură (nu există coridor de care să aibă
  // două) și RIGID, din alt motiv decât colțul — colțul e rigid fiindcă barele
  // pe care le leagă își poartă elasticitatea, casa fiindcă nu se leagă de
  // nimic: nu există run care s-o întindă. 12 × banda e un dreptunghi de casă
  // obișnuită; se trage de laturi ca la orice modul.
  izolat: { role: "standalone", front: 12, laturi: ["A"], rigid: true },
};

/** Rolul pe care-l scrie în document categoria aleasă în ecranul de pornire.
 *
 *  Harta categorie -> rol stă într-un singur loc, aici: importul din imagine
 *  are nevoie de ea la fel ca modulul gol, fiindcă și planșa citită se naște
 *  într-o categorie aleasă de om, iar importatorul n-o poate ghici din desen. */
export function rolulCategoriei(c: CategorieTronson): TronsonDoc["role"] {
  return FORMA_TIPICA[c].role;
}

/** DRUMUL INVERS: în ce categorie cade un rol.
 *
 *  Șase roluri, patru categorii — deci harta nu se poate întoarce din
 *  `FORMA_TIPICA`: «core» e un MIJLOC cu nucleu (rămășiță de pe vremea când
 *  singurul modul cu scară era la mijloc), iar colțul are două forme, L și T.
 *  Trebuie să spună exact ce spune `_CATEGORII` din `api/catalog.py`: serverul
 *  o folosește pentru cartonașul din colecție, ecranul pentru panoul modulului,
 *  iar două hărți care diferă ar pune același modul în două categorii, după
 *  ecran. O probă le ține laolaltă (`categorie.test.ts`). */
const CATEGORIA_ROLULUI: Record<TronsonDoc["role"], CategorieTronson> = {
  end: "capat",
  mid: "mijloc",
  core: "mijloc",
  "corner-L": "colt",
  "corner-T": "colt",
  standalone: "izolat",
};

export const categoriaRolului = (rol: TronsonDoc["role"]): CategorieTronson =>
  CATEGORIA_ROLULUI[rol] ?? "mijloc";

/** CATEGORIA SE POATE SCHIMBA ȘI DUPĂ CE MODULUL EXISTĂ.
 *
 *  Până acum se alegea o singură dată, în fereastra de pornire, și rămânea
 *  acolo: o planșă importată pe categoria greșită — iar din desen categoria nu
 *  se poate citi, deci o alege omul înainte să vadă rezultatul — nu se mai
 *  putea îndrepta decât desenând modulul din nou.
 *
 *  CE SE SCHIMBĂ E DOAR ROLUL. Categoria spune unde stă modulul în bară, nu ce
 *  e desenat în el, deci desenul nu se clintește. Numai că anvelopa se citește
 *  în trei cadre diferite, după rol (v. `doc::anvelopa`) — bara are axa
 *  coridorului la y = 0, casa izolată și colțul au originea în colțul lor —,
 *  așa că trecerea dintre ele ar muta conturul de sub ziduri cu o jumătate de
 *  bandă, fără ca cineva să fi tras de ceva. De-aia conturul se SCRIE înainte
 *  (`cuConturScris`): din clipa în care e poligon, el spune forma, și rolul nu
 *  mai are ce muta.
 *
 *  MODULUL GOL FACE INVERS. N-are ce păstra, iar un contur înghețat l-ar lăsa
 *  cu dreptunghiul categoriei de dinainte — un «colț» care se desenează ca o
 *  bară. Deci ia forma tipică a categoriei noi, exact cea pe care ar fi
 *  primit-o dacă se năștea în ea.
 *
 *  DOUĂ CURĂȚENII, amândouă cerute de probele catalogului
 *  (`layout/tronsoane.py::_check`): un colț nu se poate oglindi exact, iar
 *  cifrele de colț (latura măsurată, crestătura) n-au înțeles pe altceva decât
 *  un colț. Lăsate pe loc, ar fi fost un modul care nu se poate publica și
 *  nimeni n-ar fi aflat până la publicare. */
export function schimbaCategoria(doc: TronsonDoc,
                                 categorie: CategorieTronson): TronsonDoc {
  // Aceeași categorie: NU se rescrie rolul. Altfel un «mijloc cu nucleu»
  // (`core`) ales încă o dată «mijloc» ar coborî la `mid`, iar un colț în T ar
  // deveni unul în L — o pierdere pentru un gest care n-a cerut nimic.
  if (categoriaRolului(doc.role) === categorie) return doc;

  const desenat = (doc.plan?.walls?.length ?? 0) > 0
    || (doc.plan?.rooms?.length ?? 0) > 0
    || (doc.plan?.outline?.length ?? 0) >= 3;
  const d = clona(desenat ? cuConturScris(doc) : doc);
  d.role = rolulCategoriei(categorie);

  if (categorie === "colt") {
    // Un colț nu se oglindește exact — v. `_check`.
    if (d.mirror === "exact") d.mirror = "none";
  } else if (d.plan) {
    delete d.plan.measured_leg_a_m;
    delete d.plan.notch_m;
  }

  if (!desenat) {
    const f = FORMA_TIPICA[categorie];
    d.front = { ...d.front, nominal_m: f.front, min_m: f.front,
                max_m: f.front, rigid: true };
    if (categorie === "colt" && d.plan) {
      d.plan.measured_leg_a_m = f.front;
      d.plan.notch_m = r3(f.front - d.band_depth_m);
    }
  }
  return d;
}

/** UN TRONSON NOU, gol, cu forma tipică a categoriei lui.
 *
 *  `cod` e cel sub care se scrie în catalog — se verifică pe server, fiindcă
 *  unicitatea lui e a întregii platforme, nu a proiectului.
 *
 *  CRESTĂTURA COLȚULUI NU E O CIFRĂ LIBERĂ: e latura minus banda. Un colț e
 *  locul în care două bare de aceeași adâncime se întâlnesc, deci brațele lui au
 *  lățimea benzii, iar golul dinăuntru e chiar ce rămâne din pătrat după ele. Pe
 *  M5 asta dă 17,55 − 15,30 = 2,25, exact crestătura măsurată din planșă — deci
 *  regula nu e inventată aici, e citită din catalog. Latura de pornire e mai
 *  lungă (24 m) tocmai ca L-ul să se vadă a L: cu latura aproape cât banda,
 *  crestătura e o știrbitură, nu o aripă. */
export function tronsonNou(cod: string,
                           categorie: CategorieTronson): TronsonDoc {
  const f = FORMA_TIPICA[categorie];
  const front = f.front;
  const doc: TronsonDoc = {
    schema_version: "2.0.0",
    id: cod,
    kind: "tronson",
    role: f.role,
    band_depth_m: BANDA_CATALOG,
    front: {
      nominal_m: front,
      // Un modul nou pornește RIGID — nimic desenat încă nu poate absorbi o
      // întindere, iar `absorbers` numește camera care o înghite. Se lărgește
      // din panou după ce există camerele, sau singur când cineva trage de
      // contur (v. `cifreleDinContur`).
      min_m: front, max_m: front, rigid: true,
    },
    // Simetria e o afirmație despre ce E modulul, iar despre unul gol nu se
    // poate afirma că latura B e oglinda lui A.
    mirror: "none",
    core: null,
    sides: Object.fromEntries(f.laturi.map((s) => [s, {
      // O locuință declarată, fără nicio cameră în ea: câte camere are modulul
      // se află desenându-le, iar `flats` nu poate fi gol (schema cere cel
      // puțin una).
      flats: [{ camere: 1, kitchen: "open", rooms: {} }],
    }])),
    variants: [{ id: "b", outdoor: f.laturi.map(() => "none"),
                 useful_m2: [], default: true }],
    plan: { rooms: [], walls: [], doors: [], windows: [] },
  };
  if (categorie === "colt") {
    doc.plan!.measured_leg_a_m = front;
    doc.plan!.notch_m = r3(front - BANDA_CATALOG);
  }
  return doc;
}

/** CE PĂȚEȘTE CONTURUL LA UN GEST DE DESEN — cele două reguli, în ordinea lor.
 *
 *  Amândouă măsoară față de documentul de la ÎNCEPUTUL gestului (`baza`), și de
 *  aceea se pot chema de zeci de ori cât ține o tragere fără să adune de zeci de
 *  ori aceeași deplasare.
 *
 *    1. `conturUrmeazaZidurile` — un zid care EXISTA și s-a mutat trage latura
 *       de care ține după el. E regula «tragi fațada în afară, modulul crește».
 *    2. `conturCuprindeZidurileNoi` — un zid DESENAT ACUM dincolo de o latură o
 *       împinge ca s-o cuprindă. E regula care face ca un tronson nou, născut cu
 *       forma tipică a categoriei lui, să-și ia forma de la ce desenezi în el.
 *
 *  Ordinea contează: a doua se măsoară pe conturul deja mutat de prima, altfel
 *  cele două s-ar bate pe aceeași latură și ar câștiga ultima chemată.
 *
 *  Mobila merge înaintea amândurora, fiindcă ea urmărește camerele, nu conturul.
 *
 *  Stă AICI, nu în pagină, fiindcă are doi chemători care trebuie să facă
 *  exact același lucru: gestul de pe masă (`editor/Masa`) și propagarea unei
 *  schimbări pe celelalte niveluri ale soluției (`plan/nivelTronson.ts`). */
export const conturulDupaDesen = (baza: TronsonDoc, d: TronsonDoc): TronsonDoc =>
  conturCuprindeZidurileNoi(
    baza, conturUrmeazaZidurile(baza, obiecteleUrmeazaZidurile(baza, d)));

// ------------------------------------------------- podestul, ca formă ---
//
// PODESTUL ERA O CIFRĂ. Adâncimea lui, și atât; forma ieșea întotdeauna
// dreptunghiulară, între capetele a două rampe. Pe planșele cu fațadă oblică
// el nu e dreptunghi: urmează fațada și ocolește puțul liftului, cu cinci sau
// șase laturi. Cerut de arhitect (13.10.2026): «să suporte podesturi de
// shape-uri diferite… să adaug puncte, să le trag», și să se lipească de
// rampe când îl muți.
//
// FORMA SCRISĂ stă pe scară (`scara.podeste`), pe indicele podestului. Lipsă,
// se deduce mai departe din rampe — deci tot ce s-a desenat până acum rămâne
// neatins până când cineva chiar trage de un punct.

/** Cât de aproape de un colț de rampă se lipește un punct tras, în metri.
 *
 *  «Should try to snap to the stair runs like connect to them»: un podest care
 *  se oprește la doi centimetri de rampă lasă o crăpătură prin care se vede
 *  pardoseala, iar la execuție nimeni n-o observă. 0,12 e cât prinde mâna la
 *  mărirea de lucru, fără să înhațe un colț de la un metru. */
export const LIPIRE_PODEST_M = 0.12;

/** Colțurile rampelor unei scări, în coordonatele planului. */
export function colturileRampelor(f: PiesaDoc): Pt[] {
  const sc = f.scara;
  if (!sc) return [];
  const out: Pt[] = [];
  const h = sc.latime_m / 2;
  for (const r of sc.rampe) {
    const dx = r.la[0] - r.de[0], dy = r.la[1] - r.de[1];
    const L = Math.hypot(dx, dy) || 1;
    const nx = -dy / L, ny = dx / L;
    out.push([r.de[0] + nx * h, r.de[1] + ny * h],
             [r.la[0] + nx * h, r.la[1] + ny * h],
             [r.la[0] - nx * h, r.la[1] - ny * h],
             [r.de[0] - nx * h, r.de[1] - ny * h]);
  }
  return out;
}

/** Punctul tras, lipit de cel mai apropiat colț de rampă — dacă e destul de
 *  aproape. Altfel rămâne unde l-a dus mâna. */
export function lipestePePodest(f: PiesaDoc, p: Pt): Pt {
  let cel: Pt | null = null;
  let celD = LIPIRE_PODEST_M;
  for (const q of colturileRampelor(f)) {
    const d = Math.hypot(q[0] - p[0], q[1] - p[1]);
    if (d < celD) { celD = d; cel = q; }
  }
  return cel ? [r3(cel[0]), r3(cel[1])] : [r3(p[0]), r3(p[1])];
}

/** Forma podestului `k` — cea scrisă, ori cea dedusă din rampe. */
export function conturulPodestului(f: PiesaDoc, k: number): Pt[] {
  const sc = f.scara;
  if (!sc || k < 0 || k + 1 >= sc.rampe.length) return [];
  const scris = sc.podeste?.find((x) => x.k === k);
  if (scris && Array.isArray(scris.contur) && scris.contur.length >= 3) {
    return scris.contur.map((q) => [q[0], q[1]] as Pt);
  }
  const rampe = sc.rampe.map((r) => ({ de: r.de, la: r.la }));
  return partileScarii(rampe, sc.latime_m, sc.podest_m).podeste[k] ?? [];
}

/** Scrie forma podestului `k` pe piesa `i`. */
function cuPodestul(doc: TronsonDoc, i: number, k: number,
                    contur: Pt[]): TronsonDoc {
  const d = clona(doc);
  const f = d.plan?.furniture?.[i];
  if (!f?.scara) return doc;
  const altele = (f.scara.podeste ?? []).filter((x) => x.k !== k);
  f.scara.podeste = [...altele,
    { k, contur: contur.map((q) => [r3(q[0]), r3(q[1])] as [number, number]) }];
  if (!inJurulCutiei(f)) refaCutiaScarii(d, i);
  return d;
}

/** CÂT ȚINE PODESTUL de la o rampă la cealaltă: depărtarea dintre liniile lor
 *  de urcare, plus o lățime de rampă — adică de la marginea de afară a uneia
 *  până la marginea de afară a celeilalte.
 *
 *  STĂ AICI, nu în panou, fiindcă n-o mai citește numai panoul: același număr îl
 *  măsoară și vârful de podest tras pe planșă (`redimPodestul`). Măsurat în două
 *  locuri, cifra scrisă și vârful tras s-ar fi despărțit la prima îndreptare. */
export function lungimeaPodestului(
  sc: { rampe: { de: [number, number]; la: [number, number] }[];
        latime_m: number },
  k: number,
): number {
  const a = sc.rampe[k], b = sc.rampe[k + 1];
  if (!a || !b) return sc.latime_m;
  const dx = a.la[0] - a.de[0], dy = a.la[1] - a.de[1];
  const L = Math.hypot(dx, dy) || 1;
  const ux = dx / L, uy = dy / L;
  // PE UN SFERT DE TUR SE MĂSOARĂ PE LUNGUL RAMPEI, nu pe normala ei.
  //
  // Depărtarea dintre rampe se citea numai pe NORMALĂ, iar la o scară cotită
  // rampa a doua pleacă chiar din dreptul celei dintâi: normala dă zero, deci
  // câmpul arăta mereu `latime_m` oricât de mare era colțul — și orice cifră
  // scrisă în el muta rampa undeva unde nu era. Pe un colț, lungimea podestului
  // E cât ține el pe direcția de urcare.
  const bx = b.la[0] - b.de[0], by = b.la[1] - b.de[1];
  const Lb = Math.hypot(bx, by) || 1;
  if (Math.abs(ux * (bx / Lb) + uy * (by / Lb)) < 0.866) {
    return Math.abs((b.de[0] - a.la[0]) * ux + (b.de[1] - a.la[1]) * uy);
  }
  const d = Math.abs((b.de[0] - a.la[0]) * -uy + (b.de[1] - a.la[1]) * ux);
  return d + sc.latime_m;
}

/** REDIMENSIONEAZĂ PODESTUL DEDUS, tras de un vârf — ȘI ÎMPINGE RAMPELE.
 *
 *  DE CE NU SE SCRIE UN CONTUR. Un podest dedus n-are formă a lui: e chiar
 *  locul dintre două rampe (`ScaraDesen::podest`). Întins cu un contur scris,
 *  el s-ar DEZLIPI de rampele care îl definesc — ar rămâne un patrulater
 *  oarecare, lipit de nimic, iar rampele ar sta unde erau. Cerut de arhitect
 *  (21.10.2026): podestul lărgit DEPĂRTEAZĂ rampele.
 *
 *  CELE DOUĂ MĂSURI, și ce e fiecare pe geometrie:
 *
 *  * DE-A LATUL rampei, podestul ține de la marginea de afară a uneia până la a
 *    celeilalte — adică E depărtarea dintre rampe. Trasă, ea mută rampa a doua,
 *    întocmai ca cifra scrisă în panou.
 *  * PE LUNGUL rampei, dincolo de linia de întoarcere, e adâncimea lui
 *    (`podest_m`) — o măsură numai a podestului, care nu atinge nicio rampă.
 *    Numai pe o scară ÎN U: pe un sfert de tur podestul e încrucișarea celor
 *    două benzi, iar `podest_m` nu-l atinge (v. `ScaraDesen::podest`).
 *
 *  LINIA DE ÎNTOARCERE NU SE TRAGE. Ea stă unde se TERMINĂ rampele, iar
 *  lungimea unei rampe e trepte × adâncime (v. `schimbaScara`): trasă de-acolo,
 *  cifra care s-ar schimba e numărul de contratrepte, adică alt gest.
 *
 *  MUTĂ TOT RAMPA A DOUA, de pe oricare margine ai trage — ca în panou, unde
 *  lungimea scrisă mută tot rampa a doua. Podestul iese cât s-a cerut oricum;
 *  ce se alege apucând o margine sau alta e doar cifra, nu care rampă stă.
 *
 *  O SINGURĂ PUNERE ÎN PRACTICĂ, împărțită cu panoul: amândouă trec prin
 *  `schimbaScaraParte`, deci cifra scrisă și vârful tras nu se pot despărți. */
export function redimPodestul(
  doc: TronsonDoc, i: number, k: number, varf: number, spre: Pt,
): TronsonDoc {
  const f = doc.plan?.furniture?.[i];
  const sc = f?.scara;
  if (!sc) return doc;
  const a = sc.rampe[k], b = sc.rampe[k + 1];
  if (!a || !b) return doc;
  const c = conturulPodestului(f!, k);
  if (varf < 0 || varf >= c.length) return doc;

  // reperul rampei dintâi: `u` pe urcare, `n` de-a latul ei
  const dx = a.la[0] - a.de[0], dy = a.la[1] - a.de[1];
  const L = Math.hypot(dx, dy) || 1;
  const ux = dx / L, uy = dy / L, nx = -uy, ny = ux;
  const peU = (q: Pt) => q[0] * ux + q[1] * uy;
  const peN = (q: Pt) => q[0] * nx + q[1] * ny;
  const us = c.map(peU), ns = c.map(peN);
  const u1 = Math.max(...us);
  const n0 = Math.min(...ns), n1 = Math.max(...ns);
  const dU = peU(spre) - us[varf];
  const dN = peN(spre) - ns[varf];

  // ÎN U = rampele merg una împotriva celeilalte; numai atunci are podestul o
  // adâncime care să se poată trage.
  const bx = b.la[0] - b.de[0], by = b.la[1] - b.de[1];
  const Lb = Math.hypot(bx, by) || 1;
  const inU = ux * (bx / Lb) + uy * (by / Lb) < -0.866;

  const ce: { lung?: number; adanc?: number } = {};
  if (Math.abs(dN) > 1e-6) {
    // marginea de care s-a apucat hotărăște semnul: dusă spre afară, crește
    const peN1 = Math.abs(ns[varf] - n1) <= Math.abs(ns[varf] - n0);
    const lung = lungimeaPodestului(sc, k) + (peN1 ? dN : -dN);
    if (lung >= sc.latime_m) ce.lung = r3(lung);
  }
  if (inU && Math.abs(dU) > 1e-6 && Math.abs(us[varf] - u1) < 1e-6) {
    const adanc = (sc.podest_m ?? sc.latime_m) + dU;
    if (adanc > 0) ce.adanc = r3(adanc);
  }
  if (ce.lung == null && ce.adanc == null) return doc;
  return schimbaScaraParte(doc, i, "podest", k, ce);
}

/** LINIA DE ÎNTOARCERE a podestului `k`: acolo se TERMINĂ rampele pe care le
 *  leagă, măsurată pe direcția de urcare a rampei dintâi.
 *
 *  Aceeași socoteală pe care o face desenul ca să așeze podestul dedus
 *  (`ScaraDesen::podest`, `t0`). Capătul rampei și colțurile ei cad pe aceeași
 *  linie, fiindcă lățimea se întinde pe NORMALĂ, iar proiecția pe `u` nu se
 *  schimbă cu ea — de-aia ajung capetele, fără colțuri. */
function liniaIntoarcerii(
  sc: { rampe: { de: [number, number]; la: [number, number] }[] }, k: number,
): { ux: number; uy: number; t0: number } | null {
  const a = sc.rampe[k], b = sc.rampe[k + 1];
  if (!a || !b) return null;
  const dx = a.la[0] - a.de[0], dy = a.la[1] - a.de[1];
  const L = Math.hypot(dx, dy) || 1;
  const ux = dx / L, uy = dy / L;
  const peU = (q: readonly [number, number]) => q[0] * ux + q[1] * uy;
  return { ux, uy, t0: Math.max(peU(a.la), peU(b.de)) };
}

/** TRAGE UN PUNCT al podestului SCRIS: îl reformează, fără să-l dezlipească.
 *
 *  VÂRFURILE DE PE LINIA DE ÎNTOARCERE RĂMÂN PE EA. Acolo podestul se
 *  întâlnește cu rampele, iar un vârf dus de-acolo deschide o crăpătură între
 *  podest și rampă — pe planșă se vede pardoseala printre ele, iar la execuție
 *  nimeni n-o observă (chiar motivul pentru care punctele se lipeau de colțuri,
 *  v. `LIPIRE_PODEST_M`). Cerut de arhitect (21.10.2026): podestul reformat
 *  rămâne lipit de rampe. SE PLIMBĂ pe linie — latura dinspre rampe își poate
 *  schimba lungimea; ce nu poate e să plece de pe ea.
 *
 *  CELELALTE VÂRFURI RĂMÂN LIBERE, și tocmai ele sunt motivul formei scrise: un
 *  podest citit de pe planșă urmează fațada oblică și ocolește puțul liftului
 *  (v. `scara.podeste`), deci laturile lui dinspre afară nu sunt ale nimănui.
 *
 *  LIPIREA ÎNTÂI, ANCORAREA PE URMĂ. Invers, un colț de la capătul CELĂLALT al
 *  rampei — tot un colț, tot în prag — ar fi tras vârful de pe linia de
 *  întoarcere, adică exact ce păzim aici. */
export function mutaVarfulPodestului(doc: TronsonDoc, i: number, k: number,
                                     varf: number, spre: Pt): TronsonDoc {
  const f = doc.plan?.furniture?.[i];
  if (!f?.scara) return doc;
  const c = conturulPodestului(f, k);
  if (varf < 0 || varf >= c.length) return doc;
  let p = lipestePePodest(f, spre);
  const l = liniaIntoarcerii(f.scara, k);
  if (l) {
    const peU = (q: Pt) => q[0] * l.ux + q[1] * l.uy;
    if (Math.abs(peU(c[varf]) - l.t0) <= LIPIRE_PODEST_M) {
      const abatere = peU(p) - l.t0;
      p = [r3(p[0] - l.ux * abatere), r3(p[1] - l.uy * abatere)];
    }
  }
  c[varf] = p;
  return cuPodestul(doc, i, k, c);
}

/** VÂRFUL PODESTULUI, TRAS — și cele două lucruri pe care le poate însemna.
 *
 *  UN PODEST DEDUS N-ARE FORMĂ A LUI, are o MĂRIME: e dreptunghiul dintre
 *  rampe, deci un vârf tras nu-l reformează, îl REDIMENSIONEAZĂ — și mărimea
 *  lui e chiar depărtarea dintre rampe, deci le împinge (`redimPodestul`).
 *
 *  UN PODEST SCRIS are formă: o citire de pe planșă urmează fațada oblică și
 *  ocolește puțul liftului, cu cinci-șase laturi (v. `scara.podeste`). Acolo un
 *  vârf tras chiar reformează, iar ce trebuie păzit e lipirea de rampe
 *  (`mutaVarfulPodestului`).
 *
 *  REGULA STĂ AICI, NU ÎN PÂNZĂ: e aceeași deosebire pe care o face panoul ca
 *  să arate ori două cifre, ori «Înapoi la dreptunghi» (v. `Proprietati`), și
 *  pusă în desen ar fi fost a doua judecată despre același podest. */
export function tragePodestul(
  doc: TronsonDoc, i: number, k: number, varf: number, spre: Pt,
): TronsonDoc {
  const sc = doc.plan?.furniture?.[i]?.scara;
  const scris = sc?.podeste?.some(
    (x) => x.k === k && Array.isArray(x.contur) && x.contur.length >= 3);
  return scris
    ? mutaVarfulPodestului(doc, i, k, varf, spre)
    : redimPodestul(doc, i, k, varf, spre);
}

/** ADAUGĂ UN PUNCT pe mijlocul muchiei `muchie`. */
export function adaugaVarfPodest(doc: TronsonDoc, i: number, k: number,
                                 muchie: number): TronsonDoc {
  const f = doc.plan?.furniture?.[i];
  if (!f?.scara) return doc;
  const c = conturulPodestului(f, k);
  if (!c.length) return doc;
  const a = c[muchie % c.length], b = c[(muchie + 1) % c.length];
  c.splice(muchie + 1, 0, [r3((a[0] + b[0]) / 2), r3((a[1] + b[1]) / 2)]);
  return cuPodestul(doc, i, k, c);
}

/** SCOATE UN PUNCT. Sub trei nu mai e o formă, deci nu se scoate. */
export function scoateVarfPodest(doc: TronsonDoc, i: number, k: number,
                                 varf: number): TronsonDoc {
  const f = doc.plan?.furniture?.[i];
  if (!f?.scara) return doc;
  const c = conturulPodestului(f, k);
  if (c.length <= 3 || varf < 0 || varf >= c.length) return doc;
  c.splice(varf, 1);
  return cuPodestul(doc, i, k, c);
}

/** MUTĂ PODESTUL ÎNTREG. Se lipește cu colțul care ajunge cel mai aproape de
 *  o rampă — altfel, mutat cu mâna, ar rămâne mereu la câțiva milimetri. */
export function mutaPodestul(doc: TronsonDoc, i: number, k: number,
                             dep: Pt): TronsonDoc {
  const f = doc.plan?.furniture?.[i];
  if (!f?.scara || (!dep[0] && !dep[1])) return doc;
  const c = conturulPodestului(f, k);
  if (!c.length) return doc;
  const mutat = c.map((q) => [q[0] + dep[0], q[1] + dep[1]] as Pt);
  // lipirea se face pe TOT podestul, nu pe fiecare punct: altfel forma s-ar
  // strâmba la fiecare mutare, trăgând numai colțurile care au prins ceva
  let cel: Pt | null = null;
  let celD = LIPIRE_PODEST_M;
  for (const q of mutat) {
    const l = lipestePePodest(f, q);
    const d = Math.hypot(l[0] - q[0], l[1] - q[1]);
    if (d > 1e-9 && d < celD) { celD = d; cel = [l[0] - q[0], l[1] - q[1]]; }
  }
  const fin = cel
    ? mutat.map((q) => [q[0] + cel![0], q[1] + cel![1]] as Pt) : mutat;
  return cuPodestul(doc, i, k, fin);
}
